"use client";

import { textDirection } from "@webdo/i18n";
import { createContext, FormEvent, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import {
    SHOP_COMMERCE_RUNTIME_ROLE_OPTION,
    SHOP_COMMERCE_RUNTIME_ROLES,
} from "../commerceRuntime";
import {
    siteBuilderBlockDelegatesOutlineToColumn,
    siteBuilderEditorBlockAttributes,
    siteBuilderEditorRootPath,
} from "../builderInstrumentation";
import {
    shopEmbedSource,
    shopMapEmbedSource,
    siteVideoSource,
} from "../embedSources";
import {
    shopAvatarInitials,
    shopPaymentBrand,
    shopPaymentBrandGlyph,
    shopPortableAspectRatio,
    shopPortableProgressLabelPosition,
} from "../portableBlocks";
import { morrowSemanticThemeCss } from "../semanticThemeCss";
import {
    siteBuilderAuthoredFormFields,
    siteBuilderFormConfig,
} from "../siteBuilderForm";
import type { SiteBuilderAuthoredFormField } from "../siteBuilderForm";
import {
    siteBuilderLeadValuesFromFormData,
    submitSiteBuilderLead,
} from "../siteBuilderLeadRuntime";
import {
    SITE_BUILDER_AUTHORITATIVE_STYLE_CSS,
    siteBuilderBlockStyle,
    siteBuilderPartOptionKey,
    siteBuilderPartStyle,
    siteBuilderRebasedAuthoritativeStyleVariables,
} from "../siteBuilderStyles";
import type { SiteBuilderStyleRecord } from "../siteBuilderStyles";
import { siteBuilderMediaPresentation } from "../siteBuilderBlockPresentation";
import { useSiteBuilderCountdown } from "../siteBuilderCountdown";
import { SiteBuilderShareAnchor } from "../SiteBuilderShareAnchor";
import { navigationFocusableElements } from "../navigationFocus";
import { SiteNavigation } from "../SiteNavigationView";
import type { SiteNavigationRenderLink } from "../SiteNavigationView";
import {
    SITE_NAVIGATION_MOBILE_BREAKPOINT_PX,
    resolveSiteNavigationSettings,
    resolveSiteNavigationThemeTokens,
    siteNavigationThemeStyle,
} from "../siteNavigation";
import { siteBuilderShareNetwork } from "../siteBuilderShare";
import { useSiteBuilderLeadForm } from "../useSiteBuilderLeadForm";
import { SHOP_GRID_CELL_GAP, SHOP_GRID_CELL_HEIGHT, SHOP_GRID_SLOT_KEY, isShopDynamicEntityField } from "../shopBlocks";
import type { ShopDynamicEntityField, ShopDynamicEntityType } from "../shopBlocks";
import type {
    PublicShopMenuItem,
    ShopRichTextSegment,
    ShopTemplateBlock,
    SiteLeadFormField,
} from "../types";
import {
    CART_ITEM_MAX_QUANTITY,
    catalogProductsFromContext,
    checkoutFormValidationMessageKey,
    contextData,
    customerErrorTextFromResponse,
    functionalStorefrontHref,
    homeProductsFromContext,
    menuItemHref,
    moneyAmount,
    nestedRecord,
    productHref,
    productImagesFromData,
    productSurchargeParametersFromData,
    productVariantsFromData,
    productsFromValue,
    storefrontLocale,
    storefrontPluralCategory,
    stringOrNull,
    textValue,
} from "./shopStorefrontCore";
import type {
    CheckoutFormState,
    PickupPointSelection,
    PublicCheckoutDeliveryOption,
    PublicCustomer,
    PublicCustomerAddress,
    PublicCustomerAddressListResponse,
    PublicCustomerAddressType,
    PublicHomeProduct,
    PublicOrderCreated,
    PublicProductImage,
    PublicProductSurchargeParameter,
    PublicProductVariant,
    ShopStorefrontPreviewAdapter,
} from "./shopStorefrontCore";
import { ensurePacketaWidget, packetaPointDisplayName } from "./packetaWidget";
import { ShopStorefrontLink, ShopStorefrontRuntime } from "./shopStorefrontRuntime";
import type { ShopStorefrontRuntimeModel, ShopStorefrontRuntimeProps } from "./shopStorefrontRuntime";
import {
    ManufacturingCustomizationFields,
    ManufacturingSpecificationSummary,
    useManufacturingCustomization,
} from "./ShopManufacturingCustomization";
import {
    MORROW_INTERACTION_CSS,
    morrowInteractionAttributes,
    morrowInteractionStyle,
} from "./morrowInteractions";

import {
    MORROW_FOOTER_LAYOUT_PLACEMENTS,
    MORROW_FOOTER_SKELETON_VERSION,
    normalizeMorrowFooterLayoutStructure,
    normalizeMorrowFooterLayoutStyle,
} from "./morrowFooter";
import type { MorrowFooterGridPlacement, MorrowFooterLayoutStyle } from "./morrowFooter";
import {
    CATALOG_PRODUCT_RIBBONS,
    catalogProductImageTemplate,
    catalogProductPageConfig,
    catalogProductSort,
    catalogProductTemplate,
    normalizeCatalogProductPageStructure,
} from "../catalogProductPage";
import type {
    CatalogCategoryStyle,
    CatalogProductRibbon,
} from "../catalogProductPage";
import {
    morrowAtomicMediaPartStyle,
    morrowEditorMediaPlaceholderSource,
} from "./morrowAtomicMediaStyle";
import { morrowButtonLeafStyle } from "./morrowButtonStyle";
import {
    MorrowCountrySelect,
    MORROW_COUNTRY_SELECT_CSS,
} from "./MorrowCountrySelect";
import { morrowFooterPageHref, morrowFooterPageTemplateKey } from "./morrowPageLinks";
import {
    orderMorrowRenderBlocks,
    withMorrowHeroMediaFallback,
} from "./morrowPresentation";
import {
    findShopHeaderRoleBlock,
    findShopHeaderRoot,
    resolveShopHeaderAlignment,
    resolveShopHeaderLogoDisplay,
    resolveShopHeaderUtilityDisplay,
    shopHeaderBlockVisibleOn,
    shopHeaderRole,
} from "./shopHeader";
import {
    CATALOG_SALE_FACET_CODE,
    activeCatalogFilterCount,
    catalogFacetSelectedCount,
    catalogFacetsFromContext,
    catalogFacetsWithSale,
    catalogFiltersFromContext,
    catalogHref,
    catalogPaginationFromContext,
    catalogPaginationHref,
    catalogQueryFromContext,
    catalogSortFromContext,
    clearCatalogFiltersHref,
    toggleCatalogFilter,
    visibleCatalogFacetOptions,
} from "./shopCatalogFilters";
import type {
    CatalogFilterState,
    CatalogSortValue,
    PublicCatalogFacet,
} from "./shopCatalogFilters";
import {
    SHOP_SEARCH_MIN_QUERY_LENGTH,
    normalizeShopSearchScope,
    normalizeShopSearchSuggestionLimit,
    parseShopSearchSuggestions,
    selectShopSearchSuggestions,
    shopSearchCategoryHref,
    shopSearchProductHref,
    shopSearchResultsHref,
    shopSearchScopeUsesCatalog,
} from "./shopSearchSuggestions";
import type {
    ShopSearchScope,
    ShopSearchSuggestionsPayload,
} from "./shopSearchSuggestions";

const MORROW_PRODUCT_IMAGE = "/templates/morrow/assets/open-market-product.webp";
const MORROW_EDITORIAL_IMAGE = "/templates/morrow/assets/open-market-editorial.webp";
const MORROW_MOBILE_NAVIGATION_DIALOG_ID = "morrow-mobile-navigation-dialog";

function morrowProductCount(runtime: ShopStorefrontRuntimeModel, count: number): string {
    const category = storefrontPluralCategory(count, runtime.storefrontLanguageCode);
    const key = `ui.catalog.productCount.${category}` as const;

    return runtime.storefrontMessage(key, { count });
}

const morrowPreviewAdapter: ShopStorefrontPreviewAdapter = {
    currentProductFromContext(context, formatMoney, storefrontHref) {
        const product = nestedRecord(contextData(context), "product");
        const id = stringOrNull(product.uuid);
        const name = stringOrNull(product.name);
        const primaryImage = productImagesFromData(product)[0] ?? null;

        if (!id || !name) return null;

        return {
            id,
            name,
            href: storefrontHref(stringOrNull(product.slug) ? `/${stringOrNull(product.slug)}` : context?.canonicalHref ?? "/"),
            price: formatMoney(stringOrNull(product.price) ?? "0"),
            image: primaryImage?.thumbnailUrl ?? primaryImage?.url ?? null,
            alt: primaryImage?.alt || name,
            brand: stringOrNull(product.manufacturerName),
            unavailable: product.availableForOrder === false,
        };
    },
    productsForIds(ids, previews) {
        return Array.from(ids).flatMap((id) => previews[id] ? [previews[id]] : []);
    },
    productsFromContext(context, formatMoney, storefrontHref) {
        const data = contextData(context);
        const values = [
            ...productsFromValue(data.products),
            ...productsFromValue(nestedRecord(data, "catalog").products),
        ];
        const unique = new Map(values.map((product) => [product.uuid, product]));

        return Array.from(unique.values()).map((product) => ({
            id: product.uuid,
            name: product.name,
            href: productHref(product) ? storefrontHref(productHref(product)) : "",
            price: formatMoney(product.price),
            image: product.primaryImage?.thumbnailUrl ?? product.primaryImage?.url ?? null,
            alt: product.primaryImage?.alt || product.name,
            brand: product.manufacturerName,
            unavailable: !product.availableForOrder,
        }));
    },
};

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function contentText(block: ShopTemplateBlock, key: string, fallback = ""): string {
    const value = record(block.content)[key];

    return typeof value === "string" ? value : fallback;
}

function richTextMarkStyle(segment: ShopRichTextSegment): CSSProperties {
    const marks = segment.marks ?? {};

    return {
        ...(marks.fontFamily ? { fontFamily: marks.fontFamily } : {}),
        ...(marks.fontSize ? { fontSize: marks.fontSize } : {}),
        ...(marks.fontStyle ? { fontStyle: marks.fontStyle } : {}),
        ...(marks.fontWeight ? { fontWeight: marks.fontWeight } : {}),
        ...(marks.lineHeight ? { lineHeight: marks.lineHeight } : {}),
        ...(marks.textAlign ? { textAlign: marks.textAlign as CSSProperties["textAlign"] } : {}),
        ...(marks.textColor ? { color: marks.textColor } : {}),
        ...(marks.textDecoration ? { textDecoration: marks.textDecoration } : {}),
    };
}

function blockRichTextSegments(block: ShopTemplateBlock, key: string): ShopRichTextSegment[] {
    const value = block.richText?.[key];

    if (!Array.isArray(value)) {
        return [];
    }

    const segments = value.filter((segment): segment is ShopRichTextSegment => (
        segment !== null
        && typeof segment === "object"
        && typeof (segment as ShopRichTextSegment).text === "string"
    ));

    return segments;
}

function richTextContent(block: ShopTemplateBlock, key: string, fallback: string): ReactNode {
    const segments = blockRichTextSegments(block, key);

    if (segments.length === 0 || segments.map((segment) => segment.text).join("") !== fallback) {
        return fallback;
    }

    return segments.map((segment, index) => (
        <span key={`${key}:${index}`} style={richTextMarkStyle(segment)}>
            {segment.text}
        </span>
    ));
}

function richTextLineContents(
    block: ShopTemplateBlock,
    key: string,
    fallback: string,
): Array<{ content: ReactNode; text: string }> {
    const lines: ShopRichTextSegment[][] = [[]];
    const storedSegments = blockRichTextSegments(block, key);
    const segments = storedSegments.length > 0
        && storedSegments.map((segment) => segment.text).join("") === fallback
        ? storedSegments
        : [{ text: fallback }];

    segments.forEach((segment) => {
        segment.text.split(/\r?\n/).forEach((text, index) => {
            if (index > 0) {
                lines.push([]);
            }

            if (text !== "") {
                lines[lines.length - 1]?.push({
                    ...segment,
                    text,
                });
            }
        });
    });

    return lines
        .map((segments, lineIndex) => ({
            content: segments.map((segment, segmentIndex) => (
                <span key={`${key}:${lineIndex}:${segmentIndex}`} style={richTextMarkStyle(segment)}>
                    {segment.text}
                </span>
            )),
            text: segments.map((segment) => segment.text).join(""),
        }))
        .filter((line) => line.text.trim() !== "");
}

function catalogTextAttributes(
    entityType: ShopDynamicEntityType,
    entityUuid: string,
    source: ShopDynamicEntityField,
    part: string,
) {
    return {
        contentEditable: false as const,
        "data-shop-block-part": part,
        "data-shop-catalog-entity-type": entityType,
        "data-shop-catalog-entity-uuid": entityUuid,
        "data-shop-catalog-source": source,
        "data-shop-readonly-text": "true" as const,
    };
}

function optionNumber(block: ShopTemplateBlock, key: string, fallback: number): number {
    const rawValue = record(block.options)[key];
    const value = typeof rawValue === "string" ? Number.parseFloat(rawValue) : Number(rawValue);

    return Number.isFinite(value) ? value : fallback;
}

function optionText(block: ShopTemplateBlock, key: string): string {
    const value = record(block.options)[key];

    return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

function mobileStackOption(block: ShopTemplateBlock | null | undefined): boolean | null {
    const value = record(block?.options).stackOnMobile;

    if (value === true || value === "true" || value === 1 || value === "1") return true;
    if (value === false || value === "false" || value === 0 || value === "0") return false;

    return null;
}

function hasPositiveOption(block: ShopTemplateBlock | null | undefined, key: string): boolean {
    const value = record(block?.options)[key];

    return Number.isFinite(Number(value)) && Number(value) > 0;
}

function hasGridLengthOption(block: ShopTemplateBlock | null | undefined, key: string): boolean {
    const rawValue = record(block?.options)[key];

    if (typeof rawValue === "number") return Number.isFinite(rawValue) && rawValue > 0;
    if (typeof rawValue !== "string") return false;

    const value = rawValue.trim();
    const numericMatch = value.match(/^(\d+(?:\.\d+)?)(?:px)?$/iu);

    return numericMatch !== null && Number(numericMatch[1]) > 0;
}

function optionCssLength(block: ShopTemplateBlock, key: string, fallback: string): string {
    const value = (optionText(block, key) || fallback).replace(/\s+(px|%|rem|em|vh|vw)$/iu, "$1");

    return /^-?\d+(?:\.\d+)?$/u.test(value) ? `${value}px` : value;
}

function gridPixelHeight(rows: number, rowHeight: string, rowGap: number): number | undefined {
    const match = rowHeight.trim().match(/^(\d+(?:\.\d+)?)px$/iu);

    if (!match) return undefined;

    return (rows * Number(match[1])) + (Math.max(0, rows - 1) * rowGap);
}

function authoredGridPlacementAxes(block: ShopTemplateBlock | null | undefined): {
    column: boolean;
    row: boolean;
} {
    return {
        column: hasPositiveOption(block, "gridX") || hasPositiveOption(block, "gridW"),
        row: hasPositiveOption(block, "gridY") || hasPositiveOption(block, "gridH"),
    };
}

function findNestedBlock(
    blocks: ShopTemplateBlock[],
    predicate: (block: ShopTemplateBlock) => boolean,
): ShopTemplateBlock | undefined {
    for (const block of blocks) {
        if (predicate(block)) return block;

        for (const children of Object.values(block.slots ?? {})) {
            const nested = findNestedBlock(children, predicate);
            if (nested) return nested;
        }
    }

    return undefined;
}

const visualOptionKeys = [
    "alignContent",
    "alignItems",
    "backgroundColor",
    "backgroundImage",
    "backdropFilter",
    "borderColor",
    "borderRadius",
    "borderStyle",
    "borderWidth",
    "boxShadow",
    "color",
    "columnGap",
    "display",
    "fontFamily",
    "fontSize",
    "fontStyle",
    "fontWeight",
    "gap",
    "height",
    "justifyContent",
    "justifyItems",
    "letterSpacing",
    "lineHeight",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "marginTop",
    "maxWidth",
    "minHeight",
    "minWidth",
    "opacity",
    "overflow",
    "padding",
    "paddingBottom",
    "paddingLeft",
    "paddingRight",
    "paddingTop",
    "rowGap",
    "textAlign",
    "textColor",
    "textDecoration",
    "textTransform",
    "transform",
    "width",
    "zIndex",
] as const;

const visualTokenValues: Partial<Record<(typeof visualOptionKeys)[number], Record<string, string | number>>> = {
    borderRadius: {
        lg: "18px",
        md: "12px",
        none: "0",
        pill: "9999px",
        sm: "8px",
        xl: "28px",
    },
    boxShadow: {
        glow: "0 0 0 1px color-mix(in srgb, var(--site-primary) 24%, transparent), 0 16px 44px color-mix(in srgb, var(--site-primary) 22%, transparent)",
        medium: "0 16px 38px var(--site-shadow-color)",
        none: "none",
        soft: "0 10px 28px color-mix(in srgb, var(--site-shadow-color) 72%, transparent)",
        strong: "0 24px 64px color-mix(in srgb, var(--site-shadow-color) 88%, var(--site-text))",
    },
    padding: {
        lg: "28px",
        md: "20px",
        none: "0",
        sm: "12px",
        xl: "40px",
        xs: "8px",
    },
};

const morrowLegacyThemeColors: Record<string, string> = {
    "#DBE8D2": "var(--site-primary-soft)",
    "#EDE0F5": "var(--site-accent-soft)",
    "#F6F2E9": "var(--site-background)",
    "rgba(21,35,31,.35)": "color-mix(in srgb, var(--site-inverse) 35%, transparent)",
    "rgba(246,242,233,.25)": "color-mix(in srgb, var(--site-on-inverse) 25%, transparent)",
    "rgba(255,255,255,.08)": "color-mix(in srgb, var(--site-on-inverse) 8%, transparent)",
    "rgba(255,255,255,.18)": "color-mix(in srgb, var(--site-on-inverse) 18%, transparent)",
};

function morrowThemeColor(value: string): string {
    return morrowLegacyThemeColors[value] ?? value;
}

function normalizedVisualValue(
    key: (typeof visualOptionKeys)[number],
    value: string | number,
): string | number {
    if (typeof value !== "string") return value;

    if (key === "backgroundColor" || key === "borderColor" || key === "color" || key === "textColor") {
        return morrowThemeColor(value);
    }

    return visualTokenValues[key]?.[value] ?? value;
}

function optionStyle(block: ShopTemplateBlock, part?: string): CSSProperties {
    const options = record(block.options);
    const sharedStyle = part ? siteBuilderPartStyle(block, part) : {};
    const style: Record<string, string | number> = {};

    for (const key of visualOptionKeys) {
        const optionKey = siteBuilderPartOptionKey(part, key);
        const value = options[optionKey];

        if (typeof value === "string" || typeof value === "number") {
            style[key === "textColor" ? "color" : key] = normalizedVisualValue(key, value);
        }
    }

    if (style.borderWidth && style.borderWidth !== "0" && style.borderWidth !== "0px" && style.borderStyle === undefined) {
        style.borderStyle = "solid";
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...sharedStyle,
        ...style,
        ...(sharedStyle.backgroundImage ? {
            backgroundImage: sharedStyle.backgroundImage,
            backgroundPosition: sharedStyle.backgroundPosition,
            backgroundRepeat: sharedStyle.backgroundRepeat,
            backgroundSize: sharedStyle.backgroundSize,
        } : {}),
    };

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(sharedStyle, resolvedStyle),
    };
}

function visualStyle(block: ShopTemplateBlock): SiteBuilderStyleRecord {
    const sharedStyle = siteBuilderBlockStyle(block);
    const templateStyle = optionStyle(block);

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...sharedStyle,
        ...templateStyle,
        ...(sharedStyle.backgroundImage ? {
            backgroundImage: sharedStyle.backgroundImage,
            backgroundPosition: sharedStyle.backgroundPosition,
            backgroundRepeat: sharedStyle.backgroundRepeat,
            backgroundSize: sharedStyle.backgroundSize,
        } : {}),
    };

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(sharedStyle, resolvedStyle),
    };
}

function morrowRegionVisualStyle(block: ShopTemplateBlock, region: string): SiteBuilderStyleRecord {
    const style = visualStyle(block);

    if (region !== "footer") {
        return style;
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...style,
        backgroundColor: style.backgroundColor === "var(--site-text)"
            ? "var(--site-inverse)"
            : style.backgroundColor,
        color: style.color === "var(--site-background)"
            ? "var(--site-on-inverse)"
            : style.color,
    };

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(style, resolvedStyle),
    };
}

function partVisualStyle(block: ShopTemplateBlock, part: string): CSSProperties {
    return {
        ...visualStyle(block),
        ...optionStyle(block, part),
        ...(morrowInteractionStyle(block, part) ?? morrowInteractionStyle(block)),
    };
}

type MorrowButtonSize = "large" | "medium" | "small";

const morrowButtonSizeStyles: Record<MorrowButtonSize, CSSProperties> = {
    large: { fontSize: "1rem", minHeight: "3.25rem", padding: "1rem 2rem" },
    medium: { fontSize: ".875rem", minHeight: "2.75rem", padding: ".75rem 1.5rem" },
    small: { fontSize: ".8125rem", minHeight: "2.25rem", padding: ".5rem 1rem" },
};

function isMorrowButtonSize(value: string): value is MorrowButtonSize {
    return value === "small" || value === "medium" || value === "large";
}

function buttonPartStyle(block: ShopTemplateBlock, part: string, inheritRoot = true): CSSProperties {
    const preset = optionText(block, `${part}ButtonPreset`) || optionText(block, "buttonPreset");
    const sizeValue = optionText(block, `${part}Size`) || optionText(block, "size") || "small";
    const alignment = optionText(block, `${part}Alignment`) || optionText(block, "alignment") || "center";
    const presetStyle: CSSProperties = preset === "primary"
        ? {
            backgroundColor: "var(--site-primary)",
            borderColor: "var(--site-primary)",
            borderStyle: "solid",
            borderWidth: "1px",
            color: "var(--site-on-primary)",
        }
        : preset === "secondary"
            ? {
                backgroundColor: "transparent",
                borderColor: "var(--site-border)",
                borderStyle: "solid",
                borderWidth: "1px",
                color: "var(--site-text)",
            }
            : {};
    const alignmentStyle: CSSProperties = alignment === "left"
        ? { justifyContent: "flex-start", textAlign: "left" }
        : alignment === "right"
            ? { justifyContent: "flex-end", textAlign: "right" }
            : { justifyContent: "center", textAlign: "center" };

    return morrowButtonLeafStyle({
        ...presetStyle,
        ...(isMorrowButtonSize(sizeValue) ? morrowButtonSizeStyles[sizeValue] : {}),
        ...alignmentStyle,
        ...(inheritRoot ? visualStyle(block) : {}),
        ...optionStyle(block, part),
        ...(morrowInteractionStyle(block, part) ?? morrowInteractionStyle(block)),
    });
}

function imageShapeStyle(shape: string): CSSProperties {
    if (shape === "circle") return { aspectRatio: "1", borderRadius: "50%" };
    if (shape === "pill") return { borderRadius: "9999px" };
    if (shape === "rounded") return { borderRadius: "24px" };
    if (shape === "blob") return { borderRadius: "58% 42% 61% 39% / 46% 58% 42% 54%" };
    if (shape === "star") return { clipPath: "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 94%,50% 72%,21% 94%,32% 57%,2% 35%,39% 35%)" };

    return {};
}

function imagePartStyle(block: ShopTemplateBlock, part: string, inheritRoot = true): CSSProperties {
    const objectFit = optionText(block, `${part}ObjectFit`) || optionText(block, "objectFit") || "cover";
    const objectPosition = optionText(block, `${part}ObjectPosition`) || optionText(block, "objectPosition") || "center";
    const alignment = optionText(block, `${part}Alignment`);
    const stretch = optionText(block, `${part}Stretch`) !== "false";
    const shape = optionText(block, `${part}Shape`);
    const baseStyle = inheritRoot ? partVisualStyle(block, part) : optionStyle(block, part);

    return morrowAtomicMediaPartStyle(block, part, {
        ...baseStyle,
        ...imageShapeStyle(shape),
        display: "block",
        marginLeft: alignment === "right" || alignment === "center" ? "auto" : undefined,
        marginRight: alignment === "center" ? "auto" : undefined,
        maxWidth: "100%",
        objectFit: objectFit as CSSProperties["objectFit"],
        objectPosition,
        width: stretch ? "100%" : baseStyle.width || "auto",
    });
}

function columnShapeStyle(block: ShopTemplateBlock): CSSProperties {
    if (optionText(block, "backgroundImage")) return {};

    const shape = optionText(block, "shape");
    const shapeColor = optionText(block, "shapeColor") || "color-mix(in srgb, var(--site-primary) 14%, transparent)";

    if (shape === "splitVertical") return { backgroundImage: `linear-gradient(90deg, transparent 0 50%, ${shapeColor} 50% 100%)` };
    if (shape === "splitHorizontal") return { backgroundImage: `linear-gradient(180deg, transparent 0 50%, ${shapeColor} 50% 100%)` };
    if (shape === "diagonalWash") return { backgroundImage: `linear-gradient(135deg, transparent 0 42%, ${shapeColor} 42% 100%)` };
    if (shape === "cornerWash") return { backgroundImage: `radial-gradient(circle at 100% 0%, ${shapeColor} 0 34%, transparent 35% 100%)` };

    return {};
}

function gridPlacementStyle(
    x: number,
    y: number,
    w: number,
    h: number,
    zIndex?: number,
): CSSProperties {
    const gridColumn = `${Math.max(1, x)} / span ${Math.max(1, w)}`;
    const gridRow = `${Math.max(1, y)} / span ${Math.max(1, h)}`;

    return {
        gridColumn,
        gridRow,
        minHeight: 0,
        minWidth: 0,
        zIndex: zIndex || undefined,
    } as CSSProperties;
}

function withAuthoredGridPlacement(
    block: ShopTemplateBlock,
    style: CSSProperties,
): CSSProperties {
    const authoredAxes = authoredGridPlacementAxes(block);

    return {
        ...style,
        "--site-builder-authored-grid-column": authoredAxes.column
            ? String(style.gridColumn)
            : undefined,
        "--site-builder-authored-grid-row": authoredAxes.row
            ? String(style.gridRow)
            : undefined,
    } as CSSProperties;
}

function gridStyle(block: ShopTemplateBlock): CSSProperties {
    return withAuthoredGridPlacement(block, gridPlacementStyle(
        optionNumber(block, "gridX", 1),
        optionNumber(block, "gridY", 1),
        optionNumber(block, "gridW", 2),
        optionNumber(block, "gridH", 1),
        optionNumber(block, "zIndex", 0),
    ));
}

function footerLayoutStyle(block: ShopTemplateBlock): MorrowFooterLayoutStyle {
    return normalizeMorrowFooterLayoutStyle(optionText(block, "footerLayoutStyle"));
}

function footerGridPlacement(container: ShopTemplateBlock, child: ShopTemplateBlock): MorrowFooterGridPlacement | null {
    const layout = footerLayoutStyle(container);

    if (
        layout === "editorial"
        || optionText(container, "footerLayoutSkeletonVersion") === MORROW_FOOTER_SKELETON_VERSION
    ) {
        return null;
    }

    const role = optionText(child, "documentRole");
    const placements: Record<string, MorrowFooterGridPlacement> = MORROW_FOOTER_LAYOUT_PLACEMENTS[layout];

    return placements[role] ?? null;
}

function regionGridStyle(region: string, container: ShopTemplateBlock, child: ShopTemplateBlock): CSSProperties {
    const placement = region === "footer" ? footerGridPlacement(container, child) : null;

    if (!placement) return gridStyle(child);

    return withAuthoredGridPlacement(child, gridPlacementStyle(
        placement.x,
        placement.y,
        placement.w,
        placement.h,
        optionNumber(child, "zIndex", 0),
    ));
}

function gridRows(items: ShopTemplateBlock[]): number {
    return Math.max(1, ...items.map((block) => (
        Math.max(1, optionNumber(block, "gridY", 1)) + Math.max(1, optionNumber(block, "gridH", 1)) - 1
    )));
}

function regionGridRows(region: string, container: ShopTemplateBlock, items: ShopTemplateBlock[]): number {
    if (region !== "footer") return gridRows(items);

    return Math.max(1, ...items.map((child) => {
        const placement = footerGridPlacement(container, child);

        return placement
            ? placement.y + placement.h - 1
            : Math.max(1, optionNumber(child, "gridY", 1)) + Math.max(1, optionNumber(child, "gridH", 1)) - 1;
    }));
}

function morrowIconSize(block: ShopTemplateBlock): number {
    const rawSize = optionText(block, "size") || optionText(block, "iconSize");
    const namedSizes: Record<string, number> = {
        "1x": 16,
        "2x": 20,
        "3x": 24,
        "4x": 32,
        "5x": 40,
        lg: 34,
        md: 28,
        sm: 22,
        xl: 42,
        xs: 18,
    };

    if (namedSizes[rawSize]) return namedSizes[rawSize];

    const parsed = Number(rawSize.replace("px", ""));

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 28;
}

function MorrowIcon({ name, size = 24 }: { name: string; size?: number }) {
    const normalizedName = name.replace(/^(lucide|social):/i, "").trim().toLowerCase();
    const common = {
        fill: "none",
        height: size,
        stroke: "currentColor",
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
        strokeWidth: 1.8,
        viewBox: "0 0 24 24",
        width: size,
        "aria-hidden": true,
    };

    if (["bag", "cart", "shoppingbag"].includes(normalizedName)) {
        return <svg {...common}><path d="M6 8h12l1 12H5L6 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>;
    }
    if (["search", "magnifier"].includes(normalizedName)) {
        return <svg {...common}><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>;
    }
    if (["heart", "favorite"].includes(normalizedName)) {
        return <svg {...common}><path d="M20.8 8.7c0 5-8.8 10.2-8.8 10.2S3.2 13.7 3.2 8.7A4.4 4.4 0 0 1 12 7.9a4.4 4.4 0 0 1 8.8.8Z"/></svg>;
    }
    if (["return", "refresh", "rotateccw"].includes(normalizedName)) {
        return <svg {...common}><path d="M4 8h11a5 5 0 1 1 0 10H9"/><path d="m8 4-4 4 4 4"/></svg>;
    }
    if (["mail", "email"].includes(normalizedName)) {
        return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/></svg>;
    }
    if (["user", "account"].includes(normalizedName)) {
        return <svg {...common}><circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0 1 14 0"/></svg>;
    }
    if (["edit", "pencil"].includes(normalizedName)) {
        return <svg {...common}><path d="M4 20h4l11-11-4-4L4 16v4Z"/><path d="m13.5 6.5 4 4"/></svg>;
    }
    if (["package", "box", "order"].includes(normalizedName)) {
        return <svg {...common}><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="M4 7v10l8 4 8-4V7M12 11v10"/></svg>;
    }
    if (["home", "house"].includes(normalizedName)) {
        return <svg {...common}><path d="m3 11 9-8 9 8"/><path d="M5 10v10h14V10M9 20v-6h6v6"/></svg>;
    }
    if (["briefcase", "work"].includes(normalizedName)) {
        return <svg {...common}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V4h8v3M3 12h18M10 12v2h4v-2"/></svg>;
    }
    if (["settings", "gear"].includes(normalizedName)) {
        return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></svg>;
    }
    if (["lock", "security"].includes(normalizedName)) {
        return <svg {...common}><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3"/></svg>;
    }
    if (["logout", "signout"].includes(normalizedName)) {
        return <svg {...common}><path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10"/></svg>;
    }
    if (["menu", "menuicon"].includes(normalizedName)) {
        return <svg {...common}><path d="M4 7h16M4 12h16M4 17h16"/></svg>;
    }
    if (["chevrondown"].includes(normalizedName)) {
        return <svg {...common}><path d="m6 9 6 6 6-6"/></svg>;
    }
    if (["chevronright"].includes(normalizedName)) {
        return <svg {...common}><path d="m9 6 6 6-6 6"/></svg>;
    }
    if (["chevronleft"].includes(normalizedName)) {
        return <svg {...common}><path d="m15 6-6 6 6 6"/></svg>;
    }
    if (["arrowright"].includes(normalizedName)) {
        return <svg {...common}><path d="M5 12h14M14 6l6 6-6 6"/></svg>;
    }
    if (["arrowupright", "externallink"].includes(normalizedName)) {
        return <svg {...common}><path d="M7 17 17 7"/><path d="M8 7h9v9"/></svg>;
    }
    if (["plus", "add"].includes(normalizedName)) {
        return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
    }
    if (["minus", "remove"].includes(normalizedName)) {
        return <svg {...common}><path d="M5 12h14"/></svg>;
    }
    if (["close", "x"].includes(normalizedName)) {
        return <svg {...common}><path d="m6 6 12 12M18 6 6 18"/></svg>;
    }
    if (["check", "checkcircle", "shieldcheck"].includes(normalizedName)) {
        return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16.5 8"/></svg>;
    }
    if (["map", "mappin"].includes(normalizedName)) {
        return <svg {...common}><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></svg>;
    }
    if (["globe", "language", "languages"].includes(normalizedName)) {
        return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.4 2.5 3.6 5.5 3.6 9S14.4 18.5 12 21c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3Z"/></svg>;
    }
    if (["coin", "coins", "currency"].includes(normalizedName)) {
        return <svg {...common}><circle cx="9" cy="12" r="6"/><path d="M12 6.8A6 6 0 1 1 12 17.2M9 9v6m-2-5h3a1.5 1.5 0 0 1 0 3H8a1.5 1.5 0 0 0 0 3h3"/></svg>;
    }
    if (["store", "shop"].includes(normalizedName)) {
        return <svg {...common}><path d="M4 10v10h16V10M3 4h18l-1 6a3 3 0 0 1-5 1 3 3 0 0 1-6 0 3 3 0 0 1-5-1L3 4Z"/><path d="M9 20v-5h6v5"/></svg>;
    }
    if (["truck", "packagecheck"].includes(normalizedName)) {
        return <svg {...common}><path d="M3 6h11v11H3zM14 10h4l3 3v4h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>;
    }
    if (["creditcard", "walletcards"].includes(normalizedName)) {
        return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h3"/></svg>;
    }
    if (["upload", "uploadcloud"].includes(normalizedName)) {
        return <svg {...common}><path d="M12 16V4m0 0L7 9m5-5 5 5"/><path d="M4 15v4h16v-4"/></svg>;
    }
    if (["download", "downloadcloud"].includes(normalizedName)) {
        return <svg {...common}><path d="M12 4v12m0 0 5-5m-5 5-5-5"/><path d="M4 17v3h16v-3"/></svg>;
    }
    if (["quote"].includes(normalizedName)) {
        return <svg {...common}><path d="M7 9H4v7h6V9c0-3-2-5-5-5M17 9h-3v7h6V9c0-3-2-5-5-5"/></svg>;
    }
    if (["info"].includes(normalizedName)) {
        return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7h.01"/></svg>;
    }
    if (normalizedName === "facebook") {
        return <svg {...common} fill="currentColor" stroke="none"><path d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.5 1.6-1.5h1.7V3.9c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3V10H7.3v3h2.8v8h3.4Z"/></svg>;
    }
    if (normalizedName === "instagram") {
        return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="5"/><circle cx="12" cy="12" r="3.5"/><circle cx="17.5" cy="6.5" r=".7" fill="currentColor" stroke="none"/></svg>;
    }
    if (normalizedName === "youtube") {
        return <svg {...common} fill="none"><rect x="3" y="6" width="18" height="12" rx="4"/><path d="m10 9 5 3-5 3Z" fill="currentColor" stroke="none"/></svg>;
    }
    if (["x", "twitter"].includes(normalizedName)) {
        return <svg {...common}><path d="m5 4 14 16M19 4 5 20"/></svg>;
    }
    if (normalizedName === "linkedin") {
        return <svg {...common}><rect x="4" y="9" width="3" height="11"/><path d="M5.5 4.5v.1M11 20v-6c0-3 5-4 5 0v6M11 10v10"/></svg>;
    }
    if (["link", "pinterest", "tiktok", "whatsapp"].includes(normalizedName)) {
        return <svg {...common}><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.2 1.2"/><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.2-1.2"/></svg>;
    }
    if (["sparkles", "star", "leaf"].includes(normalizedName)) {
        return <svg {...common}><path d="M12 3c.6 4.8 2.2 6.4 7 7-4.8.6-6.4 2.2-7 7-.6-4.8-2.2-6.4-7-7 4.8-.6 6.4-2.2 7-7Z"/><path d="M19 16c.2 1.5.8 2.1 2.3 2.3-1.5.2-2.1.8-2.3 2.3-.2-1.5-.8-2.1-2.3-2.3 1.5-.2 2.1-.8 2.3-2.3Z"/></svg>;
    }

    return <svg {...common}><circle cx="12" cy="12" r="8"/><path d="M8 12h8M12 8v8"/></svg>;
}

function socialNetworkName(iconName: string, href: string): string {
    const normalizedIcon = iconName.replace(/^social:/i, "").toLowerCase();
    if (normalizedIcon && normalizedIcon !== "link") return normalizedIcon;

    const value = href.toLowerCase();
    if (value.includes("facebook")) return "facebook";
    if (value.includes("instagram")) return "instagram";
    if (value.includes("youtube") || value.includes("youtu.be")) return "youtube";
    if (value.includes("twitter") || value.includes("x.com")) return "x";
    if (value.includes("tiktok")) return "tiktok";
    if (value.includes("linkedin")) return "linkedin";
    if (value.includes("pinterest")) return "pinterest";
    if (value.includes("whatsapp") || value.includes("wa.me")) return "whatsapp";
    if (value.startsWith("mailto:")) return "email";

    return normalizedIcon || "link";
}

function socialNetworkColor(network: string): string {
    const colors: Record<string, string> = {
        email: "var(--site-muted)",
        facebook: "var(--site-primary)",
        instagram: "var(--site-accent)",
        linkedin: "var(--site-primary)",
        pinterest: "var(--site-accent)",
        tiktok: "var(--site-inverse)",
        whatsapp: "var(--site-primary)",
        x: "var(--site-inverse)",
        youtube: "var(--site-accent)",
    };

    return colors[network] ?? "currentColor";
}

function socialTheme(theme: string): { backgroundColor: string; color: string } {
    if (theme === "dark") return { backgroundColor: "var(--site-inverse)", color: "var(--site-on-inverse)" };
    if (theme === "light") return { backgroundColor: "var(--site-surface)", color: "var(--site-on-surface)" };
    if (theme === "primary") return { backgroundColor: "var(--site-primary)", color: "var(--site-on-primary)" };
    if (theme === "muted") return { backgroundColor: "var(--site-surface)", color: "var(--site-text)" };

    return { backgroundColor: "var(--site-surface)", color: "var(--site-on-surface)" };
}

function socialIconStyle(block: ShopTemplateBlock, href: string, iconName: string): CSSProperties {
    const rawLayout = optionText(block, "socialIconLayout") || "disk";
    const layout = rawLayout === "plain" ? "none" : rawLayout === "outline" ? "circle" : rawLayout;
    const rawSize = Number((optionText(block, "socialIconSize") || "40").replace("px", ""));
    const size = Number.isFinite(rawSize) ? Math.max(24, Math.min(96, rawSize)) : 40;
    const theme = socialTheme(optionText(block, "socialIconTheme") || "default");
    const customColor = morrowThemeColor(optionText(block, "textColor") || optionText(block, "socialIconColor"));
    const customBackgroundColor = morrowThemeColor(optionText(block, "backgroundColor") || optionText(block, "socialIconBackgroundColor"));
    const customBackgroundImage = optionText(block, "socialIconBackgroundImage");
    const brandColor = optionText(block, "socialIconBrandColor") !== "false"
        ? socialNetworkColor(socialNetworkName(iconName, href))
        : "";
    const linkDecoration = optionText(block, "socialLinkStyle") || "noUnderline";
    const rootStyle = visualStyle(block);
    const buttonStyle = { ...buttonPartStyle(block, "primaryButton") };

    // The parent icon size is the fallback. Button defaults must not turn an
    // icon back into a padded text button, while authored root/part geometry
    // remains authoritative for this concrete social link.
    delete buttonStyle.fontSize;
    delete buttonStyle.minHeight;
    delete buttonStyle.padding;
    delete buttonStyle.paddingBottom;
    delete buttonStyle.paddingLeft;
    delete buttonStyle.paddingRight;
    delete buttonStyle.paddingTop;
    delete buttonStyle.justifyContent;
    delete buttonStyle.textAlign;

    return {
        ...buttonStyle,
        backgroundColor: layout === "none"
            ? "transparent"
            : customBackgroundColor
                || (typeof buttonStyle.backgroundColor === "string" ? buttonStyle.backgroundColor : "")
                || theme.backgroundColor,
        backgroundImage: layout === "none" ? undefined : customBackgroundImage || undefined,
        borderColor: layout === "none" ? "transparent" : buttonStyle.borderColor,
        borderRadius: layout === "square" ? "0" : layout === "disk" ? "12px" : "9999px",
        boxShadow: linkDecoration === "underlineAlways" ? "inset 0 -2px currentColor" : undefined,
        color: customColor
            || (typeof buttonStyle.color === "string" ? buttonStyle.color : "")
            || brandColor
            || theme.color,
        height: size,
        minHeight: size,
        padding: 0,
        textDecoration: linkDecoration === "underlineAlways" ? "underline" : "none",
        width: size,
        ...rootStyle,
        ...optionStyle(block, "primaryButton"),
    };
}

function MorrowSocialLink({
    block,
    editorMode,
    href,
    iconName,
    part,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    href: string;
    iconName: string;
    part: string;
}) {
    const rawSize = Number((optionText(block, "socialIconSize") || "40").replace("px", ""));
    const iconSize = (Number.isFinite(rawSize) ? Math.max(24, Math.min(96, rawSize)) : 40) * .52;
    const newWindow = optionText(block, "openInNewWindow") === "true";
    const linkStyle = optionText(block, "socialLinkStyle") || "noUnderline";
    const linkHref = functionalStorefrontHref(href);

    if (!editorMode && !linkHref) {
        return null;
    }

    return (
        <a
            aria-label={contentText(block, "label", block.type)}
            className={`morrow-social-link is-link-${linkStyle}`}
            data-shop-block-part="primaryButton"
            data-shop-social-link-part={part}
            href={linkHref}
            onClick={(event) => editorMode && event.preventDefault()}
            rel={newWindow ? "noopener noreferrer" : undefined}
            style={socialIconStyle(block, href, iconName)}
            target={newWindow ? "_blank" : undefined}
        >
            <MorrowIcon name={iconName} size={iconSize}/>
            <span data-shop-inline-text-part="primaryButton">
                {richTextContent(block, "label", contentText(block, "label", block.type))}
            </span>
        </a>
    );
}

function MorrowVideoMedia({
    block,
    editorMode,
    source,
    title,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    source: string;
    title: string;
}) {
    const videoSource = siteVideoSource(source);
    const style = {
        aspectRatio: shopPortableAspectRatio(
            optionText(block, "aspectRatio") || optionText(block, "embedAspectRatio"),
        ),
        height: "100%",
        width: "100%",
        ...partVisualStyle(block, "video"),
    };

    if (videoSource === null) {
        return editorMode ? (
            <div className="morrow-embed-shell" data-shop-block-part="video" style={style}>
                {title}
            </div>
        ) : null;
    }

    return videoSource.kind === "embed" ? (
        <div className="morrow-embed-shell" data-shop-block-part="video" style={style}>
            <iframe
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="morrow-embed-frame"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-forms allow-presentation allow-same-origin allow-scripts"
                src={videoSource.source}
                style={{ pointerEvents: editorMode ? "none" : undefined }}
                tabIndex={editorMode ? -1 : undefined}
                title={title}
            />
        </div>
    ) : (
        <video
            className="morrow-inner-image"
            controls
            data-shop-block-part="video"
            playsInline
            src={videoSource.source}
            style={{ ...style, pointerEvents: editorMode ? "none" : undefined }}
            tabIndex={editorMode ? -1 : undefined}
        />
    );
}

function MorrowInnerBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const content = record(block.content);
    const options = record(block.options);
    const role = typeof options.documentRole === "string" ? options.documentRole : undefined;
    const dynamicEntityField = optionText(block, "dynamicEntityField");
    const usesDynamicEntityField = isShopDynamicEntityField(dynamicEntityField);
    const dynamicEntityType = optionText(block, "dynamicEntityType");
    const dynamicEntityUuid = optionText(block, "dynamicEntityUuid");
    const runtimeText = optionText(block, "runtimeText");
    const readOnlyText = usesDynamicEntityField || runtimeText !== "";
    const style = visualStyle(block);

    if (block.type === "product" && !editorMode) {
        return null;
    }
    const nestedSlots = Object.entries(block.slots ?? {});
    const inlinePart = ["button", "ctaBadge", "donationButton", "addToCartButton", "badge"].includes(block.type)
        ? "primaryButton"
        : "text";
    const textAttributes = {
        contentEditable: readOnlyText ? false : undefined,
        "data-shop-block-part": inlinePart,
        "data-shop-catalog-entity-type": usesDynamicEntityField ? dynamicEntityType : undefined,
        "data-shop-catalog-entity-uuid": usesDynamicEntityField ? dynamicEntityUuid : undefined,
        "data-shop-catalog-source": usesDynamicEntityField ? dynamicEntityField : undefined,
        "data-shop-inline-text-part": readOnlyText ? undefined : inlinePart,
        "data-shop-readonly-text": readOnlyText ? "true" : undefined,
    };

    if (block.type === "column") {
        return <MorrowColumnBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "accordion") {
        return <MorrowAccordionBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "accordionItem") {
        return <MorrowAccordionItemBlock block={block} containerBlock={block} editorMode={editorMode} open part={part}/>;
    }

    if (block.type === "form") {
        return <MorrowFormBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "field") {
        return <MorrowFormFieldBlock block={block} editorMode={editorMode} part={part}/>;
    }

    if (block.type === "submitButton") {
        return <MorrowSubmitButtonBlock block={block}/>;
    }

    if (block.type === "chart") {
        return <MorrowChartBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "textHighlight") {
        return <MorrowTextHighlightBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "blockquote") {
        return <MorrowBlockquoteBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "badge") {
        return <MorrowBadgeBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "ctaBadge") {
        return <MorrowCtaBadgeBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "socialMedia" || block.type === "share") {
        return <MorrowSocialBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    if (block.type === "card") {
        return <MorrowCardBlock block={block} editorMode={editorMode} part={part} runtime={runtime}/>;
    }

    let node: ReactNode;

    switch (block.type) {
        case "text":
        case "paragraph": {
            const textStyle = partVisualStyle(block, "text");
            const href = contentText(block, "href", "");
            const footerPageTemplateKey = morrowFooterPageTemplateKey(block);
            const footerPageHref = morrowFooterPageHref(block, runtime.pages, runtime.storefrontLanguageCode);
            const resolvedHref = role === "footerAccountLink"
                ? runtime.accountHref
                : footerPageHref
                    ?? (href.startsWith("/") ? runtime.storefrontHref(href) : href);
            const textContent = runtimeText === "shopName" ? runtime.displayName : contentText(block, "text", editorMode ? "\u200b" : "");
            const renderedText = readOnlyText
                ? textContent
                : richTextContent(block, "text", textContent);
            const commonTextProps = {
                ...textAttributes,
                className: "morrow-inner-text",
                "data-morrow-role": role,
                style: {
                    ...textStyle,
                    justifyContent: textStyle.textAlign === "center" ? "center" : textStyle.textAlign === "right" ? "flex-end" : undefined,
                },
            };

            node = resolvedHref ? (
                <a
                    {...commonTextProps}
                    data-morrow-page-template-key={footerPageTemplateKey ?? undefined}
                    href={resolvedHref}
                    onClick={(event) => editorMode && event.preventDefault()}
                    rel={/^https?:/i.test(resolvedHref) ? "noopener noreferrer" : undefined}
                >
                    {renderedText}
                </a>
            ) : (
                <div
                    {...commonTextProps}
                >
                    {renderedText}
                </div>
            );
            break;
        }
        case "heading": {
            const level = Math.max(1, Math.min(3, optionNumber(block, "level", 2)));
            const Heading = (level === 1 ? "h1" : level === 3 ? "h3" : "h2") as "h1" | "h2" | "h3";
            const headingText = runtimeText === "shopName"
                ? runtime.displayName
                : contentText(block, "text", contentText(block, "title", editorMode ? "\u200b" : ""));

            node = (
                <Heading
                    {...textAttributes}
                    className="morrow-inner-heading"
                    data-shop-block-part="title"
                    data-shop-inline-text-part={readOnlyText ? undefined : "title"}
                    style={partVisualStyle(block, "title")}
                >
                    {readOnlyText ? headingText : richTextContent(block, "text", headingText)}
                </Heading>
            );
            break;
        }
        case "quote":
            node = <blockquote className="morrow-inline-quote" data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", editorMode ? "\u200b" : ""))}</blockquote>;
            break;
        case "list":
        case "checklist": {
            const items = multilineContent(block, "items");
            const lines = richTextLineContents(
                block,
                "items",
                typeof content.items === "string" ? content.items : items.join("\n"),
            );
            node = (
                <ul className={`morrow-content-list${block.type === "checklist" ? " is-checklist" : ""}`} data-shop-block-part="items" data-shop-inline-text-part="items" style={partVisualStyle(block, "items")}>
                    {lines.map((line, index) => <li key={`${line.text}:${index}`}>{block.type === "checklist" ? <MorrowIcon name="check" size={17}/> : null}<span>{line.content}</span></li>)}
                </ul>
            );
            break;
        }
        case "button":
        case "appointmentButton":
        case "donationButton":
        case "addToCartButton": {
            const href = role === "searchAction"
                ? runtime.searchHref
                : functionalStorefrontHref(contentText(block, "href", ""));
            const label = contentText(block, "label", contentText(block, "text", runtime.storefrontMessage("ui.common.open")));
            const contextualProductUuid = stringOrNull(nestedRecord(contextData(runtime.localizedPageContext), "product").uuid);
            const productUuid = contentText(block, "productUuid", "") || contextualProductUuid;
            const actionIcon = contentText(
                block,
                "icon",
                block.type === "donationButton" ? "heart" : block.type === "addToCartButton" ? "bag" : "",
            );

            if (block.type === "addToCartButton") {
                const adding = productUuid !== "" && runtime.addingProductUuid === productUuid;

                node = (
                    <button
                        {...textAttributes}
                        className="morrow-inner-button"
                        data-morrow-role={role}
                        disabled={productUuid === "" || adding}
                        onClick={() => {
                            if (!editorMode && productUuid) void runtime.addCartItem(productUuid);
                        }}
                        style={buttonPartStyle(block, "primaryButton")}
                        type="button"
                    >
                        {actionIcon ? <MorrowIcon name={actionIcon} size={18}/> : null}
                        <span data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton">
                            {adding ? "…" : richTextContent(block, "label", label)}
                        </span>
                    </button>
                );
                break;
            }

            node = (
                <a
                    {...textAttributes}
                    className="morrow-inner-button"
                    data-morrow-role={role}
                    href={href ? (href.startsWith("#") ? href : runtime.storefrontHref(href)) : undefined}
                    onClick={(event) => editorMode && event.preventDefault()}
                    style={buttonPartStyle(block, "primaryButton")}
                >
                    {actionIcon ? <MorrowIcon name={actionIcon} size={18}/> : null}
                    <span data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton">
                        {richTextContent(block, "label", label)}
                    </span>
                </a>
            );
            break;
        }
        case "link": {
            const href = functionalStorefrontHref(contentText(block, "href", ""));
            const label = contentText(block, "label", href ?? "");
            const linkStyle = {
                ...visualStyle(block),
                ...optionStyle(block, "text"),
                ...optionStyle(block, "primaryButton"),
                ...optionStyle(block, "label"),
                ...(morrowInteractionStyle(block, "label")
                    ?? morrowInteractionStyle(block, "primaryButton")
                    ?? morrowInteractionStyle(block)),
            };

            node = (
                <a
                    className="morrow-inner-link"
                    data-shop-block-part="label"
                    data-shop-inline-text-part="label"
                    href={href ? (href.startsWith("/") ? runtime.storefrontHref(href) : href) : undefined}
                    onClick={(event) => editorMode && event.preventDefault()}
                    style={{
                        ...linkStyle,
                        display: linkStyle.display ?? "flex",
                        justifyContent: linkStyle.textAlign === "center"
                            ? "center"
                            : linkStyle.textAlign === "right"
                                ? "flex-end"
                                : undefined,
                    }}
                >
                    {richTextContent(block, "label", label)}
                </a>
            );
            break;
        }
        case "searchBar":
            node = (
                <MorrowSearchForm
                    block={block}
                    editorMode={editorMode}
                    part={part}
                    placeholder={contentText(block, "placeholder", runtime.storefrontMessage("ui.search.placeholder"))}
                    runtime={runtime}
                    style={style}
                />
            );
            break;
        case "image":
        case "media": {
            const presentation = siteBuilderMediaPresentation(block.content);
            const videoSrc = presentation.kind === "video" ? presentation.source : "";
            const src = presentation.kind === "image"
                ? presentation.source
                : presentation.kind === "empty" && editorMode
                    ? morrowEditorMediaPlaceholderSource(block, MORROW_EDITORIAL_IMAGE)
                    : "";
            node = videoSrc ? (
                <MorrowVideoMedia
                    block={block}
                    editorMode={editorMode}
                    source={videoSrc}
                    title={contentText(block, "imageTitle", contentText(block, "title", runtime.storefrontMessage("ui.block.video")))}
                />
            ) : src ? (
                <img
                    alt={contentText(block, "alt", "")}
                    className="morrow-inner-image"
                    data-shop-block-part="image"
                    src={src}
                    title={contentText(block, "imageTitle", "") || undefined}
                    style={{
                        height: "100%",
                        ...imagePartStyle(block, "image"),
                    }}
                />
            ) : (
                <div
                    aria-hidden="true"
                    className="morrow-media-placeholder"
                    data-shop-block-part="image"
                    style={{
                        height: "100%",
                        ...visualStyle(block),
                        ...imagePartStyle(block, "image"),
                    }}
                >
                    <MorrowIcon name="image" size={30}/>
                </div>
            );
            break;
        }
        case "uploadFile": {
            const source = functionalStorefrontHref(contentText(block, "file", contentText(block, "url", contentText(block, "href", ""))));

            node = source ? (
                <a className="morrow-upload" data-shop-block-part="file" download href={source} onClick={(event) => editorMode && event.preventDefault()} style={partVisualStyle(block, "file")}>
                    <MorrowIcon name="download" size={22}/><span>{contentText(block, "label", "Download file")}</span>
                </a>
            ) : editorMode ? (
                <div className="morrow-upload" data-shop-block-part="file" style={partVisualStyle(block, "file")}>
                    <MorrowIcon name="download" size={22}/><span>{contentText(block, "label", "Download file")}</span>
                </div>
            ) : null;
            break;
        }
        case "video": {
            const src = contentText(block, "videoUrl", contentText(block, "url", ""));
            node = src
                ? <MorrowVideoMedia block={block} editorMode={editorMode} source={src} title={contentText(block, "title", runtime.storefrontMessage("ui.block.video"))}/>
                : <div data-shop-block-part="video" style={partVisualStyle(block, "video")}>{runtime.storefrontMessage("ui.block.video")}</div>;
            break;
        }
        case "emoji":
            node = <span className="morrow-emoji" data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", "🙂"))}</span>;
            break;
        case "signature":
            node = <span className="morrow-signature" data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", editorMode ? "\u200b" : ""))}</span>;
            break;
        case "icon": {
            const iconName = contentText(block, "icon", "sparkles");
            const href = contentText(block, "href", "");
            const className = role === "categoryArtIcon" ? "morrow-inner-icon morrow-category-art" : "morrow-inner-icon";
            const iconStyle = optionText(block, "iconStyle") || "plain";
            const alignment = optionText(block, "alignment");
            const selectedStyle = partVisualStyle(block, "icon");
            const iconFrameStyle: CSSProperties = {
                ...selectedStyle,
                alignSelf: alignment === "center" ? "center" : undefined,
                borderRadius: iconStyle === "circle"
                    ? "9999px"
                    : iconStyle === "rounded" || iconStyle === "outline"
                        ? "12px"
                        : iconStyle === "square"
                            ? "0"
                            : selectedStyle.borderRadius,
                borderStyle: iconStyle === "outline" ? "solid" : selectedStyle.borderStyle,
                borderWidth: iconStyle === "outline" && !selectedStyle.borderWidth ? "1px" : selectedStyle.borderWidth,
                clipPath: iconStyle === "gear" ? "polygon(50% 0%,61% 10%,76% 7%,84% 20%,98% 25%,96% 41%,100% 53%,89% 64%,88% 80%,72% 84%,62% 97%,48% 91%,34% 98%,24% 84%,8% 79%,11% 63%,0% 52%,5% 38%,2% 24%,18% 18%,27% 5%,42% 10%)" : undefined,
                justifyContent: alignment === "right" ? "flex-end" : alignment === "center" ? "center" : "flex-start",
                justifySelf: alignment === "right" || alignment === "center" ? alignment : undefined,
            };
            const icon = <MorrowIcon name={iconName} size={morrowIconSize(block)}/>;

            node = href ? (
                <a
                    aria-label={contentText(block, "label", iconName)}
                    className={`${className} morrow-inner-icon-link`}
                    data-morrow-role={role}
                    data-shop-block-part="icon"
                    data-shop-icon-part={part || undefined}
                    href={href.startsWith("/") ? runtime.storefrontHref(href) : href}
                    onClick={(event) => editorMode && event.preventDefault()}
                    rel={/^https?:/i.test(href) ? "noreferrer" : undefined}
                    style={iconFrameStyle}
                    target={/^https?:/i.test(href) ? "_blank" : undefined}
                >{icon}</a>
            ) : <span className={className} data-morrow-role={role} data-shop-block-part="icon" data-shop-icon-part={part || undefined} style={iconFrameStyle}>{icon}</span>;
            break;
        }
        case "separator": {
            const separatorWidth = optionText(block, "width") || "100%";
            node = <div className="morrow-inner-separator" data-shop-block-part="separator" style={{ ...style, borderTopColor: optionText(block, "borderColor") || "var(--site-border)", borderTopStyle: (optionText(block, "separatorStyle") || "solid") as CSSProperties["borderTopStyle"], borderTopWidth: optionText(block, "borderWidth") || "1px", height: 0, width: separatorWidth }} />;
            break;
        }
        case "alert":
            node = <div className="morrow-inner-panel" data-shop-block-part="text" data-shop-inline-text-part="text" role="status" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", ""))}</div>;
            break;
        case "rating": {
            const maximum = Math.max(1, Number(content.ratingMax) || 5);
            const rating = Math.max(0, Math.min(maximum, Number(content.ratingValue) || 0));
            const rounded = Math.round(rating);
            const mode = optionText(block, "ratingIcon") || "stars";
            const titlePosition = optionText(block, "ratingTitlePosition") || "left";
            const glyph = (active: boolean, index: number) => {
                if (mode === "custom") {
                    const iconName = contentText(block, active ? "ratingActiveIcon" : "ratingInactiveIcon", "lucide:star");

                    return <MorrowIcon key={index} name={iconName} size={18}/>;
                }
                if (mode === "thumbs") return <span key={index}>{active ? "👍" : "👎"}</span>;
                if (mode === "circles") return <span key={index}>{active ? "●" : "○"}</span>;
                if (mode === "squares") return <span key={index}>{active ? "■" : "□"}</span>;
                if (mode === "hearts") return <span key={index}>{active ? "♥" : "♡"}</span>;

                return <span key={index}>{active ? "★" : "☆"}</span>;
            };
            const score = titlePosition === "none" ? null : (
                <span className="morrow-rating-score">
                    <span data-shop-block-part="ratingValue" data-shop-inline-text-part="ratingValue" style={optionStyle(block, "ratingValue")}>{rating}</span>
                    {" / "}
                    <span data-shop-block-part="ratingMax" data-shop-inline-text-part="ratingMax" style={optionStyle(block, "ratingMax")}>{maximum}</span>
                </span>
            );
            const icons = (
                <span
                    aria-hidden="true"
                    className="morrow-rating-icons"
                    style={{ color: optionText(block, "ratingIconColor") || "var(--site-primary)" }}
                >
                    {Array.from({ length: Math.round(maximum) }, (_, index) => glyph(index < rounded, index))}
                </span>
            );

            node = <div className={`morrow-rating is-${optionText(block, "ratingSize") || "md"} is-title-${titlePosition}`} data-shop-block-part="rating" style={partVisualStyle(block, "rating")} aria-label={`${rating}/${maximum}`}>{titlePosition === "top" ? <>{score}{icons}</> : <>{score}{icons}</>}</div>;
            break;
        }
        case "progressBar": {
            const value = Math.max(0, Math.min(100, Number(content.progressValue) || 0));
            const position = shopPortableProgressLabelPosition(optionText(block, "progressLabelPosition"));
            const label = contentText(block, "title", `${value}%`);
            const labelColor = optionText(block, "progressLabelColor") || "var(--morrow-ink)";
            const track = (
                <div className="morrow-progress-track" style={{ backgroundColor: optionText(block, "progressTrackColor") || "var(--site-border)" }}>
                    <span className={optionText(block, "progressStriped") === "true" ? "is-striped" : undefined} style={{ backgroundColor: optionText(block, "progressFillColor") || "var(--morrow-green)", width: `${value}%` }}/>
                    {position === "inside" ? <small style={{ color: labelColor }}><span data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", label)}</span><span>· {value}%</span></small> : null}
                </div>
            );
            node = <div className={`morrow-progress is-${position}`} data-shop-block-part="progress" style={partVisualStyle(block, "progress")}>{position === "above" ? <small style={{ color: labelColor }}><span data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", label)}</span> · {value}%</small> : null}{track}{position === "below" || position === "after" ? <small style={{ color: labelColor }}><span data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", label)}</span> · {value}%</small> : null}</div>;
            break;
        }
        case "code": {
            const sourceCode = contentText(block, "code", contentText(block, "text", ""));
            const embedSource = shopEmbedSource(sourceCode);

            node = embedSource ? (
                <div
                    {...textAttributes}
                    className="morrow-embed-shell"
                    data-shop-block-part="code"
                    style={{ ...partVisualStyle(block, "code"), aspectRatio: shopPortableAspectRatio(optionText(block, "embedAspectRatio")) }}
                >
                    <iframe
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="morrow-embed-frame"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        sandbox="allow-forms allow-presentation allow-same-origin allow-scripts"
                        src={embedSource}
                        style={{ pointerEvents: editorMode ? "none" : undefined }}
                        title={contentText(block, "title", runtime.storefrontMessage("ui.common.open"))}
                    />
                </div>
            ) : (
                <pre data-shop-block-part="code" data-shop-inline-text-part="code" className="morrow-code" style={partVisualStyle(block, "code")}><code>{richTextContent(block, "code", sourceCode)}</code></pre>
            );
            break;
        }
        case "input":
            node = <input className="morrow-standalone-input" data-shop-block-part="input" defaultValue={contentText(block, "defaultValue", "")} name={contentText(block, "name", "field")} placeholder={contentText(block, "placeholder", "")} readOnly={editorMode} style={partVisualStyle(block, "input")}/>;
            break;
        case "textarea":
            node = <textarea className="morrow-standalone-input" data-shop-block-part="input" defaultValue={contentText(block, "defaultValue", "")} name={contentText(block, "name", "message")} placeholder={contentText(block, "placeholder", "")} readOnly={editorMode} rows={Math.max(2, optionNumber(block, "rows", 4))} style={partVisualStyle(block, "input")}/>;
            break;
        case "table": {
            const rows = multilineContent(block, "items").map((row) => row.split(/\s*\|\s*|\t/));
            node = <div className="morrow-table-wrap" data-shop-block-part="items" style={optionStyle(block, "items")}><table style={style}><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
            break;
        }
        case "supportedPaymentButton": {
            const payments = multilineContent(block, "items", ["Visa", "Mastercard", "Apple Pay"]);
            const layout = optionText(block, "paymentLayout") || "cards";
            node = (
                <div className={`morrow-payment-block is-${layout}`} style={style}>
                    {contentText(block, "title", "") ? <strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", ""))}</strong> : null}
                    <div className="morrow-payment-strip" data-shop-block-part="items" data-shop-inline-text-part="items" style={optionStyle(block, "items")}>
                        {payments.map((payment) => (
                            <span key={payment} data-payment-brand={shopPaymentBrand(payment)}>
                                <i aria-hidden="true">{shopPaymentBrandGlyph(shopPaymentBrand(payment))}</i>
                                <b>{payment}</b>
                            </span>
                        ))}
                    </div>
                </div>
            );
            break;
        }
        case "map": {
            const mapUrl = shopMapEmbedSource({
                address: contentText(block, "address", ""),
                latitude: contentText(block, "latitude", ""),
                longitude: contentText(block, "longitude", ""),
                mapUrl: contentText(block, "mapUrl", ""),
            });
            const mapTitle = contentText(block, "title", "");
            const mapTextPart = mapTitle ? "title" : "address";
            node = mapUrl
                ? (
                    <div {...textAttributes} className="morrow-map-shell" data-shop-block-part="map" style={{ ...partVisualStyle(block, "map"), aspectRatio: shopPortableAspectRatio(optionText(block, "mapAspectRatio")) }}>
                        <iframe
                            className="morrow-map-frame"
                            loading="lazy"
                            referrerPolicy="strict-origin-when-cross-origin"
                            sandbox="allow-scripts allow-same-origin allow-popups"
                            src={mapUrl}
                            style={{ pointerEvents: editorMode ? "none" : undefined }}
                            title={mapTitle || runtime.storefrontMessage("ui.block.map")}
                        />
                        {mapTitle || contentText(block, "address", "") ? (
                            <span className="morrow-map-caption" data-shop-block-part={mapTextPart} data-shop-inline-text-part={mapTextPart} style={optionStyle(block, mapTextPart)}>
                                <MorrowIcon name="map" size={18}/>
                                {richTextContent(block, mapTextPart, mapTitle || contentText(block, "address", ""))}
                            </span>
                        ) : null}
                    </div>
                )
                : <div className="morrow-inner-panel morrow-map-placeholder" data-shop-block-part="map" style={partVisualStyle(block, "map")}><MorrowIcon name="map"/><span data-shop-block-part={mapTextPart} data-shop-inline-text-part={mapTextPart} style={optionStyle(block, mapTextPart)}>{richTextContent(block, mapTextPart, mapTitle || contentText(block, "address", runtime.storefrontMessage("ui.block.map")))}</span></div>;
            break;
        }
        case "socialLink":
        case "facebook":
        case "instagram": {
            const href = contentText(block, "href", "");
            const iconName = contentText(block, "icon", block.type);
            node = <MorrowSocialLink block={block} editorMode={editorMode} href={href} iconName={iconName} part={part}/>;
            break;
        }
        case "iconText":
        case "serviceBenefitItem":
            node = <div className="morrow-icon-text" style={style}><span data-shop-block-part="icon" data-shop-icon-part={part ? `${part}:icon` : "icon"} style={optionStyle(block, "icon")}><MorrowIcon name={contentText(block, "icon", "check")} size={24}/></span><div>{contentText(block, "title", "") ? <strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", ""))}</strong> : null}<span data-shop-block-part="text" data-shop-inline-text-part="text" style={optionStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", ""))}</span></div></div>;
            break;
        case "iconList": {
            const items = multilineContent(block, "items");
            const lines = richTextLineContents(
                block,
                "items",
                typeof content.items === "string" ? content.items : items.join("\n"),
            );
            const iconStyle = optionStyle(block, "icon");
            node = <ul className="morrow-icon-list" data-shop-block-part="items" data-shop-inline-text-part="items" style={partVisualStyle(block, "items")}>{lines.map((line, index) => <li data-shop-icon-list-row="true" key={`${line.text}:${index}`}><span className="morrow-icon-list-icon" contentEditable={false} data-shop-block-part="icon" data-shop-icon-list-icon="true" data-shop-icon-part={part ? `${part}:icon` : "icon"} style={iconStyle}><MorrowIcon name={contentText(block, "icon", "check")} size={16}/></span>{line.content}</li>)}</ul>;
            break;
        }
        case "avatars": {
            const avatars = multilineContent(block, "items", ["Anna", "Tomáš", "Mia", "David"]);
            const avatarLayout = optionText(block, "avatarLayout") || "stack";
            const showNames = optionText(block, "showAvatarNames") === "true" || avatarLayout === "pills";
            node = (
                <div className={`morrow-avatar-block is-${avatarLayout}`} style={style}>
                    {contentText(block, "title", "") ? <strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", ""))}</strong> : null}
                    <div className="morrow-avatars" data-shop-block-part="items" data-shop-inline-text-part="items" style={optionStyle(block, "items")}>
                        {avatars.map((value, index) => (
                            <span aria-label={value} key={`${value}:${index}`} style={{ zIndex: avatars.length - index }}>
                                <i aria-hidden="true">{shopAvatarInitials(value)}</i>
                                {showNames ? <b>{value}</b> : null}
                            </span>
                        ))}
                    </div>
                </div>
            );
            break;
        }
        case "countdown":
            node = <MorrowCountdownBlock block={block} runtime={runtime}/>;
            break;
        case "product":
            node = <article className={`morrow-inner-product is-image-${optionText(block, "imageLayout") || "top"}`} style={style}>{contentText(block, "imageUrl", "") ? <img alt={contentText(block, "alt", contentText(block, "title", ""))} data-shop-block-part="image" src={contentText(block, "imageUrl", "")} style={imagePartStyle(block, "image", false)} title={contentText(block, "imageTitle", "") || undefined}/> : null}<strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", "Product"))}</strong><span data-shop-block-part="price" data-shop-inline-text-part="price" style={optionStyle(block, "price")}>{richTextContent(block, "price", contentText(block, "price", ""))}</span><p data-shop-block-part="text" data-shop-inline-text-part="text" style={optionStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", ""))}</p>{contentText(block, "buttonLabel", "") ? <a data-shop-block-part="button" data-shop-inline-text-part="button" href={functionalStorefrontHref(contentText(block, "buttonUrl", ""))} onClick={(event) => editorMode && event.preventDefault()} style={buttonPartStyle(block, "button", false)}>{richTextContent(block, "buttonLabel", contentText(block, "buttonLabel", ""))}</a> : null}</article>;
            break;
        default:
            node = <div className="morrow-inner-panel" data-shop-block-part="content" style={style}>{block.type}</div>;
    }

    if (block.type === "searchBar" || nestedSlots.length === 0) return node;

    return (
        <div className="morrow-nested-block">
            {node}
            {nestedSlots.map(([slotName, children]) => {
                const slotPart = morrowSlotPart(part, slotName);

                return (
                <div data-shop-slot-container={slotPart} data-shop-slot-item-count={children.length} key={slotName}>
                    {children.map((child, index) => {
                        const childPart = `${slotPart}:${index}`;

                        return <div data-shop-block-part={childPart} data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(child) ? "true" : undefined} key={child.uuid?.trim() || `${child.type}:${index}`}><MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/></div>;
                    })}
                </div>
                );
            })}
        </div>
    );
}

function morrowSlotPart(part: string, slotName: string): string {
    return part ? `${part}:slot:${slotName}` : `slot:${slotName}`;
}

function multilineContent(block: ShopTemplateBlock, key: string, fallback: string[] = []): string[] {
    const value = record(block.content)[key];

    if (Array.isArray(value)) {
        const items = value
            .map((item) => typeof item === "string" ? item.trim() : String(record(item).label ?? "").trim())
            .filter(Boolean);

        return items.length > 0 ? items : fallback;
    }

    if (typeof value !== "string") return fallback;

    const items = value
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean);

    return items.length > 0 ? items : fallback;
}

function safeJsonArray(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string" || value.trim() === "") return [];

    try {
        const parsed = JSON.parse(value) as unknown;

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function MorrowSlotBlockGroup({
    block,
    className,
    editorMode,
    itemClassName,
    part,
    renderItem,
    runtime,
    slotName,
    style,
}: {
    block: ShopTemplateBlock;
    className?: string;
    editorMode: boolean;
    itemClassName?: string;
    part: string;
    renderItem?: (child: ShopTemplateBlock, index: number, childPart: string) => ReactNode;
    runtime: ShopStorefrontRuntimeModel;
    slotName: string;
    style?: CSSProperties;
}) {
    const children = block.slots?.[slotName] ?? [];
    const slotPart = morrowSlotPart(part, slotName);

    return (
        <div
            className={className}
            data-shop-slot-container={slotPart}
            data-shop-slot-item-count={children.length}
            style={editorMode && children.length === 0 ? { minHeight: 8, ...style } : style}
        >
            {children.map((child, index) => {
                const childPart = `${slotPart}:${index}`;

                return (
                    <div
                        key={`${child.uuid ?? child.type}:${index}`}
                        {...morrowInteractionAttributes(child)}
                        className={itemClassName}
                        data-shop-block-part={childPart}
                        data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(child) ? "true" : undefined}
                        style={morrowInteractionStyle(child)}
                    >
                        {renderItem
                            ? renderItem(child, index, childPart)
                            : <MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/>}
                    </div>
                );
            })}
        </div>
    );
}

function accordionItemKey(block: ShopTemplateBlock, index: number): string {
    return optionText(block, "accordionItemId")
        || block.uuid
        || `${contentText(block, "title", block.type)}:${index}`;
}

function MorrowAccordionItemBlock({
    block,
    containerBlock,
    editorMode,
    onToggle,
    open,
    part,
}: {
    block: ShopTemplateBlock;
    containerBlock: ShopTemplateBlock;
    editorMode: boolean;
    onToggle?: () => void;
    open: boolean;
    part: string;
}) {
    const iconMode = optionText(containerBlock, "accordionIconMode") || "arrow";
    const icon = iconMode === "plusMinus"
        ? open ? "minus" : "plus"
        : optionText(containerBlock, open ? "accordionOpenIcon" : "accordionClosedIcon") || "chevronDown";
    const transition = optionText(containerBlock, "accordionTransition") || "slide";
    const styleVariant = optionText(containerBlock, "accordionStyle") || "boxed";
    const activeBackgroundColor = optionText(block, "activeBackgroundColor")
        || optionText(containerBlock, "activeBackgroundColor")
        || (styleVariant === "highlightActive" ? "color-mix(in srgb, var(--site-primary) 9%, transparent)" : "");
    const activeTextColor = optionText(block, "activeTextColor") || optionText(containerBlock, "activeTextColor");
    const baseStyle = visualStyle(block);
    const resolvedStyle: SiteBuilderStyleRecord = {
        ...baseStyle,
        backgroundColor: open && activeBackgroundColor ? activeBackgroundColor : baseStyle.backgroundColor,
        color: open && activeTextColor ? activeTextColor : baseStyle.color,
    };
    const itemStyle: SiteBuilderStyleRecord = {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(baseStyle, resolvedStyle),
    };

    return (
        <article
            className={`morrow-accordion-item is-style-${styleVariant} is-transition-${transition}${open ? " is-open" : ""}`}
            data-shop-accordion-part={part}
            style={itemStyle}
        >
            <button
                aria-expanded={open}
                className="morrow-accordion-toggle"
                data-shop-accordion-toggle="true"
                onClick={(event) => {
                    if (editorMode && (event.target as HTMLElement).closest("[data-shop-inline-text-part]")) return;
                    onToggle?.();
                }}
                type="button"
            >
                <span data-shop-accordion-title="true" data-shop-block-part="title" data-shop-inline-text-part="title" style={partVisualStyle(block, "title")}>
                    {richTextContent(block, "title", contentText(block, "title", "\u200b"))}
                </span>
                <span className="morrow-accordion-icon"><MorrowIcon name={icon} size={20}/></span>
            </button>
            <div className={`morrow-accordion-answer is-transition-${transition}`} aria-hidden={!open}>
                <div>
                    <p data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>
                        {richTextContent(block, "text", contentText(block, "text", editorMode ? "\u200b" : ""))}
                    </p>
                </div>
            </div>
        </article>
    );
}

function MorrowAccordionBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const items = block.slots?.content ?? [];
    const keys = items.map(accordionItemKey);
    const signature = keys.join("\u001f");
    const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set(keys[0] ? [keys[0]] : []));
    const style = partVisualStyle(block, "container");

    useEffect(() => {
        setOpenKeys((current) => {
            const available = new Set(keys);
            const next = new Set(Array.from(current).filter((key) => available.has(key)));

            if (next.size === 0 && keys[0]) next.add(keys[0]);
            if (next.size === current.size && Array.from(next).every((key) => current.has(key))) return current;

            return next;
        });
    }, [signature]);

    return (
        <div className={`morrow-accordion is-style-${optionText(block, "accordionStyle") || "boxed"} is-transition-${optionText(block, "accordionTransition") || "slide"}`} data-shop-block-part="container" style={style}>
            <MorrowSlotBlockGroup
                block={block}
                editorMode={editorMode}
                part={part}
                runtime={runtime}
                slotName="content"
                style={{ gap: style.gap }}
                renderItem={(child, index, childPart) => {
                    if (child.type !== "accordionItem") {
                        return <MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/>;
                    }

                    const key = accordionItemKey(child, index);

                    return (
                        <MorrowAccordionItemBlock
                            block={child}
                            containerBlock={block}
                            editorMode={editorMode}
                            onToggle={() => {
                                setOpenKeys((current) => {
                                    const next = new Set(current);
                                    if (next.has(key)) next.delete(key);
                                    else next.add(key);

                                    return next;
                                });
                            }}
                            open={openKeys.has(key)}
                            part={childPart}
                        />
                    );
                }}
            />
        </div>
    );
}

function formChoiceItems(block: ShopTemplateBlock): string[] {
    return multilineContent(block, "choices", ["Option 1", "Option 2"]);
}

type MorrowLeadFormRuntimeContextValue = {
    authoredFields: ReadonlyMap<ShopTemplateBlock, SiteBuilderAuthoredFormField>;
    enforceSchema: boolean;
    fieldsByKey: ReadonlyMap<string, SiteLeadFormField>;
    formBlock: ShopTemplateBlock;
    primaryFieldsByKey: ReadonlyMap<string, ShopTemplateBlock>;
    runtime: ShopStorefrontRuntimeModel;
    schemaResolved: boolean;
};

const MorrowLeadFormRuntimeContext = createContext<MorrowLeadFormRuntimeContextValue | null>(null);

function fieldHtmlType(fieldType: string): string {
    if (fieldType === "telephone") return "tel";
    if (fieldType === "decimalNumber" || fieldType === "number") return "number";
    if (fieldType === "datetime") return "datetime-local";
    if (fieldType === "fileupload") return "file";
    if (["date", "email", "url"].includes(fieldType)) return fieldType;

    return "text";
}

function morrowLeadFieldLabel(field: SiteLeadFormField, runtime: ShopStorefrontRuntimeModel): string {
    if (field.key === "name") return runtime.storefrontMessage("block.systemContactForm.nameInput");
    if (field.key === "email") return runtime.storefrontMessage("block.systemContactForm.emailInput");
    if (field.key === "message") return runtime.storefrontMessage("block.systemContactForm.messageInput");

    return field.label;
}

function morrowBuilderFieldType(field: SiteLeadFormField): string {
    if (field.type === "textarea") return "longText";
    if (field.type === "select") return "selection";
    if (field.type === "tel") return "telephone";

    return field.type;
}

function MorrowLeadFormField({
    contact = false,
    field,
    labelsPosition,
    readOnly = false,
    runtime,
}: {
    contact?: boolean;
    field: SiteLeadFormField;
    labelsPosition: string;
    readOnly?: boolean;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const fieldId = useId();
    const label = morrowLeadFieldLabel(field, runtime);
    const control = field.type === "textarea"
        ? <textarea id={fieldId} name={field.key} placeholder={field.placeholder ?? undefined} readOnly={readOnly} required={field.required} rows={contact ? 6 : 4}/>
        : field.type === "select"
            ? (
                <select defaultValue="" disabled={readOnly} id={fieldId} name={field.key} required={field.required}>
                    <option value="">{field.placeholder || label}</option>
                    {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
            )
            : <input autoComplete={field.type === "email" ? "email" : field.type === "tel" ? "tel" : field.key === "name" ? "name" : undefined} id={fieldId} name={field.key} placeholder={field.placeholder ?? undefined} readOnly={readOnly} required={field.required} type={field.type}/>;

    if (contact) {
        return <label htmlFor={fieldId}>{label}{control}</label>;
    }

    return (
        <div className={`morrow-form-field is-label-${labelsPosition}`} data-morrow-lead-field={field.key}>
            {labelsPosition !== "none" ? <label className="morrow-form-label" htmlFor={fieldId}><span>{label}</span>{field.required ? <sup>*</sup> : null}</label> : null}
            <div className="morrow-form-control">{control}</div>
        </div>
    );
}

function MorrowFormFieldBlock({
    block,
    editorMode,
    fieldName,
    formBlock,
    part,
    runtime,
    schema,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    fieldName?: string;
    formBlock?: ShopTemplateBlock;
    part: string;
    runtime?: ShopStorefrontRuntimeModel;
    schema?: SiteLeadFormField;
}) {
    const formRuntime = useContext(MorrowLeadFormRuntimeContext);
    const activeFormBlock = formBlock ?? formRuntime?.formBlock;
    const resolvedFieldName = fieldName
        ?? formRuntime?.authoredFields.get(block)?.key
        ?? contentText(block, "name", part.replace(/[^a-z0-9]+/gi, "-"));
    const activeSchema = schema ?? formRuntime?.fieldsByKey.get(resolvedFieldName);
    const activeRuntime = runtime ?? formRuntime?.runtime;

    if (
        !editorMode
        && formRuntime?.enforceSchema
        && formRuntime.schemaResolved
        && (
            !activeSchema
            || formRuntime.primaryFieldsByKey.get(resolvedFieldName) !== block
        )
    ) {
        return null;
    }

    const fieldType = activeSchema ? morrowBuilderFieldType(activeSchema) : optionText(block, "fieldInputType") || "text";
    const required = activeSchema?.required ?? (optionText(block, "required") === "true" || optionText(block, "requirement") === "required");
    const labelsPosition = activeFormBlock ? optionText(activeFormBlock, "formLabelsPosition") || "sideGap" : "top";
    const markedFields = activeFormBlock ? optionText(activeFormBlock, "formMarkedFields") || "required" : "required";
    const marker = activeFormBlock ? optionText(activeFormBlock, "formMarker") || "*" : "*";
    const showMarker = markedFields === "required" ? required : markedFields === "optional" ? !required : false;
    const label = activeSchema && activeRuntime ? morrowLeadFieldLabel(activeSchema, activeRuntime) : contentText(block, "label", "\u200b");
    const placeholder = formRuntime?.authoredFields.get(block)?.placeholder
        ?? activeSchema?.placeholder
        ?? contentText(block, "placeholder", "");
    const defaultValue = contentText(block, "defaultValue", "");
    const description = contentText(block, "description", "");
    const name = activeSchema?.key ?? resolvedFieldName;
    const choices = formRuntime?.authoredFields.get(block)?.options
        ?? activeSchema?.options
        ?? formChoiceItems(block);
    const choiceLayout = optionText(block, "choiceLayout") || "vertical";
    const inputStyle = optionStyle(block, "input");
    let control: ReactNode;

    if (fieldType === "longText") {
        control = <textarea defaultValue={defaultValue} name={name} placeholder={placeholder} readOnly={editorMode} required={required} rows={Math.max(2, optionNumber(block, "rows", 3))} style={inputStyle}/>;
    } else if (fieldType === "selection") {
        control = <select defaultValue={defaultValue} disabled={editorMode} name={name} required={required} style={inputStyle}><option value="">{placeholder}</option>{choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}</select>;
    } else if (fieldType === "checkbox") {
        control = <label className="morrow-choice"><input defaultChecked={defaultValue === "true"} disabled={editorMode} name={name} required={required} style={inputStyle} type="checkbox" value="true"/><span>{placeholder || label}</span></label>;
    } else if (fieldType === "multipleCheckboxes" || fieldType === "radioButtons") {
        control = (
            <div className={`morrow-choice-group is-${choiceLayout}`} style={inputStyle}>
                {choices.map((choice) => <label className="morrow-choice" key={choice}><input defaultChecked={defaultValue.split(/\r?\n|,/).includes(choice)} disabled={editorMode} name={name} type={fieldType === "radioButtons" ? "radio" : "checkbox"} value={choice}/><span>{choice}</span></label>)}
            </div>
        );
    } else {
        control = <input defaultValue={fieldType === "fileupload" ? undefined : defaultValue} disabled={fieldType === "fileupload" ? editorMode : undefined} name={name} placeholder={placeholder} readOnly={fieldType !== "fileupload" ? editorMode : undefined} required={required} step={fieldType === "decimalNumber" ? "any" : undefined} style={inputStyle} type={fieldHtmlType(fieldType)}/>;
    }

    return (
        <div className={`morrow-form-field is-label-${labelsPosition}`} data-shop-block-part="field" style={partVisualStyle(block, "field")}>
            {labelsPosition !== "none" ? (
                <label className="morrow-form-label" data-shop-block-part="label" data-shop-inline-text-part="label" style={optionStyle(block, "label")}>
                    <span>{richTextContent(block, "label", label)}</span>{showMarker ? <sup>{marker}</sup> : null}
                </label>
            ) : null}
            <div className="morrow-form-control" data-shop-block-part="input" style={optionStyle(block, "input")}>
                {control}
                {optionText(block, "showDescription") === "true" && description ? <small data-shop-block-part="description" data-shop-inline-text-part="description" style={optionStyle(block, "description")}>{richTextContent(block, "description", description)}</small> : null}
            </div>
        </div>
    );
}

function MorrowSubmitButtonBlock({ block, disabled = false }: { block: ShopTemplateBlock; disabled?: boolean }) {
    const position = optionText(block, "buttonPosition") || "right";

    return (
        <div className={`morrow-submit is-${position}`} style={visualStyle(block)}>
            <button data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton" disabled={disabled} style={buttonPartStyle(block, "primaryButton")} type="submit">
                {richTextContent(block, "label", contentText(block, "label", "Submit"))}
            </button>
        </div>
    );
}

function morrowNestedFormBlocks(block: ShopTemplateBlock): ShopTemplateBlock[] {
    const children = [
        ...Object.values(block.slots ?? {}).flat(),
        ...(block.children ?? []),
    ];

    return children.flatMap((child) => [
        child,
        ...(child.type === "form" ? [] : morrowNestedFormBlocks(child)),
    ]);
}

function morrowAuthoredFormFieldNames(
    block: ShopTemplateBlock,
): ReadonlyMap<ShopTemplateBlock, string> {
    return new Map(
        siteBuilderAuthoredFormFields(block).map((field) => [
            field.block,
            field.key,
        ]),
    );
}

function MorrowFormBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const [error, setError] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [startedAt] = useState(() => Math.floor(Date.now() / 1_000));
    const labelsPosition = optionText(block, "formLabelsPosition") || "sideGap";
    const labelsWidth = optionText(block, "formLabelsWidth") || "200px";
    const actionUrl = contentText(block, "actionUrl", "").trim();
    const formAction = optionText(block, "formAction") || "sendEmail";
    const legacyPurpose = contentText(block, "purpose", "contact").trim() || "contact";
    const formKey = siteBuilderFormConfig(block).formKey ?? legacyPurpose;
    const leadFormLookup = useSiteBuilderLeadForm({
        enabled: !editorMode && actionUrl === "" && formAction !== "createCustomer",
        formKey,
    });
    const leadForm = leadFormLookup.form;
    const authoredFields = useMemo(
        () => siteBuilderAuthoredFormFields(block),
        [block],
    );
    const authoredFieldNames = useMemo(
        () => morrowAuthoredFormFieldNames(block),
        [block],
    );
    const authoredFieldKeys = new Set(authoredFieldNames.values());
    const missingLeadFields = leadForm?.fields.filter((field) => !authoredFieldKeys.has(field.key)) ?? [];
    const formRuntime = useMemo<MorrowLeadFormRuntimeContextValue>(() => {
        const authoredByBlock = new Map(authoredFields.map((field) => [
            field.block,
            field,
        ]));
        const primaryFieldsByKey = new Map<string, ShopTemplateBlock>();

        authoredFields.forEach((field) => {
            if (!primaryFieldsByKey.has(field.key)) {
                primaryFieldsByKey.set(field.key, field.block);
            }
        });

        return {
            authoredFields: authoredByBlock,
            enforceSchema: actionUrl === "" && formAction !== "createCustomer",
            fieldsByKey: new Map((leadForm?.fields ?? []).map((field) => [field.key, field])),
            formBlock: block,
            primaryFieldsByKey,
            runtime,
            schemaResolved: leadForm !== null,
        };
    }, [actionUrl, authoredFields, block, formAction, leadForm, runtime]);
    const hasFileUpload = morrowNestedFormBlocks(block).some((child) => (
        child.type === "field" && optionText(child, "fieldInputType") === "fileupload"
    ));
    const style = {
        ...partVisualStyle(block, "form"),
        "--morrow-form-label-width": labelsWidth,
    } as CSSProperties;

    async function submitForm(event: FormEvent<HTMLFormElement>): Promise<void> {
        if (editorMode) {
            event.preventDefault();

            return;
        }

        if (actionUrl !== "") {
            if (runtime.storefrontPreview) {
                event.preventDefault();
            }

            return;
        }

        event.preventDefault();

        if (submitting || leadFormLookup.loading) {
            return;
        }

        const form = event.currentTarget;
        const ownerWindow = form.ownerDocument.defaultView;
        const payload = siteBuilderLeadValuesFromFormData(new FormData(form));
        setError(false);
        setSubmitted(false);
        setSubmitting(true);

        try {
            const succeeded = formAction === "createCustomer"
                ? await runtime.registerCustomer(
                    String(payload.email ?? ""),
                    String(payload.password ?? ""),
                    String(payload.passwordConfirmation ?? ""),
                    payload.newsletterOptOut === "true",
                )
                : (await submitSiteBuilderLead({
                    endpoint: "/api/lead/submit",
                    fields: payload,
                    form: leadForm,
                    honeypotValue: String(payload.honeypotValue ?? ""),
                    landingUrl: runtime.storefrontLocationHref
                        ?? ownerWindow?.location.href
                        ?? "",
                    purpose: formKey,
                    startedAt,
                })).ok;

            if (!succeeded) {
                throw new Error("form-submit-failed");
            }

            form.reset();
            const successMode = optionText(block, "formOnSuccess") || "message";
            const successUrl = contentText(block, "successUrl", "").trim();
            const localizedSuccessUrl = successUrl === "" ? "" : runtime.storefrontHref(successUrl);
            const hasPublishedSuccessPage = runtime.pages.some((page) => (
                runtime.storefrontHref(page.href ?? `/${page.slug}`) === localizedSuccessUrl
            ));

            if (successMode === "redirect" && localizedSuccessUrl && hasPublishedSuccessPage) {
                await runtime.navigateCatalog(localizedSuccessUrl);

                return;
            }

            setSubmitted(successMode !== "nothing");
        } catch {
            setError(true);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form
            action={actionUrl || undefined}
            aria-busy={submitting || leadFormLookup.loading}
            className={`morrow-builder-form is-label-${labelsPosition}`}
            data-shop-block-part="form"
            encType={actionUrl && hasFileUpload ? "multipart/form-data" : undefined}
            method={actionUrl ? "post" : undefined}
            onSubmit={(event) => void submitForm(event)}
            style={style}
        >
            {actionUrl === "" ? <input autoComplete="off" hidden name="honeypotValue" tabIndex={-1} type="text"/> : null}
            {error ? <p className="morrow-error" role="alert">{runtime.storefrontMessage("ui.common.retry")}</p> : null}
            {submitted ? (
                <p className="morrow-account-success" role="status">
                    {runtime.storefrontMessage(
                        formAction === "createCustomer"
                            ? "ui.account.registrationCheckEmail"
                            : "block.systemContactForm.successMessage",
                    )}
                </p>
            ) : null}
            <MorrowLeadFormRuntimeContext.Provider value={formRuntime}>
                {missingLeadFields.length > 0 ? (
                    <div className="morrow-form-items" data-morrow-lead-schema-fields="true" style={{ marginBottom: 16 }}>
                        {missingLeadFields.map((field) => (
                            <MorrowLeadFormField
                                field={field}
                                key={field.key}
                                labelsPosition={labelsPosition}
                                runtime={runtime}
                            />
                        ))}
                    </div>
                ) : null}
                <MorrowSlotBlockGroup
                    block={block}
                    className="morrow-form-items"
                    editorMode={editorMode}
                    part={part}
                    runtime={runtime}
                    slotName="items"
                    renderItem={(child, _index, childPart) => child.type === "field"
                        ? (
                            <MorrowFormFieldBlock
                                block={child}
                                editorMode={editorMode}
                                fieldName={authoredFieldNames.get(child)}
                                formBlock={block}
                                part={childPart}
                                runtime={runtime}
                                schema={leadForm?.fields.find((field) => (
                                    field.key === authoredFieldNames.get(child)
                                ))}
                            />
                        )
                        : child.type === "submitButton"
                            ? <MorrowSubmitButtonBlock block={child} disabled={submitting || leadFormLookup.loading}/>
                            : <MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/>}
                />
            </MorrowLeadFormRuntimeContext.Provider>
        </form>
    );
}

type MorrowChartSeries = { borderColor: string; color: string; label: string };
type MorrowChartRow = { label: string; values: number[] };

const morrowChartPalette = [
    "var(--site-primary)",
    "var(--site-accent)",
    "color-mix(in srgb, var(--site-primary) 62%, var(--site-accent))",
    "color-mix(in srgb, var(--site-accent) 62%, var(--site-primary))",
    "color-mix(in srgb, var(--site-primary) 52%, var(--site-text))",
    "color-mix(in srgb, var(--site-accent) 52%, var(--site-text))",
];

function morrowChartSeries(block: ShopTemplateBlock): MorrowChartSeries[] {
    const parsed = safeJsonArray(record(block.content).chartSeries)
        .map((item, index): MorrowChartSeries | null => {
            const value = record(item);
            const color = typeof value.color === "string" && value.color ? value.color : morrowChartPalette[index % morrowChartPalette.length];
            const label = typeof value.label === "string" && value.label ? value.label : `Series ${index + 1}`;

            return color ? { borderColor: typeof value.borderColor === "string" && value.borderColor ? value.borderColor : color, color, label } : null;
        })
        .filter((item): item is MorrowChartSeries => item !== null);

    return parsed.length > 0 ? parsed : [{ borderColor: "var(--site-border-strong)", color: "var(--site-primary)", label: "Series 1" }];
}

function morrowChartRows(block: ShopTemplateBlock, seriesCount: number): MorrowChartRow[] {
    const parsed = safeJsonArray(record(block.content).chartRows)
        .map((item): MorrowChartRow | null => {
            const value = record(item);
            if (typeof value.label !== "string") return null;
            const values = Array.isArray(value.values) ? value.values : [];

            return {
                label: value.label,
                values: Array.from({ length: seriesCount }, (_, index) => {
                    const number = Number(values[index]);

                    return Number.isFinite(number) ? number : 0;
                }),
            };
        })
        .filter((item): item is MorrowChartRow => item !== null);

    if (parsed.length > 0) return parsed;

    return multilineContent(block, "items").map((item) => {
        const [label, rawValue] = item.split(":");
        const value = Number(rawValue);

        return { label: label || "Item", values: [Number.isFinite(value) ? value : 0, ...Array.from({ length: Math.max(0, seriesCount - 1) }, () => 0)] };
    });
}

function morrowChartBounds(block: ShopTemplateBlock, rows: MorrowChartRow[]): { max: number; min: number } {
    const values = rows.flatMap((row) => row.values);
    const configuredMin = Number(optionText(block, "chartMinAxis"));
    const configuredMax = Number(optionText(block, "chartMaxAxis"));
    const dataMin = Math.min(0, ...values);
    const dataMax = Math.max(1, ...values);
    const min = optionText(block, "chartMinAxis") && Number.isFinite(configuredMin) ? configuredMin : dataMin;
    const max = optionText(block, "chartMaxAxis") && Number.isFinite(configuredMax) ? configuredMax : Math.ceil(dataMax / 5) * 5;

    return max > min ? { max, min } : { max: min + 1, min };
}

function morrowChartRatio(value: number, min: number, max: number): number {
    return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

function polarPoint(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
    const radians = (angle - 90) * Math.PI / 180;

    return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function pieArc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
    const start = polarPoint(cx, cy, radius, endAngle);
    const end = polarPoint(cx, cy, radius, startAngle);

    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${endAngle - startAngle > 180 ? 1 : 0} 0 ${end.x} ${end.y} Z`;
}

function MorrowChartGraphic({
    block,
    rows,
    series,
}: {
    block: ShopTemplateBlock;
    rows: MorrowChartRow[];
    series: MorrowChartSeries[];
}) {
    const type = optionText(block, "chartType") || "barVertical";
    const tooltip = optionText(block, "chartTooltip") !== "false";
    const strokeWidth = Math.max(0, optionNumber(block, "chartBorderWidth", 2));
    const { max, min } = morrowChartBounds(block, rows);
    const grid = "var(--site-border)";
    const ink = "var(--site-text)";

    if (type === "pie" || type === "doughnut") {
        const values = rows.map((row) => Math.max(0, row.values[0] ?? 0));
        const total = values.reduce((sum, value) => sum + value, 0) || 1;
        let angle = 0;

        return (
            <svg className="morrow-chart-svg" role="img" viewBox="0 0 680 320">
                {rows.map((row, index) => {
                    const value = values[index] ?? 0;
                    const next = angle + value / total * 360;
                    const path = pieArc(340, 160, 125, angle, next);
                    const configuredSeries = series[index % Math.max(1, series.length)] ?? series[0];
                    const color = configuredSeries?.color || morrowChartPalette[index % morrowChartPalette.length];
                    const borderColor = configuredSeries?.borderColor || "var(--site-background)";
                    angle = next;

                    return <path d={path} fill={color} key={`${row.label}:${index}`} stroke={borderColor} strokeWidth={strokeWidth}>{tooltip ? <title>{`${row.label}: ${value}`}</title> : null}</path>;
                })}
                {type === "doughnut" ? <circle cx="340" cy="160" fill="var(--site-background)" r="70"/> : null}
            </svg>
        );
    }

    if (type === "radar") {
        const center = { x: 340, y: 160 };
        const radius = 116;
        const angles = rows.map((_, index) => index * 360 / Math.max(1, rows.length));

        return (
            <svg className="morrow-chart-svg" role="img" viewBox="0 0 680 320">
                {[.25, .5, .75, 1].map((ring) => <polygon fill="none" key={ring} points={angles.map((angle) => {
                    const point = polarPoint(center.x, center.y, radius * ring, angle);
                    return `${point.x},${point.y}`;
                }).join(" ")} stroke={grid}/>)}
                {series.map((item, seriesIndex) => <polygon fill={item.color} fillOpacity=".18" key={item.label} points={rows.map((row, rowIndex) => {
                    const point = polarPoint(center.x, center.y, radius * morrowChartRatio(row.values[seriesIndex] ?? 0, min, max), angles[rowIndex] ?? 0);
                    return `${point.x},${point.y}`;
                }).join(" ")} stroke={item.borderColor} strokeWidth={strokeWidth || 2}>{tooltip ? <title>{item.label}</title> : null}</polygon>)}
                {rows.map((row, index) => {
                    const point = polarPoint(center.x, center.y, radius + 22, angles[index] ?? 0);
                    return <text dominantBaseline="middle" fill={ink} fontSize="12" fontWeight="700" key={row.label} textAnchor="middle" x={point.x} y={point.y}>{row.label}</text>;
                })}
            </svg>
        );
    }

    if (type === "barHorizontal") {
        const plot = { height: 225, left: 108, top: 26, width: 520 };
        const rowHeight = plot.height / Math.max(1, rows.length);
        const barHeight = Math.max(7, Math.min(20, rowHeight / Math.max(1, series.length) - 3));

        return (
            <svg className="morrow-chart-svg" role="img" viewBox="0 0 680 320">
                {[0, .25, .5, .75, 1].map((step) => <line key={step} stroke={grid} x1={plot.left + plot.width * step} x2={plot.left + plot.width * step} y1={plot.top} y2={plot.top + plot.height}/>)}
                {rows.map((row, rowIndex) => <g key={row.label}><text dominantBaseline="middle" fill={ink} fontSize="12" fontWeight="700" textAnchor="end" x={plot.left - 10} y={plot.top + rowIndex * rowHeight + rowHeight / 2}>{row.label}</text>{series.map((item, seriesIndex) => {
                    const value = row.values[seriesIndex] ?? 0;
                    return <rect fill={item.color} height={barHeight} key={item.label} rx="3" stroke={item.borderColor} strokeWidth={strokeWidth} width={plot.width * morrowChartRatio(value, min, max)} x={plot.left} y={plot.top + rowIndex * rowHeight + (rowHeight - barHeight * series.length) / 2 + seriesIndex * barHeight}>{tooltip ? <title>{`${row.label} · ${item.label}: ${value}`}</title> : null}</rect>;
                })}</g>)}
            </svg>
        );
    }

    const plot = { height: 225, left: 54, top: 24, width: 580 };
    const categoryWidth = plot.width / Math.max(1, rows.length);

    return (
        <svg className="morrow-chart-svg" role="img" viewBox="0 0 680 320">
            {[0, .25, .5, .75, 1].map((step) => {
                const y = plot.top + plot.height * (1 - step);
                return <g key={step}><line stroke={grid} x1={plot.left} x2={plot.left + plot.width} y1={y} y2={y}/><text fill={ink} fontSize="11" textAnchor="end" x={plot.left - 9} y={y + 4}>{Math.round(min + (max - min) * step)}</text></g>;
            })}
            {rows.map((row, index) => <text fill={ink} fontSize="11" fontWeight="700" key={row.label} textAnchor="middle" x={plot.left + index * categoryWidth + categoryWidth / 2} y={plot.top + plot.height + 24}>{row.label}</text>)}
            {type === "line" ? series.map((item, seriesIndex) => {
                const points = rows.map((row, rowIndex) => {
                    const x = plot.left + rowIndex * categoryWidth + categoryWidth / 2;
                    const y = plot.top + plot.height * (1 - morrowChartRatio(row.values[seriesIndex] ?? 0, min, max));
                    return `${x},${y}`;
                }).join(" ");

                return <polyline fill="none" key={item.label} points={points} stroke={item.borderColor} strokeLinecap="round" strokeLinejoin="round" strokeWidth={Math.max(2, strokeWidth)}/>;
            }) : rows.flatMap((row, rowIndex) => {
                const groupWidth = Math.min(86, categoryWidth * .75);
                const barWidth = Math.max(6, groupWidth / Math.max(1, series.length) - 4);

                return series.map((item, seriesIndex) => {
                    const value = row.values[seriesIndex] ?? 0;
                    const height = plot.height * morrowChartRatio(value, min, max);
                    const x = plot.left + rowIndex * categoryWidth + (categoryWidth - groupWidth) / 2 + seriesIndex * (barWidth + 4);
                    const y = plot.top + plot.height - height;
                    return <rect fill={item.color} height={height} key={`${row.label}:${item.label}`} rx="4" stroke={item.borderColor} strokeWidth={strokeWidth} width={barWidth} x={x} y={y}>{tooltip ? <title>{`${row.label} · ${item.label}: ${value}`}</title> : null}</rect>;
                });
            })}
        </svg>
    );
}

function MorrowChartLegend({ series }: { series: MorrowChartSeries[] }) {
    return <div className="morrow-chart-legend">{series.map((item) => <span key={item.label}><i style={{ backgroundColor: item.color }}/>{item.label}</span>)}</div>;
}

function MorrowChartBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const series = morrowChartSeries(block);
    const rows = morrowChartRows(block, series.length);
    const legend = optionText(block, "chartLegend") || "top";
    const sideLegend = legend === "left" || legend === "right";
    const graphic = <MorrowChartGraphic block={block} rows={rows} series={series}/>;

    return (
        <section className="morrow-chart" data-shop-block-part="chart" style={partVisualStyle(block, "chart")}>
            <h3 data-shop-block-part="title" data-shop-inline-text-part="title" style={partVisualStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", runtime.storefrontMessage("block.chart.title")))}</h3>
            <MorrowSlotBlockGroup block={block} className="morrow-chart-after-title" editorMode={editorMode} part={part} runtime={runtime} slotName="afterTitle"/>
            <div className={`morrow-chart-layout is-${legend}`}>
                {legend === "left" ? <MorrowChartLegend series={series}/> : null}
                <div className={sideLegend ? "morrow-chart-graphic" : "morrow-chart-graphic is-stacked"}>
                    {legend === "top" ? <MorrowChartLegend series={series}/> : null}
                    {graphic}
                    {legend === "bottom" ? <MorrowChartLegend series={series}/> : null}
                </div>
                {legend === "right" ? <MorrowChartLegend series={series}/> : null}
            </div>
        </section>
    );
}

function MorrowTextHighlightBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const alignment = optionText(block, "alignment") || "center";
    const style = partVisualStyle(block, "container");
    const width = style.width ?? (optionText(block, "textHighlightWidth") || "100%");
    const title = contentText(block, "title", "");

    return (
        <section className={`morrow-text-highlight is-${alignment}`} data-shop-block-part="container" style={{ ...style, "--morrow-mobile-content-width": width, marginLeft: style.marginLeft ?? (alignment === "right" || alignment === "center" ? "auto" : undefined), marginRight: style.marginRight ?? (alignment === "center" ? "auto" : undefined), width } as CSSProperties}>
            <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="beforeTitle"/>
            {title || editorMode ? <h3 data-shop-block-part="title" data-shop-inline-text-part="title" style={partVisualStyle(block, "title")}>{richTextContent(block, "title", title || "\u200b")}</h3> : null}
            <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="afterTitle"/>
            <p data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", editorMode ? "\u200b" : ""))}</p>
            <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="content"/>
        </section>
    );
}

function MorrowBlockquoteBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const showAuthor = optionText(block, "showAuthor") !== "false";
    const alignment = optionText(block, "authorAlignment") || "left";
    const decoration = optionText(block, "blockquoteDecoration") || "none";
    const shape = optionText(block, "blockquoteShape") || "none";
    const style = partVisualStyle(block, "container");
    const width = style.width ?? (optionText(block, "blockquoteWidth") || "100%");
    const edgeSpacing = optionText(block, "edgeSpacing");
    const hasDirectionalPadding = style.paddingTop !== undefined
        || style.paddingRight !== undefined
        || style.paddingBottom !== undefined
        || style.paddingLeft !== undefined;

    return (
        <blockquote className={`morrow-quote is-${shape} has-${decoration}`} data-shop-block-part="container" style={{ ...style, "--morrow-mobile-content-width": width, padding: style.padding ?? (hasDirectionalPadding ? undefined : edgeSpacing || undefined), marginLeft: style.marginLeft ?? (alignment === "right" || alignment === "center" ? "auto" : undefined), marginRight: style.marginRight ?? (alignment === "center" ? "auto" : undefined), width } as CSSProperties}>
            {decoration === "icon" ? <MorrowSlotBlockGroup block={block} className="morrow-quote-icon" editorMode={editorMode} part={part} runtime={runtime} slotName="icon"/> : null}
            <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="beforeText"/>
            <p data-shop-block-part="text" data-shop-inline-text-part="text" style={partVisualStyle(block, "text")}>{richTextContent(block, "text", contentText(block, "text", editorMode ? "\u200b" : ""))}</p>
            <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="afterText"/>
            <footer className={`is-${alignment}`}>
                <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="beforeImage"/>
                <MorrowSlotBlockGroup block={block} className="morrow-quote-media" editorMode={editorMode} part={part} runtime={runtime} slotName="media"/>
                {showAuthor ? <strong data-shop-block-part="author" data-shop-inline-text-part="author" style={partVisualStyle(block, "author")}>{richTextContent(block, "author", contentText(block, "author", editorMode ? "\u200b" : ""))}</strong> : null}
                <MorrowSlotBlockGroup block={block} className="morrow-flow-slot" editorMode={editorMode} part={part} runtime={runtime} slotName="afterImage"/>
            </footer>
        </blockquote>
    );
}

function MorrowBadgeBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const href = functionalStorefrontHref(contentText(block, "href", ""));
    const Tag = href ? "a" : "span";
    const variants: Record<string, CSSProperties> = {
        danger: { backgroundColor: "var(--site-accent-soft)", borderColor: "var(--site-accent)", color: "var(--site-text)" },
        dark: { backgroundColor: "var(--site-inverse)", borderColor: "var(--site-inverse)", color: "var(--site-on-inverse)" },
        info: { backgroundColor: "var(--site-primary-soft)", borderColor: "var(--site-primary)", color: "var(--site-text)" },
        light: { backgroundColor: "var(--site-surface)", borderColor: "var(--site-border)", color: "var(--site-on-surface)" },
        primary: { backgroundColor: "color-mix(in srgb, var(--site-primary) 12%, var(--site-background))", borderColor: "var(--site-primary)", color: "var(--site-primary)" },
        secondary: { backgroundColor: "var(--site-accent-soft)", borderColor: "var(--site-accent)", color: "var(--site-text)" },
        success: { backgroundColor: "var(--site-primary-soft)", borderColor: "var(--site-primary)", color: "var(--site-text)" },
        warning: { backgroundColor: "var(--site-accent-soft)", borderColor: "var(--site-accent)", color: "var(--site-text)" },
    };
    const variant = optionText(block, "badgeStyle") || "secondary";

    return (
        <Tag className={`morrow-badge is-${variant}`} data-shop-block-part="badge" href={href ? (href.startsWith("/") ? runtime.storefrontHref(href) : href) : undefined} onClick={href ? (event) => editorMode && event.preventDefault() : undefined} style={{ ...variants[variant], ...partVisualStyle(block, "badge") }}>
            <MorrowSlotBlockGroup block={block} className="morrow-badge-icons" editorMode={editorMode} part={part} runtime={runtime} slotName="icon"/>
            <span data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton" style={optionStyle(block, "primaryButton")}>{richTextContent(block, "label", contentText(block, "label", "Badge"))}</span>
        </Tag>
    );
}

function MorrowCtaBadgeBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const icons = block.slots?.icon ?? [];
    const slotPart = morrowSlotPart(part, "icon");
    const leading = icons.map((child, index) => ({ child, index })).filter(({ child }) => optionText(child, "alignment") !== "right");
    const trailing = icons.map((child, index) => ({ child, index })).filter(({ child }) => optionText(child, "alignment") === "right");
    const href = functionalStorefrontHref(contentText(block, "href", ""));
    const icon = ({ child, index }: { child: ShopTemplateBlock; index: number }) => {
        const childPart = `${slotPart}:${index}`;

        return <span data-shop-block-part={childPart} key={`${child.uuid ?? child.type}:${index}`}><MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/></span>;
    };

    return (
        <span className="morrow-cta-badge" data-shop-block-part="badge" data-shop-slot-container={slotPart} data-shop-slot-item-count={icons.length} style={partVisualStyle(block, "badge")}>
            {leading.map(icon)}
            <span className="morrow-cta-title" data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", ""))}</span>
            <a data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton" href={href ? (href.startsWith("/") ? runtime.storefrontHref(href) : href) : undefined} onClick={(event) => editorMode && event.preventDefault()} style={buttonPartStyle(block, "primaryButton", false)}>{richTextContent(block, "label", contentText(block, "label", runtime.storefrontMessage("ui.common.open")))}</a>
            {trailing.map(icon)}
        </span>
    );
}

function MorrowSocialBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const slotName = "items";
    const title = contentText(block, "title", "");
    const share = block.type === "share";
    const alignment = optionText(block, share ? "shareAlignment" : "socialAlignment") || "left";
    const titlePosition = optionText(block, share ? "shareTitlePosition" : "socialTitlePosition") || "none";
    const iconSize = optionText(block, share ? "shareIconSize" : "socialIconSize") || "40";
    const iconLayout = optionText(block, share ? "shareIconLayout" : "socialIconLayout") || "disk";
    const linkStyle = optionText(block, share ? "shareLinkStyle" : "socialLinkStyle") || "noUnderline";
    const titleValue = title || (editorMode && titlePosition !== "none" ? "\u200b" : "");
    const inheritedOptions: Record<string, unknown> = {
        socialIconBackgroundColor: share ? optionText(block, "backgroundColor") : optionText(block, "socialIconBackgroundColor"),
        socialIconBackgroundImage: optionText(block, "socialIconBackgroundImage"),
        socialIconBrandColor: share ? optionText(block, "shareColored") || "true" : optionText(block, "socialIconBrandColor") || "true",
        socialIconColor: share ? optionText(block, "textColor") : optionText(block, "socialIconColor"),
        socialIconLayout: iconLayout,
        socialIconSize: iconSize,
        socialIconTheme: optionText(block, "socialIconTheme") || "default",
        socialLinkStyle: linkStyle,
    };

    return (
        <div className={`morrow-social is-${alignment} has-title-${titlePosition}`} data-shop-block-part="social" style={partVisualStyle(block, "social")}>
            {titlePosition !== "none" ? <strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", titleValue)}</strong> : null}
            <MorrowSlotBlockGroup
                block={block}
                className="morrow-social-items"
                editorMode={editorMode}
                part={part}
                runtime={runtime}
                slotName={slotName}
                renderItem={(child, _index, childPart) => {
                    const effectiveBlock: ShopTemplateBlock = {
                        ...child,
                        options: {
                            ...inheritedOptions,
                            ...record(child.options),
                        },
                    };

                    if (share && child.type === "icon") {
                        const href = contentText(child, "href", "");
                        const iconName = contentText(child, "icon", "link");
                        const label = contentText(child, "label", iconName);

                        return (
                            <SiteBuilderShareAnchor
                                aria-label={label}
                                className={`morrow-social-link is-link-${linkStyle}`}
                                data-shop-block-part={childPart}
                                data-shop-share-icon-part={childPart}
                                editorMode={editorMode}
                                explicitHref={href}
                                locationHref={runtime.storefrontLocationHref}
                                network={siteBuilderShareNetwork(`${iconName} ${label} ${href}`)}
                                shareTitle={title || runtime.displayName}
                                style={socialIconStyle(effectiveBlock, href, iconName)}
                            >
                                <MorrowIcon name={iconName} size={Math.max(14, Number(iconSize.replace("px", "")) * .52 || 21)}/>
                            </SiteBuilderShareAnchor>
                        );
                    }

                    return <MorrowInnerBlock block={effectiveBlock} editorMode={editorMode} part={childPart} runtime={runtime}/>;
                }}
            />
        </div>
    );
}

function MorrowCardBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const cardContent = record(block.content);
    const imagePresentation = siteBuilderMediaPresentation({
        image: cardContent.image,
        imageUrl: cardContent.imageUrl,
        src: cardContent.src,
    });
    const imageUrl = imagePresentation.kind === "image"
        ? imagePresentation.source
        : "";
    const hasContentSlot = (block.slots?.content?.length ?? 0) > 0;
    const actionLabelKey = contentText(block, "actionLabel", "")
        ? "actionLabel"
        : contentText(block, "buttonLabel", "")
            ? "buttonLabel"
            : "label";
    const actionLabel = contentText(block, actionLabelKey, "");
    const actionHref = contentText(
        block,
        "href",
        contentText(block, "buttonUrl", contentText(block, "url", "")),
    );
    const title = contentText(block, "title", "");
    const text = contentText(block, "text", contentText(block, "description", ""));

    return (
        <article className={`morrow-inner-card is-image-${optionText(block, "imageLayout") || "top"}`} style={visualStyle(block)}>
            {imageUrl ? <img alt={contentText(block, "alt", title)} data-shop-block-part="image" src={imageUrl} style={imagePartStyle(block, "image", false)} title={contentText(block, "imageTitle", "") || undefined}/> : null}
            {hasContentSlot ? <MorrowSlotBlockGroup block={block} className="morrow-card-content" editorMode={editorMode} part={part} runtime={runtime} slotName="content"/> : <div className="morrow-card-content"><h3 data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", title)}</h3><p data-shop-block-part="text" data-shop-inline-text-part="text" style={optionStyle(block, "text")}>{richTextContent(block, "text", text)}</p>{actionLabel ? <a data-shop-block-part="primaryButton" data-shop-inline-text-part="primaryButton" href={functionalStorefrontHref(actionHref)} onClick={(event) => editorMode && event.preventDefault()} style={buttonPartStyle(block, "primaryButton", false)}>{richTextContent(block, actionLabelKey, actionLabel)}</a> : null}</div>}
        </article>
    );
}

function MorrowCountdownBlock({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const items = useSiteBuilderCountdown(contentText(block, "targetDate", ""));

    return (
        <div className="morrow-countdown-block" data-shop-block-part="countdown" style={partVisualStyle(block, "countdown")}>
            <strong data-shop-block-part="title" data-shop-inline-text-part="title" style={optionStyle(block, "title")}>{richTextContent(block, "title", contentText(block, "title", ""))}</strong>
            <div data-shop-block-part="items" style={optionStyle(block, "items")}>{items.map((item) => <span key={item.labelKey}><b>{item.text}</b><small>{runtime.storefrontMessage(item.labelKey)}</small></span>)}</div>
        </div>
    );
}

function morrowHeaderLogoChildKind(block: ShopTemplateBlock): "mark" | "text" {
    return ["icon", "image", "media"].includes(block.type) ? "mark" : "text";
}

function morrowHeaderLogoChildVisible(
    block: ShopTemplateBlock,
    display: "mark" | "markText" | "text",
): boolean {
    if (display === "markText") {
        return true;
    }

    return morrowHeaderLogoChildKind(block) === display;
}

function morrowHeaderLogoChildHasRenderableContent(block: ShopTemplateBlock): boolean {
    if (block.type === "image" || block.type === "media") {
        return siteBuilderMediaPresentation(block.content).kind !== "empty";
    }

    if (block.type === "icon") {
        return contentText(block, "icon", "").trim() !== "";
    }

    if (["heading", "paragraph", "text"].includes(block.type)) {
        return optionText(block, "runtimeText") !== ""
            || contentText(block, "text", contentText(block, "title", "")).trim() !== "";
    }

    return true;
}

function MorrowColumnBlock({
    block,
    editorMode,
    part,
    runtime,
}: {
    block: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const configuredSlots = Object.entries(block.slots ?? {});
    const slots = configuredSlots.length > 0
        ? configuredSlots
        : [["content", [] as ShopTemplateBlock[]] as const];
    const documentRole = optionText(block, "documentRole");
    const headerRole = shopHeaderRole(block);
    const headerAlignment = headerRole ? resolveShopHeaderAlignment(block) : null;
    const utilityDisplay = headerRole ? resolveShopHeaderUtilityDisplay(block) : null;
    const logoDisplay = headerRole === "headerLogo" ? resolveShopHeaderLogoDisplay(block) : null;
    const contentLayout = optionText(block, "contentLayout");
    const inlineActions = contentLayout === "inlineActions";
    const logoOrientation = headerRole === "headerLogo" && contentLayout === "vertical"
        ? "vertical"
        : "horizontal";
    const inline = inlineActions
        || contentLayout === "inline"
        || contentLayout === "horizontal"
        || headerRole === "headerLogo";
    const commerceRuntimeRole = optionText(block, SHOP_COMMERCE_RUNTIME_ROLE_OPTION);
    const alignment = headerAlignment ?? optionText(block, "alignment");
    const verticalAlign = optionText(block, "verticalAlign");
    const stackOnMobile = mobileStackOption(block) ?? true;
    const style = visualStyle(block);
    const slotGap = optionText(block, "gap") || optionText(block, "rowGap") || "12px";
    const layoutRows: Record<string, string> = {
        contactStack: "64px 240px 64px",
        editorialStack: "64px 240px 64px 64px",
        featureStack: "64px 64px 64px",
        heroStack: "64px 240px 152px 64px 64px",
        notFoundStack: "64px 240px 64px 64px",
        searchStack: "152px 64px 64px 64px",
    };
    const gridTemplateRows = layoutRows[contentLayout];
    const justifyContent = alignment === "center"
        ? "center"
        : alignment === "right"
            ? "flex-end"
            : "flex-start";

    const logoHasRenderableContent = headerRole !== "headerLogo" || slots.some(([, children]) => (
        children.some((child) => (
            morrowHeaderLogoChildVisible(child, logoDisplay ?? "markText")
            && morrowHeaderLogoChildHasRenderableContent(child)
        ))
    ));
    const contents = slots.map(([slotName, children]) => {
        const slotPart = morrowSlotPart(part, slotName);
        const visibleChildren = children
            .map((child, index) => ({ child, index }))
            .filter(({ child }) => (
                headerRole !== "headerLogo"
                || (
                    morrowHeaderLogoChildVisible(child, logoDisplay ?? "markText")
                    && (editorMode || morrowHeaderLogoChildHasRenderableContent(child))
                )
            ));

        return (
            <div
                className={`morrow-column-content${inline ? " is-inline" : ""}${contentLayout ? ` is-${contentLayout}` : ""}`}
                data-shop-slot-container={slotPart}
                data-shop-slot-item-count={children.length}
                key={slotName}
                style={{
                    alignContent: !inline
                        ? verticalAlign === "center"
                            ? "center"
                            : verticalAlign === "bottom"
                                ? "end"
                                : "start"
                        : undefined,
                    alignItems: headerRole === "headerLogo" && logoOrientation === "vertical"
                        ? alignment === "center"
                            ? "center"
                            : alignment === "right"
                                ? "flex-end"
                                : "flex-start"
                        : inline
                            ? "center"
                            : undefined,
                    display: inline ? "flex" : "grid",
                    flexDirection: headerRole === "headerLogo" ? (logoOrientation === "vertical" ? "column" : "row") : undefined,
                    flexWrap: inlineActions ? "wrap" : undefined,
                    gap: slotGap,
                    gridTemplateRows,
                    gridAutoRows: !inline && !gridTemplateRows ? "max-content" : undefined,
                    justifyContent: inline ? justifyContent : undefined,
                    justifyItems: !inline && alignment === "center" ? "center" : !inline && alignment === "right" ? "end" : undefined,
                    minHeight: editorMode && children.length === 0 ? 48 : undefined,
                    textAlign: alignment === "center" || alignment === "right" ? alignment : undefined,
                }}
            >
                {visibleChildren.map(({ child, index }) => {
                    const childPart = `${slotPart}:${index}`;
                    const buttonLike = ["button", "ctaBadge", "donationButton", "addToCartButton"].includes(child.type);
                    const searchSpacer = contentLayout === "searchStack" && index === 2;
                    const hiddenUtilityChild = headerRole !== null && (
                        (utilityDisplay === "icon" && child.type === "text")
                        || (utilityDisplay === "label" && child.type === "icon")
                    );

                    if (hiddenUtilityChild) {
                        return null;
                    }

                    return (
                        <div
                            key={child.uuid?.trim() || `${child.type}:${index}`}
                            {...morrowInteractionAttributes(child)}
                            className="morrow-column-child"
                            data-morrow-logo-kind={headerRole === "headerLogo" ? morrowHeaderLogoChildKind(child) : undefined}
                            data-shop-block-part={childPart}
                            data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(child) ? "true" : undefined}
                            style={{
                                display: "block",
                                flex: inlineActions && buttonLike ? "1 1 180px" : undefined,
                                flexBasis: inlineActions && !buttonLike ? "100%" : undefined,
                                gridRow: searchSpacer ? 4 : undefined,
                                maxWidth: "100%",
                                minWidth: 0,
                                ...morrowInteractionStyle(child),
                            }}
                        >
                            <MorrowInnerBlock block={child} editorMode={editorMode} part={childPart} runtime={runtime}/>
                        </div>
                    );
                })}
            </div>
        );
    });
    const rootStyle: CSSProperties = {
        display: "grid",
        height: "100%",
        minHeight: "100%",
        ...style,
        ...columnShapeStyle(block),
        alignContent: verticalAlign === "center" ? "center" : verticalAlign === "bottom" ? "end" : style.alignContent,
        justifyContent: headerAlignment === "center" ? "center" : headerAlignment === "right" ? "flex-end" : style.justifyContent,
    };
    const commonAttributes = {
        "data-morrow-header-alignment": headerAlignment ?? undefined,
        "data-morrow-header-role": headerRole ?? undefined,
        "data-morrow-logo-display": logoDisplay ?? undefined,
        "data-morrow-logo-orientation": headerRole === "headerLogo" ? logoOrientation : undefined,
        "data-morrow-role": documentRole || undefined,
        "data-morrow-show-desktop": headerRole ? String(shopHeaderBlockVisibleOn(block, "desktop")) : undefined,
        "data-morrow-show-mobile": headerRole ? String(shopHeaderBlockVisibleOn(block, "mobile")) : undefined,
        "data-morrow-utility-display": utilityDisplay ?? undefined,
        "data-shop-column-part": part,
        "data-shop-column-root": "true",
        "data-site-builder-stack-on-mobile": stackOnMobile ? "true" : "false",
    };

    if (headerRole === "headerLogo") {
        return (
            <a
                {...commonAttributes}
                aria-label={runtime.displayName}
                className="morrow-column morrow-brand-link"
                href={runtime.homeHref}
                onClick={(event) => editorMode && event.preventDefault()}
                style={rootStyle}
            >
                {contents}
                {!logoHasRenderableContent ? <span className="morrow-brand-fallback">{runtime.displayName}</span> : null}
            </a>
        );
    }

    if (headerRole === "headerMenu") {
        return (
            <div
                {...commonAttributes}
                className="morrow-column morrow-header-menu"
                data-shop-commerce-runtime-role={commerceRuntimeRole || undefined}
                style={rootStyle}
            >
                <button
                    aria-controls={MORROW_MOBILE_NAVIGATION_DIALOG_ID}
                    aria-expanded={runtime.mobileOpen}
                    aria-label={runtime.storefrontMessage("ui.header.openMenu")}
                    className="morrow-mobile-menu-button"
                    onClick={() => runtime.setMobileOpen(true)}
                    type="button"
                >
                    <MorrowIcon name="menu"/>
                </button>
                <MorrowSharedNavigation block={block} items={runtime.headerMenuItems} mode="desktop" runtime={runtime}/>
            </div>
        );
    }

    if (headerRole === "headerLanguageSelector" || headerRole === "headerCurrencySelector") {
        const languageSelector = headerRole === "headerLanguageSelector";
        const options = languageSelector ? runtime.languageOptions : runtime.currencyOptions;

        return (
            <div
                {...commonAttributes}
                className={`morrow-column morrow-header-selector-role is-${languageSelector ? "language" : "currency"}`}
                data-shop-commerce-runtime-role={commerceRuntimeRole || undefined}
                style={rootStyle}
            >
                {options.length > 0 ? (
                    <MorrowSelector
                        display={utilityDisplay ?? "label"}
                        icon={languageSelector ? "languages" : "coins"}
                        label={runtime.storefrontMessage(languageSelector ? "ui.header.selectLanguage" : "ui.header.selectCurrency")}
                        onSelect={languageSelector
                            ? runtime.selectStorefrontLanguage
                            : (_code, href) => void runtime.navigateCatalog(href)}
                        options={options}
                    />
                ) : null}
            </div>
        );
    }

    const headerAction = headerRole === "headerStoresButton"
        ? {
            ariaLabel: runtime.storefrontMessage("ui.header.stores"),
            className: "morrow-stores-action",
            href: runtime.storesHref,
        }
        : headerRole === "headerAccountButton"
            ? {
                ariaLabel: runtime.storefrontMessage("ui.header.myAccount"),
                className: "morrow-account-action",
                href: runtime.accountHref,
            }
            : headerRole === "headerFavoritesButton"
                ? {
                    ariaLabel: runtime.storefrontMessage("ui.account.favorites"),
                    className: "morrow-favorites-action",
                    href: runtime.storefrontHref(contentText(block, "href", "/oblibene")),
                }
                : null;

    if (headerAction) {
        return (
            <a
                {...commonAttributes}
                aria-label={headerAction.ariaLabel}
                className={`morrow-column morrow-header-action ${headerAction.className}`}
                href={headerAction.href}
                onClick={(event) => editorMode && event.preventDefault()}
                style={rootStyle}
            >
                {contents}
            </a>
        );
    }

    if (headerRole === "headerCartButton") {
        return (
            <a
                {...commonAttributes}
                aria-label={runtime.storefrontMessage("ui.cart.title")}
                className="morrow-column morrow-header-action morrow-cart-action"
                href={runtime.cartPageHref}
                onClick={(event) => editorMode && event.preventDefault()}
                style={rootStyle}
            >
                {contents}
                <span className="morrow-header-cart-count">{runtime.cart.totalQuantity}</span>
            </a>
        );
    }

    return (
        <div
            {...commonAttributes}
            className={`morrow-column${commerceRuntimeRole ? " has-commerce-runtime" : ""}`}
            data-shop-commerce-runtime-role={commerceRuntimeRole || undefined}
            style={rootStyle}
        >
            {contents}
            <MorrowCommerceRuntimeAnchor block={block} runtime={runtime}/>
        </div>
    );
}

function catalogProductsForMorrowAnchor(
    block: ShopTemplateBlock,
    runtime: ShopStorefrontRuntimeModel,
): PublicHomeProduct[] {
    const source = optionText(block, "catalogSource");
    const configuredLimit = Math.max(1, Math.round(optionNumber(block, "catalogLimit", source === "comparison" ? 3 : 8)));
    const products = availableProducts(runtime);
    const selected = source === "sale" ? products.filter((product) => product.isOnSale) : products;

    return selected.slice(0, configuredLimit);
}

function MorrowCommerceRuntimeAnchor({ block, runtime }: {
    block: ShopTemplateBlock;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const role = optionText(block, SHOP_COMMERCE_RUNTIME_ROLE_OPTION);

    if (role === SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts) {
        return (
            <div className="morrow-commerce-runtime-products" data-shop-runtime-content="products">
                <MorrowProductGrid block={block} products={catalogProductsForMorrowAnchor(block, runtime)} runtime={runtime}/>
            </div>
        );
    }

    if (role === SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories) {
        const candidates = (runtime.activeSectionItems.length > 0
            ? runtime.activeSectionItems
            : runtime.headerMenuItems.flatMap((item) => item.children.length > 0 ? item.children : [item]))
            .slice(0, Math.max(1, Math.round(optionNumber(block, "catalogLimit", 8))));

        return (
            <nav
                aria-label={runtime.storefrontMessage("ui.catalog.catalog")}
                className="morrow-commerce-runtime-categories"
                data-shop-runtime-content="categories"
            >
                {candidates.map((item, index) => (
                    <ShopStorefrontLink
                        href={functionalStorefrontHref(menuItemHref(item))}
                        key={item.uuid}
                        onClick={(event) => runtime.editorMode && event.preventDefault()}
                    >
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <strong>{item.label}</strong>
                        <MorrowIcon name="arrowUpRight" size={18}/>
                    </ShopStorefrontLink>
                ))}
            </nav>
        );
    }

    return null;
}

function MorrowSearchForm({ block, editorMode, part, placeholder, runtime, style }: {
    block?: ShopTemplateBlock;
    editorMode: boolean;
    part: string;
    placeholder: string;
    runtime: ShopStorefrontRuntimeModel;
    style?: CSSProperties;
}) {
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [payload, setPayload] = useState<ShopSearchSuggestionsPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const suggestionsId = useId();
    const normalizedQuery = query.trim();
    const actionIcon = block?.slots?.actionIcon?.[0];
    const buttonLabel = block ? contentText(block, "buttonLabel", "") : "";
    const scope = normalizeShopSearchScope(block ? optionText(block, "searchWithin") : "everything");
    const resultLimit = normalizeShopSearchSuggestionLimit(
        block ? optionNumber(block, "searchResultLimit", 6) : 6,
    );
    const suggestions = useMemo(() => selectShopSearchSuggestions({
        query: normalizedQuery,
        scope,
        limit: resultLimit,
        pages: runtime.pages,
        payload,
        resolveHref: runtime.storefrontHref,
    }), [normalizedQuery, payload, resultLimit, runtime.pages, runtime.storefrontHref, scope]);
    const showSuggestions = !editorMode
        && focused
        && normalizedQuery.length >= SHOP_SEARCH_MIN_QUERY_LENGTH;
    const hasSuggestions = suggestions.pages.length > 0
        || suggestions.products.length > 0
        || suggestions.categories.length > 0;
    const suggestionOptionIds = [
        ...suggestions.pages.map((_, index) => `${suggestionsId}-page-${index}`),
        ...suggestions.categories.map((_, index) => `${suggestionsId}-category-${index}`),
        ...suggestions.products.map((_, index) => `${suggestionsId}-product-${index}`),
    ];

    useEffect(() => {
        if (
            !showSuggestions
            || !shopSearchScopeUsesCatalog(scope)
            || !runtime.shopSearchApiPath
        ) {
            setPayload(null);
            setLoading(false);
            setActiveSuggestionIndex(-1);

            return undefined;
        }

        const abortController = new AbortController();
        setPayload(null);
        setLoading(true);
        setActiveSuggestionIndex(-1);
        const timeout = window.setTimeout(async () => {
            const parameters = new URLSearchParams({
                q: normalizedQuery,
                limit: String(resultLimit),
            });

            if (runtime.storefrontLanguageCode) {
                parameters.set("language", runtime.storefrontLanguageCode);
            }

            try {
                const response = await fetch(`${runtime.shopSearchApiPath}?${parameters.toString()}`, {
                    cache: "no-store",
                    credentials: "same-origin",
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    setPayload(null);

                    return;
                }

                const nextPayload = parseShopSearchSuggestions(await response.json() as unknown);

                if (!abortController.signal.aborted) {
                    setPayload(nextPayload);
                }
            } catch {
                if (!abortController.signal.aborted) {
                    setPayload(null);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 220);

        return () => {
            window.clearTimeout(timeout);
            abortController.abort();
        };
    }, [normalizedQuery, resultLimit, runtime.shopSearchApiPath, runtime.storefrontLanguageCode, scope, showSuggestions]);

    function handleSearchKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
        if (event.key === "Escape") {
            setFocused(false);
            setActiveSuggestionIndex(-1);

            return;
        }

        if (!showSuggestions || loading || suggestionOptionIds.length === 0) return;

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const direction = event.key === "ArrowDown" ? 1 : -1;
            setActiveSuggestionIndex((current) => {
                if (current < 0) return direction > 0 ? 0 : suggestionOptionIds.length - 1;

                return (current + direction + suggestionOptionIds.length) % suggestionOptionIds.length;
            });

            return;
        }

        if (event.key === "Enter" && activeSuggestionIndex >= 0) {
            const ownerDocument = event.currentTarget.ownerDocument;
            const view = ownerDocument.defaultView;
            const activeOption = ownerDocument.getElementById(suggestionOptionIds[activeSuggestionIndex] ?? "");

            if (view && activeOption instanceof view.HTMLAnchorElement) {
                event.preventDefault();
                void runtime.navigateCatalog(
                    activeOption.getAttribute("href") ?? activeOption.href,
                );
            }
        }
    }

    function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (editorMode || normalizedQuery === "") return;

        if (scope === "pages" && suggestions.pages[0]) {
            void runtime.navigateCatalog(suggestions.pages[0].href);

            return;
        }

        if (scope === "categories" && suggestions.categories[0]) {
            void runtime.navigateCatalog(shopSearchCategoryHref(
                suggestions.categories[0],
                runtime.searchHref,
                runtime.storefrontHref,
            ));

            return;
        }

        void runtime.navigateCatalog(
            shopSearchResultsHref(runtime.searchHref, normalizedQuery, scope),
        );
    }

    function iconSlot(slotName: "actionIcon" | "leadingIcon", fallbackName: string, fallbackSize: number) {
        const icon = block?.slots?.[slotName]?.[0];
        const slotPart = morrowSlotPart(part, slotName);
        const childPart = `${slotPart}:0`;

        return (
            <span className="morrow-search-icon-slot" data-shop-slot-container={slotPart} data-shop-slot-item-count={icon ? 1 : 0}>
                {icon ? (
                    <span data-shop-block-part={childPart}>
                        <MorrowInnerBlock block={icon} editorMode={editorMode} part={childPart} runtime={runtime}/>
                    </span>
                ) : <MorrowIcon name={fallbackName} size={fallbackSize}/>}
            </span>
        );
    }

    return (
        <form className="morrow-search-form" onSubmit={submit} role="search" style={style}>
            {iconSlot("leadingIcon", "search", 20)}
            <input
                aria-autocomplete="list"
                aria-activedescendant={activeSuggestionIndex >= 0 ? suggestionOptionIds[activeSuggestionIndex] : undefined}
                aria-controls={showSuggestions ? suggestionsId : undefined}
                aria-expanded={showSuggestions}
                aria-label={runtime.storefrontMessage("ui.search.label")}
                autoComplete="off"
                onBlur={() => window.setTimeout(() => setFocused(false), 140)}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder={placeholder}
                role="combobox"
                type="search"
                value={query}
            />
            <button aria-label={buttonLabel || runtime.storefrontMessage("ui.search.openResults")} type="submit">
                {actionIcon ? iconSlot("actionIcon", "arrowUpRight", 18) : buttonLabel ? (
                    <span
                        data-shop-block-part="buttonLabel"
                        data-shop-inline-text-part="buttonLabel"
                        style={block ? optionStyle(block, "buttonLabel") : undefined}
                    >
                        {block ? richTextContent(block, "buttonLabel", buttonLabel) : buttonLabel}
                    </span>
                ) : iconSlot("actionIcon", "arrowUpRight", 18)}
            </button>
            {showSuggestions ? (
                <div className="morrow-search-suggestions" id={suggestionsId} role="listbox">
                    {loading && shopSearchScopeUsesCatalog(scope) ? (
                        <p className="morrow-search-suggestion-state">{runtime.storefrontMessage("ui.search.searching")}</p>
                    ) : hasSuggestions ? (
                        <>
                            {suggestions.pages.length > 0 ? (
                                <MorrowSearchSuggestionGroup label={runtime.storefrontMessage("ui.search.pages")}>
                                    {suggestions.pages.map((page, index) => (
                                        <a aria-selected={activeSuggestionIndex === index} href={page.href} id={`${suggestionsId}-page-${index}`} key={`${page.href}:${page.title}`} onClick={(event) => editorMode && event.preventDefault()} onMouseEnter={() => setActiveSuggestionIndex(index)} role="option"><span>{page.title}</span><MorrowIcon name="arrowUpRight" size={15}/></a>
                                    ))}
                                </MorrowSearchSuggestionGroup>
                            ) : null}
                            {suggestions.categories.length > 0 ? (
                                <MorrowSearchSuggestionGroup label={runtime.storefrontMessage("ui.search.categories")}>
                                    {suggestions.categories.map((category, index) => {
                                        const optionIndex = suggestions.pages.length + index;

                                        return <a aria-selected={activeSuggestionIndex === optionIndex} href={shopSearchCategoryHref(category, runtime.searchHref, runtime.storefrontHref)} id={`${suggestionsId}-category-${index}`} key={category.uuid} onClick={(event) => editorMode && event.preventDefault()} onMouseEnter={() => setActiveSuggestionIndex(optionIndex)} role="option">
                                            <span><strong>{category.name}</strong>{category.pathNames.length > 0 ? <small>{category.pathNames.join(" / ")}</small> : null}</span>
                                            <MorrowIcon name="arrowUpRight" size={15}/>
                                        </a>;
                                    })}
                                </MorrowSearchSuggestionGroup>
                            ) : null}
                            {suggestions.products.length > 0 ? (
                                <MorrowSearchSuggestionGroup label={runtime.storefrontMessage("ui.search.products")}>
                                    {suggestions.products.map((product, index) => {
                                        const optionIndex = suggestions.pages.length + suggestions.categories.length + index;

                                        return <a aria-selected={activeSuggestionIndex === optionIndex} href={shopSearchProductHref(product, runtime.searchHref, runtime.storefrontHref)} id={`${suggestionsId}-product-${index}`} key={product.uuid} onClick={(event) => editorMode && event.preventDefault()} onMouseEnter={() => setActiveSuggestionIndex(optionIndex)} role="option">
                                            <span><strong>{product.name}</strong>{product.manufacturerName ? <small>{product.manufacturerName}</small> : null}</span>
                                            <b>{runtime.storefrontFormatting.formatMoney(product.price)}</b>
                                        </a>;
                                    })}
                                </MorrowSearchSuggestionGroup>
                            ) : null}
                        </>
                    ) : (
                        <p className="morrow-search-suggestion-state">{runtime.storefrontMessage("ui.search.nothingFound")}</p>
                    )}
                </div>
            ) : null}
        </form>
    );
}

function MorrowSearchSuggestionGroup({ children, label }: { children: ReactNode; label: string }) {
    return (
        <section aria-label={label} className="morrow-search-suggestion-group" role="group">
            <p>{label}</p>
            <div role="presentation">{children}</div>
        </section>
    );
}

function MorrowRegionFrame({
    after,
    block,
    compact,
    index,
    runtime,
}: {
    after?: ReactNode;
    block: ShopTemplateBlock;
    compact?: boolean;
    index: number;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const renderedBlock = normalizeMorrowFooterLayoutStructure(block);
    const region = optionText(block, "morrowRegion") || "content";
    const items = renderedBlock.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const visibleItems = items
        .map((child, childIndex) => ({ child, childIndex }))
        .filter(({ child }) => {
            if (optionText(child, "runtimeSource") === "") {
                return true;
            }

            const headerRole = region === "header" ? shopHeaderRole(child) : null;

            return headerRole !== null;
        });
    const rows = regionGridRows(region, renderedBlock, visibleItems.map(({ child }) => child));
    const regionStyle = morrowRegionVisualStyle(block, region);
    const compactStyle: SiteBuilderStyleRecord = { ...regionStyle, minHeight: undefined };
    const sectionStyle: SiteBuilderStyleRecord = compact
        ? {
            ...compactStyle,
            ...siteBuilderRebasedAuthoritativeStyleVariables(regionStyle, compactStyle),
        }
        : regionStyle;
    const legacyHeader = region === "header" && findShopHeaderRoleBlock(renderedBlock, "headerMenu") === null;
    const headerHasAnnouncement = region === "header" && items.some((child) => optionText(child, "documentRole") === "announcement");
    const headerGridTemplateRows = region === "header"
        ? headerHasAnnouncement
            ? `30px repeat(${Math.max(1, rows - 1)}, minmax(${SHOP_GRID_CELL_HEIGHT}px, 1fr))`
            : `repeat(${rows}, minmax(${SHOP_GRID_CELL_HEIGHT}px, 1fr))`
        : undefined;
    const regionId = region === "categories"
        ? "shop-categories"
        : region === "productFeed" ? "fresh-finds" : undefined;
    const emptyEditorGrid = runtime.editorMode && visibleItems.length === 0;
    const emptyRuntimeOverlay = emptyEditorGrid && after != null;
    const hasAuthoredGridColumns = hasPositiveOption(block, "gridColumns");
    const hasAuthoredGridRows = hasGridLengthOption(block, "gridRowHeight");
    const gridColumnCount = hasAuthoredGridColumns
        ? Math.max(1, Math.round(optionNumber(block, "gridColumns", 12)))
        : 12;
    const gridRowCount = rows;
    const gridRowHeight = hasGridLengthOption(block, "gridRowHeight")
        ? optionCssLength(block, "gridRowHeight", `${SHOP_GRID_CELL_HEIGHT}px`)
        : `${SHOP_GRID_CELL_HEIGHT}px`;
    const gridTemplateColumns = `repeat(${gridColumnCount}, minmax(0, 1fr))`;
    const gridTemplateRows = hasAuthoredGridRows
        ? `repeat(${gridRowCount}, ${gridRowHeight})`
        : headerGridTemplateRows ?? `repeat(${rows}, ${SHOP_GRID_CELL_HEIGHT}px)`;
    const gridRowGap = region === "header" || region === "hero" ? 0 : SHOP_GRID_CELL_GAP;
    const authoredGridMinHeight = hasAuthoredGridRows
        ? gridPixelHeight(
            gridRowCount,
            gridRowHeight,
            gridRowGap,
        )
        : undefined;
    const mobileStackValues = [
        mobileStackOption(renderedBlock),
        ...visibleItems.map(({ child }) => mobileStackOption(child)),
    ].filter((value): value is boolean => value !== null);
    const stackOnMobile = mobileStackValues.length === 0
        || mobileStackValues.some(Boolean);

    return (
        <section
            {...morrowInteractionAttributes(block)}
            {...siteBuilderEditorBlockAttributes({
                block,
                editorMode: runtime.editorMode,
                path: siteBuilderEditorRootPath(block, index),
            })}
            className={`morrow-region morrow-region-${region}${legacyHeader ? " is-legacy-header" : ""}`}
            data-morrow-region={region}
            data-morrow-footer-layout={region === "footer" ? footerLayoutStyle(block) : undefined}
            data-shop-block-index={index}
            data-shop-block-type={block.type}
            id={regionId}
            style={{
                ...sectionStyle,
                ...(region === "header" ? {
                    "--morrow-header-padding-top": sectionStyle.paddingTop ?? "0",
                    overflow: "visible",
                } : {}),
                ...morrowInteractionStyle(block),
            } as CSSProperties}
        >
            <div className="morrow-region-inner">
                {(visibleItems.length > 0 || runtime.editorMode) ? (
                    <div
                        className="morrow-canvas-grid"
                        data-builder-empty-slot={emptyEditorGrid ? "true" : undefined}
                        data-site-builder-authored-grid={
                            hasAuthoredGridColumns || hasAuthoredGridRows ? "true" : undefined
                        }
                        data-site-builder-authored-grid-columns={hasAuthoredGridColumns ? "true" : undefined}
                        data-site-builder-authored-grid-rows={hasAuthoredGridRows ? "true" : undefined}
                        data-shop-grid-cols={gridColumnCount}
                        data-shop-grid-container={SHOP_GRID_SLOT_KEY}
                        data-shop-grid-rows={gridRowCount}
                        data-shop-grid-stack-on-mobile={stackOnMobile ? "true" : "false"}
                        data-shop-slot-container={SHOP_GRID_SLOT_KEY}
                        data-shop-slot-item-count={items.length}
                        style={{
                            "--site-builder-authored-grid-columns": hasAuthoredGridColumns
                                ? gridTemplateColumns
                                : undefined,
                            "--site-builder-authored-grid-rows": hasAuthoredGridRows
                                ? gridTemplateRows
                                : undefined,
                            "--site-builder-authored-min-height": hasAuthoredGridRows && authoredGridMinHeight !== undefined
                                ? `${authoredGridMinHeight}px`
                                : undefined,
                            gridTemplateColumns,
                            gridTemplateRows,
                            rowGap: region === "header" ? 0 : undefined,
                            ...(emptyRuntimeOverlay ? {
                                bottom: 0,
                                insetInline: 0,
                                minHeight: 0,
                                pointerEvents: "none",
                                position: "absolute",
                                top: 0,
                            } : {
                                minHeight: emptyEditorGrid
                                    ? authoredGridMinHeight
                                        ?? Math.max(72, (rows * SHOP_GRID_CELL_HEIGHT) + ((rows - 1) * SHOP_GRID_CELL_GAP))
                                    : hasAuthoredGridRows
                                        ? authoredGridMinHeight
                                        : region === "header"
                                            ? sectionStyle.minHeight
                                            : (rows * SHOP_GRID_CELL_HEIGHT) + ((rows - 1) * SHOP_GRID_CELL_GAP),
                            }),
                        } as SiteBuilderStyleRecord}
                    >
                        {visibleItems.map(({ child, childIndex }) => {
                            const part = `slot:${SHOP_GRID_SLOT_KEY}:${childIndex}`;
                            const displayBlock = withMorrowHeroMediaFallback(region, child);
                            const headerRole = region === "header" ? shopHeaderRole(child) : null;
                            const authoredAxes = authoredGridPlacementAxes(child);

                            return (
                                <div
                                    key={`${child.type}:${childIndex}`}
                                    {...morrowInteractionAttributes(child)}
                                    className="morrow-grid-child"
                                    data-morrow-header-role={headerRole ?? undefined}
                                    data-morrow-role={optionText(child, "documentRole") || undefined}
                                    data-morrow-show-desktop={headerRole ? String(shopHeaderBlockVisibleOn(child, "desktop")) : undefined}
                                    data-morrow-show-mobile={headerRole ? String(shopHeaderBlockVisibleOn(child, "mobile")) : undefined}
                                    data-site-builder-authored-grid-placement={
                                        authoredAxes.column || authoredAxes.row ? "true" : undefined
                                    }
                                    data-site-builder-authored-grid-column={authoredAxes.column ? "true" : undefined}
                                    data-site-builder-authored-grid-row={authoredAxes.row ? "true" : undefined}
                                    data-shop-block-part={part}
                                    data-shop-outline-disabled={siteBuilderBlockDelegatesOutlineToColumn(child) ? "true" : undefined}
                                    style={{ ...regionGridStyle(region, renderedBlock, child), ...morrowInteractionStyle(child) }}
                                >
                                    <MorrowInnerBlock block={displayBlock} editorMode={runtime.editorMode} part={part} runtime={runtime}/>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
                {after}
            </div>
        </section>
    );
}

function MorrowHeader({ block, index, runtime }: { block: ShopTemplateBlock; index: number; runtime: ShopStorefrontRuntimeModel }) {
    const legacyHeader = findShopHeaderRoleBlock(block, "headerMenu") === null;

    return (
        <header className="morrow-header-shell">
            <MorrowRegionFrame block={block} index={index} runtime={runtime} after={legacyHeader ? (
                <div className="morrow-navigation">
                    <button aria-controls={MORROW_MOBILE_NAVIGATION_DIALOG_ID} aria-expanded={runtime.mobileOpen} aria-label={runtime.storefrontMessage("ui.header.openMenu")} className="morrow-mobile-menu-button" onClick={() => runtime.setMobileOpen(true)} type="button"><MorrowIcon name="menu"/></button>
                    <MorrowSharedNavigation block={block} items={runtime.headerMenuItems} mode="desktop" runtime={runtime}/>
                    <MorrowHeaderLocaleSelectors header={block} runtime={runtime} viewport="desktop"/>
                </div>
            ) : undefined}/>
        </header>
    );
}

function MorrowHeaderLocaleSelectors({
    header,
    runtime,
    viewport,
}: {
    header: ShopTemplateBlock | null;
    runtime: ShopStorefrontRuntimeModel;
    viewport: "desktop" | "mobile";
}) {
    const menuBlock = findShopHeaderRoleBlock(header, "headerMenu");
    const languageBlock = findShopHeaderRoleBlock(header, "headerLanguageSelector");
    const currencyBlock = findShopHeaderRoleBlock(header, "headerCurrencySelector");
    const legacyDefaults = menuBlock === null;
    const showLanguage = runtime.languageOptions.length > 0
        && (legacyDefaults || (languageBlock !== null && shopHeaderBlockVisibleOn(languageBlock, viewport)));
    const showCurrency = runtime.currencyOptions.length > 0
        && (legacyDefaults || (currencyBlock !== null && shopHeaderBlockVisibleOn(currencyBlock, viewport)));

    if (!showLanguage && !showCurrency) {
        return null;
    }

    return (
        <div className="morrow-header-selectors">
            {showLanguage ? <MorrowSelector display={languageBlock ? resolveShopHeaderUtilityDisplay(languageBlock) : "label"} icon="languages" label={runtime.storefrontMessage("ui.header.selectLanguage")} onSelect={runtime.selectStorefrontLanguage} options={runtime.languageOptions}/> : null}
            {showCurrency ? <MorrowSelector display={currencyBlock ? resolveShopHeaderUtilityDisplay(currencyBlock) : "label"} icon="coins" label={runtime.storefrontMessage("ui.header.selectCurrency")} onSelect={(_code, href) => void runtime.navigateCatalog(href)} options={runtime.currencyOptions}/> : null}
        </div>
    );
}

function MorrowHeroChips({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    const menuItems = runtime.headerMenuItems
        .filter((item) => item.label.trim() !== "")
        .slice(0, 3);

    return (
        <nav aria-label={runtime.storefrontMessage("ui.header.customerSegments")} className="morrow-hero-chips">
            {menuItems.length > 0 ? menuItems.map((item) => (
                <ShopStorefrontLink
                    href={functionalStorefrontHref(menuItemHref(item)) ?? "#shop-categories"}
                    key={item.uuid}
                    onClick={(event) => runtime.editorMode && event.preventDefault()}
                    target={item.openInNewTab ? "_blank" : undefined}
                >
                    {item.label}
                </ShopStorefrontLink>
            )) : (
                <>
                    <ShopStorefrontLink href="#shop-categories">{runtime.storefrontMessage("template.morrow.home.hero.secondary")}</ShopStorefrontLink>
                    <ShopStorefrontLink href={runtime.searchHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("template.morrow.home.products.all")}</ShopStorefrontLink>
                </>
            )}
        </nav>
    );
}

function MorrowSelector({
    display = "label",
    icon,
    label,
    onSelect,
    options,
}: {
    display?: "icon" | "iconLabel" | "label";
    icon?: string;
    label: string;
    onSelect: (code: string, href: string) => void;
    options: Array<{ active: boolean; code: string; href: string; label: string }>;
}) {
    const active = options.find((option) => option.active) ?? options[0];

    return (
        <label className={`morrow-selector is-${display}`}>
            <span className="sr-only">{label}</span>
            {display !== "label" && icon ? <span aria-hidden="true" className="morrow-selector-icon"><MorrowIcon name={icon} size={16}/></span> : null}
            <select
                aria-label={label}
                onChange={(event) => {
                    const option = options.find((item) => item.href === event.target.value);

                    if (option) {
                        onSelect(option.code, option.href);
                    }
                }}
                value={active?.href ?? ""}
            >
                {options.map((option) => <option key={option.code} value={option.href}>{option.code.toUpperCase()}</option>)}
            </select>
        </label>
    );
}

function deepestActiveNavigationUuid(
    items: readonly PublicShopMenuItem[],
    activeUuids: ReadonlySet<string>,
): string | undefined {
    for (const item of items) {
        const activeChild = deepestActiveNavigationUuid(item.children, activeUuids);

        if (activeChild) return activeChild;
        if (activeUuids.has(item.uuid)) return item.uuid;
    }

    return undefined;
}

function MorrowSharedNavigation({
    block,
    items,
    mode,
    runtime,
}: {
    block: ShopTemplateBlock | null | undefined;
    items: readonly PublicShopMenuItem[];
    mode: "desktop" | "mobile";
    runtime: ShopStorefrontRuntimeModel;
}) {
    const settings = useMemo(() => resolveSiteNavigationSettings(block, {
        theme: "inherit",
        variant: "editorialOverlay",
    }), [block]);
    const style = useMemo(() => siteNavigationThemeStyle(
        resolveSiteNavigationThemeTokens(runtime.design, settings.theme),
    ), [runtime.design, settings.theme]);
    const renderLink: SiteNavigationRenderLink = useCallback(({
        ariaCurrent,
        children,
        className,
        href,
        id,
        item,
        onClick,
    }) => {
        const resolvedHref = functionalStorefrontHref(runtime.storefrontHref(href));

        if (!resolvedHref) {
            return <span aria-current={ariaCurrent} className={className} data-site-navigation-item-uuid={item.uuid} data-site-navigation-label="true" data-site-navigation-link-owner="true" id={id}>{children}</span>;
        }

        return (
            <ShopStorefrontLink
                aria-current={ariaCurrent}
                className={className}
                data-site-navigation-item-uuid={item.uuid}
                data-site-navigation-link-owner="true"
                href={resolvedHref}
                id={id}
                onClick={(event) => {
                    if (runtime.editorMode) event.preventDefault();
                    if (!runtime.editorMode && mode === "mobile") runtime.closeOverlay();
                    onClick?.();
                }}
                rel={item.openInNewTab ? "noopener noreferrer" : undefined}
                target={item.openInNewTab ? "_blank" : undefined}
            >
                {children}
            </ShopStorefrontLink>
        );
    }, [mode, runtime]);

    return (
        <SiteNavigation
            activeItemUuid={deepestActiveNavigationUuid(items, runtime.activeMenuUuids)}
            className={mode === "desktop" ? "morrow-shared-navigation" : "morrow-shared-mobile-navigation"}
            includeStyles={mode === "desktop"}
            items={items}
            labels={{
                closeSubmenu: runtime.storefrontMessage("ui.header.closeMenu"),
                navigation: runtime.storefrontMessage("ui.header.mainNavigation"),
                openSubmenu: runtime.storefrontMessage("ui.header.openMenu"),
                recommended: runtime.storefrontMessage("ui.header.recommended"),
                showAll: runtime.storefrontMessage("block.systemSubcategoryList.moreButtonLabel"),
            }}
            mode={mode}
            renderLink={renderLink}
            settings={settings}
            style={style}
        />
    );
}

const MORROW_MOBILE_UTILITY_ROLES = [
    "headerStoresButton",
    "headerFavoritesButton",
    "headerLanguageSelector",
    "headerCurrencySelector",
] as const;

type MorrowMobileUtilityRole = typeof MORROW_MOBILE_UTILITY_ROLES[number];

function isMorrowMobileUtilityRole(role: string | null): role is MorrowMobileUtilityRole {
    return role !== null && (MORROW_MOBILE_UTILITY_ROLES as readonly string[]).includes(role);
}

function MorrowMobileHeaderUtilities({
    header,
    runtime,
}: {
    header: ShopTemplateBlock | null;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const legacyHeader = findShopHeaderRoleBlock(header, "headerMenu") === null;
    const authoredUtilities = (header?.slots?.[SHOP_GRID_SLOT_KEY] ?? [])
        .map((block) => ({ block, role: shopHeaderRole(block) }))
        .filter((item): item is { block: ShopTemplateBlock; role: MorrowMobileUtilityRole } => (
            isMorrowMobileUtilityRole(item.role)
            && shopHeaderBlockVisibleOn(item.block, "mobile")
        ));
    const utilities: Array<{ block: ShopTemplateBlock | null; role: MorrowMobileUtilityRole }> = legacyHeader
        ? [
            { block: null, role: "headerLanguageSelector" },
            { block: null, role: "headerCurrencySelector" },
        ]
        : authoredUtilities;

    const visibleUtilities = utilities.filter(({ role }) => {
        if (role === "headerLanguageSelector") return runtime.languageOptions.length > 0;
        if (role === "headerCurrencySelector") return runtime.currencyOptions.length > 0;

        return true;
    });

    if (visibleUtilities.length === 0) {
        return null;
    }

    return (
        <div className="morrow-mobile-utility-links">
            {visibleUtilities.map(({ block, role }, index) => {
                if (role === "headerStoresButton") {
                    return (
                        <a href={runtime.storesHref} key={`${role}:${index}`} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                            <MorrowIcon name="store" size={18}/>{runtime.storefrontMessage("ui.header.stores")}
                        </a>
                    );
                }

                if (role === "headerFavoritesButton") {
                    const favoritesHref = block ? contentText(block, "href", "/oblibene") : "/oblibene";

                    return (
                        <a href={runtime.storefrontHref(favoritesHref)} key={`${role}:${index}`} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                            <MorrowIcon name="heart" size={18}/>{runtime.storefrontMessage("ui.account.favorites")}
                        </a>
                    );
                }

                const languageSelector = role === "headerLanguageSelector";

                return (
                    <div className="morrow-mobile-selector-utility" data-morrow-mobile-utility-role={role} key={`${role}:${index}`}>
                        <MorrowSelector
                            display={block ? resolveShopHeaderUtilityDisplay(block) : "label"}
                            icon={languageSelector ? "languages" : "coins"}
                            label={runtime.storefrontMessage(languageSelector ? "ui.header.selectLanguage" : "ui.header.selectCurrency")}
                            onSelect={languageSelector
                                ? runtime.selectStorefrontLanguage
                                : (_code, href) => void runtime.navigateCatalog(href)}
                            options={languageSelector ? runtime.languageOptions : runtime.currencyOptions}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function MorrowMobileMenu({ header, runtime }: {
    header: ShopTemplateBlock | null;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const panelRef = useRef<HTMLElement>(null);
    const titleId = useId();

    useEffect(() => {
        if (!runtime.mobileOpen) return;

        const panel = panelRef.current;
        const ownerDocument = panel?.ownerDocument;
        const view = ownerDocument?.defaultView;

        if (!panel || !ownerDocument || !view) return;

        openerRef.current = ownerDocument.activeElement instanceof view.HTMLElement
            ? ownerDocument.activeElement as HTMLElement
            : null;
        const previousOverflow = ownerDocument.body.style.overflow;
        ownerDocument.body.style.overflow = "hidden";
        const focusFrame = view.requestAnimationFrame(() => closeButtonRef.current?.focus());
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                runtime.closeOverlay();
                return;
            }

            if (event.key !== "Tab") return;

            const focusable = navigationFocusableElements(panel);

            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (!panel.contains(ownerDocument.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && ownerDocument.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && ownerDocument.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        const mobileQuery = view.matchMedia(`(max-width: ${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px)`);
        const closeOutsideMobile = (event: MediaQueryListEvent) => {
            if (!event.matches) runtime.closeOverlay();
        };

        if (!mobileQuery.matches) {
            runtime.closeOverlay();

            return () => {
                view.cancelAnimationFrame(focusFrame);
                ownerDocument.body.style.overflow = previousOverflow;
                openerRef.current?.focus();
            };
        }

        ownerDocument.addEventListener("keydown", handleKeyDown);
        mobileQuery.addEventListener("change", closeOutsideMobile);

        return () => {
            view.cancelAnimationFrame(focusFrame);
            ownerDocument.removeEventListener("keydown", handleKeyDown);
            mobileQuery.removeEventListener("change", closeOutsideMobile);
            ownerDocument.body.style.overflow = previousOverflow;
            openerRef.current?.focus();
        };
    }, [runtime.closeOverlay, runtime.mobileOpen]);

    if (!runtime.mobileOpen) return null;

    const legacyHeader = findShopHeaderRoleBlock(header, "headerMenu") === null;
    const accountBlock = findShopHeaderRoleBlock(header, "headerAccountButton");
    const showAccount = legacyHeader
        || (accountBlock !== null && shopHeaderBlockVisibleOn(accountBlock, "mobile"));

    return (
        <div aria-labelledby={titleId} aria-modal="true" className="morrow-overlay" id={MORROW_MOBILE_NAVIGATION_DIALOG_ID} role="dialog">
            <button aria-label={runtime.storefrontMessage("ui.header.closeMenu")} className="morrow-overlay-backdrop" onClick={runtime.closeOverlay} type="button"/>
            <aside className="morrow-mobile-panel" ref={panelRef}>
                <div className="morrow-panel-head">
                    <strong id={titleId}>{runtime.displayName}</strong>
                    <button aria-label={runtime.storefrontMessage("ui.header.closeMenu")} onClick={runtime.closeOverlay} ref={closeButtonRef} type="button">×</button>
                </div>
                <MorrowSharedNavigation
                    block={findShopHeaderRoleBlock(header, "headerMenu") ?? header}
                    items={runtime.mobileMenuItems}
                    mode="mobile"
                    runtime={runtime}
                />
                <MorrowMobileHeaderUtilities header={header} runtime={runtime}/>
                {showAccount ? <a className="morrow-primary-button" href={runtime.accountHref} onClick={(event) => runtime.editorMode && event.preventDefault()}><MorrowIcon name="user" size={18}/>{runtime.storefrontMessage("ui.header.myAccount")}</a> : null}
            </aside>
        </div>
    );
}

function MorrowSearchOverlay({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    if (!runtime.searchOpen) return null;

    const searchBlock = findNestedBlock(runtime.pageContentBlocks ?? [], (block) => block.type === "searchBar");

    return (
        <div className="morrow-search-overlay" role="dialog" aria-modal="true">
            <div className="morrow-search-overlay-head"><span>{runtime.storefrontMessage("ui.search.label")}</span><button onClick={runtime.closeOverlay} type="button">×</button></div>
            <MorrowSearchForm block={searchBlock} editorMode={runtime.editorMode} part="search-overlay" placeholder={runtime.storefrontMessage("ui.search.placeholder")} runtime={runtime}/>
            <p>{runtime.storefrontMessage("ui.search.tryBroader")}</p>
        </div>
    );
}

function catalogMenuItemEntityType(item: PublicShopMenuItem): "category" | "section" | null {
    if (item.type === "category" || item.type === "section") return item.type;

    return null;
}

function categoryTemplateForMenuItem(template: ShopTemplateBlock, item: PublicShopMenuItem): ShopTemplateBlock {
    const entityType = catalogMenuItemEntityType(item);

    if (!entityType) return template;

    return {
        ...template,
        slots: {
            ...(template.slots ?? {}),
            content: (template.slots?.content ?? []).map((child) => (
                isShopDynamicEntityField(optionText(child, "dynamicEntityField"))
                    ? {
                        ...child,
                        content: { ...record(child.content), text: item.label },
                        options: {
                            ...record(child.options),
                            dynamicEntityType: entityType,
                            dynamicEntityUuid: item.uuid,
                        },
                    }
                    : child
            )),
        },
    };
}

function MorrowCategoryLinks({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const grid = block.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const sourceIndex = grid.findIndex((child) => optionText(child, "runtimeSource") === "categoryCards");
    const templates = sourceIndex >= 0 ? grid[sourceIndex]?.slots?.content ?? [] : [];
    const systemReference = runtimeSystemBlockReference(block, "categoriesSystem");
    const candidates = runtime.headerMenuItems
        .flatMap((item) => item.children.length > 0 ? item.children : [item])
        .slice(0, templates.length || 4);

    return (
        <div
            className="morrow-category-links"
            data-shop-block-part={systemReference?.part}
            style={systemReference ? visualStyle(systemReference.block) : undefined}
        >
            {candidates.map((item, index) => {
                const template = templates[index];
                const templatePart = `slot:${SHOP_GRID_SLOT_KEY}:${sourceIndex}:slot:content:${index}`;
                const entityType = catalogMenuItemEntityType(item);

                return (
                    <ShopStorefrontLink
                        data-shop-block-part={template ? templatePart : undefined}
                        href={functionalStorefrontHref(menuItemHref(item))}
                        key={item.uuid}
                        onClick={(event) => runtime.editorMode && event.preventDefault()}
                    >
                        {template ? (
                            <MorrowInnerBlock
                                block={categoryTemplateForMenuItem(template, item)}
                                editorMode={runtime.editorMode}
                                part={templatePart}
                                runtime={runtime}
                            />
                        ) : entityType ? (
                            <strong
                                {...catalogTextAttributes(
                                    entityType,
                                    item.uuid,
                                    "nameMenu",
                                    "title",
                                )}
                                style={systemReference ? partVisualStyle(systemReference.block, "title") : undefined}
                            >
                                {item.label}
                            </strong>
                        ) : <strong>{item.label}</strong>}
                    </ShopStorefrontLink>
                );
            })}
        </div>
    );
}

function availableProducts(runtime: ShopStorefrontRuntimeModel): PublicHomeProduct[] {
    const home = homeProductsFromContext(runtime.localizedPageContext);
    const catalog = catalogProductsFromContext(runtime.localizedPageContext);

    return home.length > 0 ? home : catalog;
}

type MorrowBlockReference = { block: ShopTemplateBlock; part: string };

function runtimeSystemBlockReference(region: ShopTemplateBlock | undefined, sourceName: string): MorrowBlockReference | undefined {
    const grid = region?.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const sourceIndex = grid.findIndex((child) => optionText(child, "runtimeSource") === sourceName);
    const block = sourceIndex >= 0 ? grid[sourceIndex] : undefined;

    return block ? { block, part: `slot:${SHOP_GRID_SLOT_KEY}:${sourceIndex}` } : undefined;
}

function nestedSlotBlockReference(
    reference: MorrowBlockReference | undefined,
    slot: string,
): MorrowBlockReference | undefined {
    const block = reference?.block.slots?.[slot]?.[0];

    return block && reference
        ? { block, part: `${reference.part}:slot:${slot}:0` }
        : undefined;
}

function morrowCatalogProductReferences(
    productPageReference: MorrowBlockReference | undefined,
): {
    image?: MorrowBlockReference;
    product?: MorrowBlockReference;
} {
    const product = catalogProductTemplate(productPageReference?.block);
    const productReference = product && productPageReference
        ? nestedSlotBlockReference(productPageReference, "product")
        : undefined;
    const image = catalogProductImageTemplate(product);
    const imageReference = image && productReference
        ? nestedSlotBlockReference(productReference, "image")
        : undefined;

    return {
        ...(imageReference ? { image: imageReference } : {}),
        ...(productReference ? { product: productReference } : {}),
    };
}

function runtimeSourceReferences(region: ShopTemplateBlock | undefined, sourceName: string): Record<string, MorrowBlockReference> {
    const grid = region?.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const sourceIndex = grid.findIndex((child) => optionText(child, "runtimeSource") === sourceName);
    if (sourceIndex < 0) return {};

    const source = grid[sourceIndex];
    const children = source?.slots?.content ?? [];

    return Object.fromEntries(children.flatMap((child, childIndex) => {
        const role = optionText(child, "documentRole");

        return role ? [[role, { block: child, part: `slot:${SHOP_GRID_SLOT_KEY}:${sourceIndex}:slot:content:${childIndex}` }]] : [];
    }));
}

function MorrowReferencedIcon({ fallbackName, fallbackSize, reference, runtime }: {
    fallbackName: string;
    fallbackSize: number;
    reference?: MorrowBlockReference;
    runtime: ShopStorefrontRuntimeModel;
}) {
    if (!reference) return <MorrowIcon name={fallbackName} size={fallbackSize}/>;

    return (
        <span data-shop-block-part={reference.part}>
            <MorrowInnerBlock block={reference.block} editorMode={runtime.editorMode} part={reference.part} runtime={runtime}/>
        </span>
    );
}

function MorrowProductGrid({ attachSystemReference = true, block, products, runtime, systemReference }: {
    attachSystemReference?: boolean;
    block?: ShopTemplateBlock;
    products: PublicHomeProduct[];
    runtime: ShopStorefrontRuntimeModel;
    systemReference?: MorrowBlockReference;
}) {
    const icons = runtimeSourceReferences(block, "productIcons");
    const itemHoverEffect = optionText(systemReference?.block ?? block ?? { type: "column" }, "itemHoverEffect");
    const config = catalogProductPageConfig(systemReference?.block);
    const productReferences = morrowCatalogProductReferences(systemReference);
    const gridStyle = {
        "--morrow-product-columns": String(config.desktopColumns),
        "--morrow-product-mobile-columns": String(config.mobileColumns),
        ...(attachSystemReference && systemReference ? visualStyle(systemReference.block) : {}),
    } as CSSProperties;
    const systemAttributes = attachSystemReference && systemReference ? {
        "data-shop-block-part": systemReference.part,
    } : {};

    if (products.length === 0) {
        return <div {...systemAttributes} className="morrow-empty-state" style={gridStyle}><MorrowReferencedIcon fallbackName="search" fallbackSize={32} reference={icons.emptySearchIcon} runtime={runtime}/><strong>{runtime.storefrontMessage("ui.product.noMatches")}</strong><span>{runtime.storefrontMessage("ui.product.adjustSearch")}</span></div>;
    }

    return <div {...systemAttributes} className={`morrow-product-grid is-design-${config.productDesign}${config.desktopColumns === 1 ? " is-single-column" : ""}`} style={gridStyle}>{products.map((product, index) => <MorrowProductCard icons={icons} imageReference={productReferences.image} index={index} itemHoverEffect={itemHoverEffect} key={product.uuid} product={product} productReference={productReferences.product} runtime={runtime} systemReference={systemReference}/>)}</div>;
}

function MorrowProductMediaPlaceholder({
    name,
    size = "card",
}: {
    name: string;
    size?: "card" | "compact" | "detail";
}) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    const initials = (words.length > 1
        ? words.slice(0, 2).map((word) => Array.from(word)[0] ?? "").join("")
        : Array.from(words[0] ?? "").slice(0, 2).join("")
    ).toLocaleUpperCase();

    return (
        <span
            aria-hidden="true"
            className={`morrow-product-placeholder is-${size}`}
            data-morrow-product-media-placeholder="true"
        >
            <span className="morrow-product-placeholder-glow"/>
            <span className="morrow-product-placeholder-initials">{initials}</span>
        </span>
    );
}

function morrowProductRibbon(
    product: PublicHomeProduct,
    productReference: MorrowBlockReference | undefined,
): Exclude<CatalogProductRibbon, "auto" | "none"> | null {
    const stored = optionText(productReference?.block ?? { type: "product" }, "ribbon");
    const ribbon = CATALOG_PRODUCT_RIBBONS.includes(stored as CatalogProductRibbon)
        ? stored as CatalogProductRibbon
        : "auto";

    if (ribbon === "none") return null;
    if (ribbon !== "auto") return ribbon;
    if (product.isOnSale) return "sale";
    if (!product.availableForOrder) return "outOfStock";

    return null;
}

function morrowProductRibbonLabel(
    ribbon: Exclude<CatalogProductRibbon, "auto" | "none">,
    runtime: ShopStorefrontRuntimeModel,
): string {
    if (ribbon === "sale") return runtime.storefrontMessage("ui.product.sale");
    if (ribbon === "new") return runtime.storefrontMessage("ui.product.new");
    if (ribbon === "soldOut") return runtime.storefrontMessage("ui.product.soldOut");

    return runtime.storefrontMessage("ui.product.outOfStock");
}

function MorrowProductCard({ icons, imageReference, index, itemHoverEffect, product, productReference, runtime, systemReference }: { icons: Record<string, MorrowBlockReference>; imageReference?: MorrowBlockReference; index: number; itemHoverEffect: string; product: PublicHomeProduct; productReference?: MorrowBlockReference; runtime: ShopStorefrontRuntimeModel; systemReference?: MorrowBlockReference }) {
    const productPath = productHref(product);
    const href = productPath ? runtime.storefrontHref(productPath) : undefined;
    const favorite = runtime.visibleFavoriteProductIds.has(product.uuid);
    const adding = runtime.addingProductUuid === product.uuid;
    const quickAdd = product.canAddToCart && !product.requiresAttribute;
    const image = product.primaryImage?.thumbnailUrl
        ?? product.primaryImage?.url
        ?? (runtime.editorMode ? MORROW_PRODUCT_IMAGE : null);
    const productStyleReference = productReference ?? systemReference;
    const textStyle = (part: string) => productStyleReference ? partVisualStyle(productStyleReference.block, part) : undefined;
    const ribbon = morrowProductRibbon(product, productReference);

    return (
        <article
            className="morrow-product-card"
            data-shop-block-part={productReference?.part}
            data-shop-hover-effect={itemHoverEffect || undefined}
            data-shop-runtime-entity-type="product"
            data-shop-runtime-entity-uuid={product.uuid}
            data-shop-runtime-kind="product"
            style={productReference ? visualStyle(productReference.block) : undefined}
        >
            <ShopStorefrontLink
                className={`morrow-product-visual tone-${index % 4}`}
                data-shop-block-part={imageReference?.part}
                data-shop-runtime-context="gallery"
                data-shop-runtime-entity-type="product"
                data-shop-runtime-entity-uuid={product.uuid}
                data-shop-runtime-kind="media"
                href={href}
                onClick={(event) => runtime.editorMode && event.preventDefault()}
                style={imageReference ? visualStyle(imageReference.block) : undefined}
            >
                {image ? <img alt={product.primaryImage?.alt || product.name} loading="lazy" src={image} style={imageReference ? imagePartStyle(imageReference.block, "image") : undefined}/> : <MorrowProductMediaPlaceholder name={product.name}/>}
                {ribbon ? <span className={`is-ribbon-${ribbon}`}>{morrowProductRibbonLabel(ribbon, runtime)}</span> : null}
            </ShopStorefrontLink>
            <button aria-label={favorite ? runtime.storefrontMessage("ui.product.removeFromFavorites") : runtime.storefrontMessage("ui.product.addToFavorites")} className={favorite ? "morrow-favorite is-active" : "morrow-favorite"} onClick={() => runtime.toggleFavorite(product.uuid)} type="button"><MorrowReferencedIcon fallbackName="heart" fallbackSize={19} reference={icons.productFavoriteIcon} runtime={runtime}/></button>
            <div className="morrow-product-copy">
                <div>
                    {product.reference ? (
                        <span style={textStyle("productReference")}>{product.reference}</span>
                    ) : <span>{product.manufacturerName ?? runtime.displayName}</span>}
                    <ShopStorefrontLink href={href} onClick={(event) => runtime.editorMode && event.preventDefault()} style={textStyle("productName")}>{product.name}</ShopStorefrontLink>
                </div>
                <strong style={textStyle("productPrice")}>{runtime.storefrontFormatting.formatMoney(product.price)}</strong>
            </div>
            <div className="morrow-product-meta">
                <span className={product.availableForOrder ? "is-available" : ""}>{product.availableForOrder ? runtime.storefrontMessage("ui.product.inStock") : runtime.storefrontMessage("ui.product.unavailable")}</span>
                {quickAdd ? <button disabled={adding} onClick={() => void runtime.addCartItem(product.uuid)} type="button">{adding ? "…" : <MorrowReferencedIcon fallbackName="plus" fallbackSize={18} reference={icons.productAddIcon} runtime={runtime}/>}<span className="sr-only">{runtime.storefrontMessage("ui.product.addToCart")}</span></button> : <ShopStorefrontLink href={href} onClick={(event) => runtime.editorMode && event.preventDefault()}><MorrowReferencedIcon fallbackName="arrowUpRight" fallbackSize={18} reference={icons.productOpenIcon} runtime={runtime}/></ShopStorefrontLink>}
            </div>
        </article>
    );
}

function handleMorrowCatalogClick(
    event: ReactMouseEvent<HTMLAnchorElement>,
    href: string,
    runtime: ShopStorefrontRuntimeModel,
): void {
    if (runtime.editorMode) {
        event.preventDefault();

        return;
    }

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    event.preventDefault();
    void runtime.navigateCatalog(href);
}

function catalogRangeNumber(value: string): number | null {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : null;
}

function MorrowCatalogRangeFacet({
    baseHref,
    facet,
    filters,
    query,
    runtime,
    sort,
}: {
    baseHref: string;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    sort: CatalogSortValue;
}) {
    const [minimum, setMinimum] = useState(facet.selectedMin?.toString() ?? "");
    const [maximum, setMaximum] = useState(facet.selectedMax?.toString() ?? "");

    useEffect(() => {
        setMinimum(facet.selectedMin?.toString() ?? "");
        setMaximum(facet.selectedMax?.toString() ?? "");
    }, [facet.selectedMax, facet.selectedMin]);

    function submit(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();
        if (runtime.editorMode) return;

        const href = catalogHref(baseHref, sort, {
            ...filters,
            priceMin: catalogRangeNumber(minimum),
            priceMax: catalogRangeNumber(maximum),
        }, query);
        void runtime.navigateCatalog(href);
    }

    return (
        <form className="morrow-catalog-price" onSubmit={submit}>
            <label>
                <span>{runtime.storefrontMessage("ui.catalog.priceFrom")}</span>
                <input
                    max={facet.max ?? undefined}
                    min={facet.min ?? undefined}
                    onChange={(event) => setMinimum(event.target.value)}
                    placeholder={facet.min?.toString()}
                    step="any"
                    type="number"
                    value={minimum}
                />
            </label>
            <label>
                <span>{runtime.storefrontMessage("ui.catalog.priceTo")}</span>
                <input
                    max={facet.max ?? undefined}
                    min={facet.min ?? undefined}
                    onChange={(event) => setMaximum(event.target.value)}
                    placeholder={facet.max?.toString()}
                    step="any"
                    type="number"
                    value={maximum}
                />
            </label>
            <button type="submit">{runtime.storefrontMessage("ui.catalog.filterPrice")}</button>
        </form>
    );
}

function MorrowCatalogTermsFacet({
    baseHref,
    facet,
    filters,
    query,
    runtime,
    sort,
}: {
    baseHref: string;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    sort: CatalogSortValue;
}) {
    const [expanded, setExpanded] = useState(false);
    const visibleOptions = expanded ? facet.options : visibleCatalogFacetOptions(facet.options, 8);
    const hiddenCount = Math.max(0, facet.options.length - visibleOptions.length);

    return (
        <div className="morrow-catalog-filter-options">
            {visibleOptions.map((option) => {
                const href = catalogHref(baseHref, sort, toggleCatalogFilter(filters, facet, option), query);

                return (
                    <a
                        aria-current={option.selected ? "true" : undefined}
                        className={option.selected ? "is-selected" : undefined}
                        href={href}
                        key={option.uuid}
                        onClick={(event) => handleMorrowCatalogClick(event, href, runtime)}
                    >
                        <span>
                            <i className="morrow-catalog-filter-check">{option.selected ? "✓" : ""}</i>
                            {option.color ? <i className="morrow-catalog-filter-color" style={{ backgroundColor: option.color }}/> : null}
                            <span>{option.label}</span>
                        </span>
                        {facet.code !== CATALOG_SALE_FACET_CODE ? <strong>{option.count}</strong> : null}
                    </a>
                );
            })}
            {!expanded && hiddenCount > 0 ? (
                <button onClick={() => setExpanded(true)} type="button">
                    {runtime.storefrontMessage("ui.catalog.showMore", { count: hiddenCount })}
                </button>
            ) : null}
        </div>
    );
}

function MorrowCatalogFacetSection({
    baseHref,
    collapsible,
    facet,
    filters,
    index,
    query,
    runtime,
    sort,
}: {
    baseHref: string;
    collapsible: boolean;
    facet: PublicCatalogFacet;
    filters: CatalogFilterState;
    index: number;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    sort: CatalogSortValue;
}) {
    const selectedCount = catalogFacetSelectedCount(facet);
    const [open, setOpen] = useState(selectedCount > 0 || index < 3);

    useEffect(() => {
        if (selectedCount > 0) setOpen(true);
    }, [selectedCount]);

    const content = facet.type === "range" ? (
        <MorrowCatalogRangeFacet baseHref={baseHref} facet={facet} filters={filters} query={query} runtime={runtime} sort={sort}/>
    ) : (
        <MorrowCatalogTermsFacet baseHref={baseHref} facet={facet} filters={filters} query={query} runtime={runtime} sort={sort}/>
    );

    if (!collapsible) {
        return (
            <section className="morrow-catalog-facet is-static">
                <div className="morrow-catalog-facet-title">
                    <span>{facet.label}{selectedCount > 0 ? <b>{selectedCount}</b> : null}</span>
                </div>
                {content}
            </section>
        );
    }

    return (
        <details className="morrow-catalog-facet" onToggle={(event) => setOpen(event.currentTarget.open)} open={open}>
            <summary>
                <span>{facet.label}{selectedCount > 0 ? <b>{selectedCount}</b> : null}</span>
                <MorrowIcon name="chevronDown" size={16}/>
            </summary>
            {content}
        </details>
    );
}

function MorrowCatalogFilters({
    baseHref,
    collapseSections,
    facets,
    filters,
    query,
    runtime,
    saleLocked = false,
    showPriceFilter,
    showTagsFilter,
    sort,
    total,
}: {
    baseHref: string;
    collapseSections: boolean;
    facets: PublicCatalogFacet[];
    filters: CatalogFilterState;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    saleLocked?: boolean;
    showPriceFilter: boolean;
    showTagsFilter: boolean;
    sort: CatalogSortValue;
    total: number;
}) {
    const activeCount = activeCatalogFilterCount(filters, !saleLocked);
    const clearHref = clearCatalogFiltersHref(baseHref, sort, query);
    const visibleFacets = facets.filter((facet) => {
        const code = facet.code.trim().toLocaleLowerCase();
        const attributeName = facet.attributeName?.trim().toLocaleLowerCase() ?? "";

        if (!showPriceFilter && code === "price") return false;
        if (!showTagsFilter && (code === "tag" || code === "tags" || attributeName === "tag" || attributeName === "tags")) return false;

        return true;
    });

    return (
        <div className="morrow-catalog-filters">
            <div className="morrow-catalog-filters-head">
                <div>
                    <h2>{runtime.storefrontMessage("ui.catalog.filters")}</h2>
                    <span>{morrowProductCount(runtime, total)}</span>
                </div>
                {activeCount > 0 ? (
                    <a href={clearHref} onClick={(event) => handleMorrowCatalogClick(event, clearHref, runtime)}>
                        {runtime.storefrontMessage("ui.catalog.clearFilters")} ({activeCount})
                    </a>
                ) : null}
            </div>
            {visibleFacets.length > 0 ? visibleFacets.map((facet, index) => (
                <MorrowCatalogFacetSection
                    baseHref={baseHref}
                    collapsible={collapseSections}
                    facet={facet}
                    filters={filters}
                    index={index}
                    key={facet.code}
                    query={query}
                    runtime={runtime}
                    sort={sort}
                />
            )) : <p className="morrow-catalog-no-filters">{runtime.storefrontMessage("ui.catalog.noFilters")}</p>}
        </div>
    );
}

function morrowCatalogPageNumbers(currentPage: number, totalPages: number): number[] {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    if (currentPage <= 3) pages.add(2);
    if (currentPage >= totalPages - 2) pages.add(totalPages - 1);

    return Array.from(pages)
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((first, second) => first - second);
}

function MorrowCatalogPagination({
    baseHref,
    filters,
    query,
    runtime,
    sort,
}: {
    baseHref: string;
    filters: CatalogFilterState;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    sort: CatalogSortValue;
}) {
    const pagination = catalogPaginationFromContext(runtime.localizedPageContext);

    if (pagination.totalPages <= 1) return null;

    const pages = morrowCatalogPageNumbers(pagination.page, pagination.totalPages);
    let previousPage = 0;
    const linkForPage = (page: number) => catalogPaginationHref(
        baseHref,
        sort,
        filters,
        query,
        page,
        pagination.limit,
    );

    return (
        <nav aria-label={runtime.storefrontMessage("ui.catalog.catalog")} className="morrow-catalog-pagination">
            {pagination.hasPreviousPage ? (
                <a href={linkForPage(pagination.page - 1)} onClick={(event) => handleMorrowCatalogClick(event, linkForPage(pagination.page - 1), runtime)}>
                    <MorrowIcon name="chevronLeft" size={16}/>{runtime.storefrontMessage("ui.common.previous")}
                </a>
            ) : <span/>}
            <div>
                {pages.map((page) => {
                    const gap = previousPage > 0 && page - previousPage > 1;
                    previousPage = page;
                    const href = linkForPage(page);

                    return (
                        <span key={page}>
                            {gap ? <i>…</i> : null}
                            <a aria-current={page === pagination.page ? "page" : undefined} href={href} onClick={(event) => handleMorrowCatalogClick(event, href, runtime)}>{page}</a>
                        </span>
                    );
                })}
            </div>
            {pagination.hasNextPage ? (
                <a href={linkForPage(pagination.page + 1)} onClick={(event) => handleMorrowCatalogClick(event, linkForPage(pagination.page + 1), runtime)}>
                    {runtime.storefrontMessage("ui.common.next")}<MorrowIcon name="chevronRight" size={16}/>
                </a>
            ) : <span/>}
        </nav>
    );
}

function MorrowCatalogCategories({
    collapsed,
    items,
    runtime,
    style,
}: {
    collapsed: boolean;
    items: readonly PublicShopMenuItem[];
    runtime: ShopStorefrontRuntimeModel;
    style: CatalogCategoryStyle;
}) {
    const [expanded, setExpanded] = useState(!collapsed);

    useEffect(() => {
        setExpanded(!collapsed);
    }, [collapsed]);

    if (items.length === 0) return null;

    const canCollapse = collapsed && items.length > 6;
    const visibleItems = canCollapse && !expanded ? items.slice(0, 6) : items;
    const hiddenCount = Math.max(0, items.length - visibleItems.length);

    return (
        <nav aria-label={runtime.storefrontMessage("ui.search.categories")} className={`morrow-catalog-categories is-style-${style}`}>
            <div>
                {visibleItems.map((item, index) => {
                    const href = functionalStorefrontHref(menuItemHref(item));
                    const initial = Array.from(item.label.trim())[0]?.toLocaleUpperCase() ?? "";

                    return (
                        <ShopStorefrontLink href={href} key={item.uuid} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                            {style === "images" || style === "grid" || style === "largeImages" ? (
                                <span className={`morrow-catalog-category-art tone-${index % 4}`} aria-hidden="true">{initial}</span>
                            ) : null}
                            <span>{item.label}</span>
                        </ShopStorefrontLink>
                    );
                })}
            </div>
            {canCollapse ? (
                <button onClick={() => setExpanded((current) => !current)} type="button">
                    {expanded
                        ? runtime.storefrontMessage("ui.catalog.showLess")
                        : runtime.storefrontMessage("ui.catalog.showMore", { count: hiddenCount })}
                </button>
            ) : null}
        </nav>
    );
}

function MorrowCatalogToolbar({
    baseHref,
    filters,
    onOpenFilters,
    onSort,
    query,
    runtime,
    showFilterMenu,
    sort,
    toolbarSearch,
    toolbarSort,
}: {
    baseHref: string;
    filters: CatalogFilterState;
    onOpenFilters: () => void;
    onSort: (value: string) => void;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    showFilterMenu: boolean;
    sort: CatalogSortValue;
    toolbarSearch: boolean;
    toolbarSort: boolean;
}) {
    const [searchValue, setSearchValue] = useState(query ?? "");

    useEffect(() => {
        setSearchValue(query ?? "");
    }, [query]);

    if (!showFilterMenu && !toolbarSearch && !toolbarSort) return null;

    return (
        <div className="morrow-catalog-product-toolbar">
            {showFilterMenu ? (
                <button className="morrow-catalog-filter-menu-button" onClick={onOpenFilters} type="button">
                    <MorrowIcon name="settings" size={16}/>
                    {runtime.storefrontMessage("ui.catalog.filters")}
                    {activeCatalogFilterCount(filters) > 0 ? <b>{activeCatalogFilterCount(filters)}</b> : null}
                </button>
            ) : null}
            {toolbarSearch ? (
                <form
                    className="morrow-catalog-inline-search"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (runtime.editorMode) return;
                        const href = catalogHref(
                            baseHref,
                            sort,
                            filters,
                            searchValue.trim() || null,
                        );
                        void runtime.navigateCatalog(href);
                    }}
                >
                    <MorrowIcon name="search" size={16}/>
                    <input
                        aria-label={runtime.storefrontMessage("block.searchBar.buttonLabel")}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={runtime.storefrontMessage("ui.catalog.searchPlaceholder")}
                        value={searchValue}
                    />
                    <button type="submit">{runtime.storefrontMessage("block.searchBar.buttonLabel")}</button>
                </form>
            ) : null}
            {toolbarSort ? (
                <label className="morrow-catalog-sort">
                    {runtime.storefrontMessage("ui.catalog.sortLabel")}
                    <select disabled={runtime.catalogUpdating} onChange={(event) => onSort(event.target.value)} value={sort}>
                        <option value="recommended">{runtime.storefrontMessage("ui.catalog.sort.recommended")}</option>
                        <option value="newest">{runtime.storefrontMessage("ui.catalog.sort.newest")}</option>
                        <option value="bestselling">{runtime.storefrontMessage("ui.catalog.sort.bestselling")}</option>
                        <option value="rating">{runtime.storefrontMessage("ui.catalog.sort.rating")}</option>
                        <option value="priceAsc">{runtime.storefrontMessage("ui.catalog.sort.priceAsc")}</option>
                        <option value="priceDesc">{runtime.storefrontMessage("ui.catalog.sort.priceDesc")}</option>
                    </select>
                </label>
            ) : null}
        </div>
    );
}

function MorrowCatalogProductPage({
    baseHref,
    categories,
    children,
    facets,
    filters,
    onSort,
    productPageReference,
    query,
    runtime,
    saleLocked = false,
    sort,
    total,
}: {
    baseHref: string;
    categories: readonly PublicShopMenuItem[];
    children: ReactNode;
    facets: PublicCatalogFacet[];
    filters: CatalogFilterState;
    onSort: (value: string) => void;
    productPageReference?: MorrowBlockReference;
    query: string | null;
    runtime: ShopStorefrontRuntimeModel;
    saleLocked?: boolean;
    sort: CatalogSortValue;
    total: number;
}) {
    const config = catalogProductPageConfig(productPageReference?.block);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const filterDrawerRef = useRef<HTMLDivElement>(null);
    const categoriesInSidebar = config.categoriesPlacement === "sidebar" && categories.length > 0;
    const filtersInSidebar = config.filtersMode === "sidebar";
    const hasSidebar = categoriesInSidebar || filtersInSidebar;
    const filtersPanel = () => (
        <MorrowCatalogFilters
            baseHref={baseHref}
            collapseSections={config.collapseSidebarSections}
            facets={facets}
            filters={filters}
            query={query}
            runtime={runtime}
            saleLocked={saleLocked}
            showPriceFilter={config.showPriceFilter}
            showTagsFilter={config.showTagsFilter}
            sort={sort}
            total={total}
        />
    );

    useEffect(() => {
        if (!filtersOpen) return;

        const view = filterDrawerRef.current?.ownerDocument.defaultView;

        if (!view) return;

        const closeOnEscape = (event: globalThis.KeyboardEvent) => {
            if (event.key === "Escape") setFiltersOpen(false);
        };
        view.addEventListener("keydown", closeOnEscape);

        return () => view.removeEventListener("keydown", closeOnEscape);
    }, [filtersOpen]);

    useEffect(() => {
        if (config.filtersMode !== "offscreen") setFiltersOpen(false);
    }, [config.filtersMode]);

    useEffect(() => {
        setFiltersOpen(false);
    }, [runtime.currentBrowserPath, runtime.currentBrowserSearch]);

    return (
        <div
            className={`morrow-catalog-product-page is-width-${config.contentWidth} is-design-${config.productDesign}`}
            data-morrow-product-design={config.productDesign}
            data-shop-block-part={productPageReference?.part}
            style={productPageReference ? visualStyle(productPageReference.block) : undefined}
        >
            {config.categoriesPlacement === "top" ? (
                <MorrowCatalogCategories collapsed={config.collapseCategories} items={categories} runtime={runtime} style={config.categoryStyle}/>
            ) : null}
            <MorrowCatalogToolbar
                baseHref={baseHref}
                filters={filters}
                onOpenFilters={() => setFiltersOpen(true)}
                onSort={onSort}
                query={query}
                runtime={runtime}
                showFilterMenu={config.filtersMode === "offscreen"}
                sort={sort}
                toolbarSearch={config.toolbarSearch}
                toolbarSort={config.toolbarSort}
            />
            <div className={`morrow-catalog-layout${hasSidebar ? " has-sidebar" : " is-without-sidebar"}${runtime.catalogUpdating ? " morrow-is-updating" : ""}`}>
                {hasSidebar ? (
                    <aside className="morrow-catalog-sidebar">
                        {categoriesInSidebar ? <MorrowCatalogCategories collapsed={config.collapseCategories} items={categories} runtime={runtime} style={config.categoryStyle}/> : null}
                        {filtersInSidebar ? filtersPanel() : null}
                    </aside>
                ) : null}
                <div className="morrow-catalog-results">{children}</div>
            </div>
            {config.filtersMode === "offscreen" && filtersOpen ? (
                <div aria-label={runtime.storefrontMessage("ui.catalog.filters")} aria-modal="true" className="morrow-catalog-filter-drawer" ref={filterDrawerRef} role="dialog">
                    <button aria-label={runtime.storefrontMessage("ui.common.close")} className="morrow-catalog-filter-drawer-backdrop" onClick={() => setFiltersOpen(false)} type="button"/>
                    <aside className="morrow-catalog-filter-drawer-panel">
                        <div className="morrow-catalog-filter-drawer-head">
                            <strong>{runtime.storefrontMessage("ui.catalog.filters")}</strong>
                            <button aria-label={runtime.storefrontMessage("ui.common.close")} onClick={() => setFiltersOpen(false)} type="button"><MorrowIcon name="x" size={20}/></button>
                        </div>
                        {filtersPanel()}
                    </aside>
                </div>
            ) : null}
        </div>
    );
}

function MorrowCatalog({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const context = runtime.localizedPageContext;
    const entity = nestedRecord(contextData(context), context?.routeType === "section" ? "section" : "category");
    const title = textValue(entity, "name", context?.title ?? runtime.storefrontMessage("ui.catalog.catalog"));
    const shortDescription = textValue(entity, "descriptionShort", "");
    const description = shortDescription || textValue(entity, "description", context?.seoDescription ?? "");
    const descriptionField: ShopDynamicEntityField = shortDescription ? "descriptionShort" : "description";
    const uuid = stringOrNull(entity.uuid);
    const entityType = context?.routeType === "section" ? "section" : "category";
    const products = catalogProductsFromContext(context);
    const pagination = catalogPaginationFromContext(context);
    const total = pagination.total || products.length;
    const contextSort = catalogSortFromContext(context);
    const filters = catalogFiltersFromContext(context);
    const saleLocked = context?.routeType === "sale";
    const facets = catalogFacetsWithSale(
        catalogFacetsFromContext(context),
        filters,
        !saleLocked,
        runtime.storefrontMessage("ui.catalog.saleFacet"),
        runtime.storefrontMessage("ui.catalog.saleOption"),
    );
    const query = catalogQueryFromContext(context);
    const baseHref = context?.canonicalHref ?? runtime.currentBrowserPath;
    const subcategoryReference = runtimeSystemBlockReference(block, "catalogSubcategoriesSystem");
    const productListReference = runtimeSystemBlockReference(block, "catalogProductsSystem");
    const productPageConfig = catalogProductPageConfig(productListReference?.block);
    const hasExplicitSort = new URLSearchParams(runtime.currentBrowserSearch).has("sort");
    const sort = hasExplicitSort ? contextSort : productPageConfig.defaultSort;
    const appliedDefaultSortHrefRef = useRef<string | null>(null);

    useEffect(() => {
        if (hasExplicitSort) {
            appliedDefaultSortHrefRef.current = null;
            return;
        }

        if (
            runtime.editorMode
            || productPageConfig.defaultSort === "recommended"
            || contextSort === productPageConfig.defaultSort
        ) return;

        const href = catalogHref(
            baseHref,
            productPageConfig.defaultSort,
            filters,
            query,
        );

        if (appliedDefaultSortHrefRef.current === href) return;
        appliedDefaultSortHrefRef.current = href;
        void runtime.navigateCatalog(href);
    }, [baseHref, contextSort, filters, hasExplicitSort, productPageConfig.defaultSort, query, runtime]);

    function setSort(value: string) {
        if (runtime.editorMode) return;

        const nextSort = catalogProductSort(value);
        void runtime.navigateCatalog(catalogHref(baseHref, nextSort, filters, query));
    }

    return (
        <div className="morrow-catalog">
            <div data-shop-block-part={subcategoryReference?.part} style={subcategoryReference ? visualStyle(subcategoryReference.block) : undefined}>
                <div className="morrow-catalog-title">
                    <p>{morrowProductCount(runtime, total)}</p>
                    <h1
                        {...(uuid ? catalogTextAttributes(entityType, uuid, "name", "title") : {})}
                        style={subcategoryReference ? partVisualStyle(subcategoryReference.block, "title") : undefined}
                    >
                        {title}
                    </h1>
                    {description ? (
                        <div
                            {...(uuid ? catalogTextAttributes(entityType, uuid, descriptionField, "text") : {})}
                            style={subcategoryReference ? partVisualStyle(subcategoryReference.block, "text") : undefined}
                        >
                            {description}
                        </div>
                    ) : null}
                </div>
            </div>
            <MorrowCatalogProductPage
                baseHref={baseHref}
                categories={runtime.activeSectionItems}
                facets={facets}
                filters={filters}
                onSort={setSort}
                productPageReference={productListReference}
                query={query}
                runtime={runtime}
                saleLocked={saleLocked}
                sort={sort}
                total={total}
            >
                <MorrowProductGrid attachSystemReference={false} block={block} products={products} runtime={runtime} systemReference={productListReference}/>
                <MorrowCatalogPagination baseHref={baseHref} filters={filters} query={query} runtime={runtime} sort={sort}/>
            </MorrowCatalogProductPage>
        </div>
    );
}

function MorrowSearchResults({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const context = runtime.localizedPageContext;
    const data = contextData(context);
    const catalog = nestedRecord(data, "catalog");
    const search = nestedRecord(data, "search");
    const query = stringOrNull(search.query) ?? stringOrNull(catalog.query) ?? "";
    const browserScope = new URLSearchParams(runtime.currentBrowserSearch).get("scope");
    const scope: ShopSearchScope = normalizeShopSearchScope(browserScope ?? stringOrNull(search.scope));
    const products = catalogProductsFromContext(context);
    const filters = catalogFiltersFromContext(context);
    const contextSort = catalogSortFromContext(context);
    const facets = catalogFacetsWithSale(
        catalogFacetsFromContext(context),
        filters,
        true,
        runtime.storefrontMessage("ui.catalog.saleFacet"),
        runtime.storefrontMessage("ui.catalog.saleOption"),
    );
    const pagination = catalogPaginationFromContext(context);
    const canonicalHref = context?.canonicalHref ?? runtime.searchHref;
    const baseHref = scope === "everything"
        ? canonicalHref
        : `${canonicalHref}${canonicalHref.includes("?") ? "&" : "?"}scope=${encodeURIComponent(scope)}`;
    const payload = parseShopSearchSuggestions({
        query,
        products: catalog.products,
        categories: search.categories,
        productTotal: catalog.total,
        categoryTotal: search.categoryTotal,
    });
    const results = selectShopSearchSuggestions({
        query,
        scope,
        limit: 24,
        pages: runtime.pages,
        payload,
        resolveHref: runtime.storefrontHref,
    });
    const productPageCategories: PublicShopMenuItem[] = results.categories.map((category) => ({
        children: [],
        href: shopSearchCategoryHref(
            category,
            runtime.searchHref,
            runtime.storefrontHref,
        ),
        label: category.name,
        openInNewTab: false,
        type: "category",
        uuid: category.uuid,
    }));
    const showProducts = scope === "everything" || scope === "products";
    const hasResults = results.pages.length > 0
        || results.categories.length > 0
        || (showProducts && products.length > 0);
    const systemReference = runtimeSystemBlockReference(block, "searchResultsSystem");
    const productPageConfig = catalogProductPageConfig(systemReference?.block);
    const hasExplicitSort = new URLSearchParams(runtime.currentBrowserSearch).has("sort");
    const sort = hasExplicitSort ? contextSort : productPageConfig.defaultSort;
    const appliedDefaultSortHrefRef = useRef<string | null>(null);

    useEffect(() => {
        if (hasExplicitSort) {
            appliedDefaultSortHrefRef.current = null;
            return;
        }

        if (
            runtime.editorMode
            || !showProducts
            || productPageConfig.defaultSort === "recommended"
            || contextSort === productPageConfig.defaultSort
        ) return;

        const href = catalogHref(
            baseHref,
            productPageConfig.defaultSort,
            filters,
            query,
        );

        if (appliedDefaultSortHrefRef.current === href) return;
        appliedDefaultSortHrefRef.current = href;
        void runtime.navigateCatalog(href);
    }, [baseHref, contextSort, filters, hasExplicitSort, productPageConfig.defaultSort, query, runtime, showProducts]);

    function setSort(value: string) {
        if (runtime.editorMode) return;

        void runtime.navigateCatalog(catalogHref(
            baseHref,
            catalogProductSort(value),
            filters,
            query,
        ));
    }

    return (
        <div className="morrow-search-results">
            {results.pages.length > 0 ? (
                <section className="morrow-search-result-group">
                    <h2>{runtime.storefrontMessage("ui.search.pages")}</h2>
                    <div className="morrow-search-result-links">
                        {results.pages.map((page) => (
                            <ShopStorefrontLink href={page.href} key={`${page.href}:${page.title}`} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                                <span>{page.title}</span><MorrowIcon name="arrowUpRight" size={18}/>
                            </ShopStorefrontLink>
                        ))}
                    </div>
                </section>
            ) : null}
            {results.categories.length > 0 && !showProducts ? (
                <section className="morrow-search-result-group">
                    <h2>{runtime.storefrontMessage("ui.search.categories")}</h2>
                    <div className="morrow-search-result-links">
                        {results.categories.map((category) => (
                            <ShopStorefrontLink href={shopSearchCategoryHref(category, runtime.searchHref, runtime.storefrontHref)} key={category.uuid} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                                <span><strong>{category.name}</strong>{category.pathNames.length > 0 ? <small>{category.pathNames.join(" / ")}</small> : null}</span>
                                <MorrowIcon name="arrowUpRight" size={18}/>
                            </ShopStorefrontLink>
                        ))}
                    </div>
                </section>
            ) : null}
            {showProducts ? (
                <section className="morrow-search-result-group">
                    <h2>{runtime.storefrontMessage("ui.search.products")}</h2>
                    <MorrowCatalogProductPage
                        baseHref={baseHref}
                        categories={productPageCategories}
                        facets={facets}
                        filters={filters}
                        onSort={setSort}
                        productPageReference={systemReference}
                        query={query}
                        runtime={runtime}
                        sort={sort}
                        total={pagination.total || products.length}
                    >
                        <MorrowProductGrid attachSystemReference={false} block={block} products={products} runtime={runtime} systemReference={systemReference}/>
                        <MorrowCatalogPagination baseHref={baseHref} filters={filters} query={query} runtime={runtime} sort={sort}/>
                    </MorrowCatalogProductPage>
                </section>
            ) : null}
            {!hasResults && !showProducts ? (
                <div className="morrow-empty-state">
                    <strong>{runtime.storefrontMessage("ui.search.nothingFound")}</strong>
                    <span>{runtime.storefrontMessage("ui.search.tryBroader")}</span>
                </div>
            ) : null}
        </div>
    );
}

function selectedVariantForProduct(variants: PublicProductVariant[], selectedUuid: string | null): PublicProductVariant | null {
    return variants.find((variant) => variant.uuid === selectedUuid) ?? variants.find((variant) => variant.canAddToCart) ?? variants[0] ?? null;
}

function MorrowProductDetail({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const locale = storefrontLocale(runtime.storefrontLanguageCode);
    const data = nestedRecord(contextData(runtime.localizedPageContext), "product");
    const uuid = textValue(data, "uuid", "");
    const title = textValue(data, "name", runtime.localizedPageContext?.title ?? "Product");
    const basePrice = textValue(data, "price", "0");
    const variants = useMemo(() => productVariantsFromData(data, locale), [data, locale]);
    const [variantUuid, setVariantUuid] = useState<string | null>(null);
    const selectedVariant = selectedVariantForProduct(variants, variantUuid);
    const productImages = productImagesFromData(data);
    const galleryImages = selectedVariant?.images.length ? selectedVariant.images : productImages;
    const previewGalleryImages = galleryImages.length > 0
        ? galleryImages
        : runtime.editorMode
            ? [{ mediaUuid: "editor-preview", url: MORROW_PRODUCT_IMAGE, thumbnailUrl: null, alt: title, title: null, width: null, height: null }]
            : [];
    const surcharges = useMemo(() => productSurchargeParametersFromData(selectedVariant?.surchargeParameters ?? data.surchargeParameters, locale), [data.surchargeParameters, selectedVariant?.surchargeParameters, locale]);
    const [surchargeValues, setSurchargeValues] = useState<Record<string, string>>({});
    const [quantity, setQuantity] = useState(1);
    const available = data.availableForOrder !== false && (selectedVariant?.canAddToCart ?? true);
    const requiredSelected = surcharges.every((parameter) => !parameter.required || Boolean(surchargeValues[parameter.uuid]));
    const selectedSurchargeValueUuids = surcharges.flatMap((parameter) => {
        const selectedValueUuid = surchargeValues[parameter.uuid];

        return selectedValueUuid && parameter.values.some((value) => value.uuid === selectedValueUuid)
            ? [selectedValueUuid]
            : [];
    });
    const customization = useManufacturingCustomization({
        editorMode: runtime.editorMode,
        modifierOptionUuids: selectedSurchargeValueUuids,
        productUuid: uuid,
        variantUuid: selectedVariant?.uuid ?? null,
    });
    const canAdd = uuid !== "" && available && requiredSelected && customization.canSubmit;
    const price = selectedVariant?.price ?? basePrice;
    const shortDescription = textValue(data, "descriptionShort", "");
    const description = shortDescription || textValue(data, "description", runtime.localizedPageContext?.seoDescription ?? "");
    const descriptionField: ShopDynamicEntityField = shortDescription ? "descriptionShort" : "description";
    const reference = selectedVariant?.reference ?? textValue(data, "reference", "");
    const favorite = runtime.visibleFavoriteProductIds.has(uuid);
    const galleryReference = runtimeSystemBlockReference(block, "productGallerySystem");
    const summaryReference = runtimeSystemBlockReference(block, "productSummarySystem");
    const buyBoxReference = runtimeSystemBlockReference(block, "productBuyBoxSystem");
    const descriptionReference = runtimeSystemBlockReference(block, "productDescriptionSystem");

    useEffect(() => {
        setVariantUuid(variants.find((variant) => variant.canAddToCart)?.uuid ?? variants[0]?.uuid ?? null);
        setSurchargeValues({});
        setQuantity(1);
    }, [uuid]);

    return (
        <div className="morrow-product-detail">
            <div className="morrow-product-gallery" data-shop-block-part={galleryReference?.part} style={galleryReference ? visualStyle(galleryReference.block) : undefined}>
                <div className="morrow-product-main-image">{previewGalleryImages[0] ? <img alt={previewGalleryImages[0].alt || title} src={previewGalleryImages[0].url}/> : <MorrowProductMediaPlaceholder name={title} size="detail"/>}</div>
                {previewGalleryImages.slice(1, 3).map((image, index) => <div className={`morrow-product-support-image support-${index + 1}`} key={image.mediaUuid}><img alt={image.alt || title} src={image.thumbnailUrl ?? image.url}/></div>)}
            </div>
            <div className="morrow-buy-panel">
                <div data-shop-block-part={summaryReference?.part} style={summaryReference ? visualStyle(summaryReference.block) : undefined}>
                    <div className="morrow-product-kicker"><span>{textValue(data, "manufacturerName", runtime.displayName)}</span><button aria-label={favorite ? runtime.storefrontMessage("ui.product.removeFromFavorites") : runtime.storefrontMessage("ui.product.addToFavorites")} className={favorite ? "is-active" : undefined} onClick={() => runtime.toggleFavorite(uuid)} type="button"><MorrowIcon name="heart"/></button></div>
                    <h1
                        {...(uuid ? catalogTextAttributes("product", uuid, "name", "productName") : {})}
                        style={summaryReference ? partVisualStyle(summaryReference.block, "productName") : undefined}
                    >
                        {title}
                    </h1>
                    <div
                        {...(uuid ? catalogTextAttributes("product", uuid, "formattedPrice", "productPrice") : {})}
                        className="morrow-product-price"
                        style={summaryReference ? partVisualStyle(summaryReference.block, "productPrice") : undefined}
                    >
                        {runtime.storefrontFormatting.formatMoney(price)}
                    </div>
                    {reference ? (
                        <p>
                            {runtime.storefrontMessage("ui.product.productCode")}: {" "}
                            <span
                                {...(uuid ? catalogTextAttributes("product", uuid, "reference", "productReference") : {})}
                                style={summaryReference ? partVisualStyle(summaryReference.block, "productReference") : undefined}
                            >
                                {reference}
                            </span>
                        </p>
                    ) : null}
                </div>
                <div data-shop-block-part={buyBoxReference?.part} style={buyBoxReference ? visualStyle(buyBoxReference.block) : undefined}>
                    {variants.length > 0 ? <MorrowVariantPicker onChange={setVariantUuid} runtime={runtime} selected={selectedVariant} variants={variants}/> : null}
                    {surcharges.map((parameter) => <MorrowSurchargePicker key={parameter.uuid} onChange={(value) => setSurchargeValues((current) => ({ ...current, [parameter.uuid]: value }))} parameter={parameter} runtime={runtime} selected={surchargeValues[parameter.uuid]}/>) }
                    <ManufacturingCustomizationFields appearance="morrow" controller={customization} message={runtime.storefrontMessage}/>
                    <div className="morrow-stock-line"><span className={available ? "is-available" : ""}/>{available ? runtime.storefrontMessage("ui.product.inStock") : runtime.storefrontMessage("ui.product.outOfStock")}</div>
                    <div className="morrow-add-row">
                        <div className="morrow-quantity"><button aria-label={runtime.storefrontMessage("ui.product.decreaseQuantity")} onClick={() => setQuantity((value) => Math.max(1, value - 1))} type="button">−</button><span>{quantity}</span><button aria-label={runtime.storefrontMessage("ui.product.increaseQuantity")} onClick={() => setQuantity((value) => Math.min(CART_ITEM_MAX_QUANTITY, value + 1))} type="button">+</button></div>
                        <button className="morrow-primary-button" disabled={!canAdd || runtime.addingProductUuid === uuid} onClick={() => void runtime.addCartItem(uuid, selectedVariant?.uuid ?? null, quantity, selectedSurchargeValueUuids, customization.profile.profileAvailable ? customization.specification : undefined)} type="button"><MorrowIcon name="bag" size={20}/>{runtime.addingProductUuid === uuid ? runtime.storefrontMessage("ui.product.adding") : runtime.storefrontMessage("ui.product.addToCart")}</button>
                    </div>
                    {!requiredSelected ? <p className="morrow-error">{runtime.storefrontMessage("ui.product.requiredSurcharge")}</p> : null}
                </div>
                <div data-shop-block-part={descriptionReference?.part} style={descriptionReference ? visualStyle(descriptionReference.block) : undefined}>
                    {description ? (
                        <div className="morrow-product-description">
                            <strong>{runtime.storefrontMessage("ui.product.description")}</strong>
                            <p
                                {...(uuid ? catalogTextAttributes("product", uuid, descriptionField, "productDescription") : {})}
                                style={descriptionReference ? partVisualStyle(descriptionReference.block, "productDescription") : undefined}
                            >
                                {description}
                            </p>
                        </div>
                    ) : null}
                    <div className="morrow-buy-benefits"><span><MorrowIcon name="return" size={20}/>{runtime.storefrontMessage("ui.product.easyReturns")}</span><span><MorrowIcon name="sparkles" size={20}/>{runtime.storefrontMessage("ui.product.securePayment")}</span></div>
                </div>
            </div>
        </div>
    );
}

function MorrowVariantPicker({ onChange, runtime, selected, variants }: { onChange: (uuid: string) => void; runtime: ShopStorefrontRuntimeModel; selected: PublicProductVariant | null; variants: PublicProductVariant[] }) {
    return <fieldset className="morrow-option-group"><legend>{runtime.storefrontMessage("ui.product.chooseVariant")}</legend><div>{variants.map((variant) => <button className={selected?.uuid === variant.uuid ? "is-selected" : undefined} disabled={!variant.canAddToCart} key={variant.uuid} onClick={() => onChange(variant.uuid)} type="button">{variant.name}</button>)}</div></fieldset>;
}

function MorrowSurchargePicker({ onChange, parameter, runtime, selected }: { onChange: (uuid: string) => void; parameter: PublicProductSurchargeParameter; runtime: ShopStorefrontRuntimeModel; selected?: string }) {
    return <fieldset className="morrow-option-group"><legend>{parameter.displayName ?? parameter.name}{parameter.required ? " *" : ""}</legend><div>{parameter.values.map((value) => <button className={selected === value.uuid ? "is-selected" : undefined} disabled={!value.canSelect} key={value.uuid} onClick={() => onChange(value.uuid)} type="button">{value.name}{moneyAmount(value.salePrice) !== 0 ? ` +${runtime.storefrontFormatting.formatMoney(value.salePrice)}` : ""}</button>)}</div></fieldset>;
}

function MorrowCartItems({ compact = false, runtime, systemReference }: {
    compact?: boolean;
    runtime: ShopStorefrontRuntimeModel;
    systemReference?: MorrowBlockReference;
}) {
    const systemAttributes = systemReference ? {
        "data-shop-block-part": systemReference.part,
        style: visualStyle(systemReference.block),
    } : {};

    if (runtime.cartLoading && runtime.cart.items.length === 0) return <div {...systemAttributes} className="morrow-empty-state">{runtime.storefrontMessage("ui.cart.loading")}</div>;
    if (runtime.cart.items.length === 0) return <div {...systemAttributes} className="morrow-empty-state"><MorrowIcon name="bag" size={32}/><strong>{runtime.storefrontMessage("ui.cart.empty")}</strong><a href={runtime.searchHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("ui.cart.addProducts")}</a></div>;

    return (
        <div {...systemAttributes} className={compact ? "morrow-cart-items is-compact" : "morrow-cart-items"}>
            {runtime.cart.items.map((item) => (
                <article key={item.uuid}>
                    <div className="morrow-cart-thumb">{item.primaryImage ? <img alt={item.primaryImage.alt || item.name} src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url}/> : <MorrowProductMediaPlaceholder name={item.name} size="compact"/>}</div>
                    <div className="morrow-cart-copy"><strong>{item.name}</strong>{item.variantName ? <span>{item.variantName}</span> : null}<ManufacturingSpecificationSummary appearance="morrow" compact={compact} message={runtime.storefrontMessage} specification={item.manufacturingSpecification}/><small>{runtime.storefrontFormatting.formatMoney(item.unitPrice)}</small></div>
                    {!compact ? <div className="morrow-quantity"><button onClick={() => item.quantity <= 1 ? runtime.removeCartItem(item.uuid) : runtime.updateCartItem(item.uuid, item.quantity - 1)} type="button">−</button><span>{item.quantity}</span><button disabled={item.maxQuantity !== null && item.quantity >= item.maxQuantity} onClick={() => runtime.updateCartItem(item.uuid, item.quantity + 1)} type="button">+</button></div> : <span>× {item.quantity}</span>}
                    <strong>{runtime.storefrontFormatting.formatMoney(item.totalPrice)}</strong>
                    <button aria-label={runtime.storefrontMessage("ui.common.remove")} className="morrow-remove" onClick={() => runtime.removeCartItem(item.uuid)} type="button">×</button>
                </article>
            ))}
        </div>
    );
}

function MorrowCartSummary({ checkout = false, runtime, systemReference }: {
    checkout?: boolean;
    runtime: ShopStorefrontRuntimeModel;
    systemReference?: MorrowBlockReference;
}) {
    return (
        <aside className="morrow-cart-summary" data-shop-block-part={systemReference?.part} style={systemReference ? visualStyle(systemReference.block) : undefined}>
            <h2>{runtime.storefrontMessage("ui.cart.total")}</h2>
            <dl><div><dt>{runtime.storefrontMessage("ui.cart.subtotal")}</dt><dd>{runtime.storefrontFormatting.formatMoney(runtime.cart.itemsTotalPrice)}</dd></div><div><dt>{runtime.storefrontMessage("ui.checkout.shipping")}</dt><dd>{runtime.storefrontFormatting.formatMoney(runtime.cart.carrierPrice)}</dd></div><div className="is-total"><dt>{runtime.storefrontMessage("ui.cart.total")}</dt><dd>{runtime.storefrontFormatting.formatMoney(runtime.cart.totalPrice)}</dd></div></dl>
            {runtime.cartError ? <p className="morrow-error">{runtime.cartError}</p> : null}
            {!checkout ? <a className="morrow-primary-button" href={runtime.checkoutHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("ui.cart.checkout")}<span>↗</span></a> : null}
            <div className="morrow-summary-note"><MorrowIcon name="sparkles" size={18}/>{runtime.storefrontMessage("ui.product.securePayment")}</div>
        </aside>
    );
}

function MorrowCart({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const itemsReference = runtimeSystemBlockReference(block, "cartItemsSystem");
    const summaryReference = runtimeSystemBlockReference(block, "cartSummarySystem");

    return <div className="morrow-cart-layout"><MorrowCartItems runtime={runtime} systemReference={itemsReference}/><MorrowCartSummary runtime={runtime} systemReference={summaryReference}/></div>;
}

function MorrowCartDrawer({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    if (!runtime.cartOpen) return null;

    return (
        <div className="morrow-overlay" role="dialog" aria-modal="true">
            <button aria-label={runtime.storefrontMessage("ui.cart.close")} className="morrow-overlay-backdrop" onClick={runtime.closeOverlay} type="button"/>
            <aside className="morrow-cart-drawer"><div className="morrow-panel-head"><strong>{runtime.storefrontMessage("ui.cart.yourCart")}</strong><button onClick={runtime.closeOverlay} type="button">×</button></div><MorrowCartItems compact runtime={runtime}/><div className="morrow-drawer-total"><span>{runtime.storefrontMessage("ui.cart.total")}</span><strong>{runtime.storefrontFormatting.formatMoney(runtime.cart.totalPrice)}</strong></div><a className="morrow-primary-button" href={runtime.cartPageHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("ui.cart.goToCart")}<span>↗</span></a></aside>
        </div>
    );
}

function MorrowCheckout({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const [selectedCarrierRateUuid, setSelectedCarrierRateUuid] = useState<string | null>(runtime.cart.carrierRateUuid);
    const [selectedPaymentUuid, setSelectedPaymentUuid] = useState<string | null>(runtime.cart.carrierPaymentOptionUuid);
    const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPointSelection | null>(() => (
        runtime.cart.pickupPointId
            ? {
                provider: runtime.cart.pickupPointProvider ?? "pickup_point",
                id: runtime.cart.pickupPointId,
                name: runtime.cart.pickupPointName,
            }
            : null
    ));
    const [clientError, setClientError] = useState<string | null>(null);
    const stepsReference = runtimeSystemBlockReference(block, "checkoutStepsSystem");
    const summaryReference = runtimeSystemBlockReference(block, "checkoutSummarySystem");
    const selectedDelivery = runtime.checkoutOptions.deliveryOptions.find(
        (delivery) => delivery.carrierRateUuid === selectedCarrierRateUuid,
    ) ?? null;
    const selectedPayment = selectedDelivery?.paymentOptions.find(
        (payment) => payment.carrierPaymentOptionUuid === selectedPaymentUuid,
    ) ?? null;

    useEffect(() => {
        const carrierRateUuid = runtime.cart.carrierRateUuid;
        const paymentUuid = runtime.cart.carrierPaymentOptionUuid;

        if (!carrierRateUuid || !paymentUuid) {
            return;
        }

        const delivery = runtime.checkoutOptions.deliveryOptions.find(
            (option) => option.carrierRateUuid === carrierRateUuid,
        );

        if (!delivery?.paymentOptions.some((payment) => payment.carrierPaymentOptionUuid === paymentUuid)) {
            return;
        }

        setSelectedCarrierRateUuid(carrierRateUuid);
        setSelectedPaymentUuid(paymentUuid);
        setSelectedPickupPoint(runtime.cart.pickupPointId
            ? {
                provider: runtime.cart.pickupPointProvider ?? pickupPointProvider(delivery),
                id: runtime.cart.pickupPointId,
                name: runtime.cart.pickupPointName,
            }
            : null);
    }, [
        runtime.cart.carrierPaymentOptionUuid,
        runtime.cart.carrierRateUuid,
        runtime.cart.pickupPointId,
        runtime.cart.pickupPointName,
        runtime.cart.pickupPointProvider,
        runtime.checkoutOptions.deliveryOptions,
    ]);

    useEffect(() => {
        if (selectedCarrierRateUuid === null) {
            return;
        }

        const delivery = runtime.checkoutOptions.deliveryOptions.find(
            (option) => option.carrierRateUuid === selectedCarrierRateUuid,
        );

        if (!delivery) {
            setSelectedCarrierRateUuid(null);
            setSelectedPaymentUuid(null);
            setSelectedPickupPoint(null);

            return;
        }

        if (selectedPaymentUuid !== null && !delivery.paymentOptions.some(
            (payment) => payment.carrierPaymentOptionUuid === selectedPaymentUuid,
        )) {
            setSelectedPaymentUuid(null);
        }
    }, [runtime.checkoutOptions.deliveryOptions, selectedCarrierRateUuid, selectedPaymentUuid]);

    function selectDelivery(delivery: PublicCheckoutDeliveryOption): void {
        const sameDelivery = selectedCarrierRateUuid === delivery.carrierRateUuid;
        setSelectedCarrierRateUuid(delivery.carrierRateUuid);
        setSelectedPaymentUuid((current) => sameDelivery ? current : null);
        setSelectedPickupPoint((current) => (
            sameDelivery
            && delivery.requiresPickupPoint
            && current?.provider === pickupPointProvider(delivery)
                ? current
                : null
        ));
        setClientError(null);
        runtime.updateCheckoutForm({ deliveryCountryCode: delivery.countryCode });
    }

    async function selectPayment(paymentUuid: string): Promise<void> {
        if (selectedDelivery === null) {
            return;
        }

        const pickupPoint = selectedPickupPoint?.provider === pickupPointProvider(selectedDelivery)
            ? selectedPickupPoint
            : null;

        setSelectedPaymentUuid(paymentUuid);
        setSelectedPickupPoint(selectedDelivery.requiresPickupPoint ? pickupPoint : null);
        setClientError(null);

        if (!selectedDelivery.requiresPickupPoint) {
            await runtime.selectDeliveryPayment(selectedDelivery.carrierRateUuid, paymentUuid);
        } else if (pickupPoint !== null) {
            await runtime.selectDeliveryPayment(selectedDelivery.carrierRateUuid, paymentUuid, pickupPoint);
        }
    }

    async function selectPickupPoint(
        delivery: PublicCheckoutDeliveryOption,
        pickupPoint: PickupPointSelection | null,
    ): Promise<void> {
        setSelectedPickupPoint(pickupPoint);
        setClientError(null);

        if (
            pickupPoint !== null
            && selectedCarrierRateUuid === delivery.carrierRateUuid
            && selectedPaymentUuid !== null
        ) {
            await runtime.selectDeliveryPayment(
                delivery.carrierRateUuid,
                selectedPaymentUuid,
                pickupPoint,
            );
        }
    }

    async function submitCheckout(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (!runtime.cart.canCheckout) {
            setClientError(runtime.cart.checkoutBlockedReason ?? runtime.storefrontMessage("ui.error.cartCannotCheckout"));

            return;
        }

        if (selectedDelivery === null || selectedPayment === null) {
            setClientError(runtime.storefrontMessage("ui.error.selectDeliveryPayment"));

            return;
        }

        if (selectedDelivery.requiresPickupPoint && selectedPickupPoint === null) {
            setClientError(runtime.storefrontMessage("ui.checkout.pickupRequired"));

            return;
        }

        const formErrorKey = checkoutFormValidationMessageKey(runtime.checkoutForm);

        if (formErrorKey !== null) {
            setClientError(runtime.storefrontMessage(formErrorKey));

            return;
        }

        setClientError(null);
        const selectionSaved = await runtime.selectDeliveryPayment(
            selectedDelivery.carrierRateUuid,
            selectedPayment.carrierPaymentOptionUuid,
            selectedDelivery.requiresPickupPoint ? selectedPickupPoint ?? undefined : undefined,
        );

        if (selectionSaved) {
            await runtime.submitCheckout(runtime.checkoutForm);
        }
    }

    if (runtime.createdOrder || runtime.checkoutRouteStep === "completed") {
        return <div className="morrow-order-complete" data-shop-block-part={stepsReference?.part} style={stepsReference ? visualStyle(stepsReference.block) : undefined}><MorrowIcon name="sparkles" size={42}/><p>{runtime.storefrontMessage("ui.checkout.orderCompleted")}</p><h1>{runtime.createdOrder ? `${runtime.storefrontMessage("ui.checkout.orderNumber")}: ${runtime.createdOrder.reference}` : runtime.storefrontMessage("ui.checkout.orderCompleted")}</h1><a className="morrow-primary-button" href={runtime.homeHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("ui.header.home")}</a></div>;
    }

    return (
        <div className="morrow-checkout-layout">
            <form className="morrow-checkout-main" data-shop-block-part={stepsReference?.part} noValidate onSubmit={(event) => void submitCheckout(event)} style={stepsReference ? visualStyle(stepsReference.block) : undefined}>
                <div className="morrow-step"><span>01</span><div><h2>{runtime.storefrontMessage("ui.checkout.deliveryMethod")}</h2><p>{runtime.storefrontMessage("ui.checkout.shipping")}</p></div></div>
                {runtime.checkoutOptionsLoading ? <p>{runtime.storefrontMessage("ui.checkout.loadingOptions")}</p> : null}
                {runtime.checkoutOptionsError ? <p className="morrow-error">{runtime.checkoutOptionsError}</p> : null}
                {!runtime.checkoutOptionsLoading && !runtime.checkoutOptionsError && runtime.checkoutOptions.deliveryOptions.length === 0 ? <p className="morrow-empty-state">{runtime.storefrontMessage("ui.checkout.noOptions")}</p> : null}
                <div className="morrow-delivery-options">{runtime.checkoutOptions.deliveryOptions.map((delivery) => <MorrowDeliveryOption delivery={delivery} key={delivery.carrierRateUuid} onSelect={selectDelivery} runtime={runtime} selected={delivery.carrierRateUuid === selectedCarrierRateUuid}/>)}</div>
                {selectedDelivery?.requiresPickupPoint ? <MorrowPickupPointSelector delivery={selectedDelivery} onChange={(pickupPoint) => selectPickupPoint(selectedDelivery, pickupPoint)} runtime={runtime} selectedPickupPoint={selectedPickupPoint?.provider === pickupPointProvider(selectedDelivery) ? selectedPickupPoint : null}/> : null}
                <div className="morrow-step"><span>02</span><div><h2>{runtime.storefrontMessage("ui.checkout.paymentMethod")}</h2><p>{runtime.storefrontMessage("ui.checkout.payment")}</p></div></div>
                {selectedDelivery === null ? <p className="morrow-checkout-guidance">{runtime.storefrontMessage("ui.checkout.selectDeliveryFirst")}</p> : (
                    <div className="morrow-checkout-payment-options">{selectedDelivery.paymentOptions.map((payment) => <MorrowPaymentOption key={payment.carrierPaymentOptionUuid} onSelect={selectPayment} payment={payment} runtime={runtime} selected={payment.carrierPaymentOptionUuid === selectedPaymentUuid}/>)}</div>
                )}
                <div className="morrow-step"><span>03</span><div><h2>{runtime.storefrontMessage("ui.checkout.address")}</h2><p>{runtime.storefrontMessage("ui.checkout.stepContact")}</p></div></div>
                <MorrowCheckoutForm onChange={(patch) => {
                    setClientError(null);
                    runtime.updateCheckoutForm(patch);
                }} runtime={runtime}/>
                <div className="morrow-step"><span>04</span><div><h2>{runtime.storefrontMessage("ui.checkout.placeOrder")}</h2><p>{runtime.storefrontMessage("ui.checkout.priceWithVat")}</p></div></div>
                <label className="morrow-check"><input checked={runtime.checkoutForm.termsAccepted} onChange={(event) => {
                    setClientError(null);
                    runtime.updateCheckoutForm({ termsAccepted: event.target.checked });
                }} required type="checkbox"/><span>{runtime.storefrontMessage("block.systemCheckoutSteps.termsText")}</span></label>
                {clientError ? <p className="morrow-error">{clientError}</p> : null}
                {runtime.checkoutError ? <p className="morrow-error">{runtime.checkoutError}</p> : null}
                <button className="morrow-primary-button morrow-place-order" disabled={runtime.checkoutSubmitting || runtime.checkoutOptionsLoading || runtime.checkoutOptions.deliveryOptions.length === 0 || !runtime.cart.canCheckout} type="submit">{runtime.checkoutSubmitting ? runtime.storefrontMessage("ui.common.sending") : runtime.storefrontMessage("ui.checkout.placeOrder")}<span>{runtime.storefrontFormatting.formatMoney(runtime.cart.totalPrice)}</span></button>
            </form>
            <div data-shop-block-part={summaryReference?.part} style={summaryReference ? visualStyle(summaryReference.block) : undefined}><MorrowCartItems compact runtime={runtime}/><MorrowCartSummary checkout runtime={runtime}/></div>
        </div>
    );
}

function pickupPointProvider(delivery: PublicCheckoutDeliveryOption): string {
    return delivery.pickupPointProvider ?? delivery.carrierCode ?? "pickup_point";
}

function MorrowDeliveryOption({ delivery, onSelect, runtime, selected }: {
    delivery: PublicCheckoutDeliveryOption;
    onSelect: (delivery: PublicCheckoutDeliveryOption) => void;
    runtime: ShopStorefrontRuntimeModel;
    selected: boolean;
}) {
    return (
        <button aria-pressed={selected} className={selected ? "morrow-delivery-card is-selected" : "morrow-delivery-card"} onClick={() => onSelect(delivery)} type="button">
            <span className="morrow-checkout-choice-icon"><MorrowIcon name="truck" size={20}/></span>
            <span className="morrow-checkout-choice-copy"><strong>{delivery.carrierName}</strong><small>{delivery.deliveryMethodName}</small></span>
            <strong className="morrow-checkout-choice-price">{runtime.storefrontFormatting.formatMoney(delivery.price)}</strong>
            <span className="morrow-checkout-choice-check" aria-hidden>{selected ? <MorrowIcon name="check" size={20}/> : null}</span>
        </button>
    );
}

function MorrowPaymentOption({ onSelect, payment, runtime, selected }: {
    onSelect: (paymentUuid: string) => Promise<void>;
    payment: PublicCheckoutDeliveryOption["paymentOptions"][number];
    runtime: ShopStorefrontRuntimeModel;
    selected: boolean;
}) {
    return (
        <button aria-pressed={selected} className={selected ? "morrow-payment-option is-selected" : "morrow-payment-option"} onClick={() => void onSelect(payment.carrierPaymentOptionUuid)} type="button">
            <span className="morrow-checkout-choice-icon"><MorrowIcon name="creditCard" size={20}/></span>
            <span className="morrow-checkout-choice-copy"><strong>{payment.paymentName}</strong></span>
            <strong className="morrow-checkout-choice-price">{runtime.storefrontFormatting.formatMoney(payment.surcharge)}</strong>
            <span className="morrow-checkout-choice-check" aria-hidden>{selected ? <MorrowIcon name="check" size={20}/> : null}</span>
        </button>
    );
}

function MorrowPickupPointSelector({ delivery, onChange, runtime, selectedPickupPoint }: {
    delivery: PublicCheckoutDeliveryOption;
    onChange: (pickupPoint: PickupPointSelection | null) => Promise<void>;
    runtime: ShopStorefrontRuntimeModel;
    selectedPickupPoint: PickupPointSelection | null;
}) {
    const [manualId, setManualId] = useState(selectedPickupPoint?.id ?? "");
    const [manualName, setManualName] = useState(selectedPickupPoint?.name ?? "");
    const [widgetLoading, setWidgetLoading] = useState(false);
    const [widgetError, setWidgetError] = useState<string | null>(null);
    const provider = pickupPointProvider(delivery);
    const canUsePacketaWidget = provider.toLowerCase() === "packeta" && Boolean(runtime.packetaApiKey?.trim());
    const showManualFallback = !canUsePacketaWidget || widgetError !== null;

    useEffect(() => {
        setManualId(selectedPickupPoint?.id ?? "");
        setManualName(selectedPickupPoint?.name ?? "");
    }, [selectedPickupPoint?.id, selectedPickupPoint?.name]);

    async function openPacketaWidget(): Promise<void> {
        if (!canUsePacketaWidget || !runtime.packetaApiKey) {
            return;
        }

        setWidgetLoading(true);
        setWidgetError(null);

        try {
            const loaded = await ensurePacketaWidget();

            if (!loaded || !window.Packeta?.Widget) {
                setWidgetError(runtime.storefrontMessage("ui.checkout.pickupMapError"));

                return;
            }

            window.Packeta.Widget.pick(runtime.packetaApiKey, (point) => {
                if (!point?.id) {
                    return;
                }

                const id = String(point.id);
                const name = packetaPointDisplayName(point) ?? id;
                setManualId(id);
                setManualName(name);
                void onChange({ provider, id, name });
            }, {
                language: runtime.storefrontLanguageCode.split(/[-_]/, 1)[0],
            });
        } finally {
            setWidgetLoading(false);
        }
    }

    function commitManualSelection(): void {
        const id = manualId.trim();
        const name = manualName.trim();

        void onChange(id === "" ? null : {
            provider,
            id,
            name: name || id,
        });
    }

    return (
        <div className="morrow-pickup">
            <div style={{ gridColumn: "1 / -1" }}><strong>{runtime.storefrontMessage("ui.checkout.pickupPoint")}</strong><p>{runtime.storefrontMessage("ui.checkout.pickupExplanation")}</p>{selectedPickupPoint ? <p>{selectedPickupPoint.name ?? selectedPickupPoint.id}</p> : null}</div>
            {canUsePacketaWidget ? <button disabled={widgetLoading} onClick={() => void openPacketaWidget()} type="button">{widgetLoading ? runtime.storefrontMessage("ui.checkout.loadingMap") : runtime.storefrontMessage("ui.checkout.selectOnMap")}</button> : null}
            {showManualFallback ? <><label>{runtime.storefrontMessage("ui.checkout.pickupCode")}<input onChange={(event) => setManualId(event.target.value)} value={manualId}/></label><label>{runtime.storefrontMessage("ui.checkout.pickupPoint")}<input onChange={(event) => setManualName(event.target.value)} placeholder={runtime.storefrontMessage("ui.checkout.pickupPlaceholder")} value={manualName}/></label><button disabled={manualId.trim() === ""} onClick={commitManualSelection} type="button">{runtime.storefrontMessage("ui.checkout.pickupPoint")}</button></> : null}
            {widgetError ? <p className="morrow-error" style={{ gridColumn: "1 / -1" }}>{widgetError}</p> : null}
        </div>
    );
}

function MorrowCheckoutForm({ onChange, runtime }: {
    onChange: (patch: Partial<CheckoutFormState>) => void;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const form = runtime.checkoutForm;
    const field = (key: keyof CheckoutFormState, label: string, type = "text", required = false, autoComplete?: string) => <label>{label}<input autoComplete={autoComplete} onChange={(event) => onChange({ [key]: event.target.value })} required={required} type={type} value={String(form[key])}/></label>;

    return (
        <div className="morrow-form-grid">
            {field("email", runtime.storefrontMessage("block.systemCheckoutSteps.emailInput"), "email", true, "email")}
            {field("phone", runtime.storefrontMessage("block.systemCheckoutSteps.phoneInput"), "tel", false, "tel")}
            {field("firstname", runtime.storefrontMessage("block.systemCheckoutSteps.firstnameInput"), "text", true, "given-name")}
            {field("lastname", runtime.storefrontMessage("block.systemCheckoutSteps.lastnameInput"), "text", true, "family-name")}
            <div className="is-wide">{field("deliveryStreet", runtime.storefrontMessage("block.systemCheckoutSteps.streetInput"), "text", true, "street-address")}</div>
            {field("deliveryCity", runtime.storefrontMessage("block.systemCheckoutSteps.cityInput"), "text", true, "address-level2")}
            {field("deliveryPostcode", runtime.storefrontMessage("block.systemCheckoutSteps.postcodeInput"), "text", true, "postal-code")}
            <MorrowCountrySelect
                expandLabel={runtime.storefrontMessage("ui.checkout.expandCountries")}
                label={runtime.storefrontMessage("block.systemCheckoutSteps.countryInput")}
                locale={runtime.storefrontLanguageCode}
                noResults={runtime.storefrontMessage("ui.checkout.countryNoResults")}
                required
                searchPlaceholder={runtime.storefrontMessage("ui.checkout.countrySearchPlaceholder")}
                value={form.deliveryCountryCode}
                onChange={(deliveryCountryCode) => onChange({ deliveryCountryCode })}
            />
            <label className="is-wide">{runtime.storefrontMessage("block.systemCheckoutSteps.noteInput")}<textarea onChange={(event) => onChange({ note: event.target.value })} value={form.note}/></label>
            <label className="is-wide morrow-check"><input checked={!form.billingSameAsDelivery} onChange={(event) => onChange({ billingSameAsDelivery: !event.target.checked })} type="checkbox"/><span>{runtime.storefrontMessage("block.systemCheckoutSteps.billingToggleText")}</span></label>
            {!form.billingSameAsDelivery ? <>
                <div className="is-wide"><h3>{runtime.storefrontMessage("block.systemCheckoutSteps.billingTitle")}</h3></div>
                {field("billingFirstname", runtime.storefrontMessage("ui.checkout.billingFirstName"), "text", false, "billing given-name")}
                {field("billingLastname", runtime.storefrontMessage("ui.checkout.billingLastName"), "text", false, "billing family-name")}
                {field("billingCompany", runtime.storefrontMessage("ui.checkout.company"), "text", false, "organization")}
                {field("billingIc", runtime.storefrontMessage("ui.checkout.companyId"))}
                {field("billingVat", runtime.storefrontMessage("ui.checkout.vatId"))}
                {field("billingPhone", runtime.storefrontMessage("ui.checkout.billingPhone"), "tel", false, "billing tel")}
                <div className="is-wide">{field("billingStreet", runtime.storefrontMessage("ui.checkout.billingStreet"), "text", true, "billing street-address")}</div>
                {field("billingCity", runtime.storefrontMessage("ui.checkout.billingCity"), "text", true, "billing address-level2")}
                {field("billingPostcode", runtime.storefrontMessage("ui.checkout.billingPostcode"), "text", true, "billing postal-code")}
                <MorrowCountrySelect
                    expandLabel={runtime.storefrontMessage("ui.checkout.expandCountries")}
                    label={runtime.storefrontMessage("ui.checkout.billingCountry")}
                    locale={runtime.storefrontLanguageCode}
                    noResults={runtime.storefrontMessage("ui.checkout.countryNoResults")}
                    required
                    searchPlaceholder={runtime.storefrontMessage("ui.checkout.countrySearchPlaceholder")}
                    value={form.billingCountryCode}
                    onChange={(billingCountryCode) => onChange({ billingCountryCode })}
                />
            </> : <p className="is-wide">{runtime.storefrontMessage("ui.checkout.billingHint")}</p>}
        </div>
    );
}

function MorrowAccount({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const [mode, setMode] = useState<"login" | "register">("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [passwordResetMode, setPasswordResetMode] = useState<"request" | "confirm" | null>(null);
    const [passwordResetEmail, setPasswordResetEmail] = useState("");
    const [passwordResetToken, setPasswordResetToken] = useState("");
    const [passwordResetNewPassword, setPasswordResetNewPassword] = useState("");
    const [passwordResetNewPasswordConfirmation, setPasswordResetNewPasswordConfirmation] = useState("");
    const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
    const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
    const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
    const customer = runtime.customerSession.customer;
    const systemReference = runtimeSystemBlockReference(block, "accountSystem");

    useEffect(() => {
        const token = new URLSearchParams(runtime.currentBrowserSearch).get("resetToken")?.trim() ?? "";

        if (token !== "") {
            setPasswordResetToken(token);
            setPasswordResetMode("confirm");
            setPasswordResetMessage(null);
            setPasswordResetError(null);
        }
    }, [runtime.currentBrowserSearch]);

    if (runtime.customerSession.authenticated && customer) return <div data-shop-block-part={systemReference?.part} style={systemReference ? visualStyle(systemReference.block) : undefined}><MorrowCustomerDashboard customer={customer} runtime={runtime}/></div>;

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordResetMessage(null);

        if (mode === "login") {
            await runtime.loginCustomer(email, password);

            return;
        }

        if (await runtime.registerCustomer(email, password, confirmation, false)) {
            setPassword("");
            setConfirmation("");
            setPasswordResetMessage(runtime.storefrontMessage("ui.account.registrationCheckEmail"));
        }
    }

    async function submitPasswordResetRequest(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordResetSubmitting(true);
        setPasswordResetMessage(null);
        setPasswordResetError(null);

        try {
            const success = await runtime.requestCustomerPasswordReset((passwordResetEmail || email).trim());

            if (success) {
                setPasswordResetMessage(runtime.storefrontMessage("ui.account.resetSent"));
            } else {
                setPasswordResetError(runtime.storefrontMessage("ui.error.passwordResetRequest"));
            }
        } finally {
            setPasswordResetSubmitting(false);
        }
    }

    async function submitPasswordResetConfirm(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPasswordResetSubmitting(true);
        setPasswordResetMessage(null);
        setPasswordResetError(null);

        try {
            const success = await runtime.confirmCustomerPasswordReset(
                passwordResetToken.trim(),
                passwordResetNewPassword,
                passwordResetNewPasswordConfirmation,
            );

            if (success) {
                setPasswordResetNewPassword("");
                setPasswordResetNewPasswordConfirmation("");
                setPasswordResetMode(null);
                setPasswordResetMessage(runtime.storefrontMessage("ui.account.passwordReset"));
            } else {
                setPasswordResetError(runtime.storefrontMessage("ui.error.resetLinkInvalid"));
            }
        } finally {
            setPasswordResetSubmitting(false);
        }
    }

    return (
        <div className="morrow-auth-layout is-system-only" data-shop-block-part={systemReference?.part} style={systemReference ? visualStyle(systemReference.block) : undefined}>
            {passwordResetMode === "request" ? (
                <form className="morrow-auth-form" onSubmit={(event) => void submitPasswordResetRequest(event)}>
                    <div>
                        <h2>{runtime.storefrontMessage("block.systemAccountNavigation.resetTitle")}</h2>
                        <p>{runtime.storefrontMessage("block.systemAccountNavigation.resetText")}</p>
                    </div>
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.resetEmailInput")}<input autoComplete="email" onChange={(event) => setPasswordResetEmail(event.target.value)} required type="email" value={passwordResetEmail || email}/></label>
                    {passwordResetMessage ? <p className="morrow-account-success" role="status">{passwordResetMessage}</p> : null}
                    {passwordResetError ? <p className="morrow-error" role="alert">{passwordResetError}</p> : null}
                    <div className="morrow-account-form-actions">
                        <button className="morrow-account-secondary-button" onClick={() => setPasswordResetMode(null)} type="button">{runtime.storefrontMessage("block.systemAccountNavigation.resetBackButtonLabel")}</button>
                        <button className="morrow-primary-button" disabled={passwordResetSubmitting || runtime.customerLoading} type="submit">{passwordResetSubmitting ? runtime.storefrontMessage("ui.common.sending") : runtime.storefrontMessage("block.systemAccountNavigation.resetSubmitButtonLabel")}</button>
                    </div>
                </form>
            ) : passwordResetMode === "confirm" ? (
                <form className="morrow-auth-form" onSubmit={(event) => void submitPasswordResetConfirm(event)}>
                    <div>
                        <h2>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordTitle")}</h2>
                        <p>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordText")}</p>
                    </div>
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordInput")}<input autoComplete="new-password" minLength={8} onChange={(event) => setPasswordResetNewPassword(event.target.value)} required type="password" value={passwordResetNewPassword}/></label>
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordConfirmationInput")}<input autoComplete="new-password" minLength={8} onChange={(event) => setPasswordResetNewPasswordConfirmation(event.target.value)} required type="password" value={passwordResetNewPasswordConfirmation}/></label>
                    {passwordResetMessage ? <p className="morrow-account-success" role="status">{passwordResetMessage}</p> : null}
                    {passwordResetError ? <p className="morrow-error" role="alert">{passwordResetError}</p> : null}
                    <div className="morrow-account-form-actions">
                        <button className="morrow-account-secondary-button" onClick={() => setPasswordResetMode(null)} type="button">{runtime.storefrontMessage("block.systemAccountNavigation.resetBackButtonLabel")}</button>
                        <button className="morrow-primary-button" disabled={passwordResetSubmitting || runtime.customerLoading} type="submit">{passwordResetSubmitting ? runtime.storefrontMessage("ui.common.saving") : runtime.storefrontMessage("block.systemAccountNavigation.newPasswordSubmitButtonLabel")}</button>
                    </div>
                </form>
            ) : (
                <form className="morrow-auth-form" onSubmit={(event) => void submit(event)}>
                    <div className="morrow-auth-tabs"><button className={mode === "login" ? "is-active" : undefined} onClick={() => {
                        setMode("login");
                        setPasswordResetMessage(null);
                    }} type="button">{runtime.storefrontMessage("ui.account.login")}</button><button className={mode === "register" ? "is-active" : undefined} onClick={() => {
                        setMode("register");
                        setPasswordResetMessage(null);
                    }} type="button">{runtime.storefrontMessage("ui.account.register")}</button></div>
                    <label>{runtime.storefrontMessage("ui.account.email")}<input autoComplete="email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email}/></label>
                    <label>{runtime.storefrontMessage("ui.account.emailAndPassword")}<input autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={mode === "register" ? 8 : undefined} onChange={(event) => setPassword(event.target.value)} required type="password" value={password}/></label>
                    {mode === "register" ? <label>{runtime.storefrontMessage("ui.account.security")}<input autoComplete="new-password" minLength={8} onChange={(event) => setConfirmation(event.target.value)} required type="password" value={confirmation}/></label> : null}
                    {mode === "login" ? <button className="morrow-account-forgot-password" onClick={() => {
                        setPasswordResetEmail(email);
                        setPasswordResetMode("request");
                        setPasswordResetMessage(null);
                        setPasswordResetError(null);
                    }} type="button">{runtime.storefrontMessage("block.systemAccountNavigation.forgotPasswordButtonLabel")}</button> : null}
                    {passwordResetMessage ? <p className="morrow-account-success" role="status">{passwordResetMessage}</p> : null}
                    {runtime.customerError ? <p className="morrow-error">{runtime.customerError}</p> : null}
                    <button className="morrow-primary-button" disabled={runtime.customerLoading} type="submit">{runtime.customerLoading ? "…" : runtime.storefrontMessage(mode === "login" ? "ui.account.login" : "ui.account.register")}</button>
                </form>
            )}
        </div>
    );
}

type MorrowAccountSection = "overview" | "orders" | "addresses" | "favorites" | "settings";
type MorrowAccountDialog = "profile" | "password" | "address" | "order";

type MorrowOrderItem = {
    uuid: string;
    productName: string;
    productReference?: string | null;
    primaryImage: PublicProductImage | null;
    quantity: string | number;
    unitPrice?: string;
    totalPrice: string;
};

type MorrowOrder = PublicOrderCreated & {
    totalPaid?: string;
    updatedAt?: string;
    items?: MorrowOrderItem[];
};

type MorrowOrderListResponse = {
    orders: MorrowOrder[];
};

type MorrowOrderAddress = {
    firstname?: string | null;
    lastname?: string | null;
    company?: string | null;
    phone?: string | null;
    street?: string | null;
    city?: string | null;
    postcode?: string | null;
    countryCode?: string | null;
};

type MorrowOrderDetail = MorrowOrder & {
    customer: {
        email?: string | null;
        firstname?: string | null;
        lastname?: string | null;
        phone?: string | null;
    };
    billingAddress: MorrowOrderAddress & {
        ic?: string | null;
        vat?: string | null;
    };
    deliveryAddress: MorrowOrderAddress;
    itemsPrice: string;
    carrierName: string | null;
    paymentName: string | null;
    carrierPrice: string;
    paymentSurcharge: string;
    customerNote?: string | null;
    items: MorrowOrderItem[];
};

type MorrowCustomerAddressPayload = {
    firstname: string;
    lastname: string;
    company: string | null;
    email: string | null;
    phone: string | null;
    address1: string;
    houseNumber: string | null;
    address2: string | null;
    city: string;
    postcode: string;
    countryCode: string;
    addressType: PublicCustomerAddressType;
    useAsBillingAddress: boolean;
    useAsDeliveryAddress: boolean;
};

const MORROW_ACCOUNT_SECTIONS: Array<{
    id: MorrowAccountSection;
    icon: string;
}> = [
    { id: "overview", icon: "sparkles" },
    { id: "orders", icon: "package" },
    { id: "addresses", icon: "mapPin" },
    { id: "favorites", icon: "heart" },
    { id: "settings", icon: "settings" },
];

function normalizedMorrowOrderStatus(status: string): string {
    const normalized = status.trim().toLowerCase();

    return normalized.includes("_") ? normalized.split("_").at(-1) ?? normalized : normalized;
}

function morrowOrderStatusLabel(status: string, runtime: ShopStorefrontRuntimeModel): string {
    switch (normalizedMorrowOrderStatus(status)) {
        case "pending":
            return runtime.storefrontMessage("ui.order.status.pending");
        case "new":
            return runtime.storefrontMessage("ui.order.status.new");
        case "processing":
            return runtime.storefrontMessage("ui.order.status.processing");
        case "paid":
            return runtime.storefrontMessage("ui.order.status.paid");
        case "shipped":
            return runtime.storefrontMessage("ui.order.status.shipped");
        case "delivered":
            return runtime.storefrontMessage("ui.order.status.delivered");
        case "completed":
        case "done":
            return runtime.storefrontMessage("ui.order.status.completed");
        case "cancelled":
        case "canceled":
            return runtime.storefrontMessage("ui.order.status.cancelled");
        default:
            return status;
    }
}

function morrowOrderIsHistorical(order: MorrowOrder): boolean {
    return ["completed", "done", "delivered", "cancelled", "canceled"].includes(
        normalizedMorrowOrderStatus(order.status),
    );
}

function morrowAddressLine(address: PublicCustomerAddress): string {
    return [
        [address.address1, address.houseNumber].filter(Boolean).join(" "),
        [address.postcode, address.city].filter(Boolean).join(" "),
        address.countryCode,
    ].filter((value) => value.trim() !== "").join(", ");
}

function morrowOrderAddressLine(address: MorrowOrderAddress): string {
    return [
        [address.firstname, address.lastname].filter(Boolean).join(" "),
        address.company,
        address.street,
        [address.postcode, address.city].filter(Boolean).join(" "),
        address.countryCode,
    ].filter((value): value is string => typeof value === "string" && value.trim() !== "").join(", ");
}

function morrowAddressPayload(customer: PublicCustomer, firstAddress: boolean): MorrowCustomerAddressPayload {
    return {
        firstname: customer.firstname,
        lastname: customer.lastname,
        company: customer.company,
        email: customer.email,
        phone: null,
        address1: "",
        houseNumber: null,
        address2: null,
        city: "",
        postcode: "",
        countryCode: "CZ",
        addressType: "home",
        useAsBillingAddress: firstAddress,
        useAsDeliveryAddress: firstAddress,
    };
}

function morrowAddressPayloadFromAddress(address: PublicCustomerAddress): MorrowCustomerAddressPayload {
    return {
        firstname: address.firstname,
        lastname: address.lastname,
        company: address.company,
        email: address.email,
        phone: address.phone,
        address1: address.address1,
        houseNumber: address.houseNumber,
        address2: address.address2,
        city: address.city,
        postcode: address.postcode,
        countryCode: address.countryCode,
        addressType: address.addressType,
        useAsBillingAddress: address.billingForCustomer,
        useAsDeliveryAddress: address.deliveryForCustomer,
    };
}

function cleanMorrowAddressPayload(payload: MorrowCustomerAddressPayload): MorrowCustomerAddressPayload {
    const optional = (value: string | null): string | null => {
        const trimmed = (value ?? "").trim();

        return trimmed === "" ? null : trimmed;
    };

    return {
        ...payload,
        firstname: payload.firstname.trim(),
        lastname: payload.lastname.trim(),
        company: optional(payload.company),
        email: optional(payload.email),
        phone: optional(payload.phone),
        address1: payload.address1.trim(),
        houseNumber: optional(payload.houseNumber),
        address2: optional(payload.address2),
        city: payload.city.trim(),
        postcode: payload.postcode.trim(),
        countryCode: payload.countryCode.trim().toUpperCase(),
    };
}

function MorrowAccountModal({
    children,
    onClose,
    open,
}: {
    children: ReactNode;
    onClose: () => void;
    open: boolean;
}) {
    const [mounted, setMounted] = useState(open);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            setMounted(true);

            return undefined;
        }

        const timeout = window.setTimeout(() => setMounted(false), 280);

        return () => window.clearTimeout(timeout);
    }, [open]);

    useEffect(() => {
        if (!open || !mounted) {
            return undefined;
        }

        const view = modalRef.current?.ownerDocument.defaultView;

        if (!view) return undefined;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        view.addEventListener("keydown", handleKeyDown);

        return () => view.removeEventListener("keydown", handleKeyDown);
    }, [mounted, onClose, open]);

    if (!mounted) {
        return null;
    }

    return (
        <div
            className={`morrow-account-modal ${open ? "is-open" : "is-closing"}`}
            onMouseDown={(event) => {
                if (event.currentTarget === event.target) {
                    onClose();
                }
            }}
            ref={modalRef}
        >
            <div aria-modal="true" className="morrow-account-modal-sheet" role="dialog">
                {children}
            </div>
        </div>
    );
}

function MorrowCustomerDashboard({
    customer,
    runtime,
}: {
    customer: PublicCustomer;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const [activeSection, setActiveSection] = useState<MorrowAccountSection>("overview");
    const [orders, setOrders] = useState<MorrowOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const [selectedOrderUuid, setSelectedOrderUuid] = useState<string | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<MorrowOrderDetail | null>(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const [orderDetailError, setOrderDetailError] = useState<string | null>(null);
    const [reorderingOrderUuid, setReorderingOrderUuid] = useState<string | null>(null);
    const [reorderMessage, setReorderMessage] = useState<string | null>(null);
    const [profile, setProfile] = useState({ ...customer });
    const [profileSubmitting, setProfileSubmitting] = useState(false);
    const [profileMessage, setProfileMessage] = useState<string | null>(null);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [activeDialog, setActiveDialog] = useState<MorrowAccountDialog | null>(null);
    const [addressForm, setAddressForm] = useState<MorrowCustomerAddressPayload>(
        () => morrowAddressPayload(customer, runtime.customerAddresses.length === 0),
    );
    const [editingAddressUuid, setEditingAddressUuid] = useState<string | null>(null);
    const [addressSubmitting, setAddressSubmitting] = useState(false);
    const [addressMessage, setAddressMessage] = useState<string | null>(null);
    const [addressError, setAddressError] = useState<string | null>(null);
    const customerName = [customer.firstname, customer.lastname].filter(Boolean).join(" ") || customer.email;
    const customerInitials = customerName
        .split(/\s+/)
        .map((part) => part.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase() || "MO";
    const ongoingOrders = orders.filter((order) => !morrowOrderIsHistorical(order));
    const completedOrders = orders.filter(morrowOrderIsHistorical);
    const latestOrder = orders[0] ?? null;
    const defaultAddress = runtime.customerAddresses.find((address) => address.deliveryForCustomer)
        ?? runtime.customerAddresses[0]
        ?? null;
    const accountPreview = !runtime.customerApiBasePath;

    useEffect(() => setProfile({ ...customer }), [customer]);

    useEffect(() => {
        if (!runtime.customerSession.authenticated || runtime.orderApiBasePath === null) {
            setOrders([]);
            setOrdersError(null);

            return undefined;
        }

        let cancelled = false;
        const apiPath = runtime.orderApiBasePath;

        async function loadOrders() {
            setOrdersLoading(true);
            setOrdersError(null);

            try {
                const response = await fetch(apiPath, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(runtime.storefrontMessage("ui.error.ordersLoad"));
                }

                const payload = await response.json() as MorrowOrderListResponse;

                if (!cancelled) {
                    setOrders(payload.orders ?? []);
                }
            } catch {
                if (!cancelled) {
                    setOrders([]);
                    setOrdersError(runtime.storefrontMessage("ui.error.ordersLoad"));
                }
            } finally {
                if (!cancelled) {
                    setOrdersLoading(false);
                }
            }
        }

        void loadOrders();

        return () => {
            cancelled = true;
        };
    }, [
        runtime.customerSession.authenticated,
        runtime.orderApiBasePath,
        runtime.storefrontMessage,
        customer.uuid,
    ]);

    useEffect(() => {
        if (selectedOrderUuid === null || runtime.orderApiBasePath === null) {
            setOrderDetailLoading(false);
            setOrderDetailError(null);

            return undefined;
        }

        let cancelled = false;
        const apiPath = runtime.orderApiBasePath;
        const orderUuid = selectedOrderUuid;

        async function loadOrderDetail() {
            setOrderDetailLoading(true);
            setOrderDetailError(null);

            try {
                const response = await fetch(`${apiPath}/${encodeURIComponent(orderUuid)}`, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(runtime.storefrontMessage("ui.error.orderDetailLoad"));
                }

                const payload = await response.json() as MorrowOrderDetail;

                if (!cancelled) {
                    setSelectedOrder(payload);
                }
            } catch {
                if (!cancelled) {
                    setSelectedOrder(null);
                    setOrderDetailError(runtime.storefrontMessage("ui.error.orderDetailLoad"));
                }
            } finally {
                if (!cancelled) {
                    setOrderDetailLoading(false);
                }
            }
        }

        void loadOrderDetail();

        return () => {
            cancelled = true;
        };
    }, [runtime.orderApiBasePath, runtime.storefrontMessage, selectedOrderUuid]);

    function openOrderDetail(orderUuid: string) {
        setSelectedOrderUuid(orderUuid);
        setSelectedOrder(null);
        setOrderDetailLoading(true);
        setOrderDetailError(null);
        setActiveDialog("order");
    }

    function closeOrderDetail() {
        setActiveDialog(null);
        setSelectedOrderUuid(null);
    }

    async function reorderOrder(orderUuid: string) {
        setReorderingOrderUuid(orderUuid);
        setReorderMessage(null);
        setOrdersError(null);

        try {
            const result = await runtime.reorderCustomerOrder(orderUuid);

            if (result === null) {
                setOrdersError(runtime.storefrontMessage("ui.error.orderReorder"));
            } else {
                setReorderMessage(result.skippedItems.length > 0
                    ? runtime.storefrontMessage("ui.account.reorderPartial", { count: result.skippedItems.length })
                    : runtime.storefrontMessage("ui.account.reorderDone"));
            }
        } finally {
            setReorderingOrderUuid(null);
        }
    }

    async function submitProfile(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (accountPreview) {
            return;
        }

        setProfileSubmitting(true);
        setProfileMessage(null);
        setProfileError(null);

        try {
            const success = await runtime.updateCustomerProfile({
                firstname: profile.firstname.trim(),
                lastname: profile.lastname.trim(),
                email: profile.email.trim(),
                company: profile.company?.trim() || null,
                ic: profile.ic?.trim() || null,
                vat: profile.vat?.trim() || null,
                newsletterSubscribed: profile.newsletterSubscribed,
            });

            if (success) {
                setProfileMessage(runtime.storefrontMessage("ui.account.profileSaved"));
                setActiveDialog(null);
            } else {
                setProfileError(runtime.storefrontMessage("ui.error.profileSave"));
            }
        } finally {
            setProfileSubmitting(false);
        }
    }

    async function submitPassword(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (accountPreview) {
            return;
        }

        setPasswordSubmitting(true);
        setPasswordMessage(null);
        setPasswordError(null);

        try {
            const success = await runtime.changeCustomerPassword(
                currentPassword,
                newPassword,
                newPasswordConfirmation,
            );

            if (success) {
                setCurrentPassword("");
                setNewPassword("");
                setNewPasswordConfirmation("");
                setPasswordMessage(runtime.storefrontMessage("ui.account.passwordChanged"));
                setActiveDialog(null);
            } else {
                setPasswordError(runtime.storefrontMessage("ui.error.passwordChange"));
            }
        } finally {
            setPasswordSubmitting(false);
        }
    }

    function openNewAddress() {
        setEditingAddressUuid(null);
        setAddressForm(morrowAddressPayload(customer, runtime.customerAddresses.length === 0));
        setAddressMessage(null);
        setAddressError(null);
        setActiveDialog("address");
    }

    function openAddress(address: PublicCustomerAddress) {
        setEditingAddressUuid(address.uuid);
        setAddressForm(morrowAddressPayloadFromAddress(address));
        setAddressMessage(null);
        setAddressError(null);
        setActiveDialog("address");
    }

    function updateAddress(patch: Partial<MorrowCustomerAddressPayload>) {
        setAddressForm((current) => ({ ...current, ...patch }));
    }

    async function submitAddress(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!runtime.customerApiBasePath) {
            return;
        }

        setAddressSubmitting(true);
        setAddressMessage(null);
        setAddressError(null);
        let requestError = runtime.storefrontMessage("ui.error.addressSave");

        try {
            const response = await fetch(
                editingAddressUuid === null
                    ? `${runtime.customerApiBasePath}/addresses`
                    : `${runtime.customerApiBasePath}/addresses/${encodeURIComponent(editingAddressUuid)}`,
                {
                    method: editingAddressUuid === null ? "POST" : "PATCH",
                    headers: { "content-type": "application/json" },
                    credentials: "same-origin",
                    body: JSON.stringify(cleanMorrowAddressPayload(addressForm)),
                },
            );

            if (!response.ok) {
                requestError = await customerErrorTextFromResponse(
                    response,
                    requestError,
                    runtime.storefrontMessage,
                );
                throw new Error(requestError);
            }

            const payload = await response.json() as PublicCustomerAddressListResponse;
            runtime.setCustomerAddresses(payload.addresses ?? []);
            setAddressMessage(runtime.storefrontMessage(
                editingAddressUuid === null ? "ui.account.addressAdded" : "ui.account.addressSaved",
            ));
            setEditingAddressUuid(null);
            setActiveDialog(null);
        } catch {
            setAddressError(requestError);
        } finally {
            setAddressSubmitting(false);
        }
    }

    async function deleteAddress(addressUuid: string) {
        if (!runtime.customerApiBasePath || addressSubmitting) {
            return;
        }

        setAddressSubmitting(true);
        setAddressMessage(null);
        setAddressError(null);
        let requestError = runtime.storefrontMessage("ui.error.addressDelete");

        try {
            const response = await fetch(
                `${runtime.customerApiBasePath}/addresses/${encodeURIComponent(addressUuid)}`,
                {
                    method: "DELETE",
                    credentials: "same-origin",
                },
            );

            if (!response.ok) {
                requestError = await customerErrorTextFromResponse(
                    response,
                    requestError,
                    runtime.storefrontMessage,
                );
                throw new Error(requestError);
            }

            const payload = await response.json() as PublicCustomerAddressListResponse;
            runtime.setCustomerAddresses(payload.addresses ?? []);
            setAddressMessage(runtime.storefrontMessage("ui.account.addressDeleted"));

            if (editingAddressUuid === addressUuid) {
                setEditingAddressUuid(null);
                setActiveDialog(null);
            }
        } catch {
            setAddressError(requestError);
        } finally {
            setAddressSubmitting(false);
        }
    }

    const sectionLabel = (section: MorrowAccountSection): string => {
        switch (section) {
            case "overview":
                return runtime.storefrontMessage("ui.account.overview");
            case "orders":
                return runtime.storefrontMessage("ui.account.orders");
            case "addresses":
                return runtime.storefrontMessage("ui.account.addresses");
            case "favorites":
                return runtime.storefrontMessage("ui.account.favorites");
            case "settings":
                return runtime.storefrontMessage("ui.account.settings");
        }
    };

    const emptyState = (icon: string, title: string, text: string) => (
        <div className="morrow-account-empty">
            <span><MorrowIcon name={icon} size={28}/></span>
            <strong>{title}</strong>
            <p>{text}</p>
        </div>
    );

    return (
        <div className="morrow-account-dashboard">
            <aside className="morrow-account-sidebar">
                <div className="morrow-account-identity">
                    <div className="morrow-avatar">{customerInitials}</div>
                    <div>
                        <strong>{customerName}</strong>
                        <span>{customer.email}</span>
                    </div>
                </div>
                <nav aria-label={runtime.storefrontMessage("ui.account.sectionNavigation")}>
                    {MORROW_ACCOUNT_SECTIONS.map((section) => (
                        <button
                            className={activeSection === section.id ? "is-active" : undefined}
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            type="button"
                        >
                            <MorrowIcon name={section.icon} size={19}/>
                            <span>{sectionLabel(section.id)}</span>
                            <MorrowIcon name="chevronRight" size={16}/>
                        </button>
                    ))}
                </nav>
                <button
                    className="morrow-account-logout"
                    disabled={accountPreview}
                    onClick={() => void runtime.logoutCustomer()}
                    type="button"
                >
                    <MorrowIcon name="logout" size={18}/>
                    {runtime.storefrontMessage("ui.account.logout")}
                </button>
            </aside>

            <main className="morrow-account-main">
                <header className="morrow-account-heading">
                    <span>{runtime.storefrontMessage("ui.account.personalAccountBase")}</span>
                    <h1>{sectionLabel(activeSection)}</h1>
                </header>

                {activeSection === "overview" ? (
                    <div className="morrow-account-section">
                        <div className="morrow-account-stats">
                            {[
                                { id: "orders" as const, icon: "package", label: runtime.storefrontMessage("ui.account.orders"), value: orders.length },
                                { id: "addresses" as const, icon: "mapPin", label: runtime.storefrontMessage("ui.account.addresses"), value: runtime.customerAddresses.length },
                                { id: "favorites" as const, icon: "heart", label: runtime.storefrontMessage("ui.account.favorites"), value: runtime.visibleFavoriteProducts.length },
                            ].map((stat) => (
                                <button key={stat.id} onClick={() => setActiveSection(stat.id)} type="button">
                                    <span className="morrow-account-stat-icon"><MorrowIcon name={stat.icon} size={22}/></span>
                                    <span>{stat.label}</span>
                                    <strong>{stat.value}</strong>
                                    <MorrowIcon name="arrowUpRight" size={17}/>
                                </button>
                            ))}
                        </div>
                        <div className="morrow-account-overview-grid">
                            <section className="morrow-account-panel is-order">
                                <div className="morrow-account-panel-head">
                                    <div>
                                        <span>{runtime.storefrontMessage("ui.order.ongoing")}</span>
                                        <h2>{runtime.storefrontMessage("ui.account.orders")}</h2>
                                    </div>
                                    <button onClick={() => setActiveSection("orders")} type="button"><MorrowIcon name="arrowRight" size={20}/></button>
                                </div>
                                {ordersLoading
                                    ? <p className="morrow-account-muted">{runtime.storefrontMessage("ui.order.loading")}</p>
                                    : latestOrder
                                        ? (
                                            <article className="morrow-account-latest-order">
                                                <div>
                                                    <strong>{latestOrder.reference || runtime.storefrontMessage("ui.order.fallback")}</strong>
                                                    <span>{runtime.storefrontFormatting.formatDate(latestOrder.createdAt)}</span>
                                                </div>
                                                <span className={`morrow-account-status is-${normalizedMorrowOrderStatus(latestOrder.status)}`}>
                                                    {morrowOrderStatusLabel(latestOrder.status, runtime)}
                                                </span>
                                                <b>{runtime.storefrontFormatting.formatMoney(latestOrder.totalPrice, latestOrder.currencyCode)}</b>
                                            </article>
                                        )
                                        : <p className="morrow-account-muted">{runtime.storefrontMessage("ui.order.noneOverview")}</p>}
                            </section>
                            <section className="morrow-account-panel is-address">
                                <div className="morrow-account-panel-head">
                                    <div>
                                        <span>{runtime.storefrontMessage("ui.account.deliveryAndBilling")}</span>
                                        <h2>{runtime.storefrontMessage("ui.account.defaultAddress")}</h2>
                                    </div>
                                    <button onClick={() => setActiveSection("addresses")} type="button"><MorrowIcon name="arrowRight" size={20}/></button>
                                </div>
                                {defaultAddress
                                    ? (
                                        <div className="morrow-account-default-address">
                                            <span><MorrowIcon name="mapPin" size={24}/></span>
                                            <div>
                                                <strong>{[defaultAddress.firstname, defaultAddress.lastname].filter(Boolean).join(" ")}</strong>
                                                <p>{morrowAddressLine(defaultAddress)}</p>
                                            </div>
                                        </div>
                                    )
                                    : <p className="morrow-account-muted">{runtime.storefrontMessage("ui.account.noAddressDefault")}</p>}
                            </section>
                        </div>
                    </div>
                ) : null}

                {activeSection === "orders" ? (
                    <div className="morrow-account-section">
                        {ordersError ? <p className="morrow-error">{ordersError}</p> : null}
                        {reorderMessage ? <p className="morrow-account-success">{reorderMessage}</p> : null}
                        {ordersLoading
                            ? <div className="morrow-account-loading">{runtime.storefrontMessage("ui.order.loading")}</div>
                            : orders.length === 0
                                ? emptyState("package", runtime.storefrontMessage("ui.order.none"), runtime.storefrontMessage("ui.order.noneOverview"))
                                : (
                                    <div className="morrow-account-order-groups">
                                        {[
                                            { title: runtime.storefrontMessage("ui.order.ongoing"), items: ongoingOrders },
                                            { title: runtime.storefrontMessage("ui.order.history"), items: completedOrders },
                                        ].filter((group) => group.items.length > 0).map((group) => (
                                            <section key={group.title}>
                                                <h2>{group.title}</h2>
                                                <div className="morrow-account-order-list">
                                                    {group.items.map((order) => (
                                                        <article key={order.uuid}>
                                                            <div className="morrow-account-order-mark">
                                                                {order.items?.[0]?.primaryImage ? (
                                                                    <img
                                                                        alt={order.items[0].primaryImage.alt || order.items[0].productName}
                                                                        src={order.items[0].primaryImage.thumbnailUrl ?? order.items[0].primaryImage.url}
                                                                    />
                                                                ) : <MorrowIcon name="package" size={22}/>}
                                                            </div>
                                                            <div className="morrow-account-order-copy">
                                                                <strong>{order.reference || runtime.storefrontMessage("ui.order.fallback")}</strong>
                                                                <span>{runtime.storefrontFormatting.formatDate(order.createdAt)}</span>
                                                                {order.items?.[0] ? <small>{order.items[0].productName}{order.items.length > 1 ? ` +${order.items.length - 1}` : ""}</small> : null}
                                                            </div>
                                                            <span className={`morrow-account-status is-${normalizedMorrowOrderStatus(order.status)}`}>
                                                                {morrowOrderStatusLabel(order.status, runtime)}
                                                            </span>
                                                            <b>{runtime.storefrontFormatting.formatMoney(order.totalPrice, order.currencyCode)}</b>
                                                            <div className="morrow-account-order-actions">
                                                                <button
                                                                    aria-haspopup="dialog"
                                                                    className="morrow-account-secondary-button"
                                                                    disabled={accountPreview}
                                                                    onClick={() => openOrderDetail(order.uuid)}
                                                                    type="button"
                                                                >
                                                                    <MorrowIcon name="arrowRight" size={17}/>
                                                                    {runtime.storefrontMessage("ui.order.detail")}
                                                                </button>
                                                                {morrowOrderIsHistorical(order) && !["cancelled", "canceled"].includes(normalizedMorrowOrderStatus(order.status)) ? (
                                                                    <button
                                                                        className="morrow-account-secondary-button"
                                                                        disabled={reorderingOrderUuid !== null || accountPreview}
                                                                        onClick={() => void reorderOrder(order.uuid)}
                                                                        type="button"
                                                                    >
                                                                        <MorrowIcon name="refresh" size={17}/>
                                                                        {reorderingOrderUuid === order.uuid
                                                                            ? runtime.storefrontMessage("ui.account.reordering")
                                                                            : runtime.storefrontMessage("ui.account.buyAgain")}
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                        </article>
                                                    ))}
                                                </div>
                                            </section>
                                        ))}
                                    </div>
                                )}
                    </div>
                ) : null}

                {activeSection === "addresses" ? (
                    <div className="morrow-account-section">
                        <div className="morrow-account-section-intro">
                            <div>
                                <h2>{runtime.storefrontMessage("ui.account.savedAddresses")}</h2>
                                <p>{runtime.storefrontMessage("ui.account.addressIntro")}</p>
                            </div>
                            <button aria-haspopup="dialog" className="morrow-primary-button" onClick={openNewAddress} type="button">
                                <MorrowIcon name="plus" size={18}/>
                                {runtime.storefrontMessage("ui.account.addAddress")}
                            </button>
                        </div>
                        {addressMessage ? <p className="morrow-account-success">{addressMessage}</p> : null}
                        {addressError ? <p className="morrow-error">{addressError}</p> : null}
                        {runtime.customerAddresses.length === 0
                            ? emptyState("mapPin", runtime.storefrontMessage("ui.account.noAddress"), runtime.storefrontMessage("ui.account.firstAddressDefault"))
                            : (
                                <div className="morrow-account-address-grid">
                                    {runtime.customerAddresses.map((address) => (
                                        <article key={address.uuid}>
                                            <div className="morrow-account-address-icon"><MorrowIcon name={address.addressType === "work" ? "briefcase" : address.addressType === "home" ? "home" : "mapPin"} size={23}/></div>
                                            <div>
                                                <strong>{[address.firstname, address.lastname].filter(Boolean).join(" ")}</strong>
                                                <p>{morrowAddressLine(address)}</p>
                                                <div className="morrow-account-address-flags">
                                                    {address.deliveryForCustomer ? <span>{runtime.storefrontMessage("ui.account.delivery")}</span> : null}
                                                    {address.billingForCustomer ? <span>{runtime.storefrontMessage("ui.account.billing")}</span> : null}
                                                </div>
                                            </div>
                                            <div className="morrow-account-address-actions">
                                                <button aria-haspopup="dialog" onClick={() => openAddress(address)} type="button">{runtime.storefrontMessage("ui.common.edit")}</button>
                                                <button disabled={addressSubmitting || accountPreview} onClick={() => void deleteAddress(address.uuid)} type="button">{runtime.storefrontMessage("ui.common.delete")}</button>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}
                    </div>
                ) : null}

                {activeSection === "favorites" ? (
                    <div className="morrow-account-section">
                        <div className="morrow-account-section-intro">
                            <div>
                                <h2>{runtime.storefrontMessage("ui.account.favoriteProducts")}</h2>
                                <p>{runtime.storefrontMessage("ui.account.favoritesIntro")}</p>
                            </div>
                        </div>
                        {runtime.visibleFavoriteProducts.length === 0
                            ? emptyState("heart", runtime.storefrontMessage("ui.account.noFavorites"), runtime.storefrontMessage("ui.account.favoriteHint"))
                            : (
                                <div className="morrow-account-favorites-grid">
                                    {runtime.visibleFavoriteProducts.map((product) => (
                                        <article key={product.id}>
                                            <ShopStorefrontLink href={functionalStorefrontHref(product.href)} onClick={(event) => runtime.editorMode && event.preventDefault()}>
                                                <div className="morrow-account-favorite-image">
                                                    {product.image ? <img alt={product.alt} src={product.image}/> : <MorrowIcon name="heart" size={34}/>}
                                                </div>
                                                <span>{product.brand ?? runtime.storefrontMessage("ui.account.favoritesEyebrow")}</span>
                                                <strong>{product.name}</strong>
                                                <b>{product.price ?? runtime.storefrontMessage("ui.account.productUnavailable")}</b>
                                            </ShopStorefrontLink>
                                            <button aria-label={runtime.storefrontMessage("ui.common.delete")} onClick={() => runtime.toggleFavorite(product.id)} type="button"><MorrowIcon name="heart" size={18}/></button>
                                        </article>
                                    ))}
                                </div>
                            )}
                    </div>
                ) : null}

                {activeSection === "settings" ? (
                    <div className="morrow-account-section morrow-account-settings-grid">
                        <section className="morrow-account-settings-card">
                            <div className="morrow-account-form-head">
                                <div>
                                    <span>{runtime.storefrontMessage("ui.account.contact")}</span>
                                    <h2>{runtime.storefrontMessage("ui.account.personalData")}</h2>
                                </div>
                                <span className="morrow-account-form-icon"><MorrowIcon name="user" size={22}/></span>
                            </div>
                            {profileMessage ? <p className="morrow-account-success">{profileMessage}</p> : null}
                            {profileError ? <p className="morrow-error">{profileError}</p> : null}
                            <div className="morrow-account-settings-summary">
                                <div><span>{runtime.storefrontMessage("ui.account.personalAccountBase")}</span><strong>{[profile.firstname, profile.lastname].filter(Boolean).join(" ") || customerName}</strong></div>
                                <div><span>{runtime.storefrontMessage("ui.account.email")}</span><strong>{profile.email}</strong></div>
                                {profile.company ? <div><span>{runtime.storefrontMessage("ui.account.companyCustomer")}</span><strong>{profile.company}</strong></div> : null}
                            </div>
                            <button
                                aria-haspopup="dialog"
                                className="morrow-primary-button"
                                onClick={() => {
                                    setProfileError(null);
                                    setActiveDialog("profile");
                                }}
                                type="button"
                            >
                                <MorrowIcon name="edit" size={17}/>
                                {runtime.storefrontMessage("ui.common.edit")}
                            </button>
                        </section>
                        <section className="morrow-account-settings-card">
                            <div className="morrow-account-form-head">
                                <div>
                                    <span>{runtime.storefrontMessage("ui.account.security")}</span>
                                    <h2>{runtime.storefrontMessage("block.systemAccountNavigation.passwordTitle")}</h2>
                                </div>
                                <span className="morrow-account-form-icon"><MorrowIcon name="lock" size={22}/></span>
                            </div>
                            <p>{runtime.storefrontMessage("block.systemAccountNavigation.passwordText")}</p>
                            {passwordMessage ? <p className="morrow-account-success">{passwordMessage}</p> : null}
                            {passwordError ? <p className="morrow-error">{passwordError}</p> : null}
                            <button
                                aria-haspopup="dialog"
                                className="morrow-account-secondary-button"
                                onClick={() => {
                                    setPasswordError(null);
                                    setActiveDialog("password");
                                }}
                                type="button"
                            >
                                <MorrowIcon name="lock" size={17}/>
                                {runtime.storefrontMessage("block.systemAccountNavigation.passwordTitle")}
                            </button>
                        </section>
                    </div>
                ) : null}
            </main>

            <MorrowAccountModal onClose={closeOrderDetail} open={activeDialog === "order"}>
                <div className="morrow-account-password-form morrow-order-detail">
                    <div className="morrow-account-form-head">
                        <div>
                            <span>{runtime.storefrontMessage("ui.order.detail")}</span>
                            <h2>{selectedOrder?.reference ?? orders.find((order) => order.uuid === selectedOrderUuid)?.reference ?? runtime.storefrontMessage("ui.order.fallback")}</h2>
                        </div>
                        <button aria-label={runtime.storefrontMessage("ui.common.close")} onClick={closeOrderDetail} type="button"><MorrowIcon name="close" size={20}/></button>
                    </div>
                    {orderDetailLoading ? <div className="morrow-account-loading" role="status">{runtime.storefrontMessage("ui.order.loading")}</div> : null}
                    {orderDetailError ? <p className="morrow-error" role="alert">{orderDetailError}</p> : null}
                    {selectedOrder ? (
                        <>
                            <div className="morrow-order-detail-meta">
                                <div>
                                    <span>{runtime.storefrontMessage("ui.order.date")}</span>
                                    <strong>{runtime.storefrontFormatting.formatDate(selectedOrder.createdAt)}</strong>
                                </div>
                                <div>
                                    <span>{runtime.storefrontMessage("ui.order.status")}</span>
                                    <strong className={`morrow-account-status is-${normalizedMorrowOrderStatus(selectedOrder.status)}`}>{morrowOrderStatusLabel(selectedOrder.status, runtime)}</strong>
                                </div>
                                <div>
                                    <span>{runtime.storefrontMessage("ui.order.price")}</span>
                                    <strong>{runtime.storefrontFormatting.formatMoney(selectedOrder.totalPrice, selectedOrder.currencyCode)}</strong>
                                </div>
                            </div>
                            <section className="morrow-order-detail-section">
                                <h3>{runtime.storefrontMessage("ui.order.items")}</h3>
                                <div className="morrow-order-detail-items">
                                    {selectedOrder.items.map((item) => (
                                        <article key={item.uuid}>
                                            <span className="morrow-account-order-mark">
                                                {item.primaryImage
                                                    ? <img alt={item.primaryImage.alt || item.productName} src={item.primaryImage.thumbnailUrl ?? item.primaryImage.url}/>
                                                    : <MorrowIcon name="package" size={20}/>}
                                            </span>
                                            <div>
                                                <strong>{item.productName}</strong>
                                                {item.productReference ? <small>{item.productReference}</small> : null}
                                            </div>
                                            <span>{item.quantity} {runtime.storefrontMessage("ui.product.unit")}</span>
                                            <b>{runtime.storefrontFormatting.formatMoney(item.totalPrice, selectedOrder.currencyCode)}</b>
                                        </article>
                                    ))}
                                </div>
                            </section>
                            <section className="morrow-order-detail-section morrow-order-detail-summary">
                                <h3>{runtime.storefrontMessage("ui.order.payment")}</h3>
                                <dl>
                                    <div><dt>{runtime.storefrontMessage("ui.order.goods")}</dt><dd>{runtime.storefrontFormatting.formatMoney(selectedOrder.itemsPrice, selectedOrder.currencyCode)}</dd></div>
                                    <div><dt>{selectedOrder.carrierName ?? runtime.storefrontMessage("ui.order.delivery")}</dt><dd>{runtime.storefrontFormatting.formatMoney(selectedOrder.carrierPrice, selectedOrder.currencyCode)}</dd></div>
                                    <div><dt>{selectedOrder.paymentName ?? runtime.storefrontMessage("ui.order.payment")}</dt><dd>{runtime.storefrontFormatting.formatMoney(selectedOrder.paymentSurcharge, selectedOrder.currencyCode)}</dd></div>
                                    <div className="is-total"><dt>{runtime.storefrontMessage("ui.order.totalWithVat")}</dt><dd>{runtime.storefrontFormatting.formatMoney(selectedOrder.totalPrice, selectedOrder.currencyCode)}</dd></div>
                                </dl>
                            </section>
                            <div className="morrow-order-detail-addresses">
                                <section>
                                    <h3>{runtime.storefrontMessage("ui.order.shippingAddress")}</h3>
                                    <p>{morrowOrderAddressLine(selectedOrder.deliveryAddress) || "-"}</p>
                                </section>
                                <section>
                                    <h3>{runtime.storefrontMessage("ui.order.billingAddress")}</h3>
                                    <p>{morrowOrderAddressLine(selectedOrder.billingAddress) || "-"}</p>
                                </section>
                            </div>
                        </>
                    ) : null}
                </div>
            </MorrowAccountModal>

            <MorrowAccountModal onClose={() => setActiveDialog(null)} open={activeDialog === "profile"}>
                <form className="morrow-profile-form" onSubmit={(event) => void submitProfile(event)}>
                    <div className="morrow-account-form-head">
                        <div>
                            <span>{runtime.storefrontMessage("ui.account.contact")}</span>
                            <h2>{runtime.storefrontMessage("ui.account.personalData")}</h2>
                        </div>
                        <button aria-label={runtime.storefrontMessage("ui.common.close")} onClick={() => setActiveDialog(null)} type="button"><MorrowIcon name="close" size={20}/></button>
                    </div>
                    {profileMessage ? <p className="morrow-account-success is-wide">{profileMessage}</p> : null}
                    {profileError ? <p className="morrow-error is-wide">{profileError}</p> : null}
                    <label>{runtime.storefrontMessage("ui.checkout.billingFirstName")}<input onChange={(event) => setProfile((value) => ({ ...value, firstname: event.target.value }))} value={profile.firstname}/></label>
                    <label>{runtime.storefrontMessage("ui.checkout.billingLastName")}<input onChange={(event) => setProfile((value) => ({ ...value, lastname: event.target.value }))} value={profile.lastname}/></label>
                    <label className="is-wide">{runtime.storefrontMessage("ui.account.email")}<input onChange={(event) => setProfile((value) => ({ ...value, email: event.target.value }))} type="email" value={profile.email}/></label>
                    <label>{runtime.storefrontMessage("ui.account.companyCustomer")}<input onChange={(event) => setProfile((value) => ({ ...value, company: event.target.value || null }))} value={profile.company ?? ""}/></label>
                    <label>{runtime.storefrontMessage("ui.account.companyId")}<input onChange={(event) => setProfile((value) => ({ ...value, ic: event.target.value || null }))} value={profile.ic ?? ""}/></label>
                    <label>{runtime.storefrontMessage("ui.account.vatId")}<input onChange={(event) => setProfile((value) => ({ ...value, vat: event.target.value || null }))} value={profile.vat ?? ""}/></label>
                    <label className="morrow-account-newsletter is-wide"><input checked={profile.newsletterSubscribed} onChange={(event) => setProfile((value) => ({ ...value, newsletterSubscribed: event.target.checked }))} type="checkbox"/><span>{runtime.storefrontMessage("ui.account.newsletterConsent")}</span></label>
                    <div className="morrow-account-form-actions is-wide">
                        <button className="morrow-account-secondary-button" onClick={() => setActiveDialog(null)} type="button">{runtime.storefrontMessage("ui.common.cancel")}</button>
                        <button className="morrow-primary-button" disabled={profileSubmitting || accountPreview} type="submit">{runtime.storefrontMessage("ui.common.save")}</button>
                    </div>
                </form>
            </MorrowAccountModal>

            <MorrowAccountModal onClose={() => setActiveDialog(null)} open={activeDialog === "password"}>
                <form className="morrow-account-password-form" onSubmit={(event) => void submitPassword(event)}>
                    <div className="morrow-account-form-head">
                        <div>
                            <span>{runtime.storefrontMessage("ui.account.security")}</span>
                            <h2>{runtime.storefrontMessage("block.systemAccountNavigation.passwordTitle")}</h2>
                        </div>
                        <button aria-label={runtime.storefrontMessage("ui.common.close")} onClick={() => setActiveDialog(null)} type="button"><MorrowIcon name="close" size={20}/></button>
                    </div>
                    <p>{runtime.storefrontMessage("block.systemAccountNavigation.passwordText")}</p>
                    {passwordMessage ? <p className="morrow-account-success">{passwordMessage}</p> : null}
                    {passwordError ? <p className="morrow-error">{passwordError}</p> : null}
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.currentPasswordInput")}<input autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value)} type="password" value={currentPassword}/></label>
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordInput")}<input autoComplete="new-password" onChange={(event) => setNewPassword(event.target.value)} type="password" value={newPassword}/></label>
                    <label>{runtime.storefrontMessage("block.systemAccountNavigation.newPasswordConfirmationInput")}<input autoComplete="new-password" onChange={(event) => setNewPasswordConfirmation(event.target.value)} type="password" value={newPasswordConfirmation}/></label>
                    <div className="morrow-account-form-actions">
                        <button className="morrow-account-secondary-button" onClick={() => setActiveDialog(null)} type="button">{runtime.storefrontMessage("ui.common.cancel")}</button>
                        <button className="morrow-primary-button" disabled={passwordSubmitting || accountPreview} type="submit">{runtime.storefrontMessage("block.systemAccountNavigation.passwordSaveButtonLabel")}</button>
                    </div>
                </form>
            </MorrowAccountModal>

            <MorrowAccountModal onClose={() => setActiveDialog(null)} open={activeDialog === "address"}>
                <form className="morrow-account-address-form" onSubmit={(event) => void submitAddress(event)}>
                    <div className="morrow-account-form-head">
                        <div>
                            <span>{runtime.storefrontMessage("ui.account.deliveryAddress")}</span>
                            <h2>{runtime.storefrontMessage(editingAddressUuid ? "ui.account.editAddress" : "ui.account.newAddress")}</h2>
                        </div>
                        <button aria-label={runtime.storefrontMessage("ui.common.close")} onClick={() => setActiveDialog(null)} type="button"><MorrowIcon name="close" size={20}/></button>
                    </div>
                    {addressError ? <p className="morrow-error">{addressError}</p> : null}
                    <div className="morrow-account-form-grid">
                        <label>{runtime.storefrontMessage("ui.checkout.billingFirstName")}<input required value={addressForm.firstname} onChange={(event) => updateAddress({ firstname: event.target.value })}/></label>
                        <label>{runtime.storefrontMessage("ui.checkout.billingLastName")}<input required value={addressForm.lastname} onChange={(event) => updateAddress({ lastname: event.target.value })}/></label>
                        <label>{runtime.storefrontMessage("ui.account.companyCustomer")}<input value={addressForm.company ?? ""} onChange={(event) => updateAddress({ company: event.target.value })}/></label>
                        <label>{runtime.storefrontMessage("ui.checkout.billingPhone")}<input type="tel" value={addressForm.phone ?? ""} onChange={(event) => updateAddress({ phone: event.target.value })}/></label>
                        <label className="is-wide">{runtime.storefrontMessage("ui.checkout.billingStreet")}<input required value={addressForm.address1} onChange={(event) => updateAddress({ address1: event.target.value })}/></label>
                        <label>{runtime.storefrontMessage("ui.checkout.billingPostcode")}<input required value={addressForm.postcode} onChange={(event) => updateAddress({ postcode: event.target.value })}/></label>
                        <label>{runtime.storefrontMessage("ui.checkout.billingCity")}<input required value={addressForm.city} onChange={(event) => updateAddress({ city: event.target.value })}/></label>
                        <MorrowCountrySelect
                            expandLabel={runtime.storefrontMessage("ui.checkout.expandCountries")}
                            label={runtime.storefrontMessage("ui.checkout.billingCountry")}
                            locale={runtime.storefrontLanguageCode}
                            noResults={runtime.storefrontMessage("ui.checkout.countryNoResults")}
                            required
                            searchPlaceholder={runtime.storefrontMessage("ui.checkout.countrySearchPlaceholder")}
                            value={addressForm.countryCode}
                            onChange={(countryCode) => updateAddress({ countryCode })}
                        />
                        <label>{runtime.storefrontMessage("ui.account.addressType")}<select value={addressForm.addressType} onChange={(event) => updateAddress({ addressType: event.target.value as PublicCustomerAddressType })}><option value="home">{runtime.storefrontMessage("ui.address.home")}</option><option value="work">{runtime.storefrontMessage("ui.address.work")}</option><option value="other">{runtime.storefrontMessage("ui.address.other")}</option></select></label>
                    </div>
                    <div className="morrow-account-checks">
                        <label><input checked={addressForm.useAsDeliveryAddress} onChange={(event) => updateAddress({ useAsDeliveryAddress: event.target.checked })} type="checkbox"/>{runtime.storefrontMessage("ui.account.useForDelivery")}</label>
                        <label><input checked={addressForm.useAsBillingAddress} onChange={(event) => updateAddress({ useAsBillingAddress: event.target.checked })} type="checkbox"/>{runtime.storefrontMessage("ui.account.useForBilling")}</label>
                    </div>
                    <div className="morrow-account-form-actions">
                        <button className="morrow-account-secondary-button" onClick={() => setActiveDialog(null)} type="button">{runtime.storefrontMessage("ui.common.cancel")}</button>
                        <button className="morrow-primary-button" disabled={addressSubmitting || accountPreview} type="submit">{runtime.storefrontMessage("ui.common.save")}</button>
                    </div>
                </form>
            </MorrowAccountModal>
        </div>
    );
}

const MORROW_CONTACT_FALLBACK_FIELDS: SiteLeadFormField[] = [
    { key: "name", label: "Name", type: "text", required: true, placeholder: null, options: [] },
    { key: "email", label: "Email", type: "email", required: true, placeholder: null, options: [] },
    { key: "message", label: "Message", type: "textarea", required: true, placeholder: null, options: [] },
];

function MorrowContact({ block, runtime }: { block: ShopTemplateBlock; runtime: ShopStorefrontRuntimeModel }) {
    const [error, setError] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [startedAt] = useState(() => Math.floor(Date.now() / 1_000));
    const systemReference = runtimeSystemBlockReference(block, "contactFormSystem");
    const leadFormLookup = useSiteBuilderLeadForm({
        enabled: !runtime.editorMode,
        formKey: "contact",
    });
    const leadForm = leadFormLookup.form;
    const fields = leadForm && leadForm.fields.length > 0
        ? leadForm.fields
        : MORROW_CONTACT_FALLBACK_FIELDS;

    async function submitContact(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (runtime.editorMode || runtime.storefrontPreview || submitting || leadFormLookup.loading) {
            return;
        }

        const form = event.currentTarget;
        const ownerWindow = form.ownerDocument.defaultView;
        const payload = siteBuilderLeadValuesFromFormData(new FormData(form));
        setError(false);
        setSubmitted(false);
        setSubmitting(true);

        try {
            const result = await submitSiteBuilderLead({
                endpoint: "/api/lead/submit",
                fields: payload,
                form: leadForm,
                honeypotValue: String(payload.honeypotValue ?? ""),
                landingUrl: runtime.storefrontLocationHref
                    ?? ownerWindow?.location.href
                    ?? "",
                purpose: "contact",
                startedAt,
            });

            if (!result.ok) {
                throw new Error("contact-submit-failed");
            }

            form.reset();
            setSubmitted(true);
        } catch {
            setError(true);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="morrow-contact is-system-only"
            data-shop-block-part={systemReference?.part}
            style={systemReference ? visualStyle(systemReference.block) : undefined}
        >
            <form onSubmit={(event) => void submitContact(event)}>
                <input autoComplete="off" hidden name="honeypotValue" tabIndex={-1} type="text"/>
                {error ? <p className="morrow-error" role="alert">{runtime.storefrontMessage("ui.common.retry")}</p> : null}
                {submitted ? <p className="morrow-account-success" role="status">{runtime.storefrontMessage("block.systemContactForm.successMessage")}</p> : null}
                {fields.map((field) => (
                    <MorrowLeadFormField
                        contact
                        field={field}
                        key={field.key}
                        labelsPosition="top"
                        readOnly={runtime.editorMode}
                        runtime={runtime}
                    />
                ))}
                <button className="morrow-primary-button" disabled={submitting || leadFormLookup.loading || runtime.editorMode} type="submit">
                    {submitting
                        ? runtime.storefrontMessage("ui.common.sending")
                        : runtime.storefrontMessage("block.systemContactForm.submitButtonLabel")}
                </button>
            </form>
        </div>
    );
}

function MorrowFooter({ block, index, runtime }: { block: ShopTemplateBlock; index: number; runtime: ShopStorefrontRuntimeModel }) {
    return (
        <footer data-morrow-storefront-footer="content">
            <MorrowRegionFrame
                block={block}
                index={index}
                runtime={runtime}
            />
        </footer>
    );
}

function MorrowLegacyFooter({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    return (
        <footer className="morrow-legacy-footer" data-morrow-storefront-footer="fallback">
            <div>
                <ShopStorefrontLink className="morrow-legacy-footer-brand" href={runtime.homeHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.displayName}</ShopStorefrontLink>
                {runtime.headerMenuItems.length > 0 ? (
                    <nav aria-label={runtime.storefrontMessage("block.footer.title")}>
                        {runtime.headerMenuItems.slice(0, 6).map((item) => (
                            <ShopStorefrontLink href={functionalStorefrontHref(menuItemHref(item))} key={item.uuid} onClick={(event) => runtime.editorMode && event.preventDefault()}>{item.label}</ShopStorefrontLink>
                        ))}
                    </nav>
                ) : null}
                <ShopStorefrontLink href={runtime.accountHref} onClick={(event) => runtime.editorMode && event.preventDefault()}>{runtime.storefrontMessage("ui.footer.myAccount")}</ShopStorefrontLink>
            </div>
            <small>{runtime.storefrontMessage("template.morrow.footer.copyright")}</small>
        </footer>
    );
}

function regionFor(block: ShopTemplateBlock): string {
    return optionText(block, "morrowRegion");
}

function MorrowStandaloneBlock({
    block,
    index,
    runtime,
}: {
    block: ShopTemplateBlock;
    index: number;
    runtime: ShopStorefrontRuntimeModel;
}) {
    const gridHeight = Math.max(1, optionNumber(block, "gridH", 1));

    return (
        <section
            {...morrowInteractionAttributes(block)}
            {...siteBuilderEditorBlockAttributes({
                block,
                editorMode: runtime.editorMode,
                path: siteBuilderEditorRootPath(block, index),
            })}
            className="morrow-region morrow-standalone-region"
            data-shop-block-index={index}
            data-shop-block-type={block.type}
            style={morrowInteractionStyle(block)}
        >
            <div className="morrow-region-inner">
                <div className="morrow-standalone-inner" style={{ minHeight: Math.max(72, gridHeight * SHOP_GRID_CELL_HEIGHT) }}>
                    <MorrowInnerBlock block={block} editorMode={runtime.editorMode} part="" runtime={runtime}/>
                </div>
            </div>
        </section>
    );
}

function MorrowPage({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    const blocks = Array.isArray(runtime.pageContentBlocks) ? runtime.pageContentBlocks : [];
    const renderBlocks = orderMorrowRenderBlocks(blocks);
    const products = availableProducts(runtime);

    if (blocks.length === 0) {
        return (
            <>
                <main><div className="morrow-fallback"><h1>{runtime.localizedPageContext?.title ?? runtime.displayName}</h1><MorrowProductGrid products={products} runtime={runtime}/></div></main>
                {!runtime.hasFooterBlock ? <MorrowLegacyFooter runtime={runtime}/> : null}
            </>
        );
    }

    return (
        <>
            {renderBlocks.map(({ block, index }) => {
                const renderedBlock = normalizeCatalogProductPageStructure(block);
                const region = regionFor(renderedBlock);
                if (region === "header") return <MorrowHeader block={renderedBlock} index={index} key={`${region}:${index}`} runtime={runtime}/>;
                if (region === "footer") return <MorrowFooter block={renderedBlock} index={index} key={`${region}:${index}`} runtime={runtime}/>;
                if (!region && renderedBlock.type === "richContent" && Array.isArray(renderedBlock.slots?.[SHOP_GRID_SLOT_KEY])) {
                    return <MorrowRegionFrame block={renderedBlock} index={index} key={`content-grid:${index}`} runtime={runtime}/>;
                }
                if (!region) return <MorrowStandaloneBlock block={renderedBlock} index={index} key={`standalone:${renderedBlock.type}:${index}`} runtime={runtime}/>;

                let after: ReactNode = null;
                if (region === "hero") after = <MorrowHeroChips runtime={runtime}/>;
                else if (region === "categories") after = <MorrowCategoryLinks block={renderedBlock} runtime={runtime}/>;
                else if (region === "productFeed") after = <MorrowProductGrid block={renderedBlock} products={products.slice(0, 8)} runtime={runtime} systemReference={runtimeSystemBlockReference(renderedBlock, "productFeedSystem")}/>;
                else if (region === "catalog") after = <MorrowCatalog block={renderedBlock} runtime={runtime}/>;
                else if (region === "product") after = <MorrowProductDetail block={renderedBlock} runtime={runtime}/>;
                else if (region === "productRecommendations") after = <MorrowProductGrid block={renderedBlock} products={products.slice(0, 4)} runtime={runtime} systemReference={runtimeSystemBlockReference(renderedBlock, "recommendationsSystem")}/>;
                else if (region === "cart") after = <MorrowCart block={renderedBlock} runtime={runtime}/>;
                else if (region === "checkout") after = <MorrowCheckout block={renderedBlock} runtime={runtime}/>;
                else if (region === "account") after = <MorrowAccount block={renderedBlock} runtime={runtime}/>;
                else if (region === "contactForm") after = <MorrowContact block={renderedBlock} runtime={runtime}/>;
                else if (region === "searchResults") after = <MorrowSearchResults block={renderedBlock} runtime={runtime}/>;

                return <MorrowRegionFrame after={after} block={renderedBlock} compact={region === "productFeed" && products.length === 0} index={index} key={`${region}:${index}`} runtime={runtime}/>;
            })}
            {!runtime.hasFooterBlock ? <MorrowLegacyFooter runtime={runtime}/> : null}
        </>
    );
}

function MorrowStyles() {
    return <style>{`
        ${MORROW_INTERACTION_CSS}
        ${MORROW_COUNTRY_SELECT_CSS}
        .morrow-contact select{background:#fff;border:1px solid var(--site-border);border-radius:10px;font:inherit;min-height:44px;padding:10px 12px}
        .morrow-submit button:disabled{cursor:not-allowed;opacity:.5}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=announcement]{padding-inline:clamp(20px,4vw,64px)}
        .morrow-inner-text{overflow-wrap:break-word}
        .morrow-column,.morrow-column-content,.morrow-column-child{min-width:0}.morrow-column-content{height:100%}.morrow-column-child>*{min-width:0;width:100%}.morrow-column-content.is-inline>.morrow-column-child,.morrow-column-content.is-inline>.morrow-column-child>*{width:auto}.morrow-column-content.is-inlineActions>.morrow-column-child{width:auto}.morrow-column-content.is-inlineActions>.morrow-column-child>*{width:100%}
        .morrow-header-action{appearance:none;border-style:solid;cursor:pointer;font:inherit;margin:0;position:relative;text-decoration:none}.morrow-header-action .morrow-column-content{width:100%}.morrow-header-action .morrow-inner-icon{justify-content:center}.morrow-cart-action{color:#fff}.morrow-header-cart-count{align-items:center;background:var(--morrow-apricot);border-radius:99px;color:var(--morrow-ink);display:flex;font-size:10px;font-weight:850;height:19px;justify-content:center;min-width:19px;padding:0 4px;position:absolute;right:7px;top:5px}
        .morrow-search-icon-slot{align-items:center;display:flex;flex:0 0 auto;justify-content:center}.morrow-search-icon-slot>[data-shop-block-part]{align-items:center;display:flex}.morrow-search-icon-slot .morrow-inner-icon{justify-content:center}.morrow-search-form button .morrow-search-icon-slot{height:100%;width:100%}
        .morrow-inner-icon-link{justify-content:center;transition:background .18s ease,transform .18s ease}.morrow-inner-icon-link:hover{background:rgba(255,255,255,.15)!important;transform:translateY(-2px)}
                @media(width > ${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){.morrow-region-header:not([data-shop-builder-canvas-content="true"] .morrow-region-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=accountAction]{grid-column:11/span 1!important}.morrow-region-header:not([data-shop-builder-canvas-content="true"] .morrow-region-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=cartAction]{grid-column:12/span 1!important}}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=announcement]>.morrow-column[data-morrow-role=announcement]{margin-left:0;margin-right:0;padding-inline:0;width:100%}.morrow-category-links>a>.morrow-column{grid-column:1/-1;width:100%}.morrow-category-links .morrow-column-content{width:100%}.morrow-category-links .morrow-column-child:has([data-morrow-role=categoryLabel]){flex:1 1 auto}.morrow-category-links [data-morrow-role=categoryLabel]{width:100%}
        .morrow-toast{background:var(--morrow-ink);border:1px solid rgba(255,255,255,.16);border-radius:12px;bottom:24px;color:#fff;left:50%;opacity:0;padding:12px 18px;pointer-events:none;position:fixed;transform:translate(-50%,18px);transition:opacity .2s ease,transform .2s ease;z-index:110}.morrow-toast.is-visible{opacity:1;transform:translate(-50%,0)}
        .morrow-storefront{--morrow-ink:#15231f;--morrow-green:#087f5b;--morrow-apricot:#ffb36b;--morrow-blue:#dcebfa;background:var(--site-background);color:var(--site-text);min-height:100vh}.morrow-storefront *{box-sizing:border-box}.morrow-storefront a{text-decoration:none;color:inherit}.morrow-region{position:relative;padding-left:clamp(20px,4vw,64px);padding-right:clamp(20px,4vw,64px)}.morrow-region-inner{width:min(100%,var(--site-container));margin-inline:auto}.morrow-canvas-grid{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:${SHOP_GRID_CELL_GAP}px;position:relative}.morrow-grid-child{display:flex;align-items:stretch;position:relative}.morrow-grid-child>*{min-width:0;width:100%}.morrow-inner-text{display:flex;align-items:center;white-space:pre-wrap}.morrow-inner-button{display:inline-flex;align-items:center;justify-content:center;transition:transform .18s ease,background .18s ease}.morrow-inner-button:hover{transform:translateY(-2px)}.morrow-inner-image{display:block}.morrow-inner-icon{display:flex;align-items:center}.morrow-inner-separator{align-self:center;width:100%;height:1px}.morrow-search-form{align-items:center;background:#fff;border:1px solid var(--site-border);border-radius:16px;display:flex;gap:12px;min-height:48px;padding:0 16px}.morrow-search-form input{background:transparent;border:0;color:var(--site-text);font:inherit;min-width:0;outline:0;width:100%}.morrow-search-form button{background:var(--morrow-green);border:0;border-radius:10px;color:#fff;cursor:pointer;height:34px;width:38px}.morrow-inner-panel{background:#fff;border:1px solid var(--site-border);border-radius:16px;padding:20px}.morrow-inner-panel input,.morrow-inner-panel textarea{border:0;border-bottom:1px solid var(--site-border);font:inherit;padding:10px;width:100%}.morrow-inner-panel button{background:var(--morrow-green);border:0;border-radius:10px;color:#fff;margin-top:12px;padding:10px 16px}.morrow-progress{background:#d7ded9;border-radius:100px;height:10px;overflow:hidden}.morrow-progress span{background:var(--morrow-green);display:block;height:100%}.morrow-badge{background:var(--morrow-apricot);border-radius:999px;display:inline-flex;padding:7px 12px}.morrow-quote{border-left:4px solid var(--morrow-green);font-size:1.3rem;margin:0;padding:20px}.morrow-code{background:var(--morrow-ink);border-radius:14px;color:#fff;overflow:auto;padding:18px}.morrow-payment-strip,.morrow-social,.morrow-avatars,.morrow-countdown{align-items:center;display:flex;gap:10px}.morrow-payment-strip span{border:1px solid var(--site-border);border-radius:7px;font-size:11px;font-weight:800;padding:7px}.morrow-icon-list{display:grid;gap:8px;list-style:none;margin:0;padding:0}.morrow-icon-list li{align-items:center;display:flex;gap:8px}.morrow-avatars span{align-items:center;background:var(--morrow-blue);border:2px solid #fff;border-radius:50%;display:flex;font-size:10px;height:36px;justify-content:center;margin-left:-8px;width:36px}.morrow-countdown strong{font-size:28px}.morrow-chart{align-items:flex-end;display:flex;gap:8px;height:100%}.morrow-chart span{background:var(--morrow-green);border-radius:6px 6px 0 0;flex:1}.morrow-header-shell{position:relative;z-index:30}.morrow-region-header{border-bottom:1px solid var(--site-border);padding-top:0!important}.morrow-region-header .morrow-region-inner{max-width:none;width:100%}.morrow-region-header .morrow-canvas-grid{margin-inline:auto;max-width:var(--site-container)}.morrow-region-header [data-morrow-role=announcement]{margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);width:100vw}.morrow-navigation{align-items:center;border-top:1px solid var(--site-border);display:flex;justify-content:space-between;margin-top:16px;min-height:58px}.morrow-header-selectors{align-items:center;display:flex;gap:8px}.morrow-selector select{background:transparent;border:0;color:inherit;font:inherit;font-size:12px;font-weight:800;outline:0}.morrow-cart-count{align-items:center;background:var(--morrow-ink);border:0;border-radius:12px;color:#fff;display:flex;gap:8px;height:38px;padding:0 12px}.morrow-cart-count span{align-items:center;background:var(--morrow-apricot);border-radius:99px;color:var(--morrow-ink);display:flex;font-size:11px;height:20px;justify-content:center;min-width:20px}.morrow-mobile-menu-button{display:none}.morrow-region-hero{padding-bottom:42px;padding-top:42px}.morrow-region-features{background:var(--morrow-blue);padding-bottom:56px;padding-top:56px}.morrow-region-editorial{background:var(--morrow-apricot);padding-bottom:54px;padding-top:54px}.morrow-region-footer{background:var(--morrow-ink);color:inherit;padding-bottom:40px;padding-top:54px}.morrow-category-links{display:grid;gap:14px;grid-template-columns:repeat(4,1fr);padding:18px 0 54px}.morrow-category-links>a{align-items:center;border-bottom:1px solid var(--site-border);display:grid;gap:14px;grid-template-columns:72px 1fr auto;padding:14px 0}.morrow-category-art{align-items:center;background:var(--morrow-blue);border-radius:14px;display:flex;height:64px;justify-content:center}.morrow-category-art.art-1{background:var(--morrow-apricot)}.morrow-category-art.art-2{background:#dbe8d2}.morrow-category-art.art-3{background:#ede0f5}.morrow-region-productFeed,.morrow-region-catalog,.morrow-region-product,.morrow-region-cart,.morrow-region-checkout,.morrow-region-account,.morrow-region-contactForm,.morrow-region-searchResults{padding-bottom:72px;padding-top:52px}.morrow-product-grid{display:grid;gap:38px 18px;grid-template-columns:repeat(4,minmax(0,1fr));padding:28px 0 52px}.morrow-product-card{min-width:0;position:relative}.morrow-product-visual{align-items:center;aspect-ratio:4/5;background:#e5eee8;border-radius:16px;display:flex;justify-content:center;overflow:hidden;position:relative}.morrow-product-visual.tone-1{background:#f8d6b6}.morrow-product-visual.tone-2{background:#dceafa}.morrow-product-visual.tone-3{background:#ece2cf}.morrow-product-visual img{height:100%;object-fit:cover;transition:transform .2s ease;width:100%}.morrow-product-card:hover .morrow-product-visual img{transform:scale(1.025)}.morrow-product-visual>span:not(.morrow-product-placeholder){background:var(--morrow-apricot);border-radius:999px;font-size:11px;font-weight:850;left:12px;padding:7px 10px;position:absolute;top:12px}.morrow-product-placeholder{align-items:center;color:color-mix(in srgb,var(--site-text) 28%,transparent);display:flex;font-size:24px;font-weight:850;height:100%;justify-content:center;text-transform:uppercase;width:100%}.morrow-favorite{align-items:center;background:#fff;border:0;border-radius:50%;display:flex;height:36px;justify-content:center;position:absolute;right:12px;top:12px;width:36px}.morrow-favorite.is-active{background:var(--morrow-apricot);color:var(--morrow-ink)}.morrow-product-copy{align-items:start;display:grid;gap:12px;grid-template-columns:1fr auto;padding:14px 2px 8px}.morrow-product-copy div{display:grid;gap:4px}.morrow-product-copy span{color:var(--site-muted);font-size:11px;font-weight:750;text-transform:uppercase}.morrow-product-copy a{font-size:15px;font-weight:750;line-height:1.35}.morrow-product-copy>strong{font-size:15px}.morrow-product-meta{align-items:center;color:var(--site-muted);display:flex;font-size:12px;justify-content:space-between;padding:0 2px}.morrow-product-meta .is-available{color:var(--morrow-green)}.morrow-product-meta button,.morrow-product-meta>a{align-items:center;background:var(--morrow-green);border:0;border-radius:10px;color:#fff;display:flex;font-size:20px;height:34px;justify-content:center;width:38px}.morrow-empty-state{align-items:center;border:1px dashed var(--site-border);border-radius:16px;display:flex;flex-direction:column;gap:8px;grid-column:1/-1;justify-content:center;min-height:220px;padding:30px;text-align:center}.morrow-empty-state a{color:var(--morrow-green);font-weight:800}.morrow-catalog-title{display:grid;gap:18px;grid-template-columns:2fr 3fr;padding:18px 0 48px}.morrow-catalog-title p{color:var(--morrow-green);font-size:12px;font-weight:850;letter-spacing:.12em;text-transform:uppercase}.morrow-catalog-title h1{font-size:clamp(46px,7vw,98px);font-weight:800;grid-column:1/-1;letter-spacing:-.06em;line-height:.92;margin:0}.morrow-catalog-title>div{color:var(--site-muted);font-size:18px;grid-column:2;line-height:1.55}.morrow-catalog-toolbar{align-items:center;border-block:1px solid var(--site-border);display:flex;justify-content:space-between;padding:14px 0}.morrow-scroll-chips{display:flex;gap:8px;overflow:auto}.morrow-scroll-chips a{border:1px solid var(--site-border);border-radius:999px;font-size:12px;font-weight:750;padding:9px 14px;white-space:nowrap}.morrow-catalog-toolbar label{align-items:center;display:flex;font-size:12px;font-weight:800;gap:10px}.morrow-catalog-toolbar select{background:#fff;border:1px solid var(--site-border);border-radius:10px;padding:9px}.morrow-is-updating{opacity:.55;pointer-events:none}.morrow-product-detail{display:grid;gap:clamp(36px,6vw,90px);grid-template-columns:minmax(0,7fr) minmax(360px,5fr);padding:24px 0 80px}.morrow-product-gallery{display:grid;gap:14px;grid-template-columns:1fr 1fr}.morrow-product-gallery>div{border-radius:18px;overflow:hidden}.morrow-product-gallery img{display:block;height:100%;object-fit:cover;width:100%}.morrow-product-main-image{aspect-ratio:4/5;background:#e5eee8;grid-column:1/-1}.morrow-product-support-image{aspect-ratio:1}.morrow-buy-panel{align-self:start;position:sticky;top:24px}.morrow-product-kicker{align-items:center;color:var(--morrow-green);display:flex;font-size:12px;font-weight:850;justify-content:space-between;letter-spacing:.12em;text-transform:uppercase}.morrow-product-kicker button{background:transparent;border:1px solid var(--site-border);border-radius:50%;height:42px;width:42px}.morrow-product-kicker button.is-active{background:var(--morrow-apricot)}.morrow-buy-panel h1{font-size:clamp(38px,5vw,68px);letter-spacing:-.05em;line-height:.96;margin:24px 0 16px}.morrow-product-price{font-size:25px;font-weight:850}.morrow-buy-panel>p{color:var(--site-muted);font-size:12px}.morrow-option-group{border:0;border-top:1px solid var(--site-border);margin:24px 0 0;padding:22px 0 0}.morrow-option-group legend{font-size:13px;font-weight:850;padding:0}.morrow-option-group>div{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}.morrow-option-group button{background:#fff;border:1px solid var(--site-border);border-radius:10px;padding:10px 14px}.morrow-option-group button.is-selected{background:var(--morrow-ink);border-color:var(--morrow-ink);color:#fff}.morrow-stock-line{align-items:center;display:flex;font-size:13px;gap:8px;margin:24px 0}.morrow-stock-line span{background:#a8a8a8;border-radius:50%;height:8px;width:8px}.morrow-stock-line span.is-available{background:var(--morrow-green)}.morrow-add-row{display:grid;gap:10px;grid-template-columns:auto 1fr}.morrow-quantity{align-items:center;background:#fff;border:1px solid var(--site-border);border-radius:12px;display:flex;min-height:48px}.morrow-quantity button{background:transparent;border:0;font-size:20px;height:100%;width:38px}.morrow-quantity span{font-size:13px;font-weight:850;min-width:24px;text-align:center}.morrow-primary-button{align-items:center;background:var(--morrow-green);border:1px solid var(--morrow-green);border-radius:12px;color:#fff!important;display:flex;font-size:14px;font-weight:850;gap:10px;justify-content:center;min-height:50px;padding:13px 20px}.morrow-primary-button:disabled{cursor:not-allowed;opacity:.45}.morrow-product-description{border-top:1px solid var(--site-border);line-height:1.6;margin-top:30px;padding-top:24px}.morrow-product-description p{color:var(--site-muted)}.morrow-buy-benefits{display:flex;flex-wrap:wrap;gap:18px;margin-top:24px}.morrow-buy-benefits span{align-items:center;display:flex;font-size:12px;font-weight:750;gap:7px}.morrow-error{background:#fff0e8;border:1px solid #e7a982;border-radius:10px;color:#7d321a;padding:12px}.morrow-cart-layout,.morrow-checkout-layout{display:grid;gap:clamp(30px,6vw,84px);grid-template-columns:minmax(0,8fr) minmax(320px,4fr);padding-bottom:70px}.morrow-cart-items{border-top:1px solid var(--site-border)}.morrow-cart-items article{align-items:center;border-bottom:1px solid var(--site-border);display:grid;gap:18px;grid-template-columns:96px minmax(0,1fr) auto auto auto;padding:18px 0}.morrow-cart-thumb{aspect-ratio:4/5;background:#e5eee8;border-radius:12px;overflow:hidden}.morrow-cart-thumb img{height:100%;object-fit:cover;width:100%}.morrow-cart-copy{display:flex;flex-direction:column;gap:4px}.morrow-cart-copy span,.morrow-cart-copy small{color:var(--site-muted)}.morrow-remove{background:transparent;border:0;font-size:20px}.morrow-cart-summary{align-self:start;background:#fff;border:1px solid var(--site-border);border-radius:16px;padding:24px;position:sticky;top:24px}.morrow-cart-summary h2{font-size:24px;margin:0 0 24px}.morrow-cart-summary dl{margin:0}.morrow-cart-summary dl>div{display:flex;justify-content:space-between;padding:8px 0}.morrow-cart-summary .is-total{border-top:1px solid var(--site-border);font-size:20px;font-weight:850;margin-top:10px;padding-top:18px}.morrow-cart-summary .morrow-primary-button{margin-top:22px}.morrow-summary-note{align-items:center;color:var(--site-muted);display:flex;font-size:11px;gap:8px;justify-content:center;margin-top:16px}.morrow-cart-items.is-compact article{gap:12px;grid-template-columns:58px 1fr auto auto;padding:12px 0}.morrow-cart-items.is-compact .morrow-remove{display:none}.morrow-checkout-main{min-width:0}.morrow-step{align-items:center;border-bottom:1px solid var(--site-border);display:flex;gap:18px;margin:0 0 20px;padding:24px 0}.morrow-step>span{align-items:center;background:var(--morrow-apricot);border-radius:50%;display:flex;font-size:12px;font-weight:850;height:42px;justify-content:center;width:42px}.morrow-step h2,.morrow-step p{margin:0}.morrow-step p{color:var(--site-muted);font-size:12px}.morrow-delivery-options{display:grid;gap:10px;margin-bottom:26px}.morrow-delivery-card{background:#fff;border:1px solid var(--site-border);border-radius:14px;padding:18px}.morrow-delivery-card.is-selected{border-color:var(--morrow-green);box-shadow:0 0 0 2px rgba(8,127,91,.1)}.morrow-delivery-card>div:first-child{display:flex;flex-direction:column}.morrow-payment-options{display:grid;gap:8px;margin-top:14px}.morrow-payment-options label{align-items:center;border-top:1px solid var(--site-border);display:grid;gap:10px;grid-template-columns:auto 1fr auto;padding-top:12px}.morrow-pickup{display:grid;gap:10px;grid-template-columns:1fr 1fr auto;margin-top:16px}.morrow-pickup label,.morrow-form-grid label,.morrow-auth-form label,.morrow-profile-form label,.morrow-contact form label{display:flex;flex-direction:column;font-size:12px;font-weight:800;gap:7px}.morrow-pickup input,.morrow-form-grid input,.morrow-form-grid textarea,.morrow-auth-form input,.morrow-profile-form input,.morrow-contact input,.morrow-contact textarea{background:#fff;border:1px solid var(--site-border);border-radius:10px;font:inherit;min-height:44px;padding:10px 12px}.morrow-pickup button{align-self:end;background:var(--morrow-ink);border:0;border-radius:10px;color:#fff;height:44px;padding:0 14px}.morrow-form-grid{display:grid;gap:14px;grid-template-columns:1fr 1fr;margin-bottom:24px}.morrow-form-grid .is-wide{grid-column:1/-1}.morrow-form-grid textarea{resize:vertical}.morrow-check{align-items:center;display:flex;gap:10px;margin-bottom:14px}.morrow-place-order{justify-content:space-between;width:100%}.morrow-order-complete{align-items:center;display:flex;flex-direction:column;gap:14px;min-height:520px;justify-content:center;text-align:center}.morrow-auth-layout,.morrow-contact{display:grid;gap:clamp(34px,8vw,120px);grid-template-columns:1fr 1fr;padding:34px 0 90px}.morrow-auth-note>span,.morrow-contact>div>span{color:var(--morrow-green);font-size:12px;font-weight:850;letter-spacing:.14em}.morrow-auth-note h1,.morrow-contact h1{font-size:clamp(46px,7vw,88px);letter-spacing:-.06em;line-height:.94;margin:22px 0}.morrow-auth-note p,.morrow-contact p{color:var(--site-muted);font-size:18px;line-height:1.6}.morrow-auth-form,.morrow-contact form,.morrow-profile-form{background:#fff;border:1px solid var(--site-border);border-radius:16px;display:grid;gap:16px;padding:24px}.morrow-auth-tabs{display:grid;grid-template-columns:1fr 1fr}.morrow-auth-tabs button{background:transparent;border:0;border-bottom:2px solid var(--site-border);font-weight:800;padding:12px}.morrow-auth-tabs button.is-active{border-color:var(--morrow-green);color:var(--morrow-green)}.morrow-account-dashboard{display:grid;gap:64px;grid-template-columns:260px 1fr;padding:20px 0 90px}.morrow-account-dashboard>aside{align-items:center;display:flex;flex-direction:column;gap:8px;text-align:center}.morrow-account-dashboard>aside button{background:transparent;border:0;color:var(--morrow-green);font-weight:800;margin-top:12px}.morrow-avatar{align-items:center;background:var(--morrow-apricot);border-radius:50%;display:flex;font-size:24px;font-weight:850;height:92px;justify-content:center;margin-bottom:10px;width:92px}.morrow-account-content h1{font-size:clamp(40px,5vw,68px);letter-spacing:-.05em;margin:0 0 24px}.morrow-account-stats{display:grid;gap:12px;grid-template-columns:1fr 1fr;margin-bottom:20px}.morrow-account-stats article{background:var(--morrow-blue);border-radius:14px;display:flex;justify-content:space-between;padding:20px}.morrow-account-stats strong{font-size:28px}.morrow-profile-form{grid-template-columns:1fr 1fr}.morrow-profile-form h2,.morrow-profile-form button{grid-column:1/-1}.morrow-contact>div>a{color:var(--morrow-green);font-size:22px;font-weight:850}.morrow-not-found{align-items:flex-start;display:flex;flex-direction:column;gap:20px;justify-content:center;min-height:540px;padding:40px 0}.morrow-not-found>span{color:var(--morrow-green);font-size:14px;font-weight:850;letter-spacing:.14em}.morrow-not-found h1{font-size:clamp(58px,10vw,140px);letter-spacing:-.07em;line-height:.85;margin:0;max-width:900px}.morrow-fallback{margin:auto;max-width:var(--site-container);padding:80px 24px}.morrow-overlay{inset:0;position:fixed;z-index:90}.morrow-overlay-backdrop{background:rgba(21,35,31,.48);border:0;inset:0;position:absolute;width:100%}.morrow-mobile-panel,.morrow-cart-drawer{background:var(--site-background);bottom:0;display:flex;flex-direction:column;gap:20px;max-width:460px;overflow:auto;padding:24px;position:absolute;right:0;top:0;width:min(92vw,460px)}.morrow-panel-head{align-items:center;border-bottom:1px solid var(--site-border);display:flex;font-size:22px;justify-content:space-between;padding-bottom:18px}.morrow-panel-head button{background:transparent;border:0;font-size:30px}.morrow-drawer-total{display:flex;font-size:18px;justify-content:space-between;margin-top:auto}.morrow-search-overlay{background:var(--morrow-blue);left:0;padding:clamp(24px,7vw,100px);position:fixed;right:0;top:0;z-index:95}.morrow-search-overlay-head{display:flex;font-size:12px;font-weight:850;justify-content:space-between;letter-spacing:.12em;margin-bottom:30px;text-transform:uppercase}.morrow-search-overlay-head button{background:transparent;border:0;font-size:28px}.morrow-search-overlay .morrow-search-form{font-size:clamp(22px,4vw,52px);min-height:82px}.morrow-search-overlay>p{color:var(--site-muted)}
        .morrow-region-inner{position:relative}
        .morrow-media-placeholder{align-items:center;background:linear-gradient(135deg,var(--site-surface),color-mix(in srgb,var(--site-primary) 18%,var(--site-surface)));color:color-mix(in srgb,var(--site-text) 78%,var(--site-primary));display:flex;justify-content:center;min-height:100%}
        .morrow-product-placeholder{align-items:center;background:linear-gradient(145deg,var(--site-primary-soft),color-mix(in srgb,var(--site-accent) 14%,var(--site-surface)) 54%,var(--site-surface));color:color-mix(in srgb,var(--site-text) 82%,var(--site-primary));display:flex;font-size:24px;font-weight:850;height:100%;isolation:isolate;justify-content:center;overflow:hidden;position:relative;text-transform:uppercase;width:100%}.morrow-product-placeholder-glow{background:color-mix(in srgb,var(--site-primary) 16%,transparent);border-radius:50%;filter:blur(22px);height:55%;position:absolute;right:-12%;top:-12%;width:55%}.morrow-product-placeholder-initials{align-items:center;backdrop-filter:blur(4px);background:color-mix(in srgb,var(--site-surface) 76%,transparent);border:1px solid color-mix(in srgb,var(--site-primary) 30%,var(--site-border));border-radius:50%;box-shadow:0 14px 36px var(--site-shadow-color);display:flex;height:78px;justify-content:center;letter-spacing:.08em;position:relative;width:78px}.morrow-product-placeholder.is-detail .morrow-product-placeholder-initials{font-size:38px;height:112px;width:112px}.morrow-product-placeholder.is-compact .morrow-product-placeholder-initials{font-size:13px;height:34px;width:34px}
        .morrow-region-footer{color:var(--site-on-inverse)}
        .morrow-delivery-options,.morrow-checkout-payment-options{display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr));margin-bottom:28px}
        .morrow-delivery-card,.morrow-payment-option{align-items:center;appearance:none;background:var(--site-surface);border:1px solid var(--site-border);border-radius:16px;color:var(--site-text);cursor:pointer;display:grid;font:inherit;gap:12px;grid-template-columns:44px minmax(0,1fr) auto 24px;min-height:82px;padding:14px 16px;text-align:left;transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease;width:100%}
        .morrow-delivery-card:hover,.morrow-payment-option:hover{border-color:var(--morrow-green);box-shadow:0 12px 30px color-mix(in srgb,var(--site-shadow-color) 72%,transparent);transform:translateY(-2px)}
        .morrow-delivery-card.is-selected,.morrow-payment-option.is-selected{background:color-mix(in srgb,var(--site-primary-soft) 58%,var(--site-surface));border-color:var(--morrow-green);box-shadow:0 0 0 3px color-mix(in srgb,var(--morrow-green) 12%,transparent)}
        .morrow-checkout-choice-icon{align-items:center;background:var(--site-background);border:1px solid var(--site-border);border-radius:12px;color:var(--morrow-green);display:flex;height:44px;justify-content:center;width:44px}
        .morrow-checkout-choice-copy{display:grid;gap:3px;min-width:0}.morrow-checkout-choice-copy strong{font-size:14px;line-height:1.3}.morrow-checkout-choice-copy small{color:var(--site-muted);font-size:11px;line-height:1.35}
        .morrow-checkout-choice-price{font-size:13px;white-space:nowrap}.morrow-checkout-choice-check{align-items:center;color:var(--morrow-green);display:flex;height:24px;justify-content:center;width:24px}
        .morrow-checkout-guidance{background:var(--site-surface);border:1px dashed var(--site-border);border-radius:14px;color:var(--site-muted);margin:0 0 28px;padding:18px;text-align:center}
        .morrow-pickup{background:color-mix(in srgb,var(--site-primary-soft) 34%,var(--site-surface));border:1px solid var(--site-border);border-radius:16px;margin:-12px 0 28px;padding:18px}
        @media(max-width:640px){.morrow-delivery-options,.morrow-checkout-payment-options{grid-template-columns:1fr}.morrow-delivery-card,.morrow-payment-option{grid-template-columns:40px minmax(0,1fr) auto 22px;padding:12px}.morrow-checkout-choice-icon{height:40px;width:40px}}
        .morrow-legacy-footer{background:var(--site-inverse);color:var(--site-on-inverse);display:grid;gap:28px;padding:clamp(32px,5vw,64px);position:relative}.morrow-legacy-footer>div{align-items:center;display:flex;flex-wrap:wrap;gap:18px 30px;justify-content:space-between;margin-inline:auto;max-width:var(--site-container);width:100%}.morrow-legacy-footer nav{display:flex;flex-wrap:wrap;gap:12px 22px}.morrow-legacy-footer a{color:inherit;font-size:13px;font-weight:750}.morrow-legacy-footer-brand{font-size:24px!important;font-weight:900!important;letter-spacing:-.04em}.morrow-legacy-footer small{color:color-mix(in srgb,var(--site-on-inverse) 76%,transparent);margin-inline:auto;max-width:var(--site-container);width:100%}
        .morrow-account-order-mark{overflow:hidden}.morrow-account-order-mark img{height:100%;object-fit:cover;width:100%}
        @media(max-width:1024px){.morrow-product-grid{grid-template-columns:repeat(3,1fr)}.morrow-category-links{grid-template-columns:repeat(2,1fr)}.morrow-product-detail{grid-template-columns:1fr 1fr}.morrow-cart-layout,.morrow-checkout-layout{gap:28px}.morrow-header-selectors .morrow-selector{display:none}}
        @media(max-width:767px){.morrow-region{min-height:0!important;padding-left:18px;padding-right:18px}.morrow-canvas-grid{min-height:0!important}.morrow-canvas-grid[data-shop-grid-stack-on-mobile=true]{display:flex;flex-direction:column;gap:14px}.morrow-canvas-grid[data-builder-empty-slot=true]{min-height:clamp(96px,35dvh,240px)!important}.morrow-canvas-grid[data-shop-grid-stack-on-mobile=true]>.morrow-grid-child{height:auto!important;min-height:0}.morrow-column[data-site-builder-stack-on-mobile=true]{height:auto!important;min-height:0!important}.morrow-column[data-site-builder-stack-on-mobile=true]:not([data-morrow-header-role])>.morrow-column-content.is-inline{align-items:stretch!important;flex-direction:column}.morrow-column-content:not(.is-inline){grid-template-rows:auto!important}.morrow-column-content.is-searchStack>.morrow-column-child{grid-row:auto!important}.morrow-region-header .morrow-canvas-grid{display:grid;grid-template-columns:minmax(0,1fr) auto auto;grid-template-rows:auto auto!important;padding-top:10px}.morrow-region-header [data-morrow-role=announcement]{display:none}.morrow-region-header [data-morrow-role=brand]{grid-column:1!important;grid-row:1!important}.morrow-region-header [data-morrow-role=accountAction]{grid-column:2!important;grid-row:1!important}.morrow-region-header [data-morrow-role=cartAction]{grid-column:3!important;grid-row:1!important}.morrow-region-header .morrow-grid-child:has(.morrow-search-form){grid-column:1/-1!important;grid-row:2!important}.morrow-account-action .morrow-column-child:nth-child(2){display:none}.morrow-navigation{min-height:52px}.morrow-mobile-menu-button{align-items:center;background:transparent;border:0;display:flex}.morrow-product-grid{gap:28px 10px;grid-template-columns:repeat(2,minmax(0,1fr));padding-top:18px}.morrow-product-copy{display:flex;flex-direction:column;gap:7px}.morrow-product-meta>span{display:none}.morrow-product-visual{border-radius:12px}.morrow-category-links{display:flex;overflow:auto;padding-bottom:34px}.morrow-category-links>a{flex:0 0 220px}.morrow-region-hero,.morrow-region-editorial,.morrow-region-features{padding-bottom:34px;padding-top:34px}.morrow-region-hero .morrow-inner-image{aspect-ratio:4/5}.morrow-catalog-title{display:block}.morrow-catalog-title h1{font-size:52px;margin:18px 0}.morrow-catalog-title>div{font-size:16px}.morrow-catalog-toolbar{align-items:flex-start;gap:14px}.morrow-scroll-chips{width:100%}.morrow-catalog-toolbar label{display:none}.morrow-product-detail{display:block}.morrow-product-gallery{margin-bottom:30px}.morrow-product-support-image{display:none}.morrow-buy-panel{position:static}.morrow-buy-panel h1{font-size:44px}.morrow-cart-layout,.morrow-checkout-layout,.morrow-auth-layout,.morrow-contact,.morrow-account-dashboard{display:block}.morrow-cart-summary{margin-top:24px;position:static}.morrow-cart-items article{gap:10px;grid-template-columns:68px 1fr auto}.morrow-cart-items article>.morrow-quantity{grid-column:2}.morrow-cart-items article>strong{grid-column:3;grid-row:1}.morrow-cart-items article>.morrow-remove{grid-column:3;grid-row:2}.morrow-checkout-layout>div:last-child{display:none}.morrow-pickup,.morrow-form-grid,.morrow-profile-form{grid-template-columns:1fr}.morrow-pickup>*{grid-column:1}.morrow-auth-note,.morrow-contact>div{margin-bottom:34px}.morrow-account-dashboard>aside{margin-bottom:36px}.morrow-account-stats{grid-template-columns:1fr}.morrow-profile-form>*{grid-column:1!important}.morrow-add-row{grid-template-columns:1fr}.morrow-quantity{justify-content:space-between}.morrow-not-found{min-height:440px}}
        @media(max-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){.morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=brand]{grid-column:1!important;grid-row:1!important}.morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=accountAction]{grid-column:2!important;grid-row:1!important}.morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=cartAction]{grid-column:3!important;grid-row:1!important}.morrow-region-header .morrow-grid-child>.morrow-column[data-morrow-role=brand],.morrow-region-header .morrow-grid-child>.morrow-column[data-morrow-role=accountAction],.morrow-region-header .morrow-grid-child>.morrow-column[data-morrow-role=cartAction]{grid-column:auto!important;grid-row:auto!important}}
        .morrow-standalone-region{padding-bottom:46px;padding-top:46px}.morrow-standalone-inner{display:grid;min-width:0}.morrow-standalone-inner>.morrow-column{height:100%}.morrow-auth-layout.is-system-only,.morrow-contact.is-system-only{grid-template-columns:minmax(0,680px);justify-content:center}.morrow-auth-layout.is-system-only>form,.morrow-contact.is-system-only>form{width:100%}.morrow-inner-heading{letter-spacing:-.035em;line-height:1.05;margin:0}.morrow-inline-quote{border-left:3px solid var(--morrow-green);font-size:1.15rem;line-height:1.65;margin:0;padding:8px 0 8px 20px}.morrow-content-list{display:grid;gap:9px;margin:0;padding-left:22px}.morrow-content-list.is-checklist{list-style:none;padding-left:0}.morrow-content-list.is-checklist li{align-items:center;display:flex;gap:9px}.morrow-upload{align-items:center;background:#fff;border:1px dashed var(--site-border);border-radius:16px;cursor:pointer;display:flex;gap:10px;justify-content:center;min-height:96px;padding:20px}.morrow-upload input{height:1px;opacity:0;position:absolute;width:1px}.morrow-emoji{font-size:2.4rem;line-height:1}.morrow-signature{font-family:cursive;font-size:2rem}
        .morrow-accordion{background:#fff;border:1px solid var(--site-border);border-radius:18px;overflow:hidden;width:100%}.morrow-accordion.is-style-highlightActive{background:transparent}.morrow-accordion>[data-shop-slot-container]{display:grid}.morrow-accordion-item{border-bottom:1px solid var(--site-border)}.morrow-accordion>[data-shop-slot-container]>[data-shop-block-part]:last-child .morrow-accordion-item{border-bottom:0}.morrow-accordion-toggle{align-items:center;background:transparent;border:0;color:inherit;cursor:pointer;display:flex;font:inherit;font-size:16px;font-weight:800;gap:20px;justify-content:space-between;margin:0!important;padding:18px 20px;text-align:left;width:100%}.morrow-accordion-icon{align-items:center;display:flex;flex:0 0 auto;transition:transform .2s ease}.morrow-accordion-item.is-open .morrow-accordion-icon{transform:rotate(180deg)}.morrow-accordion-item.is-transition-none .morrow-accordion-icon{transition:none}.morrow-accordion-answer{display:grid;grid-template-rows:0fr;opacity:0;padding:0 20px;transition:grid-template-rows .2s ease,opacity .2s ease}.morrow-accordion-answer.is-transition-none{transition:none}.morrow-accordion-answer.is-transition-fade{transition:opacity .2s ease}.morrow-accordion-answer>div{min-height:0;overflow:hidden}.morrow-accordion-answer p{color:var(--site-muted);line-height:1.65;margin:0;padding:0 0 18px;white-space:pre-line}.morrow-accordion-item.is-open .morrow-accordion-answer{grid-template-rows:1fr;opacity:1}
        .morrow-builder-form{background:#fff;border:1px solid var(--site-border);border-radius:20px;padding:clamp(18px,3vw,30px);width:100%}.morrow-form-items{display:grid;gap:16px}.morrow-form-field{align-items:start;display:grid;gap:12px;grid-template-columns:var(--morrow-form-label-width,200px) minmax(0,1fr)}.morrow-builder-form.is-label-top .morrow-form-field,.morrow-form-field.is-label-top{grid-template-columns:1fr}.morrow-builder-form.is-label-none .morrow-form-field,.morrow-form-field.is-label-none{grid-template-columns:1fr}.morrow-builder-form.is-label-sideNoGap .morrow-form-field{gap:0}.morrow-form-label{font-size:13px;font-weight:800;line-height:1.4;padding-top:12px}.morrow-form-label sup{color:#d05a70;font-size:13px;margin-left:3px}.morrow-form-control{display:grid;gap:6px;min-width:0}.morrow-form-control>input,.morrow-form-control>textarea,.morrow-form-control>select,.morrow-standalone-input{background:var(--site-background);border:1px solid var(--site-border);border-radius:11px;color:inherit;font:inherit;min-height:46px;padding:10px 13px;width:100%}.morrow-form-control>textarea{min-height:112px;resize:vertical}.morrow-form-control small{color:var(--site-muted);font-size:11px}.morrow-choice-group{display:grid;gap:9px}.morrow-choice-group.is-horizontal{display:flex;flex-wrap:wrap;gap:12px}.morrow-choice{align-items:center;display:flex;font-size:13px;gap:8px}.morrow-choice input{accent-color:var(--morrow-green)}.morrow-submit{display:flex}.morrow-submit.is-left{justify-content:flex-start}.morrow-submit.is-center{justify-content:center}.morrow-submit.is-right{justify-content:flex-end}.morrow-submit.is-inputAligned{padding-left:calc(var(--morrow-form-label-width,200px) + 12px)}.morrow-submit button{background:var(--morrow-green);border:0;border-radius:11px;color:#fff;cursor:pointer;font:inherit;font-weight:850;margin:0!important;min-height:46px;padding:11px 22px}
        .morrow-chart{align-items:stretch;background:#fff;border:1px solid var(--site-border);border-radius:20px;display:block;gap:0;height:100%;min-height:390px;padding:clamp(18px,3vw,30px);width:100%}.morrow-chart span{background:transparent;border-radius:0;flex:initial}.morrow-chart>h3{font-size:clamp(28px,4vw,52px);letter-spacing:-.055em;line-height:.98;margin:0}.morrow-chart-after-title{display:grid;gap:8px;margin-top:10px}.morrow-chart-layout{align-items:center;display:flex;gap:20px;height:calc(100% - 64px);margin-top:20px;min-height:280px}.morrow-chart-layout.is-top,.morrow-chart-layout.is-bottom,.morrow-chart-layout.is-none{display:block}.morrow-chart-graphic{flex:1;min-width:0}.morrow-chart-graphic.is-stacked{display:flex;flex-direction:column;height:100%}.morrow-chart-svg{display:block;height:100%;min-height:280px;width:100%}.morrow-chart-legend{display:flex;flex-wrap:wrap;font-size:12px;font-weight:800;gap:9px 16px;justify-content:center}.morrow-chart-layout.is-left .morrow-chart-legend,.morrow-chart-layout.is-right .morrow-chart-legend{align-content:center;flex-direction:column;flex-wrap:nowrap;justify-content:center;min-width:90px}.morrow-chart-legend span{align-items:center;display:flex;gap:7px}.morrow-chart-legend i{border-radius:99px;height:8px;width:22px}.morrow-chart-graphic>.morrow-chart-legend:first-child{margin-bottom:10px}.morrow-chart-graphic>.morrow-chart-legend:last-child{margin-top:10px}
        .morrow-text-highlight{align-items:center;background:var(--morrow-apricot);border-radius:18px;display:flex;flex-direction:column;justify-content:center;min-height:170px;padding:clamp(22px,4vw,44px);text-align:center}.morrow-text-highlight.is-left{align-items:flex-start;text-align:left}.morrow-text-highlight.is-right{align-items:flex-end;text-align:right}.morrow-text-highlight h3{font-size:clamp(28px,4vw,52px);letter-spacing:-.05em;line-height:1;margin:0}.morrow-text-highlight p{font-size:16px;line-height:1.6;margin:14px 0 0;max-width:700px}.morrow-flow-slot{display:grid;gap:10px;width:100%}
        .morrow-quote{background:#f8f3f1;border:0;border-left:0;border-radius:20px;display:grid;font-size:1rem;gap:13px;margin:0;overflow:hidden;padding:clamp(22px,4vw,38px);position:relative}.morrow-quote.has-leftLine{border-left:5px solid var(--morrow-green)}.morrow-quote.is-slant{clip-path:polygon(3% 0,100% 0,97% 100%,0 100%)}.morrow-quote.is-arch{border-radius:90px 90px 20px 20px}.morrow-quote.is-wave{border-radius:44px 14px 44px 14px}.morrow-quote.is-jagged{clip-path:polygon(0 5%,3% 0,6% 5%,9% 0,12% 5%,15% 0,100% 0,100% 100%,0 100%)}.morrow-quote p{font-size:clamp(18px,2.5vw,28px);font-weight:650;line-height:1.45;margin:0;white-space:pre-line}.morrow-quote footer{align-items:center;display:flex;flex-wrap:wrap;gap:10px}.morrow-quote footer.is-center{justify-content:center}.morrow-quote footer.is-right{justify-content:flex-end}.morrow-quote-media{height:46px;width:46px}.morrow-quote-media>[data-shop-block-part],.morrow-quote-media img{border-radius:50%;height:100%!important;overflow:hidden;width:100%!important}.morrow-quote-icon{justify-self:start}
        .morrow-badge{align-items:center;background:var(--morrow-apricot);border-radius:999px;display:inline-flex;font-size:12px;font-weight:850;gap:7px;padding:7px 12px;width:max-content}.morrow-badge.is-primary{background:var(--morrow-green);color:#fff}.morrow-badge.is-success{background:#d8f3e7;color:#075e43}.morrow-badge.is-info{background:var(--morrow-blue)}.morrow-badge.is-warning{background:#ffe1a8}.morrow-badge.is-danger{background:#f9ccd3;color:#811f32}.morrow-badge.is-dark{background:var(--morrow-ink);color:#fff}.morrow-badge-icons{align-items:center;display:flex}.morrow-cta-badge{align-items:center;background:#fff;border:1px solid var(--site-border);border-radius:999px;display:inline-flex;gap:8px;max-width:100%;min-height:42px;padding:6px 12px;width:max-content}.morrow-cta-title{font-size:12px;font-weight:750;white-space:nowrap}.morrow-cta-badge>a{color:var(--morrow-green);font-size:12px;font-weight:850;text-decoration:underline!important;text-underline-offset:3px}.morrow-cta-badge>[data-shop-block-part]{align-items:center;display:flex}
        .morrow-social{align-items:center;display:flex;gap:12px}.morrow-social.has-title-top{align-items:flex-start;flex-direction:column}.morrow-social.has-title-left>strong{margin-right:4px}.morrow-social.is-center{justify-content:center}.morrow-social.is-right{justify-content:flex-end}.morrow-social-items{display:flex;flex-wrap:wrap;gap:8px}.morrow-social-items>[data-shop-block-part]{align-items:center;display:flex}.morrow-social-link{align-items:center;border:1px solid var(--site-border);display:inline-flex;justify-content:center;transition:transform .18s ease,background .18s ease,box-shadow .18s ease}.morrow-social-link:hover{transform:translateY(-2px)}.morrow-social-link.is-link-underlineOnHover:hover{box-shadow:inset 0 -2px currentColor}.morrow-social .morrow-social-link>span{display:none}
        .morrow-inner-card{background:#fff;border:1px solid var(--site-border);border-radius:18px;overflow:hidden}.morrow-inner-card>img{aspect-ratio:16/10;display:block;object-fit:cover;width:100%}.morrow-inner-card.is-image-left,.morrow-inner-card.is-image-right{align-items:stretch;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.2fr)}.morrow-inner-card.is-image-right>img{order:2}.morrow-inner-card.is-image-bottom>img{order:2}.morrow-card-content{display:grid;gap:9px;padding:20px}.morrow-card-content h3,.morrow-card-content p{margin:0}.morrow-card-content p{color:var(--site-muted);line-height:1.55}.morrow-card-content>a,.morrow-inner-product>a{align-items:center;display:inline-flex;justify-content:center;width:max-content}.morrow-countdown-block{display:grid;gap:14px}.morrow-countdown-block>strong{font-size:13px;text-transform:uppercase}.morrow-countdown-block>div{display:grid;gap:8px;grid-template-columns:repeat(4,minmax(0,1fr))}.morrow-countdown-block>div>span{background:#fff;border:1px solid var(--site-border);border-radius:13px;display:grid;padding:12px;text-align:center}.morrow-countdown-block b{font-size:24px}.morrow-countdown-block small{color:var(--site-muted);font-size:9px;text-transform:uppercase}
        .morrow-rating{align-items:center;display:flex;gap:10px}.morrow-rating.is-title-top{align-items:flex-start;flex-direction:column}.morrow-rating-icons{align-items:center;color:#e3a20a;display:inline-flex;gap:2px;letter-spacing:2px}.morrow-rating-icons svg{height:1em;width:1em}.morrow-rating.is-xs{font-size:11px}.morrow-rating.is-sm{font-size:13px}.morrow-rating.is-lg{font-size:20px}.morrow-rating.is-xl{font-size:24px}.morrow-progress{background:transparent;border-radius:0;display:grid;gap:5px;height:auto;overflow:visible;width:100%}.morrow-progress small span{background:transparent;display:inline;height:auto}.morrow-progress-track{border-radius:999px;height:18px;overflow:hidden;position:relative}.morrow-progress-track>span{border-radius:inherit;display:block;height:100%}.morrow-progress-track>span.is-striped{background-image:repeating-linear-gradient(45deg,rgba(255,255,255,.28) 0 7px,transparent 7px 14px)}.morrow-progress-track>small{align-items:center;display:flex;font-size:9px;font-weight:800;gap:4px;inset:0;justify-content:center;position:absolute}.morrow-progress.is-after{align-items:center;display:flex}.morrow-progress.is-after .morrow-progress-track{flex:1}.morrow-map-frame{border:0;border-radius:16px;min-height:250px;width:100%}.morrow-map-placeholder{align-items:center;display:flex;gap:10px;justify-content:center;min-height:160px}.morrow-table-wrap{overflow:auto}.morrow-table-wrap table{border-collapse:collapse;min-width:100%}.morrow-table-wrap td{border:1px solid var(--site-border);padding:10px 12px}.morrow-icon-text{align-items:flex-start;display:flex;gap:12px}.morrow-icon-text>span{align-items:center;background:var(--morrow-blue);border-radius:11px;display:flex;height:42px;justify-content:center;width:42px}.morrow-icon-text>div{display:grid;gap:4px}.morrow-icon-text>div>span{color:var(--site-muted)}.morrow-icon-list-icon{align-items:center;display:inline-flex;justify-content:center}.morrow-inner-product{background:#fff;border:1px solid var(--site-border);border-radius:16px;display:grid;gap:8px;overflow:hidden;padding:16px}.morrow-inner-product.is-image-left,.morrow-inner-product.is-image-right{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.morrow-inner-product.is-image-right>img{order:2}.morrow-inner-product.is-image-bottom>img{order:5}.morrow-inner-product img{margin:-16px -16px 8px;object-fit:cover;width:calc(100% + 32px)}.morrow-inner-product p{color:var(--site-muted);margin:0}
        .morrow-column.has-commerce-runtime{gap:clamp(18px,3vw,34px)}.morrow-commerce-runtime-products{min-width:0;width:100%}.morrow-commerce-runtime-categories{display:grid;gap:10px;grid-template-columns:repeat(4,minmax(0,1fr));width:100%}.morrow-commerce-runtime-categories>a{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:18px;color:var(--site-on-surface);display:grid;gap:14px;grid-template-columns:auto 1fr auto;min-height:96px;padding:20px;text-decoration:none;transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease}.morrow-commerce-runtime-categories>a:hover{border-color:var(--site-border-strong);box-shadow:0 16px 40px var(--site-shadow-color);transform:translateY(-3px)}.morrow-commerce-runtime-categories>a>span{color:var(--site-muted);font-size:11px;font-weight:850;letter-spacing:.12em}.morrow-commerce-runtime-categories>a>strong{font-size:clamp(15px,1.5vw,19px)}
        @media(max-width:960px){.morrow-commerce-runtime-categories{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:560px){.morrow-commerce-runtime-categories{grid-template-columns:1fr}}
        @media(max-width:767px){.morrow-standalone-region{padding-bottom:28px;padding-top:28px}.morrow-standalone-inner{min-height:0!important}.morrow-form-field{grid-template-columns:1fr!important}.morrow-form-label{padding-top:0}.morrow-submit.is-inputAligned{padding-left:0}.morrow-chart{min-height:340px;padding:18px}.morrow-chart>h3{font-size:32px}.morrow-chart-layout{height:auto;min-height:250px}.morrow-chart-layout.is-left,.morrow-chart-layout.is-right{align-items:stretch;display:flex;flex-direction:column}.morrow-chart-layout.is-left>.morrow-chart-legend,.morrow-chart-layout.is-right>.morrow-chart-legend{flex-direction:row;flex-wrap:wrap}.morrow-chart-svg{min-height:240px}.morrow-text-highlight{width:100%!important}.morrow-quote{width:100%!important}.morrow-countdown-block>div{grid-template-columns:repeat(2,1fr)}}
        .morrow-region-header{padding-top:var(--morrow-header-padding-top,0)!important}
        @media(max-width:767px){.morrow-text-highlight,.morrow-quote{width:var(--morrow-mobile-content-width,100%)!important}}
        .morrow-inner-button{gap:9px}.morrow-inner-button:disabled{cursor:not-allowed;opacity:.5;transform:none}
        .morrow-embed-shell,.morrow-map-shell{background:var(--site-surface);border:1px solid var(--site-border);border-radius:var(--site-radius);min-height:180px;overflow:hidden;position:relative;width:100%}.morrow-embed-frame,.morrow-map-shell .morrow-map-frame{border:0;border-radius:inherit;height:100%;inset:0;min-height:100%;position:absolute;width:100%}.morrow-map-caption{align-items:center;background:color-mix(in srgb,var(--site-inverse) 88%,transparent);border:1px solid color-mix(in srgb,var(--site-on-inverse) 16%,transparent);border-radius:999px;bottom:14px;color:var(--site-on-inverse);display:flex;font-size:12px;font-weight:800;gap:8px;left:14px;max-width:calc(100% - 28px);overflow:hidden;padding:9px 12px;position:absolute;text-overflow:ellipsis;white-space:nowrap}
        .morrow-payment-block,.morrow-avatar-block{display:grid;gap:10px}.morrow-payment-block>strong,.morrow-avatar-block>strong{color:var(--site-text);font-size:12px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}.morrow-payment-strip{color:var(--site-on-surface);flex-wrap:wrap;font-size:11px;font-weight:800}.morrow-payment-strip span{align-items:center;background:var(--site-surface);border-color:var(--site-border);color:inherit;display:inline-flex;font:inherit;gap:8px;min-height:38px;padding:8px 12px}.morrow-payment-strip i{align-items:center;color:var(--site-primary);display:inline-flex;font-size:11px;font-style:normal;font-weight:950;letter-spacing:-3px;min-width:18px}.morrow-payment-strip b{font:inherit;white-space:nowrap}.morrow-payment-block.is-compact .morrow-payment-strip span{background:transparent;border:0;padding-inline:3px}.morrow-payment-strip [data-payment-brand=mastercard] i{color:var(--site-accent)}
        .morrow-avatar-block .morrow-avatars{color:var(--site-text);flex-wrap:wrap;font-size:12px}.morrow-avatar-block .morrow-avatars>span{align-items:center;background:transparent;border:0;border-radius:999px;color:inherit;display:inline-flex;font:inherit;height:auto;margin:0;width:auto}.morrow-avatar-block .morrow-avatars i{align-items:center;background:var(--site-primary-soft);border:3px solid var(--site-surface);border-radius:50%;color:var(--site-on-surface);display:inline-flex;font-size:11px;font-style:normal;font-weight:900;height:42px;justify-content:center;width:42px}.morrow-avatar-block .morrow-avatars b{color:inherit;font:inherit;padding:0 10px 0 5px}.morrow-avatar-block.is-stack .morrow-avatars>span+span{margin-left:-13px}.morrow-avatar-block.is-pills .morrow-avatars>span{background:var(--site-surface);border:1px solid var(--site-border);padding:3px}
        .morrow-account-dashboard{align-items:start;display:grid;gap:clamp(24px,4vw,54px);grid-template-columns:minmax(230px,280px) minmax(0,1fr);padding:8px 0 92px}.morrow-account-sidebar{background:var(--site-surface);border:1px solid var(--site-border);border-radius:26px;box-shadow:0 24px 64px var(--site-shadow-color);overflow:hidden;padding:16px;position:sticky;top:24px}.morrow-account-identity{align-items:center;background:var(--site-primary-soft);border:1px solid color-mix(in srgb,var(--site-primary) 22%,var(--site-border));border-radius:20px;display:flex;gap:13px;padding:18px}.morrow-account-identity .morrow-avatar{background:var(--site-accent);color:var(--site-on-accent);flex:0 0 auto;font-size:17px;height:54px;margin:0;width:54px}.morrow-account-identity>div:last-child{display:grid;gap:3px;min-width:0}.morrow-account-identity strong,.morrow-account-identity span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.morrow-account-identity strong{font-size:14px}.morrow-account-identity span{color:var(--site-muted);font-size:11px}.morrow-account-sidebar nav{display:grid;gap:5px;margin-top:14px}.morrow-account-sidebar nav button{align-items:center;background:transparent;border:0;border-radius:14px;color:var(--site-muted);cursor:pointer;display:grid;font:inherit;font-size:13px;font-weight:800;gap:11px;grid-template-columns:auto 1fr auto;padding:12px 13px;text-align:left;transition:background .18s ease,color .18s ease,transform .18s ease;width:100%}.morrow-account-sidebar nav button:hover{background:var(--site-background);color:var(--site-text);transform:translateX(2px)}.morrow-account-sidebar nav button.is-active{background:var(--site-primary);color:var(--site-on-primary);box-shadow:0 10px 24px color-mix(in srgb,var(--site-primary) 22%,transparent)}.morrow-account-sidebar nav button>svg:last-child{opacity:.55}.morrow-account-logout{align-items:center;background:transparent;border:0;border-top:1px solid var(--site-border);color:var(--site-muted);cursor:pointer;display:flex;font:inherit;font-size:12px;font-weight:800;gap:9px;margin-top:13px;padding:16px 12px 4px;width:100%}.morrow-account-logout:disabled{cursor:default;opacity:.45}.morrow-account-main{min-width:0}.morrow-account-heading{border-bottom:1px solid var(--site-border);display:grid;gap:7px;margin-bottom:24px;padding:3px 0 22px}.morrow-account-heading>span,.morrow-account-panel-head>div>span,.morrow-account-form-head>div>span{color:var(--site-primary);font-size:10px;font-weight:900;letter-spacing:.14em;text-transform:uppercase}.morrow-account-heading h1{font-size:clamp(38px,5vw,66px);letter-spacing:-.055em;line-height:.94;margin:0}.morrow-account-section{display:grid;gap:20px}.morrow-account-stats{display:grid;gap:12px;grid-template-columns:repeat(3,minmax(0,1fr));margin:0}.morrow-account-stats>button{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:20px;color:inherit;cursor:pointer;display:grid;font:inherit;gap:7px;grid-template-columns:auto 1fr auto;padding:18px;text-align:left;transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease}.morrow-account-stats>button:hover{border-color:var(--site-primary);box-shadow:0 18px 42px var(--site-shadow-color);transform:translateY(-3px)}.morrow-account-stats>button>span:nth-child(2){color:var(--site-muted);font-size:11px;font-weight:800}.morrow-account-stats>button>strong{font-size:26px;grid-column:2}.morrow-account-stats>button>svg{grid-column:3;grid-row:1/3}.morrow-account-stat-icon{align-items:center;background:var(--site-primary-soft);border-radius:13px;color:var(--site-primary);display:flex;grid-row:1/3;height:44px;justify-content:center;width:44px}.morrow-account-overview-grid{display:grid;gap:14px;grid-template-columns:1.15fr .85fr}.morrow-account-panel{background:var(--site-surface);border:1px solid var(--site-border);border-radius:22px;min-height:230px;padding:22px}.morrow-account-panel.is-order{background:var(--site-primary-soft)}.morrow-account-panel.is-address{background:var(--site-accent-soft)}.morrow-account-panel-head{align-items:start;display:flex;justify-content:space-between}.morrow-account-panel-head h2,.morrow-account-section-intro h2,.morrow-account-form-head h2,.morrow-account-order-groups h2{font-size:22px;letter-spacing:-.035em;margin:5px 0 0}.morrow-account-panel-head>button,.morrow-account-form-head>button{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:50%;color:var(--site-text);cursor:pointer;display:flex;height:40px;justify-content:center;width:40px}.morrow-account-muted{color:var(--site-muted);font-size:13px;line-height:1.65;margin:35px 0 0}.morrow-account-latest-order{align-items:center;border-top:1px solid var(--site-border);display:grid;gap:13px;grid-template-columns:minmax(0,1fr) auto;margin-top:32px;padding-top:20px}.morrow-account-latest-order>div{display:grid;gap:3px}.morrow-account-latest-order>div span{color:var(--site-muted);font-size:11px}.morrow-account-latest-order>b{font-size:17px}.morrow-account-default-address{align-items:flex-start;border-top:1px solid var(--site-border);display:flex;gap:14px;margin-top:32px;padding-top:20px}.morrow-account-default-address>span{align-items:center;background:var(--site-surface);border-radius:13px;color:var(--site-primary);display:flex;height:46px;justify-content:center;width:46px}.morrow-account-default-address>div{display:grid;gap:4px}.morrow-account-default-address p{color:var(--site-muted);font-size:12px;line-height:1.55;margin:0}.morrow-account-status{background:var(--site-primary-soft);border-radius:999px;color:var(--site-primary);font-size:10px;font-weight:900;padding:7px 10px;white-space:nowrap}.morrow-account-status.is-completed,.morrow-account-status.is-done,.morrow-account-status.is-delivered{background:#d9f3e5;color:#116348}.morrow-account-status.is-cancelled,.morrow-account-status.is-canceled{background:#f8d8dc;color:#8f2938}.morrow-account-status.is-processing,.morrow-account-status.is-shipped{background:color-mix(in srgb,var(--site-accent) 42%,var(--site-surface));color:var(--site-text)}.morrow-account-loading{background:var(--site-surface);border:1px solid var(--site-border);border-radius:20px;color:var(--site-muted);padding:40px;text-align:center}.morrow-account-empty{align-items:center;background:var(--site-surface);border:1px dashed var(--site-border-strong);border-radius:22px;display:flex;flex-direction:column;justify-content:center;min-height:310px;padding:38px;text-align:center}.morrow-account-empty>span{align-items:center;background:var(--site-primary-soft);border-radius:18px;color:var(--site-primary);display:flex;height:60px;justify-content:center;margin-bottom:14px;width:60px}.morrow-account-empty>strong{font-size:20px}.morrow-account-empty p{color:var(--site-muted);line-height:1.6;margin:8px 0 0;max-width:470px}.morrow-account-order-groups{display:grid;gap:28px}.morrow-account-order-groups>section{display:grid;gap:13px}.morrow-account-order-list{display:grid;gap:9px}.morrow-account-order-list article{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:18px;display:grid;gap:14px;grid-template-columns:auto minmax(0,1fr) auto auto auto;padding:16px}.morrow-account-order-mark{align-items:center;background:var(--site-primary-soft);border-radius:13px;color:var(--site-primary);display:flex;height:46px;justify-content:center;width:46px}.morrow-account-order-copy{display:grid;gap:2px}.morrow-account-order-copy>span,.morrow-account-order-copy>small{color:var(--site-muted);font-size:11px}.morrow-account-order-copy>small{margin-top:3px}.morrow-account-secondary-button{align-items:center;background:transparent;border:1px solid var(--site-border);border-radius:12px;color:inherit;cursor:pointer;display:inline-flex;font:inherit;font-size:11px;font-weight:850;gap:7px;justify-content:center;min-height:40px;padding:9px 13px}.morrow-account-secondary-button:hover{border-color:var(--site-primary);color:var(--site-primary)}.morrow-account-secondary-button:disabled{cursor:not-allowed;opacity:.45}.morrow-account-success{background:#e4f5ec;border:1px solid #b5dec9;border-radius:13px;color:#155e45;font-size:12px;margin:0;padding:12px 14px}.morrow-account-section-intro{align-items:end;display:flex;gap:20px;justify-content:space-between}.morrow-account-section-intro p{color:var(--site-muted);line-height:1.6;margin:8px 0 0;max-width:620px}.morrow-account-section-intro .morrow-primary-button{flex:0 0 auto}.morrow-account-address-grid{display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-account-address-grid article{align-items:start;background:var(--site-surface);border:1px solid var(--site-border);border-radius:20px;display:grid;gap:13px;grid-template-columns:auto minmax(0,1fr);padding:18px;position:relative}.morrow-account-address-icon{align-items:center;background:var(--site-primary-soft);border-radius:14px;color:var(--site-primary);display:flex;height:48px;justify-content:center;width:48px}.morrow-account-address-grid article p{color:var(--site-muted);font-size:12px;line-height:1.55;margin:5px 0 0}.morrow-account-address-flags{display:flex;flex-wrap:wrap;gap:5px;margin-top:11px}.morrow-account-address-flags span{background:var(--site-background);border-radius:999px;font-size:9px;font-weight:850;padding:6px 9px}.morrow-account-address-actions{display:flex;gap:10px;grid-column:1/-1;justify-content:flex-end}.morrow-account-address-actions button{background:transparent;border:0;color:var(--site-primary);cursor:pointer;font:inherit;font-size:11px;font-weight:850}.morrow-account-address-actions button:disabled{opacity:.4}.morrow-account-address-form,.morrow-profile-form,.morrow-account-password-form{background:var(--site-surface);border:1px solid var(--site-border);border-radius:22px;padding:22px}.morrow-account-address-form{display:grid;gap:18px}.morrow-account-form-head{align-items:start;display:flex;grid-column:1/-1;justify-content:space-between}.morrow-account-form-grid{display:grid;gap:13px;grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-account-form-grid .is-wide{grid-column:1/-1}.morrow-account-form-grid label,.morrow-profile-form label,.morrow-account-password-form label{display:flex;flex-direction:column;font-size:11px;font-weight:850;gap:7px}.morrow-account-form-grid input,.morrow-account-form-grid select,.morrow-profile-form input,.morrow-account-password-form input{background:var(--site-background);border:1px solid var(--site-border);border-radius:12px;color:inherit;font:inherit;min-height:46px;padding:10px 12px;width:100%}.morrow-account-form-grid input:focus,.morrow-account-form-grid select:focus,.morrow-profile-form input:focus,.morrow-account-password-form input:focus{border-color:var(--site-primary);outline:3px solid color-mix(in srgb,var(--site-primary) 12%,transparent)}.morrow-account-checks{display:grid;gap:9px}.morrow-account-checks label,.morrow-account-newsletter{align-items:center!important;display:flex!important;flex-direction:row!important;font-size:12px!important;font-weight:750!important;gap:9px!important}.morrow-account-checks input,.morrow-account-newsletter input{accent-color:var(--site-primary);height:17px;width:17px}.morrow-account-form-actions{display:flex;gap:10px;justify-content:flex-end}.morrow-account-favorites-grid{display:grid;gap:16px;grid-template-columns:repeat(3,minmax(0,1fr))}.morrow-account-favorites-grid article{background:var(--site-surface);border:1px solid var(--site-border);border-radius:18px;overflow:hidden;position:relative}.morrow-account-favorites-grid article>a{display:grid;gap:5px;padding:12px}.morrow-account-favorite-image{align-items:center;aspect-ratio:4/3;background:var(--site-primary-soft);border-radius:13px;color:var(--site-primary);display:flex;justify-content:center;margin-bottom:7px;overflow:hidden}.morrow-account-favorite-image img{height:100%;object-fit:cover;width:100%}.morrow-account-favorites-grid article>a>span{color:var(--site-muted);font-size:9px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}.morrow-account-favorites-grid article>a>b{font-size:13px;margin-top:5px}.morrow-account-favorites-grid article>button{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:50%;color:var(--site-primary);cursor:pointer;display:flex;height:36px;justify-content:center;position:absolute;right:20px;top:20px;width:36px}.morrow-account-settings-grid{align-items:start;grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-account-settings-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:22px;display:grid;gap:18px;min-height:280px;padding:22px}.morrow-account-settings-card>p{color:var(--site-muted);font-size:12px;line-height:1.6;margin:0}.morrow-account-settings-card>.morrow-primary-button,.morrow-account-settings-card>.morrow-account-secondary-button{align-self:end;justify-self:start}.morrow-account-settings-summary{background:var(--site-background);border:1px solid var(--site-border);border-radius:16px;display:grid;padding:4px 14px}.morrow-account-settings-summary>div{display:grid;gap:3px;padding:12px 0}.morrow-account-settings-summary>div+div{border-top:1px solid var(--site-border)}.morrow-account-settings-summary span{color:var(--site-muted);font-size:10px;font-weight:800}.morrow-account-settings-summary strong{font-size:13px;overflow-wrap:anywhere}.morrow-profile-form{display:grid;gap:14px;grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-profile-form .is-wide,.morrow-profile-form>button{grid-column:1/-1}.morrow-account-password-form{display:grid;gap:14px}.morrow-account-password-form>p{color:var(--site-muted);font-size:12px;line-height:1.6;margin:0}.morrow-account-form-icon{align-items:center;background:var(--site-primary-soft);border-radius:13px;color:var(--site-primary);display:flex;height:44px;justify-content:center;width:44px}.morrow-account-modal{align-items:center;background:color-mix(in srgb,var(--site-inverse) 58%,transparent);display:flex;inset:0;justify-content:center;opacity:0;overflow:auto;padding:24px;position:fixed;transition:opacity .24s ease;z-index:1400}.morrow-account-modal.is-open{opacity:1}.morrow-account-modal-sheet{background:var(--site-surface);border:1px solid var(--site-border);border-radius:26px;box-shadow:0 30px 90px color-mix(in srgb,var(--site-inverse) 30%,transparent);max-height:calc(100dvh - 48px);max-width:720px;overflow:auto;transform:translateY(34px) scale(.985);transition:transform .34s cubic-bezier(.22,1,.36,1);width:100%}.morrow-account-modal.is-open .morrow-account-modal-sheet{transform:translateY(0) scale(1)}.morrow-account-modal-sheet>.morrow-account-address-form,.morrow-account-modal-sheet>.morrow-profile-form,.morrow-account-modal-sheet>.morrow-account-password-form{border:0;border-radius:inherit;padding:clamp(22px,4vw,34px)}.morrow-account-modal-sheet .morrow-account-form-head>button{transition:background .18s ease,transform .18s ease}.morrow-account-modal-sheet .morrow-account-form-head>button:hover{background:var(--site-primary-soft);transform:rotate(6deg)}
        @media(max-width:1050px){.morrow-account-dashboard{grid-template-columns:220px minmax(0,1fr)}.morrow-account-overview-grid,.morrow-account-settings-grid{grid-template-columns:1fr}.morrow-account-favorites-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-account-order-list article{grid-template-columns:auto minmax(0,1fr) auto}.morrow-account-order-list article>.morrow-account-status{grid-column:2}.morrow-account-order-list article>b{grid-column:3;grid-row:1}.morrow-account-order-list article>.morrow-account-secondary-button{grid-column:2/4}}
        @media(max-width:767px){.morrow-account-dashboard{display:block;padding-bottom:48px}.morrow-account-sidebar{margin-bottom:22px;padding:10px;position:static}.morrow-account-identity{padding:11px}.morrow-account-identity .morrow-avatar{height:44px;width:44px}.morrow-account-sidebar nav{display:flex;gap:7px;margin-top:9px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none}.morrow-account-sidebar nav::-webkit-scrollbar{display:none}.morrow-account-sidebar nav button{display:flex;flex:0 0 auto;min-height:40px;padding:9px 12px;width:auto}.morrow-account-sidebar nav button>svg:last-child{display:none}.morrow-account-sidebar nav button:hover{transform:none}.morrow-account-logout{justify-content:center;margin-top:8px;padding-top:12px}.morrow-account-heading{margin-bottom:18px;padding-bottom:17px}.morrow-account-heading h1{font-size:42px}.morrow-account-stats{grid-template-columns:1fr}.morrow-account-overview-grid,.morrow-account-address-grid,.morrow-account-favorites-grid,.morrow-account-form-grid,.morrow-profile-form{grid-template-columns:1fr}.morrow-account-section-intro{align-items:stretch;flex-direction:column}.morrow-account-section-intro .morrow-primary-button{width:100%}.morrow-account-order-list article{grid-template-columns:auto minmax(0,1fr)}.morrow-account-order-list article>.morrow-account-status{grid-column:2}.morrow-account-order-list article>b{grid-column:2;grid-row:auto}.morrow-account-order-list article>.morrow-account-secondary-button{grid-column:1/-1}.morrow-account-latest-order{grid-template-columns:1fr}.morrow-account-latest-order>.morrow-account-status{justify-self:start}.morrow-account-form-grid .is-wide,.morrow-profile-form .is-wide,.morrow-profile-form>button{grid-column:1}.morrow-account-form-actions{flex-direction:column-reverse}.morrow-account-form-actions button{width:100%}.morrow-account-settings-card{min-height:0}.morrow-account-modal{align-items:flex-end;padding:12px 0 0}.morrow-account-modal-sheet{border-bottom:0;border-radius:24px 24px 0 0;max-height:92dvh;transform:translateY(100%)}.morrow-account-modal-sheet>.morrow-account-address-form,.morrow-account-modal-sheet>.morrow-profile-form,.morrow-account-modal-sheet>.morrow-account-password-form{padding:22px 18px 26px}}
        .morrow-account-modal.is-open{animation:morrowAccountBackdropIn .24s ease both}.morrow-account-modal-sheet{--morrow-account-modal-offset:34px;transform:translateY(var(--morrow-account-modal-offset)) scale(.985)}.morrow-account-modal.is-open .morrow-account-modal-sheet{animation:morrowAccountSheetIn .34s cubic-bezier(.22,1,.36,1) both}@keyframes morrowAccountBackdropIn{from{opacity:0}to{opacity:1}}@keyframes morrowAccountSheetIn{from{opacity:0;transform:translateY(var(--morrow-account-modal-offset)) scale(.985)}to{opacity:1;transform:translateY(0) scale(1)}}@media(max-width:767px){.morrow-account-sidebar nav{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));overflow:visible}.morrow-account-sidebar nav button{font-size:10px;gap:5px;justify-content:center;min-width:0;padding:9px 6px}.morrow-account-sidebar nav button span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.morrow-account-modal-sheet{--morrow-account-modal-offset:100%;transform:translateY(var(--morrow-account-modal-offset))}}
        .morrow-auth-form input{font-size:16px}.morrow-auth-form>div>h2{font-size:24px;letter-spacing:-.035em;margin:0}.morrow-auth-form>div>p{color:var(--site-muted);font-size:13px;line-height:1.6;margin:8px 0 0}.morrow-account-forgot-password{background:transparent;border:0;color:var(--site-primary);cursor:pointer;font:inherit;font-size:12px;font-weight:850;justify-self:start;padding:0;text-decoration:underline;text-underline-offset:4px}.morrow-auth-form>.morrow-account-form-actions{align-items:center}
        .morrow-account-order-actions{display:flex;flex-wrap:wrap;gap:7px;justify-content:flex-end}.morrow-order-detail{gap:20px}.morrow-order-detail-meta{display:grid;gap:10px;grid-template-columns:repeat(3,minmax(0,1fr))}.morrow-order-detail-meta>div{background:var(--site-background);border:1px solid var(--site-border);border-radius:14px;display:grid;gap:7px;padding:14px}.morrow-order-detail-meta>div>span{color:var(--site-muted);font-size:10px;font-weight:850;text-transform:uppercase}.morrow-order-detail-meta .morrow-account-status{justify-self:start}.morrow-order-detail-section{display:grid;gap:12px}.morrow-order-detail-section h3,.morrow-order-detail-addresses h3{font-size:16px;margin:0}.morrow-order-detail-items{border:1px solid var(--site-border);border-radius:16px;overflow:hidden}.morrow-order-detail-items article{align-items:center;display:grid;gap:12px;grid-template-columns:auto minmax(0,1fr) auto auto;padding:13px}.morrow-order-detail-items article+article{border-top:1px solid var(--site-border)}.morrow-order-detail-items article>div{display:grid;gap:3px}.morrow-order-detail-items article small,.morrow-order-detail-items article>span:nth-last-child(2){color:var(--site-muted);font-size:11px}.morrow-order-detail-summary dl{border:1px solid var(--site-border);border-radius:16px;margin:0;padding:8px 14px}.morrow-order-detail-summary dl>div{display:flex;gap:18px;justify-content:space-between;padding:9px 0}.morrow-order-detail-summary dl>div+div{border-top:1px solid var(--site-border)}.morrow-order-detail-summary dt{color:var(--site-muted);font-size:12px}.morrow-order-detail-summary dd{font-size:12px;font-weight:850;margin:0}.morrow-order-detail-summary .is-total dt,.morrow-order-detail-summary .is-total dd{color:var(--site-text);font-size:15px;font-weight:900}.morrow-order-detail-addresses{display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-order-detail-addresses section{background:var(--site-background);border:1px solid var(--site-border);border-radius:16px;padding:16px}.morrow-order-detail-addresses p{color:var(--site-muted);font-size:12px;line-height:1.6;margin:8px 0 0}
        @media(max-width:1050px){.morrow-account-order-list article>.morrow-account-order-actions{grid-column:2/4}}
        @media(max-width:767px){.morrow-account-order-list article>.morrow-account-order-actions{grid-column:1/-1;justify-content:stretch}.morrow-account-order-actions>.morrow-account-secondary-button{flex:1}.morrow-order-detail-meta,.morrow-order-detail-addresses{grid-template-columns:1fr}.morrow-order-detail-items article{grid-template-columns:auto minmax(0,1fr) auto}.morrow-order-detail-items article>b{grid-column:2/4}.morrow-auth-form>.morrow-account-form-actions{align-items:stretch}}
        .morrow-storefront{overflow-x:clip}
        .morrow-search-form{position:relative}.morrow-search-suggestions{background:var(--site-surface);border:1px solid var(--site-border);border-radius:16px;box-shadow:0 24px 64px var(--site-shadow-color);color:var(--site-text);display:grid;font-size:14px;gap:4px;left:0;max-height:min(68vh,560px);overflow:auto;padding:10px;position:absolute;text-align:left;top:calc(100% + 8px);width:max(100%,min(520px,calc(100vw - 36px)));z-index:100}.morrow-search-suggestion-group{display:grid;gap:3px}.morrow-search-suggestion-group>p{color:var(--site-muted);font-size:10px;font-weight:850;letter-spacing:.1em;margin:8px 9px 3px;text-transform:uppercase}.morrow-search-suggestion-group>div{display:grid}.morrow-search-suggestion-group a{align-items:center;border-radius:10px;display:flex;gap:12px;justify-content:space-between;min-height:44px;padding:8px 10px}.morrow-search-suggestion-group a:hover,.morrow-search-suggestion-group a:focus-visible,.morrow-search-suggestion-group a[aria-selected=true]{background:var(--site-primary-soft);outline:0}.morrow-search-suggestion-group a>span{display:grid;gap:2px;min-width:0}.morrow-search-suggestion-group a strong,.morrow-search-suggestion-group a>span>span{font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.morrow-search-suggestion-group a small{color:var(--site-muted);font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.morrow-search-suggestion-group a>b{font-size:12px;white-space:nowrap}.morrow-search-suggestion-state{color:var(--site-muted);font-size:13px;margin:0;padding:16px;text-align:center}.morrow-search-overlay .morrow-search-suggestions{font-size:14px}
        .morrow-catalog-layout{align-items:start;display:grid;gap:clamp(24px,4vw,52px);grid-template-columns:minmax(220px,280px) minmax(0,1fr);padding-top:32px}.morrow-catalog-results{min-width:0}.morrow-catalog-results>.morrow-product-grid{padding-top:0}.morrow-catalog-filters{background:var(--site-surface);border:1px solid var(--site-border);border-radius:16px;overflow:hidden;position:sticky;top:20px}.morrow-catalog-filters-head{border-bottom:1px solid var(--site-border);display:grid;gap:10px;padding:18px}.morrow-catalog-filters-head>div{align-items:start;display:flex;gap:10px;justify-content:space-between}.morrow-catalog-filters-head h2{font-size:18px;margin:0}.morrow-catalog-filters-head span{color:var(--site-muted);font-size:11px;font-weight:750}.morrow-catalog-filters-head>a{color:var(--site-primary);font-size:11px;font-weight:850}.morrow-catalog-facet{border-bottom:1px solid var(--site-border)}.morrow-catalog-facet:last-child{border-bottom:0}.morrow-catalog-facet>summary{align-items:center;cursor:pointer;display:flex;font-size:13px;font-weight:850;justify-content:space-between;list-style:none;padding:16px 18px}.morrow-catalog-facet>summary::-webkit-details-marker{display:none}.morrow-catalog-facet>summary span{align-items:center;display:flex;gap:7px}.morrow-catalog-facet>summary b{align-items:center;background:var(--site-primary);border-radius:999px;color:var(--site-on-primary);display:inline-flex;font-size:9px;height:19px;justify-content:center;min-width:19px;padding:0 5px}.morrow-catalog-facet>summary svg{transition:transform .16s ease}.morrow-catalog-facet[open]>summary svg{transform:rotate(180deg)}.morrow-catalog-filter-options{display:grid;gap:2px;padding:0 12px 15px}.morrow-catalog-filter-options>a{align-items:center;border-radius:9px;display:flex;font-size:12px;gap:8px;justify-content:space-between;padding:8px 7px}.morrow-catalog-filter-options>a:hover{background:var(--site-primary-soft)}.morrow-catalog-filter-options>a>span{align-items:center;display:flex;gap:7px;min-width:0}.morrow-catalog-filter-options>a>span>span{overflow:hidden;text-overflow:ellipsis}.morrow-catalog-filter-options>a>strong{color:var(--site-muted);font-size:10px}.morrow-catalog-filter-check{align-items:center;border:1px solid var(--site-border);border-radius:4px;display:flex;font-size:9px;font-style:normal;height:17px;justify-content:center;width:17px}.morrow-catalog-filter-options>a.is-selected .morrow-catalog-filter-check{background:var(--site-primary);border-color:var(--site-primary);color:var(--site-on-primary)}.morrow-catalog-filter-color{border:1px solid var(--site-border);border-radius:50%;display:block;height:13px;width:13px}.morrow-catalog-filter-options>button{background:transparent;border:0;color:var(--site-primary);cursor:pointer;font:inherit;font-size:11px;font-weight:850;padding:8px;text-align:left}.morrow-catalog-price{display:grid;gap:9px;grid-template-columns:1fr 1fr;padding:0 16px 16px}.morrow-catalog-price label{display:grid;gap:5px}.morrow-catalog-price label span{color:var(--site-muted);font-size:9px;font-weight:800}.morrow-catalog-price input{background:var(--site-background);border:1px solid var(--site-border);border-radius:8px;color:inherit;font:inherit;min-width:0;padding:8px;width:100%}.morrow-catalog-price button{background:var(--site-primary);border:0;border-radius:9px;color:var(--site-on-primary);cursor:pointer;font:inherit;font-size:11px;font-weight:850;grid-column:1/-1;padding:9px}.morrow-catalog-no-filters{color:var(--site-muted);font-size:12px;line-height:1.5;margin:0;padding:18px}.morrow-catalog-pagination{align-items:center;border-top:1px solid var(--site-border);display:grid;gap:16px;grid-template-columns:1fr auto 1fr;padding:22px 0}.morrow-catalog-pagination>a{align-items:center;display:flex;font-size:12px;font-weight:850;gap:6px}.morrow-catalog-pagination>a:last-child{justify-self:end}.morrow-catalog-pagination>div{align-items:center;display:flex;gap:3px}.morrow-catalog-pagination>div>span{align-items:center;display:flex}.morrow-catalog-pagination i{color:var(--site-muted);font-style:normal;padding:0 4px}.morrow-catalog-pagination>div a{align-items:center;border-radius:8px;display:flex;font-size:12px;font-weight:800;height:34px;justify-content:center;min-width:34px}.morrow-catalog-pagination>div a[aria-current=page]{background:var(--site-primary);color:var(--site-on-primary)}
        .morrow-search-results{display:grid;gap:42px;padding-top:20px}.morrow-search-result-group{display:grid;gap:16px}.morrow-search-result-group>h2{font-size:24px;margin:0}.morrow-search-result-links{display:grid;gap:10px;grid-template-columns:repeat(3,minmax(0,1fr))}.morrow-search-result-links>a{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:14px;display:flex;gap:12px;justify-content:space-between;min-height:76px;padding:16px}.morrow-search-result-links>a:hover{border-color:var(--site-primary)}.morrow-search-result-links>a>span{display:grid;gap:4px;min-width:0}.morrow-search-result-links small{color:var(--site-muted);font-size:10px}.morrow-search-result-group>.morrow-product-grid{padding-bottom:0;padding-top:0}.morrow-catalog-layout.is-search{padding-top:0}
        @media(max-width:1024px){.morrow-catalog-layout{grid-template-columns:minmax(210px,240px) minmax(0,1fr)}.morrow-search-result-links{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:767px){.morrow-search-suggestions{left:50%;transform:translateX(-50%);width:min(520px,calc(100vw - 36px))}.morrow-catalog-layout{display:block}.morrow-catalog-filters{margin-bottom:28px;position:static}.morrow-catalog-toolbar label{display:flex}.morrow-search-result-links{grid-template-columns:1fr}.morrow-catalog-pagination{gap:8px}.morrow-catalog-pagination>a{font-size:0}.morrow-catalog-pagination>a svg{height:18px;width:18px}.morrow-catalog-pagination>div a{height:30px;min-width:30px}}
        .morrow-region:has(.morrow-catalog-product-page.is-width-full) .morrow-region-inner{max-width:none;width:100%}.morrow-catalog-product-page{min-width:0}.morrow-catalog-product-page>.morrow-catalog-layout{padding-top:26px}.morrow-catalog-layout.is-without-sidebar{display:block}.morrow-catalog-sidebar{display:grid;gap:18px;min-width:0;position:sticky;top:20px}.morrow-catalog-sidebar .morrow-catalog-filters{position:static}.morrow-catalog-product-toolbar{align-items:center;border-block:1px solid var(--site-border);display:flex;gap:12px;justify-content:flex-end;padding:13px 0}.morrow-catalog-filter-menu-button{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:10px;color:inherit;cursor:pointer;display:flex;font:inherit;font-size:12px;font-weight:850;gap:7px;min-height:38px;padding:8px 12px}.morrow-catalog-filter-menu-button b{align-items:center;background:var(--site-primary);border-radius:999px;color:var(--site-on-primary);display:inline-flex;font-size:9px;height:18px;justify-content:center;min-width:18px}.morrow-catalog-inline-search{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:10px;display:flex;flex:1;gap:8px;max-width:440px;min-height:38px;padding-left:11px}.morrow-catalog-inline-search input{background:transparent;border:0;color:inherit;font:inherit;font-size:12px;min-width:0;outline:0;width:100%}.morrow-catalog-inline-search button{align-self:stretch;background:var(--site-primary);border:0;border-radius:0 9px 9px 0;color:var(--site-on-primary);cursor:pointer;font:inherit;font-size:11px;font-weight:850;padding:0 13px}.morrow-catalog-sort{align-items:center;display:flex;font-size:12px;font-weight:800;gap:9px}.morrow-catalog-sort select{background:var(--site-surface);border:1px solid var(--site-border);border-radius:10px;color:inherit;font:inherit;min-height:38px;padding:7px 9px}.morrow-catalog-categories{padding:12px 0}.morrow-catalog-categories>div{display:flex;flex-wrap:wrap;gap:8px}.morrow-catalog-categories a{align-items:center;display:flex;font-size:12px;font-weight:800;gap:9px;min-height:36px;padding:8px 10px}.morrow-catalog-categories>button{background:transparent;border:0;color:var(--site-primary);cursor:pointer;font:inherit;font-size:11px;font-weight:850;margin-top:8px;padding:6px}.morrow-catalog-categories.is-style-bordered a{border:1px solid var(--site-border);border-radius:9px}.morrow-catalog-categories.is-style-tabs{border-bottom:1px solid var(--site-border);padding-bottom:0}.morrow-catalog-categories.is-style-tabs a{border-bottom:2px solid transparent;border-radius:0;padding-inline:4px}.morrow-catalog-categories.is-style-tabs a:hover{border-color:var(--site-primary);color:var(--site-primary)}.morrow-catalog-categories.is-style-pills a{background:var(--site-surface);border:1px solid var(--site-border);border-radius:999px;padding-inline:14px}.morrow-catalog-categories.is-style-images>div,.morrow-catalog-categories.is-style-grid>div,.morrow-catalog-categories.is-style-largeImages>div{display:grid;grid-template-columns:repeat(6,minmax(0,1fr))}.morrow-catalog-categories.is-style-images a,.morrow-catalog-categories.is-style-grid a,.morrow-catalog-categories.is-style-largeImages a{align-items:stretch;border:1px solid var(--site-border);border-radius:14px;display:grid;gap:8px;overflow:hidden;padding:7px}.morrow-catalog-category-art{align-items:center;aspect-ratio:4/3;background:var(--site-primary-soft);border-radius:9px;color:var(--site-primary);display:flex;font-size:22px;font-weight:900;justify-content:center}.morrow-catalog-category-art.tone-1{background:color-mix(in srgb,var(--site-accent) 24%,var(--site-surface))}.morrow-catalog-category-art.tone-2{background:color-mix(in srgb,var(--site-primary) 15%,var(--site-surface))}.morrow-catalog-category-art.tone-3{background:color-mix(in srgb,var(--site-text) 10%,var(--site-surface))}.morrow-catalog-categories.is-style-grid>div{grid-template-columns:repeat(4,minmax(0,1fr))}.morrow-catalog-categories.is-style-largeImages>div{grid-template-columns:repeat(3,minmax(0,1fr))}.morrow-catalog-categories.is-style-largeImages .morrow-catalog-category-art{aspect-ratio:16/8;font-size:34px}.morrow-catalog-sidebar .morrow-catalog-categories{background:var(--site-surface);border:1px solid var(--site-border);border-radius:16px;padding:12px}.morrow-catalog-sidebar .morrow-catalog-categories>div{display:grid;grid-template-columns:1fr}.morrow-catalog-sidebar .morrow-catalog-categories a{min-width:0}.morrow-catalog-sidebar .morrow-catalog-categories.is-style-images .morrow-catalog-category-art,.morrow-catalog-sidebar .morrow-catalog-categories.is-style-grid .morrow-catalog-category-art,.morrow-catalog-sidebar .morrow-catalog-categories.is-style-largeImages .morrow-catalog-category-art{aspect-ratio:3/1;font-size:18px}.morrow-catalog-facet-title{align-items:center;display:flex;font-size:13px;font-weight:850;justify-content:space-between;padding:16px 18px}.morrow-catalog-facet-title span{align-items:center;display:flex;gap:7px}.morrow-catalog-facet-title b{align-items:center;background:var(--site-primary);border-radius:999px;color:var(--site-on-primary);display:inline-flex;font-size:9px;height:19px;justify-content:center;min-width:19px;padding:0 5px}.morrow-catalog-filter-drawer{inset:0;position:fixed;z-index:1500}.morrow-catalog-filter-drawer-backdrop{background:color-mix(in srgb,var(--site-inverse) 55%,transparent);border:0;inset:0;position:absolute;width:100%}.morrow-catalog-filter-drawer-panel{background:var(--site-background);bottom:0;box-shadow:-24px 0 64px var(--site-shadow-color);display:flex;flex-direction:column;max-width:420px;overflow:auto;position:absolute;right:0;top:0;width:min(92vw,420px)}.morrow-catalog-filter-drawer-head{align-items:center;border-bottom:1px solid var(--site-border);display:flex;font-size:18px;justify-content:space-between;padding:18px}.morrow-catalog-filter-drawer-head button{align-items:center;background:transparent;border:0;color:inherit;cursor:pointer;display:flex}.morrow-catalog-filter-drawer-panel>.morrow-catalog-filters{border:0;border-radius:0;position:static}.morrow-catalog-product-page .morrow-product-grid{grid-template-columns:repeat(var(--morrow-product-columns,4),minmax(0,1fr))}.morrow-product-grid.is-design-multiThumbnail{gap:18px 12px}.morrow-product-grid.is-design-multiThumbnail .morrow-product-visual{aspect-ratio:1}.morrow-product-grid.is-design-multiThumbnail .morrow-product-copy{padding-top:9px}.morrow-product-grid.is-design-multiThumbnail .morrow-product-meta{display:none}.morrow-product-grid.is-design-multiChips{gap:12px}.morrow-product-grid.is-design-multiChips .morrow-product-card{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:999px;display:grid;grid-template-columns:64px minmax(0,1fr);padding:5px}.morrow-product-grid.is-design-multiChips .morrow-product-visual{aspect-ratio:1;border-radius:50%;grid-row:1/3}.morrow-product-grid.is-design-multiChips .morrow-product-copy{display:block;padding:5px 12px}.morrow-product-grid.is-design-multiChips .morrow-product-copy>strong{display:block;margin-top:4px}.morrow-product-grid.is-design-multiChips .morrow-product-meta,.morrow-product-grid.is-design-multiChips .morrow-favorite{display:none}.morrow-product-grid.is-design-multiCards .morrow-product-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:18px;padding:9px}.morrow-product-grid.is-design-multiCards .morrow-product-visual{border-radius:12px}.morrow-product-grid.is-design-multiBento{grid-auto-flow:dense}.morrow-product-grid.is-design-multiBento .morrow-product-card:nth-child(5n+1){grid-column:span 2;grid-row:span 2}.morrow-product-grid.is-design-multiBento.is-single-column .morrow-product-card:nth-child(5n+1){grid-column:span 1;grid-row:span 1}.morrow-product-grid.is-design-multiBento .morrow-product-card:nth-child(5n+1) .morrow-product-visual{aspect-ratio:1}.morrow-product-grid.is-design-singleThumbnails,.morrow-product-grid.is-design-singleCards,.morrow-product-grid.is-design-singleGrid,.morrow-product-grid.is-design-singleShowcase,.morrow-product-grid.is-design-singleCondensed,.morrow-product-grid.is-design-singleLarge{grid-template-columns:1fr}.morrow-product-grid.is-design-singleThumbnails .morrow-product-card,.morrow-product-grid.is-design-singleCards .morrow-product-card,.morrow-product-grid.is-design-singleGrid .morrow-product-card,.morrow-product-grid.is-design-singleShowcase .morrow-product-card,.morrow-product-grid.is-design-singleCondensed .morrow-product-card{align-items:center;border-bottom:1px solid var(--site-border);display:grid;grid-template-columns:112px minmax(0,1fr) auto;min-height:118px;padding:10px 0}.morrow-product-grid.is-design-singleThumbnails .morrow-product-visual,.morrow-product-grid.is-design-singleCards .morrow-product-visual,.morrow-product-grid.is-design-singleGrid .morrow-product-visual,.morrow-product-grid.is-design-singleShowcase .morrow-product-visual,.morrow-product-grid.is-design-singleCondensed .morrow-product-visual{aspect-ratio:1;grid-row:1/3}.morrow-product-grid.is-design-singleThumbnails .morrow-product-copy,.morrow-product-grid.is-design-singleCards .morrow-product-copy,.morrow-product-grid.is-design-singleGrid .morrow-product-copy,.morrow-product-grid.is-design-singleShowcase .morrow-product-copy,.morrow-product-grid.is-design-singleCondensed .morrow-product-copy{padding:8px 18px}.morrow-product-grid.is-design-singleThumbnails .morrow-product-meta,.morrow-product-grid.is-design-singleCards .morrow-product-meta,.morrow-product-grid.is-design-singleGrid .morrow-product-meta,.morrow-product-grid.is-design-singleShowcase .morrow-product-meta,.morrow-product-grid.is-design-singleCondensed .morrow-product-meta{gap:14px;padding-right:6px}.morrow-product-grid.is-design-singleThumbnails .morrow-product-card{grid-template-columns:84px minmax(0,1fr) auto;min-height:92px}.morrow-product-grid.is-design-singleCards .morrow-product-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:18px;grid-template-columns:160px minmax(0,1fr) auto;overflow:hidden;padding:9px}.morrow-product-grid.is-design-singleGrid .morrow-product-card{grid-template-columns:120px minmax(0,1fr) minmax(130px,auto)}.morrow-product-grid.is-design-singleShowcase .morrow-product-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:20px;grid-template-columns:minmax(240px,42%) minmax(0,1fr) auto;overflow:hidden;padding:12px}.morrow-product-grid.is-design-singleShowcase .morrow-product-visual{aspect-ratio:16/10}.morrow-product-grid.is-design-singleCondensed{gap:0}.morrow-product-grid.is-design-singleCondensed .morrow-product-card{grid-template-columns:58px minmax(0,1fr) auto;min-height:68px;padding:5px 0}.morrow-product-grid.is-design-singleCondensed .morrow-product-copy{padding-block:4px}.morrow-product-grid.is-design-singleLarge .morrow-product-card{border-bottom:1px solid var(--site-border);padding-bottom:28px}.morrow-product-grid.is-design-singleLarge .morrow-product-visual{aspect-ratio:16/7}.morrow-product-visual>span.is-ribbon-soldOut,.morrow-product-visual>span.is-ribbon-outOfStock{background:var(--site-inverse);color:var(--site-on-inverse)}.morrow-product-visual>span.is-ribbon-new{background:var(--site-primary);color:var(--site-on-primary)}
        @media(max-width:1024px){.morrow-catalog-categories.is-style-images>div{grid-template-columns:repeat(3,minmax(0,1fr))}.morrow-catalog-categories.is-style-grid>div,.morrow-catalog-categories.is-style-largeImages>div{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:767px){.morrow-catalog-product-toolbar{align-items:stretch;flex-wrap:wrap}.morrow-catalog-inline-search{flex-basis:100%;max-width:none;order:-1}.morrow-catalog-sort{flex:1;justify-content:flex-end}.morrow-catalog-sort select{min-width:0;max-width:150px}.morrow-catalog-product-page>.morrow-catalog-layout{padding-top:18px}.morrow-catalog-product-page .morrow-product-grid{grid-template-columns:repeat(var(--morrow-product-mobile-columns,2),minmax(0,1fr))}.morrow-product-grid.is-design-multiBento .morrow-product-card:nth-child(5n+1){grid-column:auto;grid-row:auto}.morrow-product-grid.is-design-singleThumbnails,.morrow-product-grid.is-design-singleCards,.morrow-product-grid.is-design-singleGrid,.morrow-product-grid.is-design-singleShowcase,.morrow-product-grid.is-design-singleCondensed,.morrow-product-grid.is-design-singleLarge{grid-template-columns:1fr}.morrow-product-grid.is-design-singleCards .morrow-product-card,.morrow-product-grid.is-design-singleGrid .morrow-product-card,.morrow-product-grid.is-design-singleShowcase .morrow-product-card{grid-template-columns:88px minmax(0,1fr);min-height:96px}.morrow-product-grid.is-design-singleCards .morrow-product-meta,.morrow-product-grid.is-design-singleGrid .morrow-product-meta,.morrow-product-grid.is-design-singleShowcase .morrow-product-meta{grid-column:2;padding:0 12px 8px}.morrow-product-grid.is-design-singleLarge .morrow-product-visual{aspect-ratio:4/3}.morrow-catalog-categories.is-style-images>div,.morrow-catalog-categories.is-style-grid>div,.morrow-catalog-categories.is-style-largeImages>div{grid-template-columns:repeat(2,minmax(0,1fr))}.morrow-catalog-sidebar{position:static}}
        .morrow-product-grid.is-design-multiChips{gap:14px}
        .morrow-product-grid.is-design-multiChips .morrow-product-card{align-items:center;background:var(--site-surface);border:1px solid var(--site-border);border-radius:22px;box-shadow:0 10px 28px var(--site-shadow-color);display:grid;grid-template-columns:72px minmax(0,1fr);min-height:88px;padding:7px;transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease}
        .morrow-product-grid.is-design-multiChips .morrow-product-card:hover{border-color:var(--site-primary);box-shadow:0 16px 38px var(--site-shadow-color);transform:translateY(-2px)}
        .morrow-product-grid.is-design-multiChips .morrow-product-visual{aspect-ratio:1;border-radius:16px;grid-row:auto}
        .morrow-product-grid.is-design-multiChips .morrow-product-visual>span:not(.morrow-product-placeholder){font-size:9px;left:6px;padding:4px 6px;top:6px}
        .morrow-product-grid.is-design-multiChips .morrow-product-copy{align-items:center;display:grid;gap:10px;grid-template-columns:minmax(0,1fr) auto;padding:6px 10px 6px 12px}
        .morrow-product-grid.is-design-multiChips .morrow-product-copy div{min-width:0}
        .morrow-product-grid.is-design-multiChips .morrow-product-copy span{font-size:9px}
        .morrow-product-grid.is-design-multiChips .morrow-product-copy a{display:-webkit-box;font-size:14px;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2}
        .morrow-product-grid.is-design-multiChips .morrow-product-copy>strong{font-size:14px;margin:0;white-space:nowrap}
        .morrow-product-grid.is-design-multiChips .morrow-product-meta,.morrow-product-grid.is-design-multiChips .morrow-favorite{display:none}
        @media(max-width:767px){.morrow-product-grid.is-design-multiChips .morrow-product-card{grid-template-columns:64px minmax(0,1fr);min-height:78px}.morrow-product-grid.is-design-multiChips .morrow-product-copy{display:block;padding-inline:9px}.morrow-product-grid.is-design-multiChips .morrow-product-copy>strong{display:block;margin-top:4px}}

        /* The committed Morrow preview is also the runtime layout contract. */
        .morrow-region-header{padding-inline:clamp(18px,2vw,24px)!important}
        .morrow-region-header .morrow-region-inner{position:relative}
        .morrow-region-header .morrow-canvas-grid{column-gap:14px;row-gap:0}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=announcement]{align-items:center;background:var(--morrow-green);grid-column:1/-1!important;grid-row:1!important;min-height:30px;padding-inline:24px}
        .morrow-region-header [data-morrow-role=announcementText]{color:#fff!important;font-size:10px!important;font-weight:800!important;justify-content:center;letter-spacing:.14em!important;line-height:1!important;text-transform:uppercase}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role]{align-items:center}
        @media(width > ${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){.morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-show-desktop=false]{display:none!important}}
        .morrow-brand-link{align-items:center;display:flex!important;gap:10px;height:100%!important;min-height:0!important;min-width:0}
        .morrow-brand-link>.morrow-column-content{height:auto;max-width:100%;min-width:0;width:auto}
        .morrow-brand-link [data-morrow-logo-kind=mark]{align-items:center;display:flex;flex:0 0 auto;max-height:54px;max-width:100%}
        .morrow-brand-link [data-morrow-logo-kind=mark] :is(.morrow-inner-image,.morrow-inner-icon,.morrow-media-placeholder){max-height:54px;max-width:min(160px,100%);object-fit:contain}
        .morrow-brand-fallback{font-size:25px;font-weight:850;letter-spacing:-.04em;line-height:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .morrow-brand-link[data-morrow-header-alignment=center]{justify-content:center!important}
        .morrow-brand-link[data-morrow-header-alignment=right]{justify-content:flex-end!important}
        .morrow-region-header .morrow-search-form{border-radius:14px;min-height:42px;padding-inline:14px}
        .morrow-region-header .morrow-search-form button{background:transparent;color:var(--morrow-ink);height:32px;width:28px}
        .morrow-region-header .morrow-header-action{align-self:center;background:transparent!important;border:0!important;border-radius:12px!important;color:var(--morrow-ink)!important;height:42px!important;min-height:42px!important;padding:0!important}
        .morrow-region-header .morrow-header-action .morrow-column-content{justify-content:center}
        .morrow-region-header .morrow-cart-action .morrow-inner-icon{color:var(--morrow-ink)!important}
        .morrow-region-header .morrow-header-cart-count{right:0;top:5px}
        .morrow-header-menu{align-items:center;display:flex!important;height:100%!important;min-height:0!important;overflow:visible!important}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerMenu]{position:static}
        .morrow-header-menu>.morrow-shared-navigation{height:100%;min-width:0;position:static;width:100%}
        .morrow-header-menu>.morrow-shared-navigation .site-navigation-primary{height:100%;gap:clamp(2px,.45vw,8px);max-width:100%;overflow-x:auto;overscroll-behavior-inline:contain;scrollbar-width:none}
        .morrow-header-menu>.morrow-shared-navigation .site-navigation-primary::-webkit-scrollbar{display:none}
        .morrow-header-menu>.morrow-shared-navigation .site-navigation-primary-item{flex:0 0 auto}
        .morrow-header-menu>.morrow-shared-navigation :is(.site-navigation-primary-trigger,.site-navigation-primary-link){font-size:clamp(10px,.95vw,12px);font-weight:800;gap:3px;letter-spacing:.01em;padding-inline:clamp(3px,.4vw,6px)}
        .morrow-header-menu[data-morrow-header-alignment=center]>.morrow-shared-navigation .site-navigation-primary{justify-content:center}
        .morrow-header-menu[data-morrow-header-alignment=right]>.morrow-shared-navigation .site-navigation-primary{justify-content:flex-end}
        .morrow-header-selector-role{align-items:center;background:var(--morrow-ink)!important;color:#fff!important;display:flex!important;height:40px!important;min-height:40px!important;overflow:visible;padding:0!important}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerLanguageSelector]{justify-self:start;width:50%;z-index:2}
        .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerCurrencySelector]{justify-self:end;width:50%;z-index:2}
        .morrow-header-selector-role.is-language{border-radius:13px 0 0 13px!important}
        .morrow-header-selector-role.is-currency{border-left:1px solid rgba(255,255,255,.2)!important;border-radius:0 13px 13px 0!important}
        .morrow-selector{align-items:center;display:inline-flex;gap:3px;justify-content:center;min-width:0;position:relative;width:100%}
        .morrow-selector-icon{align-items:center;display:inline-flex;flex:0 0 auto}
        .morrow-selector select{background:transparent;border:0;color:inherit;font:inherit;font-size:10px;font-weight:800;min-width:0;outline:0;padding:0;width:auto}
        .morrow-selector.is-icon select{cursor:pointer;height:100%;inset:0;opacity:0;position:absolute;width:100%}
        .morrow-header-selector-role option{background:#fff;color:var(--morrow-ink)}

        .morrow-region-header.is-legacy-header{min-height:124px!important}
        .morrow-region-header.is-legacy-header .morrow-canvas-grid{grid-template-rows:30px 94px!important;min-height:124px!important}
        .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=brand]{grid-column:1/span 2!important;grid-row:2!important}
        .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=headerSearch]{grid-column:6/span 4!important;grid-row:2!important}
        .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=accountAction]{grid-column:10/span 1!important;grid-row:2!important}
        .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=cartAction]{grid-column:11/span 1!important;grid-row:2!important}
        .morrow-region-header.is-legacy-header .morrow-navigation{border:0;height:94px;inset:30px 0 auto;margin:0;min-height:0;pointer-events:none;position:absolute}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-shared-navigation{height:94px;margin-left:calc(16.6667% + 14px);pointer-events:auto;position:static;width:calc(25% - 20px)}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-shared-navigation .site-navigation-primary{height:94px;gap:clamp(14px,1.8vw,28px)}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-shared-navigation :is(.site-navigation-primary-trigger,.site-navigation-primary-link){font-size:12px;font-weight:800}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors{align-items:center;background:var(--morrow-ink);border-radius:13px;color:#fff;display:flex;gap:4px;height:40px;justify-content:center;pointer-events:auto;position:absolute;right:0;top:27px;width:calc(8.3333% - 2px)}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors .morrow-selector{display:flex}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors .morrow-selector+.morrow-selector:before{color:rgba(255,255,255,.5);content:"·";margin-right:3px}
        .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors option{background:#fff;color:var(--morrow-ink)}

        .morrow-region-hero{background:var(--site-background)!important;min-height:0!important;padding:18px clamp(18px,2vw,24px)!important}
        .morrow-region-hero .morrow-region-inner{position:relative}
        .morrow-region-hero .morrow-canvas-grid{background:var(--morrow-blue);border-radius:18px;column-gap:0;grid-template-rows:repeat(4,minmax(0,1fr))!important;min-height:368px!important;overflow:hidden;row-gap:0}
        .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=heroCopy]{grid-column:1/span 6!important;grid-row:1/-1!important;z-index:2}
        .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child:has(>.morrow-inner-image),.morrow-region-hero .morrow-canvas-grid>.morrow-grid-child:has(>.morrow-media-placeholder){grid-column:7/span 6!important;grid-row:1/-1!important;overflow:hidden}
        .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child>.morrow-inner-image,.morrow-region-hero .morrow-canvas-grid>.morrow-grid-child>.morrow-media-placeholder{border-radius:0!important;height:368px!important;object-fit:cover;width:100%}
        .morrow-region-hero [data-morrow-role=heroCopy]>.morrow-column{padding:34px 38px 70px!important}
        .morrow-region-hero [data-morrow-role=heroCopy]>.morrow-column>.morrow-column-content{align-content:center!important;display:flex!important;flex-direction:column;gap:14px!important;grid-template-rows:none!important;justify-content:center}
        .morrow-region-hero [data-morrow-role=heroCopy]>.morrow-column>.morrow-column-content>.morrow-column-child:has(.morrow-search-form){display:none!important}
        .morrow-region-hero [data-morrow-role=eyebrow]{font-size:11px!important;letter-spacing:.16em!important}
        .morrow-region-hero [data-morrow-role=title]{font-size:clamp(38px,4vw,54px)!important;letter-spacing:-.045em!important;line-height:.98!important}
        .morrow-region-hero [data-morrow-role=paragraph]{font-size:14px!important;line-height:1.45!important;max-width:520px}
        .morrow-region-hero [data-morrow-role=heroActions] .morrow-column-content{gap:10px!important}
        .morrow-region-hero [data-morrow-role=heroActions] .morrow-inner-button{flex:0 1 auto;min-height:48px!important;padding-inline:22px!important;width:auto!important}
        .morrow-hero-chips{bottom:28px;display:flex;gap:8px;left:38px;position:absolute;z-index:3}
        .morrow-hero-chips a{background:color-mix(in srgb,var(--site-background) 92%,transparent);border:1px solid color-mix(in srgb,var(--site-text) 12%,transparent);border-radius:999px;font-size:10px;font-weight:800;padding:8px 13px;white-space:nowrap}

        .morrow-region-productFeed{background:var(--site-background)!important;min-height:0!important;padding:26px clamp(18px,2vw,24px) 48px!important}
        .morrow-region-productFeed .morrow-canvas-grid{min-height:64px!important}
        .morrow-region-productFeed [data-morrow-role=title]{font-size:clamp(27px,3vw,38px)!important;letter-spacing:-.035em!important}
        .morrow-region-productFeed [data-morrow-role=paragraph]{font-size:13px!important;line-height:1.45!important}
        .morrow-region-productFeed .morrow-product-grid{padding-top:18px}

        @media(max-width:1024px) and (width > ${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){

            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=headerSearch]{grid-column:5/span 4!important}
            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=accountAction]{grid-column:9/span 1!important}
            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=cartAction]{grid-column:10/span 1!important}
            .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-shared-navigation{width:calc(16.6667% - 12px)}
            .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors{right:0;width:calc(16.6667% - 8px)}
        }

        @media(max-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){
            .morrow-region-header{min-height:146px!important;padding-inline:18px!important}
            .morrow-region-header .morrow-canvas-grid{column-gap:8px;display:grid!important;grid-template-columns:42px minmax(0,1fr) 42px 42px;grid-template-rows:30px 62px 54px!important;min-height:146px!important;padding-top:0;row-gap:0}
            .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=announcement]{display:flex;grid-column:1/-1!important;grid-row:1!important;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);padding-inline:16px;width:100vw}
            .morrow-region-header [data-morrow-role=announcementText]{font-size:8px!important;letter-spacing:.09em!important}
            .morrow-region-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-show-mobile=false]{display:none!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerMenu]{grid-column:1!important;grid-row:2!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerLogo]{grid-column:2!important;grid-row:2!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerAccountButton]{grid-column:3!important;grid-row:2!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerCartButton]{grid-column:4!important;grid-row:2!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child[data-morrow-header-role=headerSearch]{grid-column:1/-1!important;grid-row:3!important}
            .morrow-region-header:not(.is-legacy-header) .morrow-canvas-grid>.morrow-grid-child:is([data-morrow-header-role=headerLanguageSelector],[data-morrow-header-role=headerCurrencySelector],[data-morrow-header-role=headerStoresButton],[data-morrow-header-role=headerFavoritesButton]){display:none!important}
            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=brand]{grid-column:2!important;grid-row:2!important}
            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=accountAction]{grid-column:3!important;grid-row:2!important}
            .morrow-region-header.is-legacy-header .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=cartAction]{grid-column:4!important;grid-row:2!important}
            .morrow-region-header.is-legacy-header .morrow-grid-child:has(.morrow-search-form){grid-column:1/-1!important;grid-row:3!important}
            .morrow-brand-link{height:62px!important;gap:8px;padding-left:2px!important}
            .morrow-brand-link [data-morrow-logo-kind=mark]{max-height:46px}
            .morrow-brand-link [data-morrow-logo-kind=mark] :is(.morrow-inner-image,.morrow-inner-icon,.morrow-media-placeholder){max-height:46px;max-width:132px}
            .morrow-brand-fallback{font-size:21px}
            .morrow-region-header .morrow-search-form{min-height:42px}
            .morrow-header-menu>.morrow-shared-navigation{display:none}
            .morrow-header-menu>.morrow-mobile-menu-button{align-items:center;background:transparent;border:0;display:flex;height:42px;justify-content:center;position:static;width:42px}
            .morrow-region-header.is-legacy-header .morrow-navigation{height:62px;inset:30px 0 auto}
            .morrow-region-header.is-legacy-header .morrow-navigation>.morrow-shared-navigation,.morrow-region-header.is-legacy-header .morrow-navigation>.morrow-header-selectors{display:none}
            .morrow-region-header.is-legacy-header .morrow-mobile-menu-button{align-items:center;display:flex;height:42px;justify-content:center;left:0;pointer-events:auto;position:absolute;top:10px;width:42px}
            .morrow-region-hero{padding:14px 18px 22px!important}
            .morrow-region-hero .morrow-canvas-grid{display:grid!important;grid-template-columns:1fr;grid-template-rows:auto 232px!important;min-height:0!important}
            .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child[data-morrow-role=heroCopy]{grid-column:1!important;grid-row:1!important}
            .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child:has(>.morrow-inner-image),.morrow-region-hero .morrow-canvas-grid>.morrow-grid-child:has(>.morrow-media-placeholder){grid-column:1!important;grid-row:2!important;min-height:232px}
            .morrow-region-hero .morrow-canvas-grid>.morrow-grid-child>.morrow-inner-image,.morrow-region-hero .morrow-canvas-grid>.morrow-grid-child>.morrow-media-placeholder{aspect-ratio:auto;height:232px!important;min-height:232px;object-position:center 58%}
            .morrow-region-hero [data-morrow-role=heroCopy]>.morrow-column{padding:25px 24px 24px!important}
            .morrow-region-hero [data-morrow-role=heroCopy]>.morrow-column>.morrow-column-content{gap:12px!important}
            .morrow-region-hero [data-morrow-role=title]{font-size:clamp(34px,10vw,42px)!important}
            .morrow-region-hero [data-morrow-role=paragraph]{font-size:13px!important}
            .morrow-region-hero [data-morrow-role=heroActions] .morrow-column-content{align-items:stretch!important;display:grid!important;grid-template-columns:1fr 1fr!important}
            .morrow-region-hero [data-morrow-role=heroActions] .morrow-inner-button{font-size:11px!important;min-height:44px!important;padding-inline:12px!important;width:100%!important}
            .morrow-hero-chips{bottom:auto;left:auto;margin-top:12px;overflow-x:auto;padding-bottom:2px;position:relative}
            .morrow-hero-chips a{flex:0 0 auto}
            .morrow-region-productFeed{padding-top:24px!important}
            .morrow-region-productFeed .morrow-canvas-grid{gap:10px!important}
            .morrow-region-productFeed [data-morrow-role=productFeedIntro]{display:none}
            .morrow-region-productFeed [data-morrow-role=productFeedAction]{max-width:170px}
            .morrow-mobile-panel>.morrow-shared-mobile-navigation{width:100%}
            .morrow-mobile-utility-links{display:grid;gap:8px;grid-template-columns:repeat(2,minmax(0,1fr))}
            .morrow-mobile-utility-links>a,.morrow-mobile-selector-utility{align-items:center;border:1px solid var(--site-border);border-radius:12px;display:flex;font-size:12px;font-weight:800;gap:8px;justify-content:center;min-height:44px;padding:8px 10px}
            .morrow-mobile-selector-utility{background:var(--morrow-ink);color:#fff;min-width:0}
            .morrow-mobile-selector-utility .morrow-selector{display:flex;width:100%}
            .morrow-mobile-selector-utility .morrow-selector select{color:#fff}
            .morrow-mobile-selector-utility .morrow-selector option{background:#fff;color:var(--morrow-ink)}
            .morrow-mobile-panel .morrow-header-selectors{background:var(--morrow-ink);border-radius:13px;color:#fff;display:flex;justify-content:center;padding:12px}
            .morrow-mobile-panel .morrow-selector{display:flex!important}
            .morrow-mobile-panel .morrow-selector select{color:#fff}
            .morrow-mobile-panel .morrow-selector option{background:#fff;color:var(--morrow-ink)}
        }
        ${morrowSemanticThemeCss}
        @media(min-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX + 1}px){
            .morrow-region[data-shop-block-index][data-shop-block-type] .morrow-canvas-grid[data-site-builder-authored-grid-columns="true"]{grid-template-columns:var(--site-builder-authored-grid-columns)!important}
            .morrow-region[data-shop-block-index][data-shop-block-type] .morrow-canvas-grid[data-site-builder-authored-grid-rows="true"]{grid-template-rows:var(--site-builder-authored-grid-rows)!important}
            .morrow-region[data-shop-block-index][data-shop-block-type] .morrow-canvas-grid>.morrow-grid-child[data-site-builder-authored-grid-column="true"]{grid-column:var(--site-builder-authored-grid-column)!important}
            .morrow-region[data-shop-block-index][data-shop-block-type] .morrow-canvas-grid>.morrow-grid-child[data-site-builder-authored-grid-row="true"]{grid-row:var(--site-builder-authored-grid-row)!important}
        }
        @media(min-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX + 1}px){
            ${SITE_BUILDER_AUTHORITATIVE_STYLE_CSS}
        }
    `}</style>;
}

function MorrowStorefrontView({ runtime }: { runtime: ShopStorefrontRuntimeModel }) {
    const header = (runtime.pageContentBlocks ?? [])
        .map((block) => findShopHeaderRoot(block))
        .find((block): block is ShopTemplateBlock => block !== null) ?? null;

    return (
        <div className="morrow-storefront" lang={storefrontLocale(runtime.storefrontLanguageCode)} dir={textDirection(runtime.storefrontLanguageCode)}>
            <MorrowStyles/>
            <MorrowPage runtime={runtime}/>
            <MorrowMobileMenu header={header} runtime={runtime}/>
            <MorrowSearchOverlay runtime={runtime}/>
            <MorrowCartDrawer runtime={runtime}/>
            <div className={runtime.toastVisible ? "morrow-toast is-visible" : "morrow-toast"}>{runtime.storefrontMessage("ui.newsletter.subscribed")}</div>
        </div>
    );
}

export function MorrowTemplateClient(props: ShopStorefrontRuntimeProps) {
    return (
        <ShopStorefrontRuntime {...props} fallbackSiteName="Morrow" previewAdapter={morrowPreviewAdapter}>
            {(runtime) => <MorrowStorefrontView runtime={runtime}/>}
        </ShopStorefrontRuntime>
    );
}
